import React,{useState,useEffect,useCallback,useRef,createContext,useContext} from 'react';
import {Routes,Route,useNavigate,useLocation,Navigate,Link,useParams} from 'react-router-dom';
import axios from 'axios';
import { IntelSearchPage, CampaignPage, AdvancedTrapsPage, ThreatIntelPage, DomainManagerPage, WorkflowPage, IntegrationsPage, CaseBuilderPage, AdvancedOsintPage } from './pages/extensions';
const canaryGif = "/thisIsCanaryGif.gif";
import { motion, AnimatePresence } from "framer-motion";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import {
  Rocket, Activity, FileText, ShieldAlert, FolderOpen, User, Users,
  Target, Network, Radar, Search, Image as ImageIcon, Shield, Settings,
  BarChart3, LifeBuoy, Link2, ChevronUp, ChevronDown, Lock,
  Globe, Camera, Mail, Film, Type, Cpu, Zap, Edit3,
  AlertTriangle, CheckCircle, XCircle, Info, Bell, Eye, EyeOff,
  Download, Upload, Copy, Trash2, Plus, Minus, RefreshCw,
  ZoomIn, ZoomOut, Maximize2, Minimize2, PanelLeftClose, PanelRightClose,
  PanelLeftOpen, PanelRightOpen, LayoutDashboard, Grip,
  Layers, Map, MessageSquare, Hash, Tag, Flag, Star,
  ArrowLeft, ArrowRight, MoreHorizontal, Package, Cpu as CpuIcon,
  Wifi, Navigation, Fingerprint, Terminal, Code, GitBranch,
  CheckSquare, Square, Radio, Crosshair, ScanLine, Bot, Wand2,
  Moon, Sun, Monitor, CreditCard, Folder} from 'lucide-react';

// ═══ THEME SYSTEM ═══
const THEMES = ['dark', 'system', 'light'];
const THEME_ICONS = { dark: 'dark', system: 'system', light: 'light' }; // unused - icons via Lucide

function useTheme() {
  const [theme, setThemeState] = React.useState(() => {
    return localStorage.getItem('zx_theme') || 'dark';
  });

  const applyTheme = React.useCallback((t) => {
    const root = document.documentElement;
    if (t === 'system') {
      const sys = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
      root.setAttribute('data-theme', sys);
    } else {
      root.setAttribute('data-theme', t);
    }
  }, []);

  React.useEffect(() => {
    applyTheme(theme);
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      const handler = () => applyTheme('system');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme, applyTheme]);

  const setTheme = (t) => {
    setThemeState(t);
    localStorage.setItem('zx_theme', t);
  };

  return { theme, setTheme };
}

function ThemeToggle({ theme, setTheme, compact = false }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const labels = { dark: 'Dark', system: 'System', light: 'Light' };
  const icons = {
    dark: <Moon size={14}/>,
    system: <Monitor size={14}/>,
    light: <Sun size={14}/>,
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="h-9 w-9 rounded-xl border border-[#1a1a1a] bg-[#050505] flex items-center justify-center text-[#777] hover:text-white hover:border-[#333] transition"
        title={`Theme: ${labels[theme]}`}
      >
        {icons[theme]}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-36 border border-[#1a1a1a] bg-[#050505] rounded-2xl overflow-hidden shadow-2xl z-50">
          {THEMES.map(t => (
            <button key={t} onClick={() => { setTheme(t); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-[13px] transition ${theme===t?'text-white bg-[#0d0d0d]':'text-[#666] hover:text-white hover:bg-[#080808]'}`}>
              <span className="flex-shrink-0">{icons[t]}</span>
              {labels[t]}
              {theme===t && <CheckCircle size={11} className="ml-auto text-emerald-400"/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const API=process.env.REACT_APP_API_BASE_URL||'';

const EYE = ({ children, className = '' }) => (
  <div className={`text-[10px] font-black uppercase tracking-[0.2em] text-[#333] mb-2 ${className}`}>
    {children}
  </div>
);
// Icon mapper for canary token types
const CANARY_ICON_MAP = {
  globe: Globe, link: Link2, mail: Mail, file: FileText,
  'file-text': FileText, image: ImageIcon, zap: Zap,
  search: Search, radio: Radio, scan: ScanLine,
};
const CanaryIcon = ({ name, size=18 }) => {
  const Icon = CANARY_ICON_MAP[name] || Zap;
  return <Icon size={size}/>;
};
const api=axios.create({baseURL:API});
api.interceptors.request.use(c => {
  const t = localStorage.getItem('zx_t');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use(r=>r,e=>{if(e.response?.status===401){localStorage.removeItem('zx_t');localStorage.removeItem('zx_u');window.location.href='/login';}return Promise.reject(e);});
const E=e=>e?.response?.data?.detail||e?.message||'Error';

// ═══ LOGO ═══
const ZIcon=({s=14,c='#fff'})=><svg width={s} height={s} viewBox="0 0 12.42 12.43"><path fill={c} d="M12.42,3.47c0-.5-.4-.9-.9-.9h-1.67L7.28,0h-3.83c-.5,0-.9.4-.9.9v1.67h.01L0,5.14v3.83c0,.5.4.9.9.9h1.66l2.56,2.56h3.83c.5,0,.9-.4.9-.9v-1.66l2.57-2.57v-3.83ZM2.57,9.03V3.4c0-.46.37-.83.83-.83h5.63c.46,0,.83.37.83.83v5.64c0,.46-.37.83-.83.83H3.4c-.46,0-.83-.37-.83-.83Z"/></svg>;

// ═══ Context ═══
const ToastCtx=createContext();const useToast=()=>useContext(ToastCtx);
function ToastProvider({children}){const[ts,sT]=useState([]);const show=(m,t='info')=>{const id=Date.now();sT(p=>[...p,{id,m,t}]);setTimeout(()=>sT(p=>p.filter(x=>x.id!==id)),3500);};return<ToastCtx.Provider value={show}>{children}<div className="fixed bottom-4 right-4 z-[300] space-y-2">{ts.map(t=><div key={t.id} className={`px-4 py-3 rounded-lg text-[13px] font-medium shadow-lg backdrop-blur ${t.t==='error'?'bg-red-600/90 text-white':t.t==='success'?'bg-emerald-600/90 text-white':'bg-[#222]/90 text-white border border-[#444]'}`}>{t.m}</div>)}</div></ToastCtx.Provider>;}
function useModal(){const[s,sS]=useState(null);const ask=m=>new Promise(r=>{sS({m,r});});const Modal=()=>s?<div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4" onClick={()=>{s.r(false);sS(null);}}><div className="bg-[#111] border border-[#333] rounded-xl p-6 max-w-sm w-full" onClick={e=>e.stopPropagation()}><p className="text-white text-[14px] mb-6">{s.m}</p><div className="flex gap-3 justify-end"><button onClick={()=>{s.r(false);sS(null);}} className="px-4 py-2 rounded-lg text-[13px] text-[#888] border border-[#333]">Cancel</button><button onClick={()=>{s.r(true);sS(null);}} className="px-4 py-2 rounded-lg text-[13px] bg-white text-black">Confirm</button></div></div></div>:null;return{ask,Modal};}
// ═══ UI ═══
const B=({children,v='default'})=>{const s={default:'bg-[#1a1a1a] text-[#888] border-[#333]',success:'bg-emerald-950 text-emerald-400 border-emerald-800',danger:'bg-red-950 text-red-400 border-red-800',warning:'bg-amber-950 text-amber-400 border-amber-800',blue:'bg-blue-950 text-blue-400 border-blue-800'};return<span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${s[v]||s.default}`}>{children}</span>;};
const IR=({l,val})=>val!=null&&String(val).length>0&&val!=='undefined'?<div className="flex items-center justify-between py-1.5 border-b border-[#1a1a1a] last:border-0"><span className="text-[12px] text-[#666]">{l}</span><span className="text-[12px] text-[#ccc] font-mono truncate max-w-[260px]">{String(val)}</span></div>:null;
const SC=({l,v,c})=><div className={`border border-[#222] rounded-xl p-4 bg-[#0a0a0a] ${c||''}`}><p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">{l}</p><p className="text-[22px] font-light text-white">{v??0}</p></div>;
const Inp=({label,...p})=><div>{label&&<label className="block text-[12px] text-[#888] mb-1">{label}</label>}<input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] focus:border-[#555] outline-none placeholder:text-[#333]" {...p}/></div>;
const PwI=({label,...p})=>{const[s,sS]=useState(false);return<div>{label&&<label className="block text-[12px] text-[#888] mb-1">{label}</label>}<div className="relative"><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none pr-14" type={s?'text':'password'} {...p}/><button type="button" onClick={()=>sS(!s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#555]">{s?'Hide':'Show'}</button></div></div>;};
const Btn=({children,v='primary',className:cn='',...p})=>{const s={primary:'bg-white text-black hover:bg-gray-200',secondary:'bg-[#111] text-white border border-[#333] hover:border-[#555]',danger:'bg-red-950 text-red-400 border border-red-900 hover:bg-red-900',ghost:'bg-transparent text-[#888] hover:text-white'};return<button className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all disabled:opacity-30 ${s[v]||s.primary} ${cn}`} {...p}>{children}</button>;};
const Tog=({l,c,o})=><div className="flex items-center justify-between py-2 border-b border-[#1a1a1a]"><span className="text-[12px] text-[#888]">{l}</span><button type="button" onClick={o} className={`w-9 h-5 rounded-full relative transition ${c?'bg-white':'bg-[#333]'}`}><div className={`w-3.5 h-3.5 rounded-full absolute top-[3px] transition-all ${c?'right-[3px] bg-black':'left-[3px] bg-[#555]'}`}/></button></div>;
const Panel=({title,sub,extra,children})=><div className="border border-[#222] rounded-xl bg-[#0a0a0a]"><div className="px-5 py-3 border-b border-[#1a1a1a] flex items-center justify-between gap-2"><div className="min-w-0"><h2 className="text-[14px] font-semibold text-white">{title}</h2>{sub&&<p className="text-[13px] text-[#444] mt-0.5">{sub}</p>}</div>{extra&&<div className="flex-shrink-0 flex gap-2 items-center">{extra}</div>}</div><div className="p-5">{children}</div></div>;
const CopyBtn=({text})=>{const t=useToast();const copy=async()=>{if(navigator.clipboard&&navigator.clipboard.writeText){try{await navigator.clipboard.writeText(text);t('Copied','success');return;}catch(e){}}const el=document.createElement('textarea');el.value=text;document.body.appendChild(el);el.select();document.execCommand('copy');document.body.removeChild(el);t('Copied','success');};return<button onClick={copy} className="text-[10px] text-[#666] hover:text-white border border-[#333] px-2 py-0.5 rounded">Copy</button>;};
const Tabs=({tabs,active,onChange})=><div className="flex border border-[#222] rounded-lg overflow-hidden flex-wrap">{tabs.map(t=><button key={t.id} onClick={()=>onChange(t.id)} className={`px-3 py-1.5 text-[12px] transition ${active===t.id?'bg-white text-black':'text-[#666] hover:text-white hover:bg-[#111]'}`}>{t.l}</button>)}</div>;
function PSearch({onSelect}){const[q,sQ]=useState('');const[o,sO]=useState(false);const[r,sR]=useState([]);const ref=useRef();useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))sO(false);};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);return<div ref={ref} className="relative"><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-1.5 text-[12px] text-white rounded-lg outline-none" placeholder="Link to profile..." value={q} onChange={e=>{sQ(e.target.value);sO(true);if(e.target.value)api.get(`/api/v1/profiles/search?q=${encodeURIComponent(e.target.value)}`).then(x=>sR(x.data)).catch(()=>sR([]));}} onFocus={()=>{sO(true);if(!q)api.get('/api/v1/profiles/search?q=').then(x=>sR(x.data.slice(0,5))).catch(()=>{});}}/>
  {o&&r.length>0&&<div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-[#333] rounded-lg overflow-hidden z-50 max-h-40 overflow-y-auto">{r.map(p=><button key={p.id} onClick={()=>{onSelect(p.id);sO(false);sQ('');}} className="w-full text-left px-3 py-2 hover:bg-[#1a1a1a] text-[12px] text-white border-b border-[#1a1a1a]">{p.codename}</button>)}</div>}</div>;}

// ═══ CD ═══
function CD({r}){if(!r)return null;const ta=r.Threat_Analysis_Engine||{};const nt=r.Network_Topology||{};const hw=r.Hardware_Fingerprint||{};const pc=r.Physical_Context||{};const sys=r.system||{};
  const vpn=nt.VPN_Detected||String(JSON.stringify(ta.Analysis_Factors||[])).toLowerCase().includes('vpn');
  return<div className="space-y-3"><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
    <div className="bg-[#060606] rounded-lg p-3 border border-[#1a1a1a]"><p className="text-[10px] text-emerald-500 uppercase font-bold mb-2">Network</p>{vpn&&<div className="bg-amber-950 border border-amber-700 rounded px-2 py-1 mb-2 text-center"><span className="text-[10px] text-amber-400 font-bold">VPN/PROXY</span></div>}<IR l="IP" val={nt.Public_IP}/><IR l="ISP" val={nt.ISP}/><IR l="GeoIP" val={nt.Spoofed_GeoIP}/><IR l="Local" val={nt.Local_IP_Leak}/>{nt.GPS_Coordinates&&<IR l="GPS" val={`${nt.GPS_Coordinates.lat?.toFixed(4)},${nt.GPS_Coordinates.lon?.toFixed(4)}`}/>}</div>
    <div className="bg-[#060606] rounded-lg p-3 border border-[#1a1a1a]"><p className="text-[10px] text-blue-400 uppercase font-bold mb-2">Fingerprint</p><IR l="GPU" val={hw.GPU_Renderer}/><IR l="Screen" val={hw.Screen_Metrics}/><IR l="Cores" val={hw.Hardware_Concurrency}/><IR l="Platform" val={hw.Platform}/></div>
    <div className="bg-[#060606] rounded-lg p-3 border border-[#1a1a1a]"><p className="text-[10px] text-amber-400 uppercase font-bold mb-2">Behavioral</p><IR l="Risk" val={ta.OPSEC_Risk_Level}/><IR l="Score" val={ta.Risk_Score}/><IR l="TZ" val={pc.timezone}/><IR l="Lang" val={pc.language}/></div>
    <div className="bg-[#060606] rounded-lg p-3 border border-[#1a1a1a]"><p className="text-[10px] text-red-400 uppercase font-bold mb-2">OPSEC</p><div className={`p-2 rounded mb-1 ${ta.OPSEC_Risk_Level==='CRITICAL'?'bg-red-950':'bg-emerald-950'}`}><p className={`text-[11px] ${ta.OPSEC_Risk_Level==='CRITICAL'?'text-red-400':'text-emerald-400'}`}>{ta.System_Verdict||'—'}</p></div>{(ta.Analysis_Factors||[]).map((f,i)=><p key={i} className="text-[10px] text-[#888] pl-2 border-l border-[#333] mt-1">{f}</p>)}</div>
  </div>
  {(sys.permissions_granted||sys.location||sys.camera_capture)&&<div className="bg-[#060606] rounded-lg p-3 border border-[#1a1a1a]"><p className="text-[10px] text-amber-400 uppercase font-bold mb-2">Permissions</p><div className="grid grid-cols-2 md:grid-cols-4 gap-2">{sys.location&&<div className="border border-emerald-800 bg-emerald-950 rounded p-2"><p className="text-[10px] text-emerald-400">Location</p><p className="text-[11px] text-white font-mono">{sys.location.lat?.toFixed(4)},{sys.location.lon?.toFixed(4)}</p></div>}{sys.camera_capture&&<div className="border border-emerald-800 bg-emerald-950 rounded p-2"><p className="text-[10px] text-emerald-400">Camera</p><img src={sys.camera_capture} alt="" className="w-full h-16 object-cover rounded mt-1"/></div>}{sys.permissions_granted&&Object.entries(sys.permissions_granted).map(([k,v])=><div key={k} className={`border rounded p-2 ${v==='granted'?'border-emerald-800 bg-emerald-950':'border-red-900 bg-red-950'}`}><p className="text-[10px] text-[#888]">{k}</p><p className={`text-[11px] ${v==='granted'?'text-emerald-400':'text-red-400'}`}>{v}</p></div>)}</div></div>}</div>;}

const pageReveal = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut"
    }
  }
}


const HUB_SECTIONS = {
  operations: {
    title: 'Operations',
    eyebrow: 'Collection Workspace',
    description: 'Launch collection workflows and move quickly between active operational surfaces.',
    cards: [
      { title: 'Deploy', fkey: 'deploy', desc: 'Create and manage collection links, templates, and live operational assets.', to: '/deploy', icon: Rocket },
      { title: 'Feed', fkey: 'feed', desc: 'Review incoming captures, telemetry, and fresh operational activity.', to: '/feed', icon: Activity },
      { title: 'Links', fkey: 'links', desc: 'Manage generated links, track visitors, and control operational status.', to: '/links', icon: Link2 },
      { title: 'Pages', fkey: 'pages', desc: 'Manage hosted landing pages and supporting operational surfaces.', to: '/pages', icon: FileText },
      { title: 'Canary', fkey: 'canary', desc: 'Track beaconed documents, canary assets, and alerting workflows.', to: '/canary', icon: ShieldAlert },
    ],
  },
  investigations: {
    title: 'Investigations',
    eyebrow: 'Analysis Workspace',
    description: 'Move from raw collection into profiles, cases, and graph-driven investigative workflows.',
    cards: [
      { title: 'Cases', fkey: 'investigations', desc: 'Open investigations, review evidence, and manage case progression.', to: '/investigations', icon: FolderOpen },
      { title: 'Profiles', fkey: 'profiles', desc: 'Link captures to identities, entities, and structured subject profiles.', to: '/profiles', icon: User },
      { title: 'Graph Intelligence', fkey: 'graph', desc: 'Visualize relationships between entities, indicators, and activity.', to: '/graph', icon: Network },
    ],
  },
  osint: {
    title: 'OSINT',
    eyebrow: 'Open-Source Intelligence',
    description: 'Access reconnaissance tooling from one organized intelligence workspace.',
    cards: [
      { title: 'Workspace', fkey: 'osint', desc: 'Use the main OSINT environment for lookups, enrichment, and collection support.', to: '/osint', icon: Radar },
      { title: 'Dorking', fkey: 'dorking', desc: 'Generate and manage structured search operators for external discovery.', to: '/dorking', icon: Search },
      { title: 'EXIF', fkey: 'exif', desc: 'Inspect image metadata and extract technical clues from media artifacts.', to: '/exif', icon: ImageIcon },
    ],
  },
  control: {
    title: 'Control',
    eyebrow: 'Administrative Control',
    description: 'Monitor the platform, inspect analytics, and access administrative control surfaces.',
    cards: [
      { title: 'SOC', fkey: 'soc', desc: 'Review monitoring, alerts, operational health, and platform watchpoints.', to: '/soc', admin: true, icon: Shield },
      { title: 'Admin', fkey: 'admin', desc: 'Manage users, access levels, restrictions, and platform-wide settings.', to: '/admin', admin: true, icon: Settings },
      { title: 'Analytics', fkey: 'analytics', desc: 'Inspect usage, activity trends, and high-level operational metrics.', to: '/analytics', icon: BarChart3 },
    ],
  },
  system: {
    title: 'System',
    eyebrow: 'Platform Services',
    description: 'Access support and configuration pages from one focused system layer.',
    cards: [
      { title: 'Support', fkey: 'support', desc: 'Open tickets, review assistance requests, and follow platform support items.', to: '/tickets', icon: LifeBuoy },
      { title: 'Settings', fkey: 'settings', desc: 'Update your account, security settings, and workspace preferences.', to: '/settings', icon: Settings },
      { title: 'Your Domains', fkey: 'domains', desc: 'Add and manage custom domains for your deployments. Connect your own domain for full operational privacy.', to: '/my-domains', icon: Globe },
    ],
  },
};

function HubPage({ config, user }) {
  const nav = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isMonitor = user?.role === 'monitor';

  let cards = config?.cards || [];
  
  // Filter for admin only cards
  cards = cards.filter((card) => !card.admin || isAdmin);

  // Monitor role restriction: ONLY SOC
  if (isMonitor) {
    cards = cards.filter(c => c.to === '/soc');
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="border border-[#1a1a1a] rounded-[28px] bg-gradient-to-b from-[#070707] to-black p-6 md:p-9"
      >
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#666] mb-4">{config.eyebrow}</p>
        <div className="max-w-[820px] space-y-4">
          <h1 className="text-[32px] md:text-[54px] leading-[0.95] tracking-tight font-semibold text-white">
            {config.title}
          </h1>
          <p className="text-[14px] md:text-[16px] text-[#7a7a7a] leading-[1.8]">
            {config.description}
          </p>
        </div>
      </motion.div>

      <div className={`grid gap-4 ${cards.length === 2 ? 'md:grid-cols-2' : cards.length === 3 ? 'md:grid-cols-2 xl:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {cards.map((card, i) => {
          const Icon = card.icon;
          const isLocked = user?.visible_features?.[card.fkey] === false;

          return (
            <motion.button
              key={card.to}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              onClick={() => !isLocked && nav(card.to)}
              className={`text-left group relative border rounded-[28px] p-6 transition-all duration-500 overflow-hidden ${
                isLocked 
                  ? 'border-red-900/30 bg-[#080202] cursor-not-allowed' 
                  : 'border-[#1a1a1a] bg-[#050505] hover:border-[#333] hover:shadow-[0_0_40px_-15px_rgba(255,255,255,0.05)]'
              }`}
            >
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                isLocked 
                  ? 'bg-gradient-to-br from-red-950/60 to-red-900/20 text-red-500' 
                  : 'bg-[#0f0f0f] border border-[#1a1a1a] text-white group-hover:bg-white group-hover:text-black group-hover:scale-110'
              }`}>
                {isLocked ? (
                  <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                    <Lock size={20} />
                  </motion.div>
                ) : (
                  <Icon size={20} />
                )}
              </div>
              <h3 className={`text-[17px] font-semibold mb-2 transition-colors duration-500 ${isLocked ? 'text-red-500/80' : 'text-white group-hover:text-white'}`}>{card.title}</h3>
              <p className={`text-[13px] leading-relaxed transition-colors duration-500 ${isLocked ? 'text-red-950' : 'text-[#444] group-hover:text-[#888]'}`}>
                {isLocked ? 'System access restricted. This operational module is currently locked by administration.' : card.desc}
              </p>
              
              {isLocked && (
                <div className="absolute inset-0 bg-red-950/5 pointer-events-none backdrop-blur-[1px]" />
              )}

              {isLocked && (
                <div className="absolute top-6 right-6">
                  <div className="flex items-center gap-1 bg-red-950/40 border border-red-900/50 px-2 py-1 rounded-lg">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-[9px] text-red-500 font-bold uppercase tracking-wider">Restricted</span>
                  </div>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}


const FOOTER_GROUPS = {
  company: "Company",
  services: "Services",
  legal: "Legal",
  support: "Support",
};

const defaultFooterLinks = {
  company: [
    { title: "About", slug: "about" },
    { title: "Careers", slug: "careers" },
    { title: "Contact", slug: "contact" },
    { title: "Partners", slug: "partners" },
  ],
  services: [
    { title: "Services", slug: "services" },
    { title: "Pricing", slug: "pricing" },
    { title: "Bug Bounty", slug: "bug-bounty" },
    { title: "Blog", slug: "blog", path: "/blog" },
    { title: "Canary Docs", slug: "canary-docs", path: "/canary-docs" },
  ],
  legal: [
    { title: "Terms & Conditions", slug: "terms-and-conditions" },
    { title: "Terms of Use", slug: "terms-of-use" },
    { title: "Privacy Notice", slug: "privacy-notice" },
    { title: "Cookie Policy", slug: "cookie-policy" },
    { title: "Policy Document", slug: "policy-document" },
  ],
  support: [
    { title: "Help Center", slug: "help-center" },
    { title: "Documentation", slug: "documentation" },
    { title: "Contact Support", slug: "contact-support" },
  ],
};

const socialPlatformLabels = {
  social_x: "X",
  social_linkedin: "LinkedIn",
  social_instagram: "Instagram",
  social_facebook: "Facebook",
  social_youtube: "YouTube",
  social_tiktok: "TikTok",
  social_telegram: "Telegram",
  social_whatsapp: "WhatsApp",
};

function buildFooterGroups(pages = []) {
  const grouped = { company: [], services: [], legal: [], support: [] };
  pages.forEach((page) => {
    const group = page.nav_group && grouped[page.nav_group] ? page.nav_group : "company";
    grouped[group].push(page);
  });
  Object.keys(grouped).forEach((key) => {
    if (!grouped[key].length) grouped[key] = defaultFooterLinks[key];
  });
  return grouped;
}

function buildSocialLinks(settings = {}) {
  return Object.entries(socialPlatformLabels)
    .map(([key, label]) => ({ key, label, url: settings[key] || "" }))
    .filter((item) => item.url);
}

function LandingBugBountyForm() {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name:'', email:'', handle:'',
    title:'', vuln_type:'xss', severity:'medium',
    cvss_score:'', target_url:'', affected_component:'',
    summary:'', steps_to_reproduce:'', impact:'', remediation:'',
    attachment_url:'', poc_code:''
  });

  const VULN_TYPES = [
    {id:'xss',l:'XSS'},{id:'sqli',l:'SQL Injection'},{id:'rce',l:'RCE'},
    {id:'idor',l:'IDOR'},{id:'ssrf',l:'SSRF'},{id:'csrf',l:'CSRF'},
    {id:'auth_bypass',l:'Auth Bypass'},{id:'broken_access',l:'Access Control'},
    {id:'info_disclosure',l:'Info Disclosure'},{id:'xxe',l:'XXE'},
    {id:'path_traversal',l:'Path Traversal'},{id:'crypto',l:'Cryptographic'},
    {id:'misconfiguration',l:'Misconfiguration'},{id:'other',l:'Other'},
  ];
  const SEV_COLORS = {
    critical:'border-red-800 bg-red-950 text-red-400',
    high:'border-orange-800 bg-orange-950 text-orange-400',
    medium:'border-amber-800 bg-amber-950 text-amber-400',
    low:'border-blue-800 bg-blue-950 text-blue-400',
    informational:'border-emerald-800 bg-emerald-950 text-emerald-400'
  };
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const canNext = () => {
    if(step===1) return form.name && form.email;
    if(step===2) return form.title && form.severity;
    if(step===3) return form.summary && form.steps_to_reproduce;
    return true;
  };

  const submit = async() => {
    if(!form.title||!form.summary) return toast('Fill required fields','error');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/v1/public/bug-bounty/report`, form);
      setSubmitted(true);
    } catch(e){ toast(e?.response?.data?.detail||'Error','error'); }
    setSubmitting(false);
  };

  if(submitted) return (
    <div className="border border-emerald-800 bg-emerald-950/20 rounded-[28px] p-8 text-center">
      <div className="mb-4 flex justify-center"><CheckCircle size={40} className="text-emerald-500"/></div>
      <h3 className="text-white text-[22px] font-semibold mb-3">Report Received</h3>
      <p className="text-emerald-300/80 text-[14px] leading-[1.8] mb-6">Your security report has been submitted and will be reviewed by our team. We appreciate your responsible disclosure.</p>
      <button onClick={()=>{setSubmitted(false);setStep(1);setForm({name:'',email:'',handle:'',title:'',vuln_type:'xss',severity:'medium',cvss_score:'',target_url:'',affected_component:'',summary:'',steps_to_reproduce:'',impact:'',remediation:'',attachment_url:'',poc_code:''});}} className="text-[#666] text-[13px] hover:text-white transition">← Submit another report</button>
    </div>
  );

  return (
    <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] overflow-hidden">
      {/* Step indicator */}
      <div className="px-6 pt-6 pb-4 border-b border-[#111]">
        <div className="flex items-center gap-2">
          {[{n:1,l:'Reporter'},{n:2,l:'Vuln'},{n:3,l:'Details'},{n:4,l:'Submit'}].map((s,i)=>(
            <div key={s.n} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${step>s.n?'bg-emerald-500 text-white':step===s.n?'bg-white text-black':'bg-[#111] text-[#444] border border-[#333]'}`}>{step>s.n?<CheckCircle size={12}/>:s.n}</div>
                <span className={`text-[9px] mt-1 uppercase tracking-wider hidden sm:block ${step===s.n?'text-white':'text-[#444]'}`}>{s.l}</span>
              </div>
              {i<3&&<div className={`flex-1 h-px mx-1 ${step>s.n?'bg-emerald-500':'bg-[#1a1a1a]'}`}/>}
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {step===1&&<>
          <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Reporter Information</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[12px] text-[#888] mb-1">Name *</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="Your name" value={form.name} onChange={e=>f('name',e.target.value)}/></div>
            <div><label className="block text-[12px] text-[#888] mb-1">Email *</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" type="email" placeholder="you@example.com" value={form.email} onChange={e=>f('email',e.target.value)}/></div>
          </div>
          <div><label className="block text-[12px] text-[#888] mb-1">Handle / Alias</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="@security_researcher" value={form.handle} onChange={e=>f('handle',e.target.value)}/></div>
        </>}

        {step===2&&<>
          <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Vulnerability Classification</p>
          <div><label className="block text-[12px] text-[#888] mb-1">Title *</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="Stored XSS in profile bio" value={form.title} onChange={e=>f('title',e.target.value)}/></div>
          <div>
            <label className="block text-[12px] text-[#888] mb-2">Vulnerability Type</label>
            <div className="flex flex-wrap gap-2">{VULN_TYPES.map(v=>(
              <button key={v.id} type="button" onClick={()=>f('vuln_type',v.id)} className={`px-3 py-1.5 rounded-lg text-[11px] border transition ${form.vuln_type===v.id?'border-white text-white bg-[#111]':'border-[#222] text-[#555]'}`}>{v.l}</button>
            ))}</div>
          </div>
          <div>
            <label className="block text-[12px] text-[#888] mb-2">Severity *</label>
            <div className="flex gap-2 flex-wrap">{['informational','low','medium','high','critical'].map(s=>(
              <button key={s} type="button" onClick={()=>f('severity',s)} className={`px-4 py-2 rounded-xl text-[12px] border capitalize transition ${form.severity===s?(SEV_COLORS[s]||'border-white text-white'):'border-[#222] text-[#555]'}`}>{s}</button>
            ))}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[12px] text-[#888] mb-1">CVSS Score</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" type="number" min="0" max="10" step="0.1" placeholder="8.5" value={form.cvss_score} onChange={e=>f('cvss_score',e.target.value)}/></div>
            <div><label className="block text-[12px] text-[#888] mb-1">Target URL</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="https://..." value={form.target_url} onChange={e=>f('target_url',e.target.value)}/></div>
          </div>
        </>}

        {step===3&&<>
          <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Technical Details</p>
          <div><label className="block text-[12px] text-[#888] mb-1">Summary *</label><textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] h-20 resize-none outline-none" placeholder="Brief description of the vulnerability..." value={form.summary} onChange={e=>f('summary',e.target.value)}/></div>
          <div><label className="block text-[12px] text-[#888] mb-1">Steps to Reproduce *</label><textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] h-28 resize-none outline-none font-mono" placeholder={'1. Login as user\n2. Navigate to /profile\n3. Enter payload...'} value={form.steps_to_reproduce} onChange={e=>f('steps_to_reproduce',e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[12px] text-[#888] mb-1">Impact</label><textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] h-20 resize-none outline-none" placeholder="Data exposure, account takeover..." value={form.impact} onChange={e=>f('impact',e.target.value)}/></div>
            <div><label className="block text-[12px] text-[#888] mb-1">Remediation</label><textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] h-20 resize-none outline-none" placeholder="Sanitize input, add CSP headers..." value={form.remediation} onChange={e=>f('remediation',e.target.value)}/></div>
          </div>
          <div><label className="block text-[12px] text-[#888] mb-1">Proof of Concept</label><textarea className="w-full bg-black border border-[#222] px-3 py-2 text-emerald-400 rounded-lg text-[12px] h-20 resize-none outline-none font-mono" placeholder="// PoC code or curl command" value={form.poc_code} onChange={e=>f('poc_code',e.target.value)}/></div>
          <div><label className="block text-[12px] text-[#888] mb-1">Evidence URL</label><input className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none" placeholder="https://imgur.com/..." value={form.attachment_url} onChange={e=>f('attachment_url',e.target.value)}/></div>
        </>}

        {step===4&&<>
          <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Review & Submit</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black"><p className="text-[10px] text-[#555] uppercase mb-1">Reporter</p><p className="text-white text-[13px]">{form.name}</p><p className="text-[#666] text-[11px]">{form.email}</p></div>
            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black"><p className="text-[10px] text-[#555] uppercase mb-1">Severity</p><p className={`text-[13px] capitalize font-medium ${form.severity==='critical'?'text-red-400':form.severity==='high'?'text-orange-400':form.severity==='medium'?'text-amber-400':'text-blue-400'}`}>{form.severity}</p><p className="text-[#666] text-[11px]">{form.vuln_type}</p></div>
          </div>
          <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black"><p className="text-[10px] text-[#555] uppercase mb-1">Title</p><p className="text-white text-[13px]">{form.title}</p></div>
          <div className="border border-amber-900/30 bg-amber-950/10 rounded-xl p-3">
            <p className="text-[11px] text-amber-400/70">By submitting, you confirm authorized testing and agree to responsible disclosure — no public disclosure until resolved.</p>
          </div>
        </>}

        <div className="flex justify-between pt-2 border-t border-[#111]">
          {step>1?<button type="button" onClick={()=>setStep(s=>s-1)} className="text-[#666] hover:text-white text-[13px] transition">← Back</button>:<div/>}
          {step<4
            ?<button type="button" onClick={()=>canNext()&&setStep(s=>s+1)} disabled={!canNext()} className="bg-white text-black px-5 py-2 rounded-xl text-[13px] font-medium disabled:opacity-30 transition hover:bg-[#eee]">Next →</button>
            :<button type="button" onClick={submit} disabled={submitting} className="bg-white text-black px-5 py-2 rounded-xl text-[13px] font-medium disabled:opacity-30 transition hover:bg-[#eee]">{submitting?'Submitting...':'Submit Report'}</button>
          }
        </div>
      </div>
    </div>
  );
}


// ═══ LANDING ═══
function Landing() {
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [publicArticles, setPublicArticles] = useState([]);
  const [publicPages, setPublicPages] = useState([]);
  const [publicSections, setPublicSections] = useState([]);
  const [activePopups, setActivePopups] = useState([]);
  const [dismissedPopupIds, setDismissedPopupIds] = useState([]);

  const [stats, setStats] = useState({
    users: 0,
    investigations: 0,
    captures: 0,
  });

  const [timeLeft, setTimeLeft] = useState({
    days: 30,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    axios
      .get(`${API}/api/v1/public/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});

    axios
      .get(`${API}/api/v1/public/settings`)
      .then((r) => setSiteSettings(r.data || {}))
      .catch(() => {});

    axios
      .get(`${API}/api/v1/public/articles`)
      .then((r) => setPublicArticles(r.data || []))
      .catch(() => {});

    axios
      .get(`${API}/api/v1/public/cms/pages`)
      .then((r) => setPublicPages(r.data || []))
      .catch(() => {});

    axios
      .get(`${API}/api/v1/public/cms/sections`)
      .then((r) => setPublicSections(r.data || []))
      .catch(() => {});

    axios
      .get(`${API}/api/v1/popups/active`)
      .then((r) => setActivePopups(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const target = Date.now() + 30 * 24 * 60 * 60 * 1000;

    const updateCountdown = () => {
      const now = Date.now();
      const diff = Math.max(target - now, 0);

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0 },
  };

  const stagger = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const features = [
    "Mission-driven investigations",
    "Structured intelligence workflows",
    "Clear operational visibility",
    "Faster, calmer analyst experience",
  ];

  const modules = [
    {
      title: "OSINT Intelligence",
      desc: "Collect and correlate multi-source intelligence signals in one structured environment for deeper operational visibility.",
    },
    {
      title: "Behavioral Analysis",
      desc: "Understand interaction patterns, device behavior, and operational indicators through a cleaner intelligence workflow.",
    },
    {
      title: "Link Operations",
      desc: "Deploy controlled intelligence collection workflows and monitor results through a centralized operational layer.",
    },
    {
      title: "Investigation Workspace",
      desc: "Organize cases, profiles, captured intelligence, and evidence with a clearer and more focused operational structure.",
    },
    {
      title: "Graph Intelligence",
      desc: "Visualize relationships between entities, profiles, infrastructure, and events to accelerate analysis and investigation.",
    },
    {
      title: "Operational Visibility",
      desc: "Track intelligence events, investigations, and activity through calm, readable, and structured monitoring surfaces.",
    },
  ];

  const surfaces = [
    {
      eyebrow: "Collection",
      title: "Capture signals with control.",
      text: "Deploy structured workflows designed for intelligence gathering, controlled interaction, and more organized collection layers.",
    },
    {
      eyebrow: "Analysis",
      title: "Turn raw activity into findings.",
      text: "Convert device, network, and behavior signals into intelligence that is easier to read, compare, and act on.",
    },
    {
      eyebrow: "Operations",
      title: "Move from event to case.",
      text: "Link events to profiles, structure investigations, and keep the operational workflow unified in one platform.",
    },
  ];

  const countdownItems = [
    { label: "Days", value: timeLeft.days },
    { label: "Hours", value: timeLeft.hours },
    { label: "Minutes", value: timeLeft.minutes },
    { label: "Seconds", value: timeLeft.seconds },
  ];

  const activePopup = (activePopups || []).find((popup) => !dismissedPopupIds.includes(popup.id));
  const heroTitle = siteSettings.hero_title || "Intelligence\ninfrastructure\nfor modern operations.";
  const heroDescription = siteSettings.hero_description || "ZureX92 is a structured cyber intelligence environment built for analysts, investigators, and operational teams who need stronger visibility, clearer collection workflows, and a more organized way to move from signal to investigation.";
  const primaryCtaText = siteSettings.primary_cta_text || "Request Access";
  const secondaryCtaText = siteSettings.secondary_cta_text || "Sign In";
  const footerText = siteSettings.footer_text || "ZureX92";

  return (
<motion.div
initial="hidden"
animate="show"
variants={pageReveal}
className="min-h-screen bg-black text-white overflow-x-hidden"
>      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 border-b border-[#111] bg-black/80 backdrop-blur-md">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="flex items-center gap-2"
          >
            <ZIcon s={18} />
            <span className="text-[14px] font-semibold tracking-tight">
              {siteSettings.site_name || "ZureX92"}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="hidden md:flex items-center gap-8"
          >
            <a href="#overview" className="text-[13px] text-[#666] hover:text-white transition">Overview</a>
            <a href="#modules" className="text-[13px] text-[#666] hover:text-white transition">Modules</a>
            <a href="#showcase" className="text-[13px] text-[#666] hover:text-white transition">Showcase</a>
            <a href="#bug-bounty" className="text-[13px] text-[#666] hover:text-white transition">Bug Bounty</a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="flex items-center gap-3"
          >
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden text-white"
              aria-label="Open menu"
            >
                          </button>

            <button
              onClick={() => nav("/login")}
              className="hidden sm:block text-[13px] text-[#777] hover:text-white transition"
            >
              Log In
            </button>

            <button
              onClick={() => nav("/register")}
              className="bg-white text-black px-4 py-2 rounded-md text-[13px] font-medium hover:bg-[#eaeaea] transition"
            >
              Get Started
            </button>
          </motion.div>
        </div>
      </nav>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="md:hidden border-b border-[#111] bg-black">
          <div className="max-w-[1200px] mx-auto px-4 py-4 flex flex-col gap-4 text-[14px]">
            <a href="#overview" className="text-[#ccc]" onClick={() => setMenuOpen(false)}>
              Overview
            </a>
            <a href="#modules" className="text-[#ccc]" onClick={() => setMenuOpen(false)}>
              Modules
            </a>
            <a href="#showcase" className="text-[#ccc]" onClick={() => setMenuOpen(false)}>
              Showcase
            </a>
            <a href="#countdown" className="text-[#ccc]" onClick={() => setMenuOpen(false)}>
              Countdown
            </a>

            <button
              onClick={() => {
                setMenuOpen(false);
                nav("/login");
              }}
              className="text-left text-[#ccc]"
            >
              Log In
            </button>

            <button
              onClick={() => {
                setMenuOpen(false);
                nav("/register");
              }}
              className="bg-white text-black px-4 py-2 rounded-lg text-left"
            >
              Get Started
            </button>
          </div>
        </div>
      )}

      {activePopup && (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-[560px] border border-[#1a1a1a] rounded-[28px] bg-[#050505] overflow-hidden shadow-2xl">
            {activePopup.image_url && (
              <img src={activePopup.image_url} alt={activePopup.title} className="w-full h-[220px] object-cover border-b border-[#111]" />
            )}
            <div className="p-6 md:p-7">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">Announcement</p>
                  <h3 className="text-[24px] md:text-[28px] font-semibold text-white tracking-tight">{activePopup.title}</h3>
                </div>
                <button
                  onClick={() => setDismissedPopupIds((prev) => [...prev, activePopup.id])}
                  className="text-[#666] hover:text-white text-[18px] leading-none"
                >
                  ×
                </button>
              </div>
              <p className="text-[14px] text-[#777] leading-[1.85]">{activePopup.message}</p>
              <div className="flex gap-3 mt-6 flex-wrap">
                {activePopup.button_text && activePopup.button_link && (
                  <button
                    onClick={() => window.open(activePopup.button_link, '_blank')}
                    className="bg-white text-black px-5 py-2.5 rounded-xl text-[13px] font-medium hover:bg-[#ebebeb] transition"
                  >
                    {activePopup.button_text}
                  </button>
                )}
                <button
                  onClick={() => setDismissedPopupIds((prev) => [...prev, activePopup.id])}
                  className="border border-[#2a2a2a] px-5 py-2.5 rounded-xl text-[13px] hover:border-[#444] transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
{/* HERO */}
<section className="relative border-b border-[#111] bg-black overflow-hidden">

  <div className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-24 pb-24">

    <div className="grid lg:grid-cols-2 gap-16 items-center">

      {/* LEFT SIDE */}
      <div className="max-w-[720px] order-2 lg:order-1">

        <p className="text-[12px] tracking-[0.35em] text-[#6a6a6a] mb-6">
          {siteSettings.hero_badge || "CYBER INTELLIGENCE PLATFORM"}
        </p>

        <h1 className="text-[48px] sm:text-[64px] leading-[1.05] font-semibold tracking-tight text-white mb-8 whitespace-pre-line">
          {heroTitle}
        </h1>

        <p className="text-[17px] text-[#9c9c9c] leading-[1.7] max-w-[560px] mb-10">
          {heroDescription}
        </p>

        <div className="flex gap-4">

          <button className="px-6 py-3 rounded-lg bg-white text-black text-sm font-medium hover:opacity-90 transition">
            {primaryCtaText}
          </button>

          <button className="px-6 py-3 rounded-lg border border-[#2a2a2a] text-white text-sm hover:bg-[#111] transition">
            {secondaryCtaText}
          </button>

        </div>

      </div>


{/* RIGHT SIDE (GIF) */}
<div className="flex justify-center lg:justify-end order-1 lg:order-2">

  <div className="relative">

    <img
  src={canaryGif}
  alt="Cyber Intelligence Visualization"
  className="w-[360px] sm:w-[420px] lg:w-[520px] object-contain opacity-95"
/>

  </div>

</div>

</div>

</div>

</section>

{/* INTELLIGENCE PANEL */}
<section className="border-b border-[#111] bg-black">

  <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-20">

    <div className="grid lg:grid-cols-3 gap-8">

      {/* ACTIVE CASES */}
      <div className="border border-[#151515] rounded-xl p-6 bg-[#070707]">

        <p className="text-xs tracking-wider text-[#6c6c6c] mb-3">
          ACTIVE CASES
        </p>

        <p className="text-[40px] font-semibold text-white">
          27
        </p>

      </div>


      {/* EVENTS */}
      <div className="border border-[#151515] rounded-xl p-6 bg-[#070707]">

        <p className="text-xs tracking-wider text-[#6c6c6c] mb-3">
          EVENTS
        </p>

        <p className="text-[40px] font-semibold text-white">
          80
        </p>

      </div>


      {/* INTELLIGENCE FLOW */}
      <div className="border border-[#151515] rounded-xl p-6 bg-[#070707]">

        <p className="text-xs tracking-wider text-[#6c6c6c] mb-6">
          INTELLIGENCE FLOW
        </p>

        <div className="space-y-4">

          <div className="flex justify-between text-sm text-[#cfcfcf]">
            <span>Collect multi-source signals</span>
            <span className="text-[#666]">01</span>
          </div>

          <div className="flex justify-between text-sm text-[#cfcfcf]">
            <span>Correlate events and entities</span>
            <span className="text-[#666]">02</span>
          </div>

          <div className="flex justify-between text-sm text-[#cfcfcf]">
            <span>Map intelligence to profiles</span>
            <span className="text-[#666]">03</span>
          </div>

          <div className="flex justify-between text-sm text-[#cfcfcf]">
            <span>Escalate findings into investigations</span>
            <span className="text-[#666]">04</span>
          </div>

        </div>

      </div>

    </div>

  </div>

</section>
      {/* STATS */}
      <section className="border-b border-[#111]">
        <div className="max-w-[980px] mx-auto px-4 sm:px-6 py-12 sm:py-14 md:py-16">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            variants={stagger}
            className="grid grid-cols-3 gap-4 sm:gap-6 text-center"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.45 }}>
              <p className="text-[24px] sm:text-[30px] md:text-[42px] font-semibold tracking-tight">
                {stats.users || 0}
              </p>
              <p className="text-[11px] sm:text-[12px] text-[#555] mt-2">Operators</p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <p className="text-[24px] sm:text-[30px] md:text-[42px] font-semibold tracking-tight">
                {stats.investigations || 0}
              </p>
              <p className="text-[11px] sm:text-[12px] text-[#555] mt-2">Investigations</p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{ duration: 0.55 }}>
              <p className="text-[24px] sm:text-[30px] md:text-[42px] font-semibold tracking-tight">
                {stats.captures || 0}
              </p>
              <p className="text-[11px] sm:text-[12px] text-[#555] mt-2">Intelligence Events</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="border-b border-[#111]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28 grid md:grid-cols-[0.95fr_1.05fr] gap-12 md:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55 }}
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-6">
              Strategic Layer
            </p>
            <h2 className="text-[30px] sm:text-[36px] md:text-[54px] leading-[1.04] tracking-tight font-semibold">
              A calmer,
              <br />
              clearer experience
              <br />
              for cyber intelligence.
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.22 }}
            variants={stagger}
            className="space-y-5"
          >
            {features.map((item, i) => (
              <motion.div
                key={item}
                variants={fadeUp}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="border-b border-[#111] pb-5"
              >
                <p className="text-[20px] sm:text-[24px] md:text-[31px] text-[#d1d1d1] leading-[1.15] tracking-tight">
                  {item}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SURFACES */}
      <section className="border-b border-[#111]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mb-12 md:mb-14"
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">
              Capability
            </p>
            <h2 className="text-[28px] sm:text-[34px] md:text-[48px] leading-[1.05] tracking-tight font-semibold max-w-[780px]">
              Built to support collection, analysis, and operational clarity in one platform.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {surfaces.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="border border-[#171717] rounded-[24px] bg-[#050505] p-6 md:p-7"
              >
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#555] mb-4">
                  {card.eyebrow}
                </p>
                <h3 className="text-[20px] md:text-[22px] leading-[1.15] tracking-tight font-medium mb-4">
                  {card.title}
                </h3>
                <p className="text-[14px] text-[#777] leading-[1.85]">
                  {card.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="border-b border-[#111]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="mb-12 md:mb-14"
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">
              Modules
            </p>
            <h2 className="text-[28px] sm:text-[34px] md:text-[48px] leading-[1.05] tracking-tight font-semibold max-w-[760px]">
              Focused surfaces for intelligence work, investigation, and platform visibility.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {modules.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="border border-[#171717] rounded-[24px] p-6 md:p-7 bg-[#050505] hover:border-[#242424] transition"
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <h3 className="text-[18px] md:text-[21px] font-medium tracking-tight">
                    {item.title}
                  </h3>
                  <span className="text-[11px] text-[#555]">0{i + 1}</span>
                </div>

                <p className="text-[14px] text-[#777] leading-[1.85]">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section id="showcase" className="border-b border-[#111]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28 grid lg:grid-cols-[1.02fr_0.98fr] gap-8 md:gap-14 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.18 }}
            transition={{ duration: 0.55 }}
            className="border border-[#171717] rounded-[28px] p-7 sm:p-8 md:p-10 bg-[#050505] flex flex-col justify-between"
          >
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-5">
                Investigation Flow
              </p>

              <h2 className="text-[30px] sm:text-[36px] md:text-[54px] leading-[1.02] tracking-tight font-semibold mb-6">
                One link.
                <br />
                More intelligence.
              </h2>

              <p className="text-[15px] md:text-[16px] text-[#777] leading-[1.85] max-w-[540px]">
                Capture device indicators, network traces, behavioral context,
                and structured findings from a more focused and controlled workflow.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-10">
              {["Network Node", "Unit Fingerprint", "Behavioral Analysis", "Profile Mapping"].map((tag, i) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="border border-[#2a2a2a] px-3 py-1.5 text-[12px] rounded-full text-[#c9c9c9]"
                >
                  {tag}
                </motion.span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.18 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="border border-[#171717] rounded-[28px] overflow-hidden bg-[#050505]"
          >
            <div className="border-b border-[#111] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#2f2f2f]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#2f2f2f]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#2f2f2f]" />
              </div>
              <span className="text-[11px] text-[#555]">Analyst Console</span>
            </div>

            <div className="p-6 md:p-8">
              <pre className="text-[12px] md:text-[13px] leading-[1.95] text-[#a0a0a0] font-mono whitespace-pre-wrap overflow-x-auto">
{`const session = deployIntelligenceLink({
  mode: "controlled",
  modules: ["network", "device", "behavior"],
  profile: "ZX-92"
})

const findings = await session.collect()

return {
  risk: findings.risk,
  profile: findings.profile,
  graph: findings.entityMap
}`}
              </pre>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COUNTDOWN */}
      <section id="countdown" className="border-b border-[#111]">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="border border-[#171717] rounded-[32px] bg-[#050505] overflow-hidden"
          >
            <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
              <div className="p-7 sm:p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-[#111]">
                <p className="text-[11px] uppercase tracking-[0.34em] text-[#555] mb-5">
                  Countdown
                </p>

                <h2 className="text-[30px] sm:text-[36px] md:text-[54px] leading-[1.02] tracking-tight font-semibold mb-6">
                  30-day launch
                  <br />
                  sequence.
                </h2>

                <p className="text-[15px] md:text-[16px] text-[#777] leading-[1.85] max-w-[520px]">
                  A creative live countdown to the next operational milestone.
                  Calm in style, precise in detail, and visually anchored at the end of the landing page.
                </p>
              </div>

              <div className="p-7 sm:p-8 md:p-12 flex items-center">
                <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-4">
                  {countdownItems.map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                      className="relative border border-[#151515] rounded-[24px] p-5 md:p-6 text-center bg-black"
                    >
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#2e2e2e] to-transparent" />
                      <motion.p
                        key={item.value}
                        initial={{ opacity: 0.45, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.22 }}
                        className="text-[28px] sm:text-[34px] md:text-[46px] font-semibold tracking-tight"
                      >
                        {String(item.value).padStart(2, "0")}
                      </motion.p>
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mt-3">
                        {item.label}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {publicSections.length > 0 && (
        <section className="border-b border-[#111]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-24 space-y-5">
            <div className="mb-8">
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">Managed Sections</p>
              <h2 className="text-[28px] sm:text-[34px] md:text-[48px] leading-[1.05] tracking-tight font-semibold max-w-[760px]">
                Flexible homepage sections managed directly from the admin workspace.
              </h2>
            </div>
            <div className="space-y-4">
              {publicSections.map((section) => (
                <div key={section.id} className="border border-[#171717] rounded-[24px] bg-[#050505] overflow-hidden">
                  <div className={`grid ${section.image ? 'lg:grid-cols-[1fr_340px]' : 'grid-cols-1'} gap-0`}>
                    <div className="p-6 md:p-8">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#666] mb-4">Section</p>
                      <h3 className="text-[24px] md:text-[32px] text-white font-semibold tracking-tight mb-4">
                        {section.title}
                      </h3>
                      <p className="text-[14px] text-[#777] leading-[1.9] whitespace-pre-wrap">
                        {section.body}
                      </p>
                    </div>
                    {section.image && (
                      <div className="border-t lg:border-t-0 lg:border-l border-[#111]">
                        <img src={section.image} alt={section.title} className="w-full h-full min-h-[220px] object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {publicArticles.length > 0 && (
        <section className="border-b border-[#111]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-24">
            <div className="mb-10">
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">Articles</p>
              <h2 className="text-[28px] sm:text-[34px] md:text-[48px] leading-[1.05] tracking-tight font-semibold max-w-[760px]">
                Latest updates, platform notes, and published intelligence content.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {publicArticles.slice(0, 6).map((article) => (
                <div key={article.id} className="border border-[#171717] rounded-[24px] bg-[#050505] overflow-hidden">
                  {article.image_url && (
                    <img src={article.image_url} alt={article.title} className="w-full h-[180px] object-cover border-b border-[#111]" />
                  )}
                  <div className="p-6">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#555] mb-3">
                      {article.author_name || 'ZureX92'}
                    </p>
                    <h3 className="text-[20px] text-white font-medium tracking-tight mb-3">
                      {article.title}
                    </h3>
                    <p className="text-[13px] text-[#777] leading-[1.8] line-clamp-4">
                      {article.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BUG BOUNTY */}
      <section id="bug-bounty" className="border-t border-[#111] py-16 sm:py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">Security Research</p>
              <h2 className="text-[32px] sm:text-[42px] leading-[1.04] tracking-tight font-semibold mb-5">Bug Bounty<br/>Program</h2>
              <p className="text-[15px] text-[#777] leading-[1.85] mb-8">Discovered a security vulnerability? Report it responsibly and get rewarded based on severity and impact.</p>
              <div className="space-y-3">
                {[{s:'Critical',r:'$500–$2,000',c:'text-red-400 border-red-900 bg-red-950'},
                  {s:'High',r:'$200–$500',c:'text-orange-400 border-orange-900 bg-orange-950'},
                  {s:'Medium',r:'$50–$200',c:'text-amber-400 border-amber-900 bg-amber-950'},
                  {s:'Low',r:'$0–$50',c:'text-blue-400 border-blue-900 bg-blue-950'},
                ].map(x=>(
                  <div key={x.s} className={`flex items-center justify-between border rounded-xl px-4 py-3 ${x.c}`}>
                    <span className="text-[13px] font-medium capitalize">{x.s}</span>
                    <span className="text-[13px] font-semibold">{x.r}</span>
                  </div>
                ))}
              </div>
            </div>
            <LandingBugBountyForm/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 md:py-28">
        <div className="max-w-[980px] mx-auto px-4 sm:px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-5"
          >
            Start
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[32px] sm:text-[40px] md:text-[58px] leading-[1.03] tracking-tight font-semibold mb-6"
          >
            Build a better
            <br />
            intelligence workflow.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-[15px] md:text-[16px] text-[#777] leading-[1.85] max-w-[680px] mx-auto mb-8"
          >
            Start with a clearer operational foundation for investigations,
            intelligence collection, profile mapping, and analysis.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-center gap-3"
          >
            <button
              onClick={() => nav("/register")}
              className="bg-white text-black px-7 py-3 rounded-xl text-[14px] font-medium hover:bg-[#ebebeb] transition"
            >
              Get Started
            </button>

            <button
              onClick={() => nav("/login")}
              className="border border-[#2a2a2a] px-7 py-3 rounded-xl text-[14px] hover:border-[#444] transition"
            >
              Sign In
            </button>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#111] py-10 sm:py-12">
        {(() => {
          const footerGroups = buildFooterGroups(publicPages);
          const socialLinks = buildSocialLinks(siteSettings);
          const contactItems = [
            { label: "Phone", value: siteSettings.contact_phone || "" },
            { label: "Email", value: siteSettings.contact_email || "" },
            { label: "Location", value: siteSettings.contact_location || "" },
          ].filter((item) => item.value);
          return (
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
              <div className="grid md:grid-cols-[1.15fr_1fr_1fr_1fr_1fr] gap-8 mb-10">
                <div>
                  <h4 className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-4">
                    {siteSettings.footer_company_title || "About us"}
                  </h4>
                  <p className="text-[13px] text-[#777] leading-[1.9] max-w-[320px] whitespace-pre-wrap">
                    {siteSettings.footer_company_text || siteSettings.about_us || "Structured cyber intelligence platform content can be managed from the admin workspace."}
                  </p>

                  {contactItems.length > 0 && (
                    <div className="mt-6 space-y-2">
                      {contactItems.map((item) => (
                        <div key={item.label} className="text-[13px] text-[#666]">
                          <span className="text-[#999]">{item.label}:</span> {item.value}
                        </div>
                      ))}
                    </div>
                  )}

                  {socialLinks.length > 0 && (
                    <div className="mt-6">
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-3">Social</p>
                      <div className="flex flex-wrap gap-2">
                        {socialLinks.map((item) => (
                          <a
                            key={item.key}
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[12px] text-[#666] hover:text-white transition border border-[#1f1f1f] rounded-full px-3 py-1.5"
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {Object.entries(footerGroups).map(([key, links]) => (
                  <div key={key}>
                    <h4 className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-4">
                      {siteSettings[`footer_${key}_title`] || FOOTER_GROUPS[key]}
                    </h4>
                    <div className="space-y-2">
                      {links.map((page) => {
                        const to = page.path || `/page/${page.slug}`;
                        return (
                          <Link key={page.slug || page.title} to={to} className="block text-[13px] text-[#666] hover:text-white transition">
                            {page.title}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#111] pt-6 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <ZIcon s={12} c="#666" />
                  <span className="text-[12px] text-[#666]">{footerText}</span>
                </div>
                <p className="text-[12px] text-[#555]">© {new Date().getFullYear()}</p>
              </div>
            </div>
          );
        })()}
      </footer>
    </motion.div>
  );
}
// ═══ STATIC PAGES ═══
const PW=({title,children})=><div className="min-h-screen bg-black text-white"><nav className="border-b border-[#1a1a1a] px-6 h-16 flex items-center justify-between max-w-[800px] mx-auto"><Link to="/" className="flex items-center gap-2"><ZIcon s={14}/><span className="text-[13px] font-semibold">ZureX92</span></Link><div className="flex gap-3"><Link to="/login" className="text-[#888] text-[13px]">Log In</Link><Link to="/register" className="bg-white text-black px-4 py-2 rounded-lg text-[13px] font-medium">Get Started</Link></div></nav><div className="max-w-[700px] mx-auto px-6 py-16"><h1 className="text-[28px] font-bold mb-8">{title}</h1><div className="text-[15px] text-[#888] leading-relaxed space-y-4">{children}</div></div></div>;

function AboutPage(){
  return (
    <PW title="About ZureX92">
      <div className="space-y-8">
        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Mission</h2>
          <p>
            ZureX92 is built to provide a calmer, clearer, and more structured cyber intelligence experience for modern operational teams.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Why it exists</h2>
          <p>
            Most intelligence workflows become fragmented between collection, correlation, and case management. ZureX92 brings these layers together into a focused operational platform.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Who it serves</h2>
          <p>
            Analysts, investigation teams, cyber intelligence operators, and organizations that need cleaner visibility and stronger structure across operational workflows.
          </p>
        </div>
      </div>
    </PW>
  );
}

function FAQPage(){
  const faqs = [
    ["What is ZureX92?","ZureX92 is a cyber intelligence platform designed for structured collection, analysis, and investigation workflows."],
    ["Who can use it?","The platform is intended for analysts, security researchers, operational teams, and authorized cyber investigation environments."],
    ["Does it support intelligence workflows?","Yes. The platform is designed around collection, analysis, profile linking, case organization, and operational visibility."],
    ["Is the platform secure?","ZureX92 is designed with encryption, access control, audit logging, and a role-aware operational model."],
    ["Can teams use it together?","Yes. The platform is built to support structured multi-user workflows and clearer operational collaboration."],
    ["Is enterprise deployment available?","Yes. Enterprise plans can include private deployment, custom integrations, and additional operational controls."],
  ];

  return (
    <PW title="FAQs">
      <div className="space-y-4">
        {faqs.map(([q,a],i)=>(
          <motion.div
            key={q}
            initial={{opacity:0,y:20}}
            whileInView={{opacity:1,y:0}}
            viewport={{once:true}}
            transition={{duration:0.4, delay:i*0.04}}
            className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]"
          >
            <h3 className="text-white text-[18px] font-medium mb-3">{q}</h3>
            <p className="text-[#777] leading-[1.8]">{a}</p>
          </motion.div>
        ))}
      </div>
    </PW>
  );
}

function ContactPage(){return<PW title="Contact"><p>Use the in-platform ticket system for technical assistance and vulnerability reports.</p></PW>;}

function PricingPage(){
  const plans = [
    {
      name: "Starter",
      price: "$29",
      desc: "For individual analysts and lightweight investigations.",
      features: [
        "Basic intelligence workspace",
        "Link operations",
        "Case organization",
        "Email support",
      ],
    },
    {
      name: "Professional",
      price: "$79",
      desc: "For teams that need deeper operational visibility.",
      features: [
        "Advanced intelligence modules",
        "Graph intelligence",
        "Behavior analysis",
        "Priority support",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      desc: "For organizations requiring private deployment and advanced control.",
      features: [
        "Private deployment",
        "Role-based workflows",
        "Custom integrations",
        "Dedicated support",
      ],
    },
  ];

  return (
    <PW title="Pricing">
      <p>
        Flexible access plans for analysts, investigation teams, and enterprise deployments.
      </p>

      <div className="grid md:grid-cols-3 gap-4 not-prose">
        {plans.map((plan,i)=>(
          <motion.div
            key={plan.name}
            initial={{opacity:0,y:24}}
            whileInView={{opacity:1,y:0}}
            viewport={{once:true}}
            transition={{duration:0.45, delay:i*0.06}}
            className={`border rounded-2xl p-6 bg-[#050505] ${
              plan.name==="Professional" ? "border-white" : "border-[#1a1a1a]"
            }`}
          >
            <p className="text-[12px] text-[#666] uppercase tracking-[0.24em] mb-3">{plan.name}</p>
            <h3 className="text-[34px] font-semibold text-white mb-3">{plan.price}</h3>
            <p className="text-[14px] text-[#777] leading-[1.8] mb-6">{plan.desc}</p>

            <div className="space-y-3">
              {plan.features.map((f)=>(
                <div key={f} className="text-[14px] text-[#cfcfcf] border-b border-[#111] pb-3">
                  {f}
                </div>
              ))}
            </div>

            <button className="mt-6 w-full bg-white text-black rounded-xl py-3 text-[14px] font-medium">
              {plan.name === "Enterprise" ? "Contact Sales" : "Get Started"}
            </button>
          </motion.div>
        ))}
      </div>
    </PW>
  );
}

function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/v1/public/cms/pages/${slug}`)
      .then((r) => setPage(r.data || null))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PW title="Loading">
        <p>Loading page...</p>
      </PW>
    );
  }

  if (!page) {
    return (
      <PW title="Page not found">
        <p>The requested page is not available.</p>
      </PW>
    );
  }

  if (slug === 'bug-bounty') {
    return <BugBountyPage page={page} />;
  }

  return (
    <PW title={page.title}>
      <div className="space-y-8">
        {page.image && (
          <img
            src={page.image}
            alt={page.title}
            className="w-full max-h-[340px] object-cover rounded-2xl border border-[#1a1a1a]"
          />
        )}
        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">{page.title}</h2>
          <p className="whitespace-pre-wrap">{page.content}</p>
        </div>
      </div>
    </PW>
  );
}


function BlogPage() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/v1/public/articles`).then((r) => setArticles(r.data || [])).catch(() => {});
  }, []);
  return (
    <PW title="Blog">
      <div className="space-y-6">
        <p>Published articles from the admin workspace appear here.</p>
        <div className="space-y-4">
          {articles.length === 0 ? (
            <p>No articles published yet.</p>
          ) : (
            articles.map((article) => (
              <div key={article.id} className="border border-[#1a1a1a] rounded-2xl bg-[#050505] overflow-hidden">
                {article.image_url && (
                  <img src={article.image_url} alt={article.title} className="w-full max-h-[280px] object-cover border-b border-[#111]" />
                )}
                <div className="p-6">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#666] mb-3">{article.author_name || "ZureX92"}</p>
                  <h2 className="text-white text-[24px] font-semibold mb-3">{article.title}</h2>
                  <p className="whitespace-pre-wrap text-[#888] leading-[1.9]">{article.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </PW>
  );
}

function CanaryDocsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const tokenTypes = [
    { id: 'web', icon: 'globe', title: 'Web Bug', desc: 'Invisible 1×1 pixel embedded in web pages or HTML emails. Fires when rendered.', use: 'Email tracking, document opens, web page visits', detection: 'HTTP request to beacon URL', reward: 'IP, browser, timestamp, user-agent' },
    { id: 'dns', icon: 'search', title: 'DNS Token', desc: 'A unique subdomain that triggers an alert when resolved via DNS lookup.', use: 'Network monitoring, exfiltration detection, canary traps', detection: 'DNS query', reward: 'Resolver IP, timestamp, query type' },
    { id: 'pdf', icon: 'file', title: 'PDF Token', desc: 'A PDF file with an embedded beacon. Opens trigger an alert with reader metadata.', use: 'Document access tracking, insider threat detection', detection: 'PDF open event', reward: 'IP, reader version, OS, timestamp' },
    { id: 'word', icon: 'file-text', title: 'Word Document', desc: 'Microsoft Word document with a canary URL that phones home when opened.', use: 'Sensitive document tracking, exfiltration alerts', detection: 'HTTP GET on open', reward: 'IP, Office version, timestamp' },
    { id: 'link', icon: 'link', title: 'Tracked URL', desc: 'A redirect URL that logs every visit before forwarding to the real destination.', use: 'Phishing simulations, access tracking, campaigns', detection: 'HTTP redirect click', reward: 'Full device fingerprint, geo, ISP' },
    { id: 'img', icon: 'image', title: 'Image Token', desc: 'A unique image URL that triggers when loaded in any context.', use: 'Email tracking, forum monitoring, embed traps', detection: 'HTTP GET for image', reward: 'IP, referer, user-agent, timestamp' },
  ];
  const steps = [
    { n: '01', title: 'Deploy', desc: 'Create a canary token from the Canary section. Choose the token type based on where you want to plant it.' },
    { n: '02', title: 'Plant', desc: 'Embed the token in a document, email, web page, or send as a link. The target does not know it exists.' },
    { n: '03', title: 'Monitor', desc: 'When the token is triggered, ZureX92 captures full telemetry: IP, device fingerprint, geo, and timestamps.' },
    { n: '04', title: 'Investigate', desc: 'Review the capture in the Feed. Link it to a profile, escalate to a case, or export as an intelligence report.' },
  ];
  const faqs = [
    ['What is a canary token?', 'A canary token is a deliberate trap — a piece of data planted in a document, system, or network location to detect unauthorized access. When triggered, it silently alerts you.'],
    ['Are tokens detectable?', 'Basic tokens are invisible (1px images, DNS records, hidden redirects). Advanced adversaries with traffic analysis tools may detect beacon requests, but casual or opportunistic access will not.'],
    ['What data is collected?', 'ZureX92 canary tokens collect: public IP address, approximate geolocation, ISP, device fingerprint, browser/OS, and timestamp. With one-click mode: GPS, camera, and permissions.'],
    ['Can I use tokens in legal investigations?', 'Canary tokens are legal in most jurisdictions when used in systems you own or have authorization to monitor. Always ensure you have appropriate authorization before deploying.'],
    ['How long do tokens stay active?', 'Tokens remain active until manually deactivated. There is no expiration. You can toggle status from the Links management panel.'],
  ];
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-[#1a1a1a] px-6 h-16 flex items-center justify-between max-w-[1200px] mx-auto">
        <a href="/" className="flex items-center gap-2"><ZIcon s={14}/><span className="text-[13px] font-semibold">ZureX92</span></a>
        <div className="flex gap-3"><a href="/login" className="text-[#888] text-[13px]">Log In</a><a href="/register" className="bg-white text-black px-4 py-2 rounded-lg text-[13px] font-medium">Get Started</a></div>
      </nav>
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="py-20 border-b border-[#111]">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-5">Canary Intelligence</p>
          <h1 className="text-[52px] md:text-[72px] leading-[0.96] font-semibold tracking-tight mb-6">Canary Tokens<br/>Documentation</h1>
          <p className="text-[17px] text-[#888] leading-[1.8] max-w-[680px] mb-10">Silent trip-wires embedded in documents, emails, and systems. When triggered, they report back with full telemetry — IP, device, location, and timestamp.</p>
          <div className="flex gap-3 flex-wrap">
            <a href="/canary" className="bg-white text-black px-6 py-3 rounded-xl text-[14px] font-medium hover:bg-[#eee] transition">Deploy a Token →</a>
            <button onClick={() => setActiveTab('tokens')} className="border border-[#2a2a2a] px-6 py-3 rounded-xl text-[14px] hover:border-[#444] transition">View Token Types</button>
          </div>
        </div>
        <div className="flex gap-1 py-6 border-b border-[#111] overflow-x-auto">
          {[{id:'overview',l:'Overview'},{id:'tokens',l:'Token Types'},{id:'howto',l:'How It Works'},{id:'faq',l:'FAQ'}].map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)} className={`px-4 py-2 rounded-lg text-[13px] whitespace-nowrap transition ${activeTab===t.id?'bg-white text-black':'text-[#666] hover:text-white'}`}>{t.l}</button>
          ))}
        </div>
        {activeTab==='overview'&&(
          <div className="py-16 space-y-20">
            <div className="grid md:grid-cols-3 gap-6">
              {[{icon:'zap',t:'Zero Footprint',d:'Tokens are invisible. No installation, no agent, no detectable presence on the target system.'},
                {icon:'radio',t:'Real-Time Alerts',d:'Triggers are captured instantly and visible in your ZureX92 Feed within seconds.'},
                {icon:'scan',t:'Rich Telemetry',d:'Every trigger returns IP, geolocation, device fingerprint, browser, OS, and timestamp.'}
              ].map((c,i)=>(
                <div key={i} className="border border-[#1a1a1a] rounded-[24px] p-7 bg-[#050505]">
                  <div className="text-[28px] mb-4">{c.icon}</div>
                  <h3 className="text-[18px] font-medium mb-3">{c.t}</h3>
                  <p className="text-[14px] text-[#666] leading-[1.8]">{c.d}</p>
                </div>
              ))}
            </div>
            <div className="border border-[#1a1a1a] rounded-[28px] overflow-hidden">
              <div className="border-b border-[#111] px-7 py-5"><p className="text-[11px] text-[#555] uppercase tracking-wider mb-1">Use Cases</p><h2 className="text-[26px] font-medium">Where canary tokens are used</h2></div>
              <div className="divide-y divide-[#111]">
                {[['Insider Threat Detection','Embed tokens in sensitive documents. If an unauthorized person opens the file, you know immediately.'],
                  ['Phishing Campaign Monitoring','Plant tokens in bait emails to detect when and from where your organization is being targeted.'],
                  ['Honeypot Infrastructure','Drop tokens in fake credential files, config documents, or database exports to catch attackers mid-exfiltration.'],
                  ['Penetration Testing','Use tokens to demonstrate access to sensitive areas during red team engagements with concrete evidence.'],
                  ['Incident Response','Deploy retroactively in suspected areas to determine if an attacker still has active access.']
                ].map(([t,d],i)=>(
                  <div key={i} className="px-7 py-5 flex justify-between gap-8 items-start">
                    <div className="flex items-center gap-4"><span className="text-[11px] text-[#444] font-mono w-6">0{i+1}</span><h3 className="text-[15px] text-white font-medium">{t}</h3></div>
                    <p className="text-[13px] text-[#666] leading-[1.7] max-w-[500px] text-right">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {activeTab==='tokens'&&(
          <div className="py-16">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">Token Library</p>
            <h2 className="text-[36px] font-medium mb-12">Available token types</h2>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {tokenTypes.map(t=>(
                <div key={t.id} className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-6 hover:border-[#2a2a2a] transition">
                  <div className="mb-4 text-[#666]"><CanaryIcon name={t.icon} size={26}/></div>
                  <h3 className="text-[18px] font-medium mb-3">{t.title}</h3>
                  <p className="text-[13px] text-[#666] leading-[1.8] mb-5">{t.desc}</p>
                  <div className="space-y-2 border-t border-[#111] pt-4">
                    <div className="flex gap-2 text-[12px]"><span className="text-[#444] w-20">Use case</span><span className="text-[#999]">{t.use}</span></div>
                    <div className="flex gap-2 text-[12px]"><span className="text-[#444] w-20">Detection</span><span className="text-[#999]">{t.detection}</span></div>
                    <div className="flex gap-2 text-[12px]"><span className="text-[#444] w-20">Returns</span><span className="text-[#999]">{t.reward}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab==='howto'&&(
          <div className="py-16 space-y-16">
            <div><p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">Process</p><h2 className="text-[36px] font-medium mb-12">How canary tokens work</h2>
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
                {steps.map(s=>(
                  <div key={s.n} className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-6">
                    <p className="text-[11px] text-[#444] font-mono mb-4">{s.n}</p>
                    <h3 className="text-[18px] font-medium mb-3">{s.title}</h3>
                    <p className="text-[14px] text-[#666] leading-[1.8]">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="border border-[#1a1a1a] rounded-[28px] overflow-hidden">
              <div className="border-b border-[#111] px-7 py-5"><p className="text-[11px] text-[#555] uppercase tracking-wider mb-1">Technical</p><h2 className="text-[22px] font-medium">How the beacon works</h2></div>
              <div className="p-7 grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-[15px] font-medium">HTTP Beacon (Web/PDF/Image)</h3>
                  <div className="bg-black border border-[#1a1a1a] rounded-2xl p-5">
                    <pre className="text-[12px] text-emerald-400 font-mono leading-relaxed">{`GET /r/{token_id} HTTP/1.1
Host: zurex92.io
User-Agent: Mozilla/5.0 ...
Accept: image/*, */*
Referer: https://target.com

→ 302 Redirect to target_url
→ ZureX92 captures full headers
→ Async fingerprint collection
→ Alert fired to Feed`}</pre>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-[15px] font-medium">DNS Beacon</h3>
                  <div className="bg-black border border-[#1a1a1a] rounded-2xl p-5">
                    <pre className="text-[12px] text-blue-400 font-mono leading-relaxed">{`; Token planted in network config
nameserver: {uuid}.canary.zurex92.io

; When resolved, DNS query hits
; ZureX92 authoritative server
; Captures: resolver IP, timestamp
; No HTTP needed — works offline

→ Alert fired with resolver data`}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab==='faq'&&(
          <div className="py-16">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#555] mb-4">FAQ</p>
            <h2 className="text-[36px] font-medium mb-12">Common questions</h2>
            <div className="space-y-3 max-w-[860px]">
              {faqs.map(([q,a],i)=>(
                <div key={i} className="border border-[#1a1a1a] rounded-[20px] bg-[#050505] p-6">
                  <h3 className="text-[16px] font-medium text-white mb-3">{q}</h3>
                  <p className="text-[14px] text-[#777] leading-[1.85]">{a}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <footer className="border-t border-[#111] py-10 mt-10">
        <div className="max-w-[1200px] mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2"><ZIcon s={12} c="#666"/><span className="text-[12px] text-[#666]">ZureX92</span></div>
          <p className="text-[12px] text-[#555]">© {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}


function BugBountyPage({ page }) {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [form, setForm] = useState({
    name:'', email:'', handle:'',
    title:'', vuln_type:'xss', severity:'medium',
    cvss_score:'', cvss_vector:'',
    target_url:'', affected_component:'',
    summary:'', steps_to_reproduce:'', impact:'', remediation:'',
    report_body:'', attachment_url:'', poc_code:''
  });

  const vulnTypes = [
    {id:'xss',label:'Cross-Site Scripting (XSS)',sev:'medium'},
    {id:'sqli',label:'SQL Injection',sev:'critical'},
    {id:'rce',label:'Remote Code Execution (RCE)',sev:'critical'},
    {id:'idor',label:'Insecure Direct Object Reference (IDOR)',sev:'high'},
    {id:'ssrf',label:'Server-Side Request Forgery (SSRF)',sev:'high'},
    {id:'csrf',label:'Cross-Site Request Forgery (CSRF)',sev:'medium'},
    {id:'auth_bypass',label:'Authentication Bypass',sev:'critical'},
    {id:'broken_access',label:'Broken Access Control',sev:'high'},
    {id:'info_disclosure',label:'Information Disclosure',sev:'low'},
    {id:'xxe',label:'XML External Entity (XXE)',sev:'high'},
    {id:'path_traversal',label:'Path Traversal / LFI',sev:'high'},
    {id:'open_redirect',label:'Open Redirect',sev:'low'},
    {id:'dos',label:'Denial of Service (DoS)',sev:'medium'},
    {id:'crypto',label:'Cryptographic Weakness',sev:'medium'},
    {id:'misconfiguration',label:'Security Misconfiguration',sev:'medium'},
    {id:'other',label:'Other / Unknown',sev:'low'},
  ];

  const rewards = [
    {sev:'critical',range:'$500 – $2,000',color:'text-red-400',bg:'border-red-900 bg-red-950',desc:'RCE, Auth Bypass, Full Account Takeover'},
    {sev:'high',range:'$200 – $500',color:'text-orange-400',bg:'border-orange-900 bg-orange-950',desc:'IDOR, SSRF, Privilege Escalation'},
    {sev:'medium',range:'$50 – $200',color:'text-amber-400',bg:'border-amber-900 bg-amber-950',desc:'XSS (Stored/Reflected), CSRF, Logic Flaws'},
    {sev:'low',range:'$0 – $50',color:'text-blue-400',bg:'border-blue-900 bg-blue-950',desc:'Info Disclosure, Open Redirect, Minor Issues'},
    {sev:'informational',range:'Hall of Fame',color:'text-emerald-400',bg:'border-emerald-900 bg-emerald-950',desc:'Best Practices, Low-Impact Findings'},
  ];

  const sevColors = {critical:'text-red-400 border-red-800 bg-red-950',high:'text-orange-400 border-orange-800 bg-orange-950',medium:'text-amber-400 border-amber-800 bg-amber-950',low:'text-blue-400 border-blue-800 bg-blue-950',informational:'text-emerald-400 border-emerald-800 bg-emerald-950'};

  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const canNext = () => {
    if(step===1) return form.name && form.email;
    if(step===2) return form.title && form.vuln_type && form.severity;
    if(step===3) return form.target_url && form.summary && form.steps_to_reproduce;
    return true;
  };

  const submit = async () => {
    if(!form.title||!form.summary||!form.steps_to_reproduce) return toast('Required fields missing','error');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/v1/public/bug-bounty/report`, form);
      setReportId(r.data?.report?.id);
      setSubmitted(true);
      toast('Report submitted successfully','success');
    } catch(e){ toast(E(e),'error'); }
    setSubmitting(false);
  };

  if(submitted) return (
    <PW title="Report Submitted">
      <div className="space-y-8">
        <div className="border border-emerald-800 bg-emerald-950/30 rounded-2xl p-8 text-center">
          <div className="mb-4 flex justify-center"><CheckCircle size={40} className="text-emerald-500"/></div>
          <h2 className="text-white text-[24px] font-semibold mb-3">Report Received</h2>
          <p className="text-[#888] text-[14px] leading-[1.8] mb-4">Your report has been submitted and will be reviewed by our security team. You will be contacted via the email provided.</p>
          {reportId && <div className="inline-flex items-center gap-2 bg-black border border-[#222] rounded-xl px-4 py-2"><span className="text-[11px] text-[#666]">Report ID</span><span className="text-white font-mono text-[13px]">BBR-{String(reportId).padStart(5,'0')}</span></div>}
        </div>
        <div className="border border-[#1a1a1a] rounded-2xl p-6 bg-[#050505]">
          <h3 className="text-white font-medium mb-3">What happens next?</h3>
          <div className="space-y-3 text-[13px] text-[#777]">
            <p><span className="text-white">1 — Triage (24-48h):</span> Our team reviews the report for validity and impact.</p>
            <p><span className="text-white">2 — Assessment (3-7 days):</span> CVSS scoring, reproduction, and severity confirmation.</p>
            <p><span className="text-white">3 — Resolution:</span> Development team patches the issue.</p>
            <p><span className="text-white">4 — Reward:</span> Reward issued based on validated severity and impact.</p>
          </div>
        </div>
        <button onClick={()=>{setSubmitted(false);setStep(1);setForm({name:'',email:'',handle:'',title:'',vuln_type:'xss',severity:'medium',cvss_score:'',cvss_vector:'',target_url:'',affected_component:'',summary:'',steps_to_reproduce:'',impact:'',remediation:'',report_body:'',attachment_url:'',poc_code:''});}} className="text-[#666] text-[13px] hover:text-white">← Submit another report</button>
      </div>
    </PW>
  );

  return (
    <PW title="Bug Bounty Program">
      <div className="space-y-10">
        {page?.image && <img src={page.image} alt={page.title} className="w-full max-h-[280px] object-cover rounded-2xl border border-[#1a1a1a]"/>}

        {/* Header */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#555] mb-3">Security Research Program</p>
          <h2 className="text-white text-[28px] font-semibold mb-3">Responsible Disclosure &amp; Bug Bounty</h2>
          <p className="text-[#777] leading-[1.9] text-[14px]">{page?.content || 'ZureX92 values the work of security researchers. If you discover a vulnerability, submit a detailed report through this program. Valid findings are rewarded based on severity and impact.'}</p>
        </div>

        {/* Reward Tiers */}
        <div>
          <h3 className="text-white text-[16px] font-medium mb-4">Reward Tiers</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {rewards.map(r=>(
              <div key={r.sev} className={`border rounded-2xl p-4 ${r.bg}`}>
                <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${r.color}`}>{r.sev}</p>
                <p className={`text-[18px] font-semibold mb-2 ${r.color}`}>{r.range}</p>
                <p className="text-[11px] text-[#888] leading-[1.6]">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scope */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]">
            <h3 className="text-emerald-400 text-[13px] font-bold uppercase tracking-wider mb-3">In Scope</h3>
            <div className="space-y-2 text-[13px] text-[#888]">
              {['Authentication and session management','API endpoints and authorization logic','Data exfiltration and injection vulnerabilities','SSRF and server-side vulnerabilities','Business logic flaws with real impact','Sensitive data exposure'].map(i=><p key={i} className="flex gap-2"><span className="text-emerald-500">—</span>{i}</p>)}
            </div>
          </div>
          <div className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]">
            <h3 className="text-red-400 text-[13px] font-bold uppercase tracking-wider mb-3">Out of Scope</h3>
            <div className="space-y-2 text-[13px] text-[#888]">
              {['Denial of service / flooding attacks','Social engineering or phishing attacks','Vulnerabilities in third-party dependencies not directly exploitable','Missing security headers (without demonstrated impact)','Attacks requiring physical device access','Previously known and reported issues'].map(i=><p key={i} className="flex gap-2"><span className="text-red-500">—</span>{i}</p>)}
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div>
          <div className="flex items-center justify-between mb-8">
            {[{n:1,l:'Reporter'},{n:2,l:'Vulnerability'},{n:3,l:'Technical Details'},{n:4,l:'Review & Submit'}].map((s,i)=>(
              <div key={s.n} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold transition-all ${step>s.n?'bg-emerald-500 text-white':step===s.n?'bg-white text-black':'bg-[#111] text-[#444] border border-[#333]'}`}>{step>s.n?<CheckCircle size={12}/>:s.n}</div>
                  <span className={`text-[10px] mt-1 uppercase tracking-wider ${step===s.n?'text-white':'text-[#444]'}`}>{s.l}</span>
                </div>
                {i<3&&<div className={`flex-1 h-px mx-2 ${step>s.n?'bg-emerald-500':'bg-[#1a1a1a]'}`}/>}
              </div>
            ))}
          </div>

          <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-6 md:p-8 space-y-5">
            {step===1&&(
              <div className="space-y-5">
                <div><p className="text-[11px] text-[#555] uppercase tracking-widest mb-4">Step 1 — Reporter Information</p></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Inp label="Full Name *" placeholder="Your real name" value={form.name} onChange={e=>f('name',e.target.value)}/>
                  <Inp label="Email Address *" type="email" placeholder="security@example.com" value={form.email} onChange={e=>f('email',e.target.value)}/>
                </div>
                <Inp label="Security Handle / Alias" placeholder="@h4x0r, HackerOne handle, etc." value={form.handle} onChange={e=>f('handle',e.target.value)}/>
                <div className="border border-[#111] rounded-xl p-4 bg-black">
                  <p className="text-[12px] text-[#555] leading-[1.8]">Your personal information is kept confidential. It will only be used to contact you regarding the report status and reward payment. We do not share researcher identities publicly without explicit consent.</p>
                </div>
              </div>
            )}
            {step===2&&(
              <div className="space-y-5">
                <div><p className="text-[11px] text-[#555] uppercase tracking-widest mb-4">Step 2 — Vulnerability Classification</p></div>
                <Inp label="Vulnerability Title *" placeholder="Stored XSS in profile bio field" value={form.title} onChange={e=>f('title',e.target.value)}/>
                <div>
                  <label className="block text-[12px] text-[#888] mb-2">Vulnerability Type *</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {vulnTypes.map(v=>(
                      <button key={v.id} type="button" onClick={()=>{f('vuln_type',v.id);f('severity',v.sev);}} className={`text-left px-3 py-2.5 rounded-xl text-[11px] border transition ${form.vuln_type===v.id?'border-white bg-[#111] text-white':'border-[#222] text-[#555] hover:text-white hover:border-[#333]'}`}>{v.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-[#888] mb-2">Severity *</label>
                  <div className="flex gap-2 flex-wrap">
                    {['informational','low','medium','high','critical'].map(s=>(
                      <button key={s} type="button" onClick={()=>f('severity',s)} className={`px-4 py-2 rounded-xl text-[12px] border capitalize transition ${form.severity===s?sevColors[s]+' border-current':'border-[#222] text-[#555]'}`}>{s}</button>
                    ))}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Inp label="CVSS Score (0.0 – 10.0)" placeholder="e.g. 8.5" type="number" step="0.1" min="0" max="10" value={form.cvss_score} onChange={e=>f('cvss_score',e.target.value)}/>
                  <Inp label="CVSS Vector String" placeholder="CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H" value={form.cvss_vector} onChange={e=>f('cvss_vector',e.target.value)}/>
                </div>
              </div>
            )}
            {step===3&&(
              <div className="space-y-5">
                <div><p className="text-[11px] text-[#555] uppercase tracking-widest mb-4">Step 3 — Technical Details</p></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Inp label="Target URL *" placeholder="https://app.zurex92.io/api/v1/..." value={form.target_url} onChange={e=>f('target_url',e.target.value)}/>
                  <Inp label="Affected Component" placeholder="Login endpoint / User profile API / Admin panel" value={form.affected_component} onChange={e=>f('affected_component',e.target.value)}/>
                </div>
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Executive Summary *</label>
                  <textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[13px] h-24 resize-y outline-none placeholder:text-[#333]" placeholder="Brief one-paragraph description of the vulnerability and its impact..." value={form.summary} onChange={e=>f('summary',e.target.value)}/>
                </div>
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Steps to Reproduce *</label>
                  <textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[13px] h-36 resize-y outline-none font-mono placeholder:text-[#333]" placeholder={"1. Log in as a normal user\n2. Navigate to /profile/edit\n3. Enter the payload in the bio field: <script>alert(1)</script>\n4. Save and view the profile\n5. Observe XSS execution"} value={form.steps_to_reproduce} onChange={e=>f('steps_to_reproduce',e.target.value)}/>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] text-[#888] mb-1">Impact Assessment</label>
                    <textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[13px] h-28 resize-y outline-none placeholder:text-[#333]" placeholder="What data/systems are at risk? Who is affected? What is the worst-case scenario?" value={form.impact} onChange={e=>f('impact',e.target.value)}/>
                  </div>
                  <div>
                    <label className="block text-[12px] text-[#888] mb-1">Suggested Remediation</label>
                    <textarea className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[13px] h-28 resize-y outline-none placeholder:text-[#333]" placeholder="How should the vulnerability be fixed? Relevant secure coding guidance..." value={form.remediation} onChange={e=>f('remediation',e.target.value)}/>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Proof of Concept Code</label>
                  <textarea className="w-full bg-black border border-[#222] px-4 py-3 text-emerald-400 rounded-xl text-[12px] h-32 resize-y outline-none font-mono placeholder:text-[#333]" placeholder={"// PoC code, payload, or curl command\ncurl -X POST https://target.com/api/... \\\n  -H 'Authorization: Bearer ...' \\\n  -d '{\"id\": \"../../etc/passwd\"}'"} value={form.poc_code} onChange={e=>f('poc_code',e.target.value)}/>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Inp label="Evidence / Screenshot URL" placeholder="https://imgur.com/... or direct link" value={form.attachment_url} onChange={e=>f('attachment_url',e.target.value)}/>
                </div>
              </div>
            )}
            {step===4&&(
              <div className="space-y-5">
                <div><p className="text-[11px] text-[#555] uppercase tracking-widest mb-4">Step 4 — Review & Submit</p></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black space-y-2">
                    <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Reporter</p>
                    <p className="text-white text-[14px]">{form.name||'—'}</p>
                    <p className="text-[#666] text-[12px]">{form.email||'—'}</p>
                    {form.handle&&<p className="text-[#666] text-[12px]">{form.handle}</p>}
                  </div>
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black space-y-2">
                    <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Vulnerability</p>
                    <p className="text-white text-[14px]">{form.title||'—'}</p>
                    <div className="flex gap-2 mt-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${sevColors[form.severity]||''}`}>{form.severity}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#333] text-[#666]">{form.vuln_type}</span>
                    </div>
                  </div>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Summary</p>
                  <p className="text-[13px] text-[#888] leading-[1.8] whitespace-pre-wrap">{form.summary||'—'}</p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Target</p>
                  <p className="text-[13px] text-white font-mono break-all">{form.target_url||'—'}</p>
                  {form.affected_component&&<p className="text-[12px] text-[#666] mt-1">{form.affected_component}</p>}
                </div>
                <div className="border border-amber-900/40 bg-amber-950/10 rounded-xl p-4">
                  <p className="text-[12px] text-amber-400/80 leading-[1.7]">By submitting this report, you confirm that: (1) you have authorization to test this system, (2) no production data was accessed beyond what was necessary to demonstrate the vulnerability, and (3) you agree to responsible disclosure practices — no public disclosure until we have resolved the issue.</p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-[#111]">
              {step>1?<Btn v="ghost" onClick={()=>setStep(s=>s-1)}>← Back</Btn>:<div/>}
              {step<4
                ?<Btn onClick={()=>canNext()&&setStep(s=>s+1)} disabled={!canNext()}>Next Step →</Btn>
                :<Btn onClick={submit} disabled={submitting}>{submitting?'Submitting Report...':'Submit Report'}</Btn>
              }
            </div>
          </div>
        </div>
      </div>
    </PW>
  );
}



// ═══ AUTH ═══
function Auth({ mode, onAuth }) {
  const nav = useNavigate();
  const toast = useToast();

  const [f, sF] = useState({
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    first_name: "",
    last_name: "",
    how_heard: "",
  });

  const [e, sE] = useState("");
  const [ld, sL] = useState(false);

  const genPw = async () => {
    try {
      const r = await axios.get(`${API}/api/v1/auth/generate-password`);
      sF((x) => ({
        ...x,
        password: r.data.password,
        confirm_password: r.data.password,
      }));
      toast("Generated", "success");
    } catch {}
  };

  const go = async (ev) => {
    ev.preventDefault();
    sE("");

    if (mode === "register" && f.password !== f.confirm_password) {
      sE("Passwords do not match");
      return;
    }

    sL(true);

    try {
      let r;

      if (mode === "login") {
        r = await axios.post(`${API}/api/v1/auth/login`, {
          username: f.username,
          password: f.password,
        });
      } else {
        r = await axios.post(`${API}/api/v1/auth/register`, {
          username: f.username,
          email: f.email,
          password: f.password,
          first_name: f.first_name,
          last_name: f.last_name,
          how_heard: f.how_heard,
        });
      }

      // Handle pending registration (no token returned)
      if (r.data.pending) {
        sE("__PENDING__");
        sL(false);
        return;
      }
      localStorage.setItem("zx_t", r.data.access_token);
      localStorage.setItem("zx_u", JSON.stringify(r.data.user));
      onAuth(r.data.user);
      nav("/dashboard");
    } catch (ex) {
      sE(E(ex));
    }

    sL(false);
  };

  const leftCards = [
    {
      title: "Operational visibility",
      desc: "Track investigations, intelligence events, and platform activity through a clearer operational surface.",
    },
    {
      title: "Structured workflows",
      desc: "Move from collection to analysis to investigation without losing context or momentum.",
    },
    {
      title: "Investigation-first design",
      desc: "Built for analysts and operators who need precision, speed, and cleaner intelligence handling.",
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="grid min-h-screen lg:grid-cols-2">
        <div className="relative hidden lg:flex border-r border-[#111] overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.06),transparent_35%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(255,255,255,0.02),transparent)] opacity-60" />

          <div className="relative z-10 w-full max-w-[720px] px-12 xl:px-16 py-14 flex flex-col justify-between">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="flex items-center gap-2"
            >
              <ZIcon s={18} />
              <span className="text-[14px] font-semibold tracking-tight">
                ZureX92
              </span>
            </motion.div>

            <div>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.05 }}
                className="text-[11px] uppercase tracking-[0.32em] text-[#666] mb-6"
              >
                Cyber Intelligence Platform
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[54px] xl:text-[66px] leading-[0.96] tracking-tight font-semibold max-w-[640px] mb-8"
              >
                Build a clearer
                <br />
                intelligence workflow.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.16 }}
                className="text-[17px] text-[#7a7a7a] leading-[1.85] max-w-[620px]"
              >
                ZureX92 helps analysts and operational teams structure
                intelligence collection, investigations, and visibility through
                a calmer, more focused platform experience.
              </motion.p>

              <div className="grid gap-4 mt-12">
                {leftCards.map((card, i) => (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: 0.25 + i * 0.08 }}
                    whileHover={{ y: -3 }}
                    className="border border-[#171717] rounded-[24px] bg-[#050505] p-6"
                  >
                    <h3 className="text-[18px] font-medium tracking-tight mb-3">
                      {card.title}
                    </h3>
                    <p className="text-[14px] text-[#777] leading-[1.8]">
                      {card.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.45 }}
              className="grid grid-cols-3 gap-4"
            >
              <div className="border border-[#151515] rounded-2xl p-4 bg-[#050505]">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#555] mb-2">
                  Cases
                </p>
                <p className="text-[28px] font-semibold">27</p>
              </div>

              <div className="border border-[#151515] rounded-2xl p-4 bg-[#050505]">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#555] mb-2">
                  Events
                </p>
                <p className="text-[28px] font-semibold">80</p>
              </div>

              <div className="border border-[#151515] rounded-2xl p-4 bg-[#050505]">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#555] mb-2">
                  Analysts
                </p>
                <p className="text-[28px] font-semibold">11</p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.04),transparent_30%)] lg:hidden" />

          <div className="relative z-10 w-full max-w-[460px]">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:hidden text-center mb-10"
            >
              <div className="inline-flex items-center gap-2 mb-5">
                <ZIcon s={20} />
                <span className="text-[15px] font-semibold">ZureX92</span>
              </div>

              <h1 className="text-[30px] sm:text-[36px] font-semibold tracking-tight leading-[1.05] mb-4">
                {mode === "login" ? "Welcome back." : "Create your account."}
              </h1>

              <p className="text-[14px] text-[#777] leading-[1.8] max-w-[360px] mx-auto">
                Access a structured intelligence platform designed for clearer
                workflows and calmer operations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
              className="border border-[#171717] bg-[#050505] rounded-[28px] p-6 sm:p-8 shadow-[0_0_0_1px_rgba(255,255,255,0.01)]"
            >
              <div className="hidden lg:block mb-8">
                <div className="flex items-center gap-2 mb-5">
                  <ZIcon s={20} />
                  <span className="text-[15px] font-semibold tracking-tight">
                    ZureX92
                  </span>
                </div>

                <h2 className="text-[34px] font-semibold tracking-tight leading-[1.05] mb-3">
                  {mode === "login" ? "Welcome back." : "Create your account."}
                </h2>

                <p className="text-[14px] text-[#777] leading-[1.8]">
                  {mode === "login"
                    ? "Sign in to continue to your intelligence workspace."
                    : "Start with a cleaner access flow built for a modern cyber intelligence platform."}
                </p>
              </div>

              {e === "__PENDING__" ? (
                <div className="border border-emerald-800 bg-emerald-950 rounded-2xl p-5 mb-4 text-center">
                  <div className="mb-3 flex justify-center"><CheckCircle size={28} className="text-emerald-500"/></div>
                  <p className="text-emerald-400 text-[15px] font-semibold mb-2">Application Submitted</p>
                  <p className="text-[13px] text-emerald-300/80 leading-[1.7]">
                    Your access request has been received. You will be contacted once your account is activated.
                  </p>
                </div>
              ) : e ? (
                <div className="border border-red-900 bg-red-950 text-red-400 text-[13px] p-3 rounded-xl mb-4">
                  {e}
                </div>
              ) : null}

              <form onSubmit={go} className="space-y-4">
                {mode === "register" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Inp
                      placeholder="First name"
                      value={f.first_name}
                      onChange={(x) => sF({ ...f, first_name: x.target.value })}
                      required
                    />
                    <Inp
                      placeholder="Last name"
                      value={f.last_name}
                      onChange={(x) => sF({ ...f, last_name: x.target.value })}
                      required
                    />
                  </div>
                )}

                <Inp
                  placeholder="Username"
                  value={f.username}
                  onChange={(x) => sF({ ...f, username: x.target.value })}
                  required
                  autoFocus
                />

                {mode === "register" && (
                  <Inp
                    type="email"
                    placeholder="Email address"
                    value={f.email}
                    onChange={(x) => sF({ ...f, email: x.target.value })}
                    required
                  />
                )}

                <div className="flex gap-2">
                  <div className="flex-1">
                    <PwI
                      placeholder="Password"
                      value={f.password}
                      onChange={(x) => sF({ ...f, password: x.target.value })}
                      required
                    />
                  </div>

                  {mode === "register" && (
                    <button
                      type="button"
                      onClick={genPw}
                      className="mt-auto px-3 py-2 bg-[#0c0c0c] border border-[#222] rounded-lg text-[11px] text-[#888] hover:text-white hover:border-[#333] transition"
                    >
                      Gen
                    </button>
                  )}
                </div>

                {mode === "register" && (
                  <>
                    <PwI
                      placeholder="Confirm password"
                      value={f.confirm_password}
                      onChange={(x) =>
                        sF({ ...f, confirm_password: x.target.value })
                      }
                      required
                    />

                    <select
                      className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2.5 text-white rounded-lg text-[13px] outline-none"
                      value={f.how_heard}
                      onChange={(x) => sF({ ...f, how_heard: x.target.value })}
                    >
                      <option value="">How did you hear about us?</option>
                      <option value="social_media">Social Media</option>
                      <option value="search_engine">Search Engine</option>
                      <option value="referral">Referral</option>
                      <option value="other">Other</option>
                    </select>
                  </>
                )}

                <button
                  type="submit"
                  disabled={ld}
                  className="w-full bg-white text-black py-3 rounded-xl text-[14px] font-medium hover:bg-[#ebebeb] transition disabled:opacity-40"
                >
                  {ld
                    ? "Please wait..."
                    : mode === "login"
                    ? "Continue"
                    : "Create account"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-[13px] text-[#555]">
                  {mode === "login" ? (
                    <>
                      No account?{" "}
                      <button
                        className="text-white hover:text-[#cfcfcf] transition"
                        onClick={() => nav("/register")}
                      >
                        Sign Up
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button
                        className="text-white hover:text-[#cfcfcf] transition"
                        onClick={() => nav("/login")}
                      >
                        Log In
                      </button>
                    </>
                  )}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
// ═══ ALL PAGES (exact same as working v12 — only branding changed) ═══
// ════════════════════════════════════════════════════════
// GUIDED TOUR v2 — Professional Step-by-Step Guide
// ════════════════════════════════════════════════════════
function GuidedTour({ steps, onClose }) {
  const [step, setStep]   = useState(0);
  const [ready, setReady] = useState(false);
  const cardRef           = React.useRef(null);
  const cur               = steps[step];
  const isLast            = step === steps.length - 1;

  // Scroll target into view & highlight
  useEffect(() => {
    setReady(false);
    const timer = setTimeout(() => {
      if (!cur?.target) { setReady(true); return; }
      const el = document.querySelector(cur.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        setTimeout(() => {
          el.setAttribute('data-tour-active', '1');
          setReady(true);
        }, 400);
      } else {
        setReady(true);
      }
      // Clean previous highlights
      document.querySelectorAll('[data-tour-active]').forEach(e => {
        if (e !== el) e.removeAttribute('data-tour-active');
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      document.querySelectorAll('[data-tour-active]').forEach(e => e.removeAttribute('data-tour-active'));
    };
  }, [step]);

  // Compute tooltip position relative to target
  const [tooltip, setTooltip] = useState({ top: '50%', left: '50%', place: 'center' });
  useEffect(() => {
    if (!ready) return;
    if (!cur?.target) {
      setTooltip({ top: '50%', left: '50%', place: 'center' });
      return;
    }
    const el = document.querySelector(cur.target);
    if (!el) { setTooltip({ top: '50%', left: '50%', place: 'center' }); return; }

    const rect  = el.getBoundingClientRect();
    const vw    = window.innerWidth;
    const vh    = window.innerHeight;
    const pad   = 16;
    const cw    = 360;
    const ch    = 240;
    const place = cur.placement || 'auto';

    let top, left, finalPlace;

    // Auto-determine best placement
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = vw - rect.right;
    const spaceLeft  = rect.left;

    if (place === 'auto' || !place) {
      if (spaceBelow >= ch + pad)       finalPlace = 'bottom';
      else if (spaceAbove >= ch + pad)  finalPlace = 'top';
      else if (spaceRight >= cw + pad)  finalPlace = 'right';
      else                              finalPlace = 'top';
    } else {
      finalPlace = place;
    }

    if (finalPlace === 'bottom') {
      top  = rect.bottom + pad + window.scrollY;
      left = Math.max(pad, Math.min(vw - cw - pad, rect.left + window.scrollX));
    } else if (finalPlace === 'top') {
      top  = rect.top - ch - pad + window.scrollY;
      left = Math.max(pad, Math.min(vw - cw - pad, rect.left + window.scrollX));
    } else if (finalPlace === 'right') {
      top  = rect.top + window.scrollY;
      left = rect.right + pad + window.scrollX;
    } else if (finalPlace === 'left') {
      top  = rect.top + window.scrollY;
      left = rect.left - cw - pad + window.scrollX;
    } else {
      top  = '50%';
      left = '50%';
      finalPlace = 'center';
    }

    setTooltip({ top, left, place: finalPlace });
  }, [ready, step, cur]);

  const handleNext = () => { if (isLast) { onClose(); } else { setStep(s => s + 1); } };
  const handlePrev = () => setStep(s => s - 1);

  const isCenter = tooltip.place === 'center';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[9998]"
        style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(3px)' }}
        onClick={onClose}
      />

      {/* Spotlight ring around target */}
      <style>{`
        [data-tour-active] {
          position: relative;
          z-index: 9999 !important;
          outline: 3px solid rgba(255,255,255,0.9) !important;
          outline-offset: 6px !important;
          border-radius: 14px !important;
          box-shadow: 0 0 0 6px rgba(255,255,255,0.15), 0 0 60px rgba(255,255,255,0.08) !important;
        }
      `}</style>

      {/* Tooltip Card */}
      <motion.div
        key={step}
        ref={cardRef}
        initial={{ opacity: 0, y: 12, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        onClick={e => e.stopPropagation()}
        style={isCenter
          ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10000, width: 380 }
          : { position: 'absolute', top: tooltip.top, left: tooltip.left, zIndex: 10000, width: 360 }
        }
        className="bg-[#0a0a0a] border border-white/15 rounded-[20px] shadow-2xl overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-0.5 bg-[#1a1a1a]">
          <motion.div
            className="h-full bg-white"
            initial={{ width: 0 }}
            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              {cur.icon && (
                <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-white flex-shrink-0">
                  {cur.icon}
                </div>
              )}
              <div>
                <p className="text-white font-semibold text-[15px] leading-tight">{cur.title}</p>
                <p className="text-[11px] text-[#555] mt-0.5">Step {step + 1} of {steps.length}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-[#444] hover:text-white transition p-1 flex-shrink-0">
              <XCircle size={15}/>
            </button>
          </div>

          {/* Description */}
          <p className="text-[#888] text-[13px] leading-[1.75] mb-5">{cur.desc}</p>

          {/* Tip */}
          {cur.tip && (
            <div className="border border-amber-900/30 bg-amber-950/20 rounded-xl px-4 py-2.5 mb-4">
              <p className="text-amber-400/90 text-[12px] leading-[1.6]"><span className="font-semibold">Tip: </span>{cur.tip}</p>
            </div>
          )}

          {/* Step dots + actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-[#111]">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`rounded-full transition-all ${i === step ? 'w-5 h-2 bg-white' : i < step ? 'w-2 h-2 bg-emerald-500' : 'w-2 h-2 bg-[#333]'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={onClose} className="text-[#444] hover:text-white text-[12px] transition px-2 py-1">Skip all</button>
              {step > 0 && (
                <button onClick={handlePrev}
                  className="px-3 py-1.5 border border-[#222] rounded-xl text-[12px] text-[#666] hover:text-white hover:border-[#333] transition">
                  ← Back
                </button>
              )}
              <button onClick={handleNext}
                className="px-4 py-1.5 bg-white text-black rounded-xl text-[13px] font-semibold hover:bg-[#eee] transition">
                {isLast ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}



function DeployPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();
  const nav = useNavigate();

  /* ── State ── */
  const [ld, sL]                     = useState(false);
  const [deploying, setDeploying]    = useState(false); // for animation
  const [result, setResult]          = useState(null);
  const [hist, setHist]              = useState([]);
  const [pages, setPages]            = useState([]);
  const [showAdv, setShowAdv]        = useState(false);
  const [ogLoading, setOgL]          = useState(false);
  const [poolDomains, setPool]       = useState([]);
  const [myDomains, setMyDomains]    = useState([]);
  const [newDomain, setNewDomain]    = useState('');
  const [addingDom, setAddingDom]    = useState(false);
  const [testingRedir, setTestRedir] = useState(false);
  const topRef                       = React.useRef(null);
  const bottomRef                    = React.useRef(null);
  const [showTour, setShowTour]      = useState(() => !localStorage.getItem('deploy_tour_done'));

  const TOUR_STEPS = [
    {
      target: '[data-tour="target-url"]', placement: 'bottom',
      icon: <Globe size={16}/>,
      title: 'Target URL — Where to redirect',
      desc: 'Enter the URL your target will land on after the tracker fires. This is the destination — could be a legitimate site, YouTube video, document, anything.',
      tip: 'Use a real, relevant URL. If the target expects a YouTube link, use one.',
    },
    {
      target: '[data-tour="campaign"]', placement: 'bottom',
      icon: <FileText size={16}/>,
      title: 'Campaign Name — Label your operation',
      desc: 'A private label for your reference only — the target never sees this. Helps organize multiple deployments in your Feed.',
      tip: 'Use codenames like "Op Falcon" or "Q4 Research" for security.',
    },
    {
      target: '[data-tour="templates"]', placement: 'bottom',
      icon: <Layers size={16}/>,
      title: 'Page Template — What the target sees',
      desc: 'The page displayed to the target before the redirect fires. CAPTCHA and CLOUDFLARE are the most convincing — they look like real security checks.',
      tip: 'Social media templates (YouTube, Instagram) work best when the OG preview matches the platform.',
    },
    {
      target: '[data-tour="slug-format"]', placement: 'bottom',
      icon: <Link2 size={16}/>,
      title: 'Link Format — Shape the URL path',
      desc: 'Controls what /r/XXXX looks like. "Word" format (e.g. /r/secure-4x2b) looks most natural in messages. "Random" is hardest to predict.',
      tip: 'Avoid "campaign" format if operational security matters — it reveals your operation name.',
    },
    {
      target: '[data-tour="collection"]', placement: 'top',
      icon: <Target size={16}/>,
      title: 'Collection Mode — Intelligence depth',
      desc: 'Zero-Click: fires silently on page load with no user interaction. One-Click: shows a button and requests device permissions when clicked — far richer data.',
      tip: 'Use One-Click when you need GPS or camera. Use Zero-Click for passive, undetectable collection.',
    },
    {
      target: '[data-tour="og-preview"]', placement: 'left',
      icon: <ImageIcon size={16}/>,
      title: 'Link Preview — Social media appearance',
      desc: 'This is what WhatsApp, Telegram, Twitter, and iMessage show when someone pastes your link. Customize it to match the context — a YouTube video, a news article, a document.',
      tip: 'Click "Fetch OG" to auto-pull the real preview from the target URL, then customize title/image to match your story.',
    },
    {
      target: '[data-tour="deploy-btn"]', placement: 'top',
      icon: <Rocket size={16}/>,
      title: 'Deploy — Launch the operation',
      desc: 'Click Deploy to generate your intelligence link. The link is live immediately. Every visit is logged in your Feed with full telemetry.',
      tip: 'After deploying, copy the link from the result box and send it through your chosen channel.',
    },
  ];

  /* ── Form ── */
  const [f, sF] = useState({
    target_url: '', campaign_name: '', template: 'STANDARD',
    collection_mode: 'zero_click', deploy_mode: 'group',
    custom_slug: '', slug_format: 'random',
    og_title: '', og_description: '', og_image: '',
    permission_flags: { geolocation:false, camera:false, microphone:false, screen:false, clipboard:false, notifications:false },
  });
  const sf = p => sF(x => ({...x, ...p}));

  /* ── Advanced domain ── */
  const [domainMethod, setDomainMethod] = useState('platform');
  const [dc, setDc] = useState({
    poolDomain: '', customDomain: '', subdomain: '',
    redirectBase: '', redirectTechnique: 'at', chainCount: 2,
  });
  const sdc = p => setDc(x => ({...x, ...p}));

  /* ── Slug preview ── */
  const [slugPreview, setSlugP] = useState('');
  useEffect(() => {
    const W = ['secure','verify','access','portal','vault','shield','relay','proxy','nexus'];
    const r = () => Math.random().toString(36).slice(2,9);
    if (f.slug_format === 'campaign')    setSlugP(`${(f.campaign_name||'op').toLowerCase().replace(/[^a-z0-9]/g,'-').slice(0,16)}-${r().slice(0,4)}`);
    else if (f.slug_format === 'word')   setSlugP(`${W[Math.floor(Math.random()*W.length)]}-${r().slice(0,4)}`);
    else if (f.slug_format === 'custom') setSlugP(f.custom_slug || 'your-slug');
    else                                 setSlugP(r());
  }, [f.slug_format, f.campaign_name, f.custom_slug]);

  /* ── Auto-fetch OG ── */
  const ogTimer = React.useRef(null);
  useEffect(() => {
    if (!f.target_url?.startsWith('http')) return;
    clearTimeout(ogTimer.current);
    ogTimer.current = setTimeout(doFetchOG, 1500);
    return () => clearTimeout(ogTimer.current);
  }, [f.target_url]);

  async function doFetchOG() {
    if (!f.target_url?.startsWith('http')) return;
    setOgL(true);
    try {
      const r = await api.get(`/api/v1/og-preview?url=${encodeURIComponent(f.target_url)}`);
      if (r.data?.ok) {
        sf({
          og_title:       r.data.title       || '',
          og_description: r.data.description || '',
          og_image:       r.data.image       || '',
        });
        toast('Link preview fetched', 'success');
      }
    } catch(e) {}
    setOgL(false);
  }

  /* ── Load ── */
  useEffect(() => {
    api.get('/api/v1/traps/history').then(r => setHist(r.data||[])).catch(()=>{});
    api.get('/api/v1/pages').then(r => setPages(r.data||[])).catch(()=>{});
    api.get('/api/v1/domains/pool').then(r => setPool(r.data||[])).catch(()=>{});
    api.get('/api/v1/domains/my').then(r => setMyDomains(r.data?.domains||[])).catch(()=>{});
  }, []);

  /* ── Open Redirect test ── */
  async function testRedirect() {
    if (!dc.redirectBase) return toast('Enter a redirect base URL first','error');
    setTestRedir(true);
    const testUrl = 'https://example.com';
    const enc = encodeURIComponent(testUrl);
    const b   = dc.redirectBase.replace(/[/?=&]+$/,'');
    let final;
    if      (dc.redirectTechnique==='at')    final = `${b}%23@${enc}`;
    else if (dc.redirectTechnique==='hash')  final = `${b}#@${enc}`;
    else if (dc.redirectTechnique==='slash') final = `${b}//${enc}`;
    else                                     final = dc.redirectBase + enc;
    try {
      await fetch(final, { method:'HEAD', mode:'no-cors', signal: AbortSignal.timeout(5000) });
      toast('Request sent — open the URL in browser to test: ' + final.slice(0,60)+'...','success');
    } catch(e) {
      toast('Test sent (CORS blocked in browser is normal). URL: ' + final.slice(0,50),'success');
    }
    setTestRedir(false);
  }

  /* ── Deploy ── */
  async function deploy(e) {
    e?.preventDefault();
    if (!f.target_url) return toast('Target URL is required','error');
    sL(true);
    try {
      const payload = {
        ...f,
        slug_format: f.slug_format,
        custom_slug: f.custom_slug,
      };
      const r = await api.post('/api/v1/traps', payload);
      const slug    = r.data.slug || r.data.custom_slug || '';
      const base    = process.env.REACT_APP_API_BASE_URL || '';
      const trapUrl = r.data.trap_url || `${base}/r/${slug}`;
      let finalUrl  = trapUrl;

      if      (domainMethod==='pool'     && dc.poolDomain)   finalUrl = `https://${dc.poolDomain}/r/${slug}`;
      else if (domainMethod==='custom'   && dc.customDomain) finalUrl = `https://${dc.customDomain}/r/${slug}`;
      else if (domainMethod==='subdomain'&& dc.subdomain)    finalUrl = `https://${dc.subdomain}.${base.replace('https://','')}/r/${slug}`;

      if (domainMethod==='cloak'||domainMethod==='chain') {
        try { const s = await api.post('/api/v1/links/cloak',{url:finalUrl,chain:dc.chainCount||2}); finalUrl=s.data.cloaked||finalUrl; } catch(e){}
      } else if (domainMethod==='redirect') {
        // @ Symbol template: trusted.com%23@tracking-domain/r/slug
        if (dc.redirectTemplateId==='at' && dc.atSymbolTrusted && dc.atSymbolDomain) {
          finalUrl = `https://${dc.atSymbolTrusted}%23@${dc.atSymbolDomain}/r/${slug}`;
        } else if (dc.redirectBase) {
          const enc = encodeURIComponent(finalUrl);
          const b   = dc.redirectBase.replace(/[/?=&]+$/,'');
          const tech = dc.redirectTechnique||'param';
          if      (tech==='at')    finalUrl = `${b}%23@${enc}`;
          else if (tech==='hash')  finalUrl = `${b}#@${enc}`;
          else if (tech==='slash') finalUrl = `${b}//${enc}`;
          else                     finalUrl = dc.redirectBase + enc;
        }
      }

      setResult({ trap_id: r.data.trap_id, slug, final_url: finalUrl, raw_url: trapUrl, method: domainMethod });
      setHist(h => [{ trap_id:r.data.trap_id, campaign_name:f.campaign_name, template:f.template, visit_count:0, created_at:new Date().toISOString() }, ...h]);
      toast('Link deployed successfully','success');
      setTimeout(() => topRef.current?.scrollIntoView({behavior:'smooth'}), 100);
    } catch(ex) { toast(E(ex),'error'); }
    sL(false);
  }

  const buildRedirectPreview = (base, tech, target='[tracking-url]') => {
    if (!base) return '';
    const enc = encodeURIComponent(target);
    const b   = base.replace(/[/?=&]+$/,'');
    if (tech==='at')    return `${b}%23@${target}`;
    if (tech==='hash')  return `${b}#@${target}`;
    if (tech==='slash') return `${b}//${target}`;
    return base + target;
  };

  const REDIRECT_TEMPLATES = [
    { id:'google',   label:'Google',    base:'https://www.google.com/url?q=' },
    { id:'youtube',  label:'YouTube',   base:'https://www.youtube.com/redirect?q=' },
    { id:'facebook', label:'Facebook',  base:'https://l.facebook.com/l.php?u=' },
    { id:'linkedin', label:'LinkedIn',  base:'https://www.linkedin.com/redir/redirect?url=' },
    { id:'slack',    label:'Slack',     base:'https://slack-redir.net/link?url=' },
    { id:'github',   label:'GitHub',    base:'https://github.com/login?return_to=' },
    { id:'custom',   label:'Custom URL',base:'' },
  ];

  const TPLS_SYS    = ['STANDARD','CAPTCHA','CLOUDFLARE','MAINTENANCE','UPDATE','COOKIE_CONSENT','AGE_VERIFY','DYNAMIC','CUSTOM'];
  const TPLS_SOCIAL = ['YOUTUBE','INSTAGRAM','FACEBOOK','X_TWITTER','TIKTOK','TELEGRAM','WHATSAPP','LINKEDIN'];
  const PERMS = [
    {id:'geolocation',  l:'GPS Location',     icon:<Navigation size={12}/>, desc:'Precise coordinates'},
    {id:'camera',       l:'Camera',           icon:<Camera size={12}/>,     desc:'Photo & video access'},
    {id:'microphone',   l:'Microphone',       icon:<Radio size={12}/>,      desc:'Audio recording'},
    {id:'screen',       l:'Screen Capture',   icon:<Monitor size={12}/>,    desc:'Screenshot capture'},
    {id:'clipboard',    l:'Clipboard',        icon:<Copy size={12}/>,       desc:'Read clipboard text'},
    {id:'notifications',l:'Notifications',    icon:<Bell size={12}/>,       desc:'Push alerts'},
  ];

  const hostname = f.target_url ? (()=>{ try{return new URL(f.target_url).hostname.replace('www.','')}catch{return 'example.com'} })() : 'example.com';

  /* ─────────────────────── RENDER ─────────────────────── */
  return (
    <div className="space-y-6" ref={topRef}>
      <Modal/>
      {showTour && <GuidedTour steps={TOUR_STEPS} onClose={()=>{ setShowTour(false); localStorage.setItem('deploy_tour_done','1'); }}/>}

      {/* Scroll controls */}
      <div className="fixed right-6 bottom-24 z-40 flex flex-col gap-2">
        <button onClick={()=>topRef.current?.scrollIntoView({behavior:'smooth'})}
          className="w-10 h-10 border border-[#1a1a1a] bg-[#050505] rounded-xl flex items-center justify-center text-[#666] hover:text-white hover:border-[#333] transition shadow-lg">
          <ChevronUp size={16}/>
        </button>
        <button onClick={()=>bottomRef.current?.scrollIntoView({behavior:'smooth'})}
          className="w-10 h-10 border border-[#1a1a1a] bg-[#050505] rounded-xl flex items-center justify-center text-[#666] hover:text-white hover:border-[#333] transition shadow-lg">
          <ChevronDown size={16}/>
        </button>
      </div>

      {/* Header */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={()=>nav('/operations')} className="flex items-center gap-1.5 text-[#555] hover:text-white transition text-[13px]">
              <ArrowLeft size={14}/> Operations
            </button>
            <button onClick={()=>setShowTour(true)} className="flex items-center gap-1.5 text-[#444] hover:text-white transition text-[12px] border border-[#1a1a1a] px-3 py-1.5 rounded-xl">
              <Info size={12}/> How it works
            </button>
          </div>
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#555] mb-2">Deploy</p>
            <h1 className="text-[30px] md:text-[38px] font-semibold tracking-tight text-white leading-none">Intelligence Link</h1>
            <p className="text-[14px] text-[#666] mt-3 max-w-[580px] leading-[1.8]">Configure a tracking link and deploy it with one click.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={deploy} disabled={ld||!f.target_url}
              className="flex items-center gap-2 px-4 py-2.5 bg-white text-black rounded-2xl text-[13px] font-semibold hover:bg-[#eee] disabled:opacity-30 transition">
              {ld ? <><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin"/><span>Deploying</span></> : <><Rocket size={13}/><span>Deploy</span></>}
            </button>
            <button onClick={()=>setShowAdv(v=>!v)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-2xl text-[13px] font-medium transition ${showAdv?'border-white text-white bg-[#111]':'border-[#222] text-[#666] hover:text-white hover:border-[#333]'}`}>
              <Settings size={13}/>
              {showAdv ? 'Hide Advanced' : 'Advanced'}
            </button>
          </div>
        </div>
      </div>



      {/* ── RESULT ── */}
      {!deploying && result && (
        <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}}
          className="border border-emerald-800/60 bg-emerald-950/20 rounded-[28px] p-6">
          <div className="flex items-start justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-950 border border-emerald-800 rounded-2xl flex items-center justify-center">
                <CheckCircle size={18} className="text-emerald-400"/>
              </div>
              <div>
                <p className="text-white font-semibold text-[16px]">Link Deployed Successfully</p>
                <p className="text-[12px] text-[#555] mt-0.5">ID: {result.trap_id}</p>
              </div>
            </div>
            <button onClick={()=>setResult(null)} className="text-[#444] hover:text-white transition p-1"><XCircle size={16}/></button>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-[#444] uppercase tracking-wider mb-2">Share this link</p>
              <div className="bg-black border border-emerald-900/30 rounded-2xl p-4 flex items-center gap-3">
                <p className="text-emerald-400 font-mono text-[14px] flex-1 break-all">{result.final_url}</p>
                <button onClick={()=>{navigator.clipboard?.writeText(result.final_url);toast('Copied','success');}}
                  className="flex-shrink-0 p-2 border border-[#1a1a1a] rounded-xl hover:border-[#333] transition">
                  <Copy size={14} className="text-[#555] hover:text-white"/>
                </button>
              </div>
            </div>
            {result.final_url !== result.raw_url && (
              <div>
                <p className="text-[11px] text-[#444] uppercase tracking-wider mb-2">Raw tracking URL</p>
                <div className="bg-black border border-[#1a1a1a] rounded-xl p-3 flex items-center gap-3">
                  <p className="text-[#555] font-mono text-[12px] flex-1 break-all">{result.raw_url}</p>
                  <button onClick={()=>{navigator.clipboard?.writeText(result.raw_url);toast('Copied','success');}}
                    className="flex-shrink-0"><Copy size={12} className="text-[#333] hover:text-white"/></button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          {/* ══════════ LEFT COLUMN ══════════ */}
          <div className="space-y-5">

            {/* ── TARGET ── */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1a1a1a]">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Step 1</p>
                <h2 className="text-[16px] font-semibold text-white">Target & Campaign</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[13px] text-[#777] mb-2">Target URL <span className="text-red-500 text-[10px]">required</span></label>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                      placeholder="https://example.com"
                      type="url"
                      value={f.target_url}
                      onChange={e => sf({target_url: e.target.value})}/>
                    <button type="button" onClick={doFetchOG} disabled={ogLoading||!f.target_url?.startsWith('http')}
                      className="px-4 py-3 border border-[#222] bg-black rounded-2xl text-[13px] text-[#666] hover:text-white hover:border-[#444] disabled:opacity-30 transition flex items-center gap-2 whitespace-nowrap">
                      {ogLoading
                        ? <><span className="w-3.5 h-3.5 border-2 border-[#333] border-t-white rounded-full animate-spin"/><span>Fetching</span></>
                        : <><Search size={13}/><span>Fetch OG</span></>
                      }
                    </button>
                  </div>
                  {ogLoading && <p className="text-[12px] text-[#444] mt-1.5">Auto-fetching link preview from URL...</p>}
                </div>
                <div>
                  <label className="block text-[13px] text-[#777] mb-2">Campaign Name</label>
                  <input className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
    data-tour="campaign" placeholder="Op Falcon, Q4 Research..." value={f.campaign_name} onChange={e=>sf({campaign_name:e.target.value})}/>
                </div>
              </div>
            </div>

            {/* ── TEMPLATE ── */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1a1a1a]">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Step 2</p>
                <h2 className="text-[16px] font-semibold text-white">Page Template</h2>
                <p className="text-[12px] text-[#555] mt-0.5">The page the target sees before being redirected</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
  <p className="text-[11px] uppercase tracking-wider text-[#444] mb-2" data-tour="templates">System</p>
                  <div className="flex flex-wrap gap-2">
                    {TPLS_SYS.map(t=>(
                      <button key={t} type="button" onClick={()=>sf({template:t})}
                        className={`px-3 py-2 rounded-xl text-[13px] border transition ${f.template===t?'border-white text-white bg-[#111]':'border-[#1a1a1a] text-[#555] hover:text-white hover:border-[#333]'}`}>
                        {t.replace('_',' ')}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#444] mb-2">Social Media</p>
                  <div className="flex flex-wrap gap-2">
                    {TPLS_SOCIAL.map(t=>(
                      <button key={t} type="button" onClick={()=>sf({template:t})}
                        className={`px-3 py-2 rounded-xl text-[13px] border transition ${f.template===t?'border-white text-white bg-[#111]':'border-[#1a1a1a] text-[#555] hover:text-white hover:border-[#333]'}`}>
                        {t.replace('_',' ')}
                      </button>
                    ))}
                  </div>
                </div>
                {f.template==='CUSTOM' && (
                  <div>
                    <label className="block text-[13px] text-[#777] mb-2">Select Saved Page</label>
                    <select className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none" value={f.custom_page_id||''} onChange={e=>sf({custom_page_id:e.target.value})}>
                      <option value="">— Select page —</option>
                      {pages.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* ── LINK FORMAT ── */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1a1a1a]">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Step 3</p>
                <h2 className="text-[16px] font-semibold text-white">Link Format</h2>
              </div>
              <div className="p-6 space-y-4">
<div data-tour="slug-format" className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[{id:'random',l:'Random',ex:'a3f9x2k'},{id:'campaign',l:'Campaign',ex:'op-falcon-3f2a'},{id:'word',l:'Word',ex:'secure-4x2b'},{id:'custom',l:'Custom',ex:'your-slug'}].map(s=>(
                    <button key={s.id} type="button" onClick={()=>sf({slug_format:s.id})}
                      className={`text-left border rounded-2xl p-4 transition ${f.slug_format===s.id?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                      <p className="text-[13px] text-white font-medium">{s.l}</p>
                      <p className="text-[11px] text-[#444] font-mono mt-1">{s.ex}</p>
                    </button>
                  ))}
                </div>
                {f.slug_format==='custom' && (
                  <input className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                    placeholder="your-custom-slug" value={f.custom_slug} onChange={e=>sf({custom_slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'')})}/>
                )}
                <div className="flex items-center gap-3 bg-black border border-[#1a1a1a] rounded-2xl px-5 py-3.5">
                  <Link2 size={14} className="text-[#444] flex-shrink-0"/>
                  <span className="text-[12px] text-[#555]">Preview:</span>
                  <span className="text-emerald-400 font-mono text-[13px]">/r/{f.slug_format==='custom'?(f.custom_slug||'your-slug'):slugPreview}</span>
                </div>
              </div>
            </div>

            {/* ── COLLECTION ── */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1a1a1a]">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Step 4</p>
                <h2 className="text-[16px] font-semibold text-white">Collection Mode</h2>
                <p className="text-[12px] text-[#555] mt-0.5">What intelligence is captured when the link fires</p>
              </div>
              <div className="p-6 space-y-4">
<div data-tour="collection" className="grid grid-cols-2 gap-3">
                  {[{id:'zero_click',t:'Zero-Click',d:'Silent on page load. No interaction.'},{id:'one_click',t:'One-Click',d:'Requests permissions on action.'}].map(m=>(
                    <button key={m.id} type="button" onClick={()=>sf({collection_mode:m.id})}
                      className={`text-left border rounded-2xl p-5 transition ${f.collection_mode===m.id?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                      <p className="text-[14px] font-semibold text-white mb-1">{m.t}</p>
                      <p className="text-[12px] text-[#555]">{m.d}</p>
                    </button>
                  ))}
                </div>
                {f.collection_mode==='one_click' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {PERMS.map(p=>(
                      <button key={p.id} type="button" onClick={()=>sf({permission_flags:{...f.permission_flags,[p.id]:!f.permission_flags[p.id]}})}
                        className={`text-left border rounded-2xl p-3 transition ${f.permission_flags[p.id]?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          {f.permission_flags[p.id] ? <CheckCircle size={12} className="text-emerald-400"/> : <Square size={12} className="text-[#333]"/>}
                          <span className={`text-[13px] font-medium ${f.permission_flags[p.id]?'text-white':'text-[#666]'}`}>{p.l}</span>
                        </div>
                        <p className="text-[11px] text-[#444] pl-5">{p.desc}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── ADVANCED ── */}
            {showAdv && (
              <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} className="space-y-5">

                {/* OG Override */}
                <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#1a1a1a]">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Advanced — OG Override</p>
                    <h2 className="text-[16px] font-semibold text-white">Link Preview Customization</h2>
                    <p className="text-[12px] text-[#555] mt-0.5">What appears when link is shared on WhatsApp, Telegram, Twitter</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-[13px] text-[#777] mb-2">Preview Title</label>
                      <input className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                        placeholder="Title shown when link is shared" value={f.og_title} onChange={e=>sf({og_title:e.target.value})}/>
                    </div>
                    <div>
                      <label className="block text-[13px] text-[#777] mb-2">Description</label>
                      <textarea className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] h-20 resize-none outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                        placeholder="Short description..." value={f.og_description} onChange={e=>sf({og_description:e.target.value})}/>
                    </div>
                    <div>
                      <label className="block text-[13px] text-[#777] mb-2">Thumbnail URL</label>
                      <input className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                        placeholder="https://..." value={f.og_image} onChange={e=>sf({og_image:e.target.value})}/>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[{l:'YouTube',t:'Watch on YouTube',d:'Click to watch',i:'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg'},
                        {l:'Document',t:'Secure Document',d:'Verify to access',i:''},
                        {l:'Instagram',t:'View on Instagram',d:'See this post',i:''},
                        {l:'News',t:'Breaking Update',d:'Read the full story',i:''},
                      ].map(p=>(
                        <button key={p.l} type="button" onClick={()=>sf({og_title:p.t,og_description:p.d,og_image:p.i})}
                          className="px-3 py-2 border border-[#1a1a1a] rounded-xl text-[12px] text-[#555] hover:text-white hover:border-[#333] transition">
                          {p.l} preset
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Domain Strategy */}
                <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#1a1a1a]">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#555] mb-0.5">Advanced — Domain</p>
                    <h2 className="text-[16px] font-semibold text-white">Domain Strategy</h2>
                    <p className="text-[12px] text-[#555] mt-0.5">Control how the link's domain appears to the target</p>
                  </div>
                  <div className="p-6 space-y-3">
                    {[
                      {id:'platform', l:'Platform Link',       desc:'Default. Zero setup.'},
                      {id:'pool',     l:'Pool Domain',         desc:'Curated clean domains from the platform.'},
                      {id:'custom',   l:'Custom Domain',       desc:'Your own domain via CNAME.'},
                      {id:'rotating', l:'Rotating Domain',     desc:'Random pool domain per deploy.'},
                      {id:'cloak',    l:'Cloaked Chain',       desc:'Multi-layer redirect. Hides origin.'},
                      {id:'chain',    l:'URL Shortener Chain', desc:'Multiple shortener layers.'},
                      {id:'redirect', l:'Open Redirect',       desc:'Wrap inside a trusted site redirect.'},
                    ].map(m=>(
                      <div key={m.id}>
                        <button type="button" onClick={()=>setDomainMethod(m.id)}
                          className={`w-full text-left border rounded-2xl px-5 py-4 transition ${domainMethod===m.id?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className={`text-[14px] font-medium ${domainMethod===m.id?'text-white':'text-[#777]'}`}>{m.l}</p>
                              <p className="text-[12px] text-[#444] mt-0.5">{m.desc}</p>
                            </div>
                            {domainMethod===m.id && <CheckCircle size={15} className="text-emerald-400 flex-shrink-0"/>}
                          </div>
                        </button>
                        {/* Inline configs */}
                        {domainMethod===m.id && m.id==='pool' && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {poolDomains.length===0
                              ? <p className="col-span-2 text-[13px] text-[#555] p-3">No pool domains. Ask admin.</p>
                              : poolDomains.map(d=>(
                                  <button key={d.id} type="button" onClick={()=>sdc({poolDomain:d.domain})}
                                    className={`px-3 py-2.5 border rounded-xl text-[13px] transition text-left ${dc.poolDomain===d.domain?'border-white text-white':'border-[#222] text-[#555]'}`}>
                                    {d.domain}
                                  </button>
                                ))
                            }
                          </div>
                        )}
                        {domainMethod===m.id && m.id==='custom' && (
                          <div className="mt-2 space-y-3">
                            {myDomains.map(d=>(
                              <button key={d.id} type="button" onClick={()=>sdc({customDomain:d.domain})}
                                className={`w-full flex items-center justify-between px-4 py-3 border rounded-xl transition ${dc.customDomain===d.domain?'border-white bg-[#0d0d0d]':'border-[#222]'}`}>
                                <span className="text-[14px] text-white">{d.domain}</span>
                                <B v={d.is_verified?'success':'warning'}>{d.is_verified?'Verified':'Pending'}</B>
                              </button>
                            ))}
                            <div className="flex gap-2">
                              <input className="flex-1 bg-black border border-[#222] px-4 py-2.5 text-white rounded-xl text-[14px] outline-none" placeholder="yourdomain.com" value={newDomain} onChange={e=>setNewDomain(e.target.value.toLowerCase())}/>
                              <Btn v="secondary" disabled={addingDom} onClick={async()=>{
                                if(!newDomain) return; setAddingDom(true);
                                try{const r=await api.post('/api/v1/domains/my',{domain:newDomain});setMyDomains(d=>[...d,r.data]);setNewDomain('');toast('Domain added','success');}catch(e){toast(E(e),'error');}
                                setAddingDom(false);
                              }}>Add</Btn>
                            </div>
                          </div>
                        )}
                        {domainMethod===m.id && (m.id==='cloak'||m.id==='chain') && (
                          <div className="mt-2 flex items-center gap-3 px-2">
                            <span className="text-[13px] text-[#666]">{m.id==='chain'?'Shorteners:':'Layers:'}</span>
                            {[1,2,3].map(n=>(
                              <button key={n} type="button" onClick={()=>sdc({chainCount:n})}
                                className={`w-9 h-9 rounded-xl border text-[13px] font-bold transition ${dc.chainCount===n?'border-white text-white bg-[#111]':'border-[#222] text-[#444]'}`}>{n}</button>
                            ))}
                          </div>
                        )}

                        {/* ── OPEN REDIRECT ── */}
                        {domainMethod===m.id && m.id==='redirect' && (
                          <div className="mt-3 space-y-4">
                            {/* Warning */}
                            <div className="border border-amber-800/50 bg-amber-950/20 rounded-2xl p-4 flex items-start gap-3">
                              <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5"/>
                              <div>
                                <p className="text-amber-400 text-[13px] font-semibold">Advanced — Test Before Using</p>
                                <p className="text-amber-400/70 text-[12px] mt-0.5 leading-[1.6]">
                                  Open redirects may be patched or rate-limited. The link will appear to come from the chosen website.
                                  Always verify with "Send Test" before deployment.
                                </p>
                              </div>
                            </div>

                            {/* Technique */}
                            <div>
                              <p className="text-[12px] text-[#555] uppercase tracking-wider mb-2">Redirect Technique</p>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  {id:'at',    l:'@ Symbol',    ex:'domain.com%23@target', power:true,  desc:'Most powerful. Browser treats base as trusted.'},
                                  {id:'param', l:'Query Param', ex:'domain.com?url=target', power:false, desc:'Classic method. Widely supported.'},
                                  {id:'hash',  l:'Hash Frag',   ex:'domain.com#@target',   power:false, desc:'Uses # fragment to bypass filters.'},
                                  {id:'slash', l:'Path',        ex:'domain.com//target',   power:false, desc:'Appends target as path segment.'},
                                ].map(t=>(
                                  <button key={t.id} type="button" onClick={()=>sdc({redirectTechnique:t.id})}
                                    className={`text-left border rounded-2xl p-4 transition ${(dc.redirectTechnique||'at')===t.id?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] hover:border-[#2a2a2a]'}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-[13px] text-white font-medium">{t.l}</span>
                                      {t.power && <span className="text-[9px] border border-red-800 text-red-400 px-1.5 py-0.5 rounded-full">Strongest</span>}
                                    </div>
                                    <p className="text-[10px] text-emerald-400 font-mono mb-1">{t.ex}</p>
                                    <p className="text-[11px] text-[#444]">{t.desc}</p>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Redirect Templates */}
                    <div className="space-y-2 mb-3">
                      <p className="text-[12px] text-[#555] uppercase tracking-wider mb-2">Template</p>
                      {[
                        {id:'at',   l:'@ Symbol — Most Powerful', b:'Strongest', desc:'trusted.com%23@tracking.com'},
                        {id:'google', l:'Google',  b:'',  desc:'google.com/url?q=LINK', base:'https://www.google.com/url?q='},
                        {id:'youtube',l:'YouTube', b:'',  desc:'youtube.com/redirect?q=LINK', base:'https://www.youtube.com/redirect?q='},
                        {id:'facebook',l:'Facebook',b:'', desc:'l.facebook.com/l.php?u=LINK', base:'https://l.facebook.com/l.php?u='},
                        {id:'linkedin',l:'LinkedIn',b:'', desc:'linkedin.com/redir/redirect?url=LINK', base:'https://www.linkedin.com/redir/redirect?url='},
                        {id:'slack',  l:'Slack',   b:'',  desc:'slack-redir.net/link?url=LINK', base:'https://slack-redir.net/link?url='},
                        {id:'custom', l:'Custom',  b:'',  desc:'Enter any redirect URL manually'},
                      ].map(t=>(
                        <button key={t.id} type="button"
                          onClick={()=>sdc({redirectTemplateId:t.id, redirectBase: t.base||dc.redirectBase})}
                          className={`w-full text-left border rounded-2xl px-4 py-3 transition ${dc.redirectTemplateId===t.id?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[13px] text-white font-medium">{t.l}</span>
                            {t.b && <span className="text-[9px] border border-red-800 text-red-400 px-1.5 py-0.5 rounded-full">{t.b}</span>}
                          </div>
                          <p className="text-[11px] text-[#444] font-mono">{t.desc}</p>
                        </button>
                      ))}
                    </div>

                    {/* @ Symbol config */}
                    {dc.redirectTemplateId === 'at' && (
                      <div className="space-y-3 border border-[#1a1a1a] rounded-2xl p-4 bg-black">
                        <p className="text-[13px] text-white font-medium">Format: <span className="text-emerald-400 font-mono">trusted.com%23@tracking-domain/r/slug</span></p>
                        <div>
                          <label className="block text-[12px] text-[#555] mb-1.5">Trusted Site (domain1)</label>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {['google.com','microsoft.com','apple.com','github.com','amazon.com','linkedin.com'].map(d=>(
                              <button key={d} type="button" onClick={()=>sdc({atSymbolTrusted:d})}
                                className={`px-3 py-1.5 border rounded-xl text-[12px] transition ${dc.atSymbolTrusted===d?'border-white text-white':'border-[#1a1a1a] text-[#555] hover:border-[#333]'}`}>
                                {d}
                              </button>
                            ))}
                          </div>
                          <input className="w-full bg-[#050505] border border-[#222] px-3 py-2.5 text-white rounded-xl text-[13px] outline-none placeholder:text-[#2a2a2a]"
                            placeholder="or enter custom trusted site" value={dc.atSymbolTrusted||''} onChange={e=>sdc({atSymbolTrusted:e.target.value})}/>
                        </div>
                        <div>
                          <label className="block text-[12px] text-[#555] mb-1.5">Tracking Domain (domain2)</label>
                          <input className="w-full bg-[#050505] border border-[#222] px-3 py-2.5 text-white rounded-xl text-[13px] outline-none placeholder:text-[#2a2a2a]"
                            placeholder="saqer-x2.vercel.app" value={dc.atSymbolDomain||''} onChange={e=>sdc({atSymbolDomain:e.target.value.replace(/https?:\/\//,'')})}/>
                        </div>
                        {(dc.atSymbolTrusted && dc.atSymbolDomain) && (
                          <div className="border border-emerald-900/30 rounded-xl px-4 py-2.5">
                            <p className="text-[11px] text-[#444] uppercase mb-1">Preview</p>
                            <p className="text-emerald-400 font-mono text-[12px] break-all">{dc.atSymbolTrusted}%23@{dc.atSymbolDomain}/r/[slug]</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Custom URL */}
                    {(dc.redirectTemplateId === 'custom' || (!dc.redirectTemplateId && !dc.redirectBase)) && (
                      <div>
                        <label className="block text-[13px] text-[#777] mb-2">Custom Redirect Base URL</label>
                        <input className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[14px] outline-none placeholder:text-[#2a2a2a] focus:border-[#444] transition"
                          placeholder="https://site.com/redirect?url=" value={dc.redirectBase} onChange={e=>sdc({redirectBase:e.target.value})}/>
                      </div>
                    )}

                    {/* OG Fetch from redirect site */}
                    <div className="border border-[#1a1a1a] rounded-2xl p-4 bg-black">
                      <p className="text-[12px] text-white font-medium mb-1">Fetch OG from Redirect Site</p>
                      <p className="text-[12px] text-[#555] mb-3">Make the link preview look like it's coming from the trusted site.</p>
                      <div className="flex gap-2">
                        <input className="flex-1 bg-[#050505] border border-[#222] px-3 py-2.5 text-white rounded-xl text-[13px] outline-none placeholder:text-[#2a2a2a]"
                          placeholder="https://trusted-site.com"
                          value={dc.ogFetchUrl||dc.atSymbolTrusted&&'https://'+dc.atSymbolTrusted||''}
                          onChange={e=>sdc({ogFetchUrl:e.target.value})}/>
                        <button type="button"
                          onClick={async()=>{
                            const url = dc.ogFetchUrl || (dc.atSymbolTrusted && 'https://'+dc.atSymbolTrusted) || dc.redirectBase;
                            if (!url) return toast('Enter a URL first','error');
                            try {
                              const r = await api.get(`/api/v1/og-preview?url=${encodeURIComponent(url)}`);
                              if (r.data.ok) { sf({og_title:r.data.title||'',og_description:r.data.description||'',og_image:r.data.image||''}); toast('OG fetched','success'); }
                            } catch(e) { toast('Fetch failed','error'); }
                          }}
                          className="px-3 py-2 border border-[#222] bg-[#050505] rounded-xl text-[12px] text-[#666] hover:text-white hover:border-[#444] transition flex items-center gap-1.5">
                          <Search size={12}/> Fetch OG
                        </button>
                      </div>
                    </div>

                    {/* Preview + Test */}
                            {dc.redirectBase && (
                              <div className="space-y-3">
                                <div className="bg-black border border-[#1a1a1a] rounded-2xl p-4">
                                  <p className="text-[11px] text-[#444] uppercase tracking-wider mb-2">Final Link Structure</p>
                                  <p className="text-emerald-400 font-mono text-[12px] break-all leading-[1.8]">
                                    {buildRedirectPreview(dc.redirectBase, dc.redirectTechnique)}
                                  </p>
                                </div>
                                <button type="button" onClick={testRedirect} disabled={testingRedir}
                                  className="flex items-center gap-2 px-4 py-2.5 border border-[#333] bg-black rounded-xl text-[13px] text-[#888] hover:text-white hover:border-[#555] disabled:opacity-30 transition">
                                  {testingRedir
                                    ? <><span className="w-3 h-3 border-2 border-[#333] border-t-white rounded-full animate-spin"/><span>Sending test...</span></>
                                    : <><Navigation size={13}/><span>Send Test Request</span></>
                                  }
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── DEPLOY BUTTON (bottom anchor) ── */}
<div ref={bottomRef} className="flex justify-end" data-tour="deploy-btn">
              <button type="button" onClick={deploy} disabled={ld||!f.target_url}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-2xl text-[14px] font-semibold hover:bg-[#eee] active:scale-[0.98] transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                {ld
                  ? <><span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin"/><span>Deploying...</span></>
                  : <><Rocket size={14}/><span>Deploy Link</span></>
                }
              </button>
            </div>
          </div>

          {/* ══════════ RIGHT COLUMN ══════════ */}
          <div className="space-y-5">
            {/* Link Preview */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#1a1a1a]">
  <p className="text-[13px] font-semibold text-white" data-tour="og-preview">Link Preview</p>
                <p className="text-[11px] text-[#555] mt-0.5">How it appears on social platforms</p>
              </div>
              <div>
                {f.og_image
                  ? <img src={f.og_image} alt="" className="w-full h-44 object-cover" onError={e=>{e.target.style.display='none'}}/>
                  : <div className="w-full h-36 bg-black flex items-center justify-center">
                      {ogLoading
                        ? <span className="w-6 h-6 border-2 border-[#222] border-t-white rounded-full animate-spin"/>
                        : <ImageIcon size={28} className="text-[#1a1a1a]"/>
                      }
                    </div>
                }
                <div className="p-5">
                  <p className="text-[11px] text-[#444] uppercase tracking-wide mb-2">{hostname}</p>
                  <p className="text-white text-[14px] font-semibold line-clamp-2 mb-1.5 min-h-[44px]">
                    {f.og_title || <span className="text-[#2a2a2a] font-normal italic">Title will appear here...</span>}
                  </p>
                  <p className="text-[#444] text-[12px] line-clamp-2 min-h-[36px]">
                    {f.og_description || <span className="text-[#222] italic">Description...</span>}
                  </p>
                </div>
              </div>
              <div className="px-5 pb-5 flex gap-2">
                <button type="button" onClick={doFetchOG} disabled={!f.target_url?.startsWith('http')||ogLoading}
                  className="flex-1 py-2.5 border border-[#1a1a1a] rounded-xl text-[12px] text-[#555] hover:text-white hover:border-[#333] disabled:opacity-30 transition flex items-center justify-center gap-1.5">
                  <Search size={12}/> {ogLoading ? 'Fetching...' : 'Refresh'}
                </button>
                {(f.og_title||f.og_image) && (
                  <button type="button" onClick={()=>sf({og_title:'',og_description:'',og_image:''})}
                    className="px-3 py-2.5 border border-[#1a1a1a] rounded-xl text-[#444] hover:text-white hover:border-[#333] transition">
                    <XCircle size={13}/>
                  </button>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-5">
              <p className="text-[13px] font-semibold text-white mb-4">Configuration</p>
              <div className="space-y-3">
                {[
                  {l:'Template',   v:f.template,                             mono:false},
                  {l:'Collection', v:f.collection_mode.replace('_',' '),     mono:false},
                  {l:'Slug Format',v:f.slug_format,                          mono:false},
                  {l:'Link',       v:`/r/${f.slug_format==='custom'?(f.custom_slug||'…'):slugPreview}`, mono:true},
                  {l:'Domain',     v:showAdv?domainMethod:'platform',        mono:false},
                ].map(row=>(
                  <div key={row.l} className="flex items-center justify-between gap-2 text-[13px]">
                    <span className="text-[#444]">{row.l}</span>
                    <span className={`truncate max-w-[160px] text-right ${row.mono?'text-emerald-400 font-mono':'text-white'}`}>{row.v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* History */}
            <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#1a1a1a]">
                <p className="text-[13px] font-semibold text-white">Recent Deployments</p>
                <p className="text-[11px] text-[#555]">{hist.length} total</p>
              </div>
              <div className="p-3">
                {hist.length===0
                  ? <p className="text-[#333] text-center py-6 text-[12px]">No deployments yet</p>
                  : <div className="space-y-1.5">
                      {hist.slice(0,8).map(h=>(
                        <div key={h.trap_id} className="border border-[#1a1a1a] rounded-2xl bg-black p-3.5">
                          <p className="text-[13px] text-white truncate">{h.campaign_name||h.trap_id}</p>
                          <p className="text-[11px] text-[#444] mt-1">{h.template} · {h.visit_count||0} visits</p>
                        </div>
                      ))}
                    </div>
                }
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}


function RenderValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-[#666]">N/A</span>;
  }

  if (typeof value === "boolean") {
    return <span>{value ? "True" : "False"}</span>;
  }

  if (typeof value === "string" || typeof value === "number") {
    return <span className="break-words">{String(value)}</span>;
  }

  return null;
}

function RenderObject({ data, level = 0 }) {
  if (!data || typeof data !== "object") return null;

  if (Array.isArray(data)) {
    return (
      <div className="space-y-3">
        {data.map((item, i) => (
          <div
            key={i}
            className="border border-[#222] rounded-xl bg-[#0a0a0a] p-3"
          >
            {isPrimitive(item) ? (
              <RenderValue value={item} />
            ) : (
              <RenderObject data={item} level={level + 1} />
            )}
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(data);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="border border-[#222] rounded-2xl bg-[#0a0a0a] overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#1c1c1c] text-[12px] tracking-[0.2em] text-[#888] uppercase">
            {prettyKey(key)}
          </div>

          <div className="p-4">
            {isPrimitive(value) ? (
              <div className="text-[14px] text-white leading-7 break-words">
                <RenderValue value={value} />
              </div>
            ) : (
              <RenderObject data={value} level={level + 1} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const sectionObj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
const prettifyKey = (k = "") =>
  String(k)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const compactValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (typeof value === "number" || typeof value === "string") return String(value);
  if (Array.isArray(value)) return value.length ? value.map(compactValue).join(", ") : "N/A";
  return Object.keys(value || {}).length ? JSON.stringify(value, null, 2) : "N/A";
};

const summarizeGeo = (report = {}) => {
  const nt = sectionObj(report.Network_Topology);
  const geo = nt.Spoofed_GeoIP || nt.GeoIP || nt.Country || "Unknown";
  return geo;
};

const summarizeISP = (report = {}) => {
  const nt = sectionObj(report.Network_Topology);
  return nt.ISP || nt.Organization || "Unknown";
};

const summarizeRisk = (report = {}) => {
  const ta = sectionObj(report.Threat_Analysis_Engine);
  return ta.OPSEC_Risk_Level || ta.Risk_Level || "LOW";
};

const riskVariant = (risk = "") => {
  const r = String(risk).toUpperCase();
  if (r === "CRITICAL" || r === "HIGH") return "danger";
  if (r === "MEDIUM") return "warning";
  if (r === "LOW") return "success";
  return "default";
};

const humanDateTime = (value) => {
  try {
    return value ? new Date(value).toLocaleString() : "—";
  } catch {
    return "—";
  }
};

const cloneSafe = (value) => {
  try {
    return JSON.parse(JSON.stringify(value ?? {}));
  } catch {
    return {};
  }
};


function LinksPage() {
  const [links, sLinks] = useState([]);
  const [load, sLoad] = useState(true);
  const toast = useToast();
  const { ask, Modal } = useModal();

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await api.get('/api/v1/traps');
      sLinks(data);
    } catch (e) { toast(E(e), 'error'); }
    finally { sLoad(false); }
  }, [toast]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const toggleStatus = async (tid) => {
    try {
      await api.put(`/api/v1/traps/${tid}/toggle`);
      toast('Status updated', 'success');
      fetchLinks();
    } catch (e) { toast(E(e), 'error'); }
  };

  const del = async (tid) => {
    if (await ask('Are you sure you want to delete this link?')) {
      try {
        await api.delete(`/api/v1/traps/${tid}`);
        toast('Link deleted', 'success');
        fetchLinks();
      } catch (e) { toast(E(e), 'error'); }
    }
  };

  if (load) return <div className="text-[#555] p-10 font-mono text-sm tracking-tighter animate-pulse">Loading operational assets...</div>;

  return (
    <motion.div initial="hidden" animate="show" variants={pageReveal} className="space-y-6">
      <Modal />
      <Panel title="Link Management" sub="Track and control your active operational collectors." extra={<Btn v="secondary" onClick={fetchLinks} className="!text-[10px] !px-3">Refresh</Btn>}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#111]">
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider">Campaign</th>
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider">Generated URL</th>
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider">Target</th>
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider">Visits</th>
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-[11px] font-bold text-[#444] uppercase tracking-wider text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.trap_id} className="border-b border-[#111] hover:bg-[#050505] transition group">
                  <td className="py-4 px-4">
                    <p className="text-[13px] text-white font-medium group-hover:text-emerald-400 transition-colors uppercase tracking-tight">{l.campaign_name}</p>
                    <p className="text-[10px] text-[#444] font-mono mt-0.5">{l.trap_id}</p>
                  </td>
                  <td className="py-4 px-4 font-mono text-[12px] text-[#ccc]">
                    <span className="text-[#333]">/r/</span>{l.custom_slug || l.trap_id}
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-[12px] text-[#555] truncate max-w-[180px] font-mono hover:text-[#888] cursor-help" title={l.target_url}>{l.target_url}</p>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-[14px] text-white font-light">{l.visit_count}</p>
                        <p className="text-[9px] text-[#444] uppercase tracking-tighter">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[14px] text-[#666] font-light">{l.unique_visitors || 0}</p>
                        <p className="text-[9px] text-[#444] uppercase tracking-tighter">Unique</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <button 
                      onClick={() => toggleStatus(l.trap_id)} 
                      className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-widest transition-all ${l.is_active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}
                    >
                      {l.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </button>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <CopyBtn text={`${window.location.origin}/r/${l.custom_slug || l.trap_id}`} />
                      <Btn v="danger" className="!px-3 !py-1 !text-[10px] !rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => del(l.trap_id)}>Delete</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {links.length === 0 && <tr><td colSpan="6" className="py-20 text-center text-[#444] text-[13px] italic tracking-tight">Deployment queue empty. No operational links found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </motion.div>
  );
}

function FeedKeyValueGrid({ data }) {
  const entries = Object.entries(sectionObj(data));

  if (!entries.length) {
    return (
      <div className="border border-[#1a1a1a] rounded-xl p-4 bg-[#050505] text-[#666] text-[12px]">
        No data
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="group border border-[#1a1a1a] rounded-xl bg-[#030303] hover:border-[#333] transition-all"
        >
          <div className="px-3 py-1.5 border-b border-[#111] text-[9px] font-black tracking-[0.2em] text-[#444] group-hover:text-[#888] uppercase transition-colors">
            {prettifyKey(key)}
          </div>
          <div className="px-3 py-3 text-[12px] text-white/90 leading-relaxed whitespace-pre-wrap break-words font-mono">
            {compactValue(value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function FeedSection({ title, data, defaultOpen = true }) {
  const count = Object.keys(sectionObj(data)).length;

  return (
    <details
      open={defaultOpen}
      className="group border border-[#1a1a1a] rounded-2xl bg-[#050505] overflow-hidden hover:border-[#222] transition-colors"
    >
      <summary className="list-none cursor-pointer px-4 py-4 flex items-center justify-between gap-3 select-none">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] flex items-center justify-center text-[#444] group-open:text-white transition-colors">
            {title.includes('Network') ? <Network size={16}/> : title.includes('Hardware') ? <Settings size={16}/> : title.includes('Threat') ? <ShieldAlert size={16}/> : <FolderOpen size={16}/>}
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#666] group-open:text-white transition-colors">{title}</p>
            <p className="text-[9px] text-[#333] mt-0.5 font-mono">{count} operational metrics</p>
          </div>
        </div>
        <div className="w-6 h-6 rounded-full border border-[#111] flex items-center justify-center transition-transform group-open:rotate-180">
          <ChevronDown size={14} className="text-[#333]" />
        </div>
      </summary>
      <div className="px-4 pb-4 border-t border-[#111] pt-4 bg-black/40">
        <FeedKeyValueGrid data={data} />
      </div>
    </details>
  );
}

function FeedSummaryPill({ label, value }) {
  return (
    <div className="border border-[#1a1a1a] rounded-xl px-3 py-2 bg-black min-w-[120px]">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#666] mb-1">{label}</p>
      <p className="text-[12px] text-white break-words">{value || "—"}</p>
    </div>
  );
}

function VisualEvidenceGallery({ images }) {
  if (!images || (!images.Camera_Shot && !images.Screen_Capture)) return null;
  
  const [zoom, setZoom] = useState(null);
  
  const formatUrl = (u) => {
    if (!u) return null;
    if (u.startsWith('http') || u.startsWith('data:')) return u;
    const base = API.replace(/\/+$/, '');
    const path = u.replace(/^\/+/, '');
    return `${base}/${path}`;
  };

  const Card = ({ src, label, icon: Icon }) => {
    if (!src) return null;
    const url = formatUrl(src);
    return (
      <div className="group relative border border-[#1a1a1a] rounded-2xl bg-[#090909] overflow-hidden hover:border-[#333] transition-all aspect-video">
        <img src={url} alt={label} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity cursor-zoom-in" onClick={() => setZoom(url)} />
        <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 bg-black/60 backdrop-blur rounded-lg border border-white/10 pointer-events-none">
          <Icon size={12} className="text-white" />
          <span className="text-[9px] font-bold text-white uppercase tracking-widest">{label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      <EYE className="mb-4">Operational Evidence Feed</EYE>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card src={images.Camera_Shot} label="Camera Snapshot" icon={Target} />
        <Card src={images.Screen_Capture} label="Screen Capture" icon={Activity} />
      </div>
      
      <AnimatePresence>
        {zoom && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setZoom(null)}
          >
            <motion.img 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              src={zoom} className="max-w-full max-h-full rounded-xl shadow-2xl border border-white/10"
            />
            <button className="absolute top-6 right-6 text-white/50 hover:text-white text-sm font-mono">CLOSE [ESC]</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function buildFeedReportHTML(trapData) {
  const trap = trapData?.trap || {};
  const logs = Array.isArray(trapData?.logs) ? trapData.logs : [];
  const reportCode = `ZRX-${trap.trap_id || "REPORT"}-${new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14)}`;

  const esc = (v) =>
    String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const htmlSection = (title, data) => {
    const obj = sectionObj(data);
    const entries = Object.entries(obj);
    return `
      <section class="section">
        <div class="section-head">
          <div>
            <div class="eyebrow">${esc(title)}</div>
            <h3>${esc(title)} Section</h3>
          </div>
          <div class="muted">${entries.length} fields</div>
        </div>
        <div class="kv-grid">
          ${
            entries.length
              ? entries
                  .map(
                    ([k, v]) => `
              <div class="kv-card">
                <div class="kv-key">${esc(prettifyKey(k))}</div>
                <div class="kv-value">${esc(compactValue(v))}</div>
              </div>`
                  )
                  .join("")
              : `<div class="empty">No data</div>`
          }
        </div>
      </section>
    `;
  };

  const cards = logs
    .map((log, index) => {
      const report = cloneSafe(log.intel_report || {});
      const network = sectionObj(report.Network_Topology);
      const fingerprint = sectionObj(report.Hardware_Fingerprint);
      const behavioral = sectionObj(report.Threat_Analysis_Engine);
      const opsec = sectionObj(report.OPSEC_Assessment || report.OPSEC || report.OPSEC_Findings);
      const rawTelemetry = Object.fromEntries(
        Object.entries(report).filter(
          ([k]) =>
            ![
              "Network_Topology",
              "Hardware_Fingerprint",
              "Threat_Analysis_Engine",
              "OPSEC_Assessment",
              "OPSEC",
              "OPSEC_Findings",
            ].includes(k)
        )
      );

      return `
        <article class="capture">
          <div class="capture-top">
            <div>
              <div class="capture-title">Capture #${index + 1}</div>
              <div class="capture-sub">${esc(log.public_ip || "Unknown IP")} · ${esc(humanDateTime(log.captured_at))}</div>
            </div>
            <div class="badge ${String(summarizeRisk(report)).toUpperCase() === "CRITICAL" ? "danger" : String(summarizeRisk(report)).toUpperCase() === "MEDIUM" ? "warning" : "success"}">
              ${esc(summarizeRisk(report))}
            </div>
          </div>

          <div class="summary-grid">
            <div class="summary-box"><span>IP</span><strong>${esc(log.public_ip || "Unknown")}</strong></div>
            <div class="summary-box"><span>Geo</span><strong>${esc(summarizeGeo(report))}</strong></div>
            <div class="summary-box"><span>ISP</span><strong>${esc(summarizeISP(report))}</strong></div>
            <div class="summary-box"><span>Profile</span><strong>${esc(log.profile_id ? `Linked #${log.profile_id}` : "Unlinked")}</strong></div>
          </div>

          ${htmlSection("Network", network)}
          ${htmlSection("Fingerprint", fingerprint)}
          ${htmlSection("Behavioral", behavioral)}
          ${htmlSection("OPSEC", opsec)}
          ${htmlSection("Raw Telemetry", rawTelemetry)}
        </article>
      `;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>ZureX92 Feed Report</title>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
*{box-sizing:border-box}body{margin:0;background:#020202;color:#fff;font-family:Inter,Arial,sans-serif;padding:32px}
.wrap{max-width:1200px;margin:0 auto}
.hero{border:1px solid #151515;background:#050505;border-radius:28px;padding:28px;margin-bottom:24px}
.brand{display:flex;align-items:center;gap:12px;margin-bottom:18px}
.logo{width:34px;height:34px;border:1px solid #222;border-radius:10px;display:flex;align-items:center;justify-content:center;background:#0a0a0a}
.small{font-size:11px;letter-spacing:.28em;text-transform:uppercase;color:#777}
h1{font-size:38px;line-height:1.02;margin:10px 0 8px}
.sub{color:#777;font-size:14px;line-height:1.8;max-width:760px}
.meta{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-top:20px}
.meta .box,.summary-box,.kv-card,.capture,.section,.empty{border:1px solid #171717;background:#050505;border-radius:18px}
.meta .box{padding:16px}.meta .box span,.summary-box span{display:block;font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#666;margin-bottom:8px}
.meta .box strong{font-size:16px}
.capture{padding:20px;margin-bottom:16px}
.capture-top{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:16px}
.capture-title{font-size:18px;font-weight:700}.capture-sub{font-size:12px;color:#666;margin-top:6px}
.badge{padding:8px 12px;border-radius:999px;font-size:11px;border:1px solid #333}
.badge.success{background:#052e1c;color:#41d38a;border-color:#0f5}.badge.warning{background:#3b2a05;color:#f2c14e;border-color:#6b520a}.badge.danger{background:#3b0a0a;color:#ff6b6b;border-color:#7f1d1d}
.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:18px}
.summary-box{padding:14px}.summary-box strong{font-size:14px;line-height:1.6;word-break:break-word}
.section{padding:14px;margin-top:14px}
.section-head{display:flex;justify-content:space-between;gap:10px;align-items:flex-start;margin-bottom:12px}
.section h3{margin:6px 0 0;font-size:16px}.muted{font-size:11px;color:#666}
.eyebrow{font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#777}
.kv-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
.kv-card{overflow:hidden}.kv-key{padding:10px 12px;border-bottom:1px solid #141414;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#888}
.kv-value{padding:12px;font-size:13px;line-height:1.7;color:#fff;white-space:pre-wrap;word-break:break-word}
.empty{padding:18px;color:#666}
.footer{margin-top:28px;padding-top:20px;border-top:1px solid #171717;font-size:11px;color:#555;text-align:center}
@media (max-width:900px){.meta,.summary-grid,.kv-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:640px){body{padding:16px}.meta,.summary-grid,.kv-grid{grid-template-columns:1fr}h1{font-size:28px}}
</style>
</head>
<body>
<div class="wrap">
  <div class="hero">
    <div class="brand">
      <div class="logo">Z</div>
      <div>
        <div class="small">ZureX92 Intelligence Report</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;">${esc(trap.campaign_name || "Unnamed Campaign")}</div>
      </div>
    </div>
    <h1>Feed Capture Report</h1>
    <div class="sub">Branded HTML report generated from the ZureX92 operational feed. Includes summary metadata, capture cards, and full intelligence sections.</div>
    <div class="meta">
      <div class="box"><span>Report Code</span><strong>${esc(reportCode)}</strong></div>
      <div class="box"><span>Trap ID</span><strong>${esc(trap.trap_id || "—")}</strong></div>
      <div class="box"><span>Total Captures</span><strong>${esc(trapData.total || logs.length)}</strong></div>
      <div class="box"><span>Unique Visitors</span><strong>${esc(trapData.unique || "0")}</strong></div>
    </div>
    <div class="sub" style="margin-top:18px;">Target: ${esc(trap.target_url || "—")}</div>
  </div>
  ${cards || '<div class="empty">No captures available</div>'}
  <div class="footer">ZureX92 | ${esc(new Date().toLocaleString())}</div>
</div>
</body>
</html>`;
}

function downloadFeedReportHTML(trapData) {
  const html = buildFeedReportHTML(trapData);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const name = (trapData?.trap?.campaign_name || "feed-report").replace(/[^a-z0-9-_]+/gi, "_");
  a.href = url;
  a.download = `ZureX92_${name}_Report.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function FeedCaptureCard({ log, sel, view, onRefresh, toggle, expanded }) {
  const toast = useToast();
  const [tab, sTab] = useState('insights');
  const report = cloneSafe(log.intel_report || {});
  
  const network = sectionObj(report.Network_Topology);
  const fingerprint = sectionObj(report.Hardware_Fingerprint);
  const behavioral = sectionObj(report.Threat_Analysis_Engine);
  const opsec = sectionObj(report.OPSEC_Assessment || report.OPSEC || report.OPSEC_Findings);
  const rawTelemetry = Object.fromEntries(
    Object.entries(report).filter(([k]) => !["Network_Topology","Hardware_Fingerprint","Threat_Analysis_Engine","OPSEC_Assessment","OPSEC","OPSEC_Findings"].includes(k))
  );
  const risk = summarizeRisk(report);

  return (
    <div className={`border border-[#111] rounded-2xl bg-[#050505] overflow-hidden transition-all duration-300 ${expanded ? 'ring-1 ring-[#222]' : 'hover:border-[#222]'}`}>
      <div className="p-3 md:p-4 flex items-center justify-between gap-4 cursor-pointer" onClick={toggle}>
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-1.5 h-10 rounded-full ${riskVariant(risk) === 'danger' ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.4)]' : riskVariant(risk) === 'warning' ? 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.4)]' : 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]'}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[14px] text-white font-mono font-bold tracking-tight">{log.public_ip || "Unknown IP"}</span>
              <B v={riskVariant(risk)} className="!text-[9px] !px-2 !py-0 !rounded-md uppercase tracking-tighter">{risk}</B>
              {report.vpn_detected && (
                <motion.div initial={{ scale: 0.8 }} animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
                  <B v="danger" className="!text-[9px] !bg-red-600 !text-white !border-red-500">VPN DETECTED</B>
                </motion.div>
              )}
              {log.profile_id && <div className="px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 text-[9px] font-bold uppercase tracking-tighter border border-blue-500/20">Linked</div>}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-[#444] font-mono tracking-tighter uppercase">
              <span>{summarizeGeo(report)}</span>
              <span>•</span>
              <span>{humanDateTime(log.captured_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {report.Network_Topology?.GPS_Coordinates?.precise && <span className="text-[9px] bg-emerald-500/20 text-emerald-500 px-1.5 py-0.5 rounded-full font-bold uppercase border border-emerald-500/30">Precise Location</span>}
            <Btn v="ghost" className="!p-2 !rounded-full text-[#333] hover:text-[#777]">{expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}</Btn>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-[#111] bg-black">
            <div className="flex border-b border-[#111] px-4">
              <button onClick={() => sTab('insights')} className={`px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${tab === 'insights' ? 'text-white border-b-2 border-white' : 'text-[#333] hover:text-[#666]'}`}>Simple Insights</button>
              <button onClick={() => sTab('technical')} className={`px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${tab === 'technical' ? 'text-white border-b-2 border-white' : 'text-[#333] hover:text-[#666]'}`}>Technical Analysis</button>
              <button onClick={() => sTab('raw')} className={`px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${tab === 'raw' ? 'text-white border-b-2 border-white' : 'text-[#333] hover:text-[#666]'}`}>Raw Discovery</button>
            </div>

            <div className="p-6">
              {tab === 'insights' && (
                <div className="space-y-8">
                  <VisualEvidenceGallery images={report.Visual_Evidence || report.visual_evidence} />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                     <div className="space-y-2">
                        <EYE>Operational Origin</EYE>
                        <div>
                          <div className="flex flex-col gap-1">
                            <p className="text-[15px] text-white font-light">
                              {summarizeGeo(report)}
                            </p>
                            {report.Network_Topology?.GPS_Coordinates && (
                              <div className="flex items-center gap-2 my-1">
                                <a 
                                  href={`https://www.google.com/maps?q=${report.Network_Topology.GPS_Coordinates.lat},${report.Network_Topology.GPS_Coordinates.lon}`} 
                                  target="_blank" rel="noreferrer" 
                                  className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-tighter transition ${report.Network_Topology.GPS_Coordinates.precise ? 'bg-emerald-600 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:bg-emerald-500' : 'bg-[#111] text-[#666] hover:text-white border border-[#222]'}`}
                                >
                                  {report.Network_Topology.GPS_Coordinates.precise ? "View Precise Location" : "View IP-Geo Map"}
                                </a>
                                {report.Network_Topology.GPS_Coordinates.precise && (
                                  <motion.div animate={{ opacity: [1, 0.5, 1] }} transition={{ repeat: Infinity, duration: 1 }}>
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 block" title="Live Precision GPS" />
                                  </motion.div>
                                )}
                              </div>
                            )}
                            {report.Threat_Analysis_Engine?.GPS_vs_IP_Analysis && (
                              <p className="text-[9px] text-red-500 font-bold uppercase tracking-widest bg-red-500/10 border border-red-500/20 px-2 py-1 rounded-md inline-block w-fit">
                                Location Mismatch: {report.Threat_Analysis_Engine.GPS_vs_IP_Analysis.distance_km} KM from IP location
                              </p>
                            )}
                          </div>
                          <p className={`text-[11px] font-mono mt-1 ${report.vpn_detected ? 'text-red-500/80 font-bold' : 'text-[#444]'}`}>
                            {report.Network_Topology?.ISP || 'Unclassified Provider'}
                            {report.vpn_detected && <span className="ml-2 uppercase text-[9px]">[VPN/PROXY INFRASTRUCTURE]</span>}
                          </p>
                        </div>
                     </div>
                    <div className="space-y-2">
                       <EYE>Target Endpoint</EYE>
                       <div>
                         <p className="text-[15px] text-white font-light">{report.Hardware_Fingerprint?.Platform || 'Unknown Platform'}</p>
                         <p className="text-[11px] text-[#444] font-mono mt-1">{report.Hardware_Fingerprint?.Browser || 'Undetected Agent'}</p>
                       </div>
                    </div>
                    <div className="space-y-2">
                       <EYE>System Verdict</EYE>
                       <div>
                         <p className={`text-[15px] font-black tracking-tighter uppercase ${riskVariant(risk)==='danger'?'text-red-500':riskVariant(risk)==='warning'?'text-amber-500':'text-emerald-500'}`}>{risk}</p>
                         <p className="text-[11px] text-[#444] font-mono mt-1">{report.Threat_Analysis_Engine?.System_Verdict || 'Operational Baseline'}</p>
                       </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-[#111]">
                    <EYE className="mb-4">Permissions Discovery</EYE>
                    <div className="flex flex-wrap gap-2.5">
                       {Object.entries(report.permissions || report.Permissions_Telemetry || {}).map(([k, v]) => {
                         const granted = v && v.granted;
                         const supported = v && v.supported !== false;
                         return (
                         <div key={k} className={`px-4 py-2 rounded-2xl bg-[#030303] border transition-all flex items-center gap-3 ${granted ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                           <div className={`w-2 h-2 rounded-full ${granted ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.3)]'}`} />
                           <span className="text-[12px] text-white capitalize font-medium">{k}</span>
                           <span className={`text-[9px] font-black uppercase tracking-widest ${granted ? 'text-emerald-500/60' : 'text-red-500/60'}`}>{!supported ? 'Unsupported' : granted ? 'Granted' : 'Blocked'}</span>
                         </div>
                         );
                       })}
                       {Object.keys(report.permissions || report.Permissions_Telemetry || {}).length === 0 && <p className="text-[11px] text-[#222] font-mono italic">No permission callbacks recorded in this session.</p>}
                    </div>
                  </div>
                </div>
              )}

              {tab === 'technical' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FeedSection title="Network Discovery" data={network} />
                    <FeedSection title="Hardware Profile" data={fingerprint} defaultOpen={false} />
                    <FeedSection title="Threat Intelligence" data={behavioral} defaultOpen={false} />
                    <FeedSection title="Operational Artifacts" data={opsec} defaultOpen={false} />
                    {Object.keys(rawTelemetry).length > 0 && <FeedSection title="Miscellaneous Discovery" data={rawTelemetry} defaultOpen={false} />}
                  </div>
                </div>
              )}

              {tab === 'raw' && (
                <div className="space-y-4">
                  <EYE>Integrated Telemetry Archive</EYE>
                  <pre className="p-5 bg-black border border-[#111] rounded-2xl text-[11px] text-emerald-500 font-mono overflow-auto max-h-[420px] leading-relaxed">
                    {JSON.stringify(report, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="p-4 bg-[#030303] border-t border-[#111] flex justify-between items-center sm:px-6">
               <div className="flex items-center gap-3">
                 <Btn v="secondary" className="!py-1.5 !px-4 !text-[11px] !rounded-full" onClick={() => view(sel)}>Refresh</Btn>
                 {log.profile_id && <Btn v="ghost" className="!py-1.5 !px-4 !text-[11px] !rounded-full !text-[#555] hover:!text-white" onClick={() => window.location.href=`/profiles/${log.profile_id}`}>Target Profile</Btn>}
               </div>
               {!log.profile_id && (
                 <div className="w-56">
                    <PSearch onSelect={async (pid) => {
                      try { await api.put(`/api/v1/logs/${log.id}/link/${pid}`); toast("Asset Linked", "success"); view(sel); onRefresh && onRefresh(); } catch (e) { toast(E(e), "error"); }
                    }} />
                 </div>
               )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


function FeedPage({ onRefresh }) {
  const toast = useToast();
  const [tab, sTab] = useState("all");
  const [traps, sTraps] = useState([]);
  const [sel, sSel] = useState(null);
  const [data, sData] = useState(null);
  const [q, sQ] = useState("");
  const [expandedLogs, sExpandedLogs] = useState({});

  const load = useCallback(async () => {
    try {
      sTraps(
        (await api.get(`/api/v1/logs/grouped${tab !== "all" ? "?mode=" + tab : ""}`)).data
      );
    } catch {}
  }, [tab]);

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [load]);

  const view = async (tid) => {
    try {
      const payload = (await api.get(`/api/v1/logs/by-trap/${tid}`)).data;
      sData(payload);
      sSel(tid);
      const firstId = payload?.logs?.[0]?.id;
      sExpandedLogs(firstId ? { [firstId]: true } : {});
    } catch (e) {
      toast(E(e), "error");
    }
  };

  const filteredTraps = traps.filter(
    (t) =>
      !q ||
      t.campaign_name?.toLowerCase().includes(q.toLowerCase()) ||
      t.target_url?.toLowerCase().includes(q.toLowerCase())
  );

  const totalVisits = traps.reduce((acc, t) => acc + (t.capture_count || 0), 0);
  const totalGroups = traps.filter((t) => t.deploy_mode === "group").length;
  const totalSingles = traps.filter((t) => t.deploy_mode === "single").length;

  if (data && sel) {
    const t = data;

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => {
              sSel(null);
              sData(null);
              sExpandedLogs({});
              load();
            }}
            className="text-[#666] hover:text-white text-[13px]"
          >
            &larr; Back to Feed
          </button>

          <div className="flex gap-2 flex-wrap">
            <Btn v="secondary" onClick={() => downloadFeedReportHTML(t)}>
              Export HTML Report
            </Btn>
            <B v={t.trap?.deploy_mode === "group" ? "blue" : "warning"}>{t.trap?.deploy_mode}</B>
            <B>{t.trap?.template}</B>
            <B v="success">{t.total}</B>
            <B>{t.unique} unique</B>
          </div>
        </div>

        <div className="border border-[#1a1a1a] rounded-2xl bg-[#050505] p-5 md:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#666] mb-3">Campaign</p>
              <h1 className="text-[24px] md:text-[28px] font-semibold text-white tracking-tight mb-2">
                {t.trap?.campaign_name || "Unnamed Campaign"}
              </h1>
              <p className="text-[13px] text-[#666] break-all">{t.trap?.target_url}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 min-w-[240px]">
              <FeedSummaryPill label="Trap ID" value={t.trap?.trap_id || "—"} />
              <FeedSummaryPill label="Template" value={t.trap?.template || "—"} />
              <FeedSummaryPill label="Captures" value={String(t.total || 0)} />
              <FeedSummaryPill label="Unique" value={String(t.unique || 0)} />
            </div>
          </div>
        </div>

        {t.trap?.deploy_mode === "group" && t.visitors?.length > 0 && (
          <Panel title="Visitors" sub={`${t.visitors.length} unique visitors`}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {t.visitors.map((v, i) => (
                <div
                  key={i}
                  className="border border-[#1a1a1a] rounded-xl p-4 bg-black"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[13px] text-white font-mono">{v.ip}</span>
                    <B v={v.visits > 5 ? "danger" : "default"}>{v.visits}x</B>
                  </div>
                  <IR l="Geo" val={v.geo} />
                  <IR l="ISP" val={v.isp} />
                </div>
              ))}
            </div>
          </Panel>
        )}

        <div className="space-y-3">
          {(t.logs || []).map((log) => (
            <FeedCaptureCard
              key={log.id}
              log={log}
              sel={sel}
              view={view}
              onRefresh={onRefresh}
              expanded={!!expandedLogs[log.id]}
              toggle={() =>
                sExpandedLogs((prev) => ({
                  ...prev,
                  [log.id]: !prev[log.id],
                }))
              }
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border border-[#1a1a1a] rounded-2xl bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#666] mb-3">
              Intelligence Layer
            </p>
            <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-white">
              Intelligence Feed
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-2 max-w-[760px] leading-[1.8]">
              Review grouped and single deployments, monitor campaign activity,
              and open intelligence records from one operational surface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Tabs
              tabs={[
                { id: "all", l: "All" },
                { id: "group", l: "Group" },
                { id: "single", l: "Single" },
              ]}
              active={tab}
              onChange={sTab}
            />
            <input
              className="bg-[#0a0a0a] border border-[#222] px-3 py-1.5 text-[12px] text-white rounded-lg w-44 outline-none"
              placeholder="Search..."
              value={q}
              onChange={(e) => sQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Total Links" v={traps.length} />
        <SC l="Total Visits" v={totalVisits} />
        <SC l="Group Deployments" v={totalGroups} />
        <SC l="Single Deployments" v={totalSingles} />
      </div>

      <div className="space-y-4">
        {filteredTraps.length === 0 ? (
          <div className="border border-dashed border-[#222] rounded-2xl py-20 text-center">
            <p className="text-[#444]">No campaigns found</p>
          </div>
        ) : (
          filteredTraps.map((t, i) => (
            <motion.div
              key={t.trap_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
              onClick={() => view(t.trap_id)}
              className="group border border-[#1a1a1a] hover:border-[#2a2a2a] transition rounded-2xl p-5 bg-[#050505] flex justify-between items-start gap-4 cursor-pointer"
            >
              <div className="space-y-2 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <B v={t.deploy_mode === "group" ? "blue" : "warning"}>{t.deploy_mode}</B>
                  <B>{t.template}</B>
                  <span className="text-white font-medium text-[15px]">
                    {t.campaign_name || "Unnamed Campaign"}
                  </span>
                </div>

                <p className="text-[#777] text-[12px] break-all">{t.target_url}</p>

                <div className="flex gap-3 text-[11px] text-[#555] mt-2 flex-wrap">
                  <span>Visits: {t.capture_count || 0}</span>
                  <span>{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 items-end flex-shrink-0">
                <B v="success">{t.capture_count || 0}</B>
                <span className="text-[10px] text-[#444] group-hover:text-[#777] transition">
                  Open
                </span>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

function IntelligenceMap() {
  const geoUrl =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  const markers = [
    { name: "Amman", coordinates: [35.91, 31.95] },
    { name: "London", coordinates: [-0.1276, 51.5072] },
    { name: "Washington", coordinates: [-77.0369, 38.9072] },
    { name: "Berlin", coordinates: [13.405, 52.52] },
    { name: "Dubai", coordinates: [55.2708, 25.2048] },
  ];

  return (
    <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-5 overflow-hidden">
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#666] mb-2">
          Geographic Intelligence
        </p>
        <h3 className="text-[18px] font-semibold text-white">
          Global activity map
        </h3>
        <p className="text-[13px] text-[#666] mt-2">
          A modern world map surface for geographic activity visualization.
        </p>
      </div>

      <div className="rounded-[18px] overflow-hidden border border-[#111] bg-black">
        <ComposableMap
          projectionConfig={{ scale: 145 }}
          style={{ width: "100%", height: "420px" }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: "#0f0f0f",
                      stroke: "#232323",
                      strokeWidth: 0.6,
                      outline: "none",
                    },
                    hover: {
                      fill: "#1c1c1c",
                      stroke: "#333",
                      strokeWidth: 0.6,
                      outline: "none",
                    },
                    pressed: {
                      fill: "#1c1c1c",
                      stroke: "#333",
                      strokeWidth: 0.6,
                      outline: "none",
                    },
                  }}
                />
              ))
            }
          </Geographies>

          {markers.map((m, i) => (
            <Marker key={i} coordinates={m.coordinates}>
              <g>
                <circle r={4} fill="#ffffff" />
                <circle r={10} fill="rgba(255,255,255,0.12)" />
              </g>
            </Marker>
          ))}
        </ComposableMap>
      </div>
    </div>
  );
}
function AnalyticsPage() {
  const [d, sD] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/api/v1/analytics'), api.get('/api/v1/stats')])
      .then(([a, s]) => sD({ ...a.data, ...s.data }))
      .catch(() => {});
  }, []);

  if (!d) {
    return <p className="text-[#666] py-8">Loading analytics...</p>;
  }

  const daily = (d.daily || []).reverse();
  const byCampaign = d.by_campaign || [];
  const byRisk = d.by_risk || [];
  const topIps = d.top_ips || [];
  const countries = d.by_country || [];

  const maxDaily = Math.max(...daily.map((x) => x.count || 0), 1);
  const maxCampaign = Math.max(...byCampaign.map((x) => x.count || 0), 1);

  const criticalCount =
    byRisk.find((x) => String(x.risk).toUpperCase() === 'CRITICAL')?.count || 0;
  const mediumCount =
    byRisk.find((x) => String(x.risk).toUpperCase() === 'MEDIUM')?.count || 0;
  const lowCount =
    byRisk.find((x) => String(x.risk).toUpperCase() === 'LOW')?.count || 0;

  const topCampaign = byCampaign[0]?.campaign_name || '—';
  const topCampaignCount = byCampaign[0]?.count || 0;

  const topIp = topIps[0]?.public_ip || '—';
  const topIpCount = topIps[0]?.visits || 0;

  const totalCountries = countries.length;

  const statCards = [
    { label: 'Captures', value: d.total || 0 },
    { label: 'Unique', value: d.unique || 0 },
    { label: 'Critical', value: d.critical || 0, danger: true },
    { label: 'Traps', value: d.traps || 0 },
    { label: 'Visits', value: d.total_visits || 0 },
    { label: 'Profiles', value: d.profiles || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.05),transparent_30%)]" />
        <div className="relative flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Intelligence Analytics
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Operational analytics
              <br />
              across the platform.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[780px] leading-[1.85]">
              Monitor capture volume, campaign activity, risk distribution, IP concentration,
              and geographic spread from a clearer intelligence analytics surface.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-[320px]">
            <div className="border border-[#1a1a1a] rounded-2xl bg-black/40 p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Top Campaign
              </p>
              <p className="text-[14px] text-white truncate">{topCampaign}</p>
              <p className="text-[12px] text-[#777] mt-1">{topCampaignCount} events</p>
            </div>

            <div className="border border-[#1a1a1a] rounded-2xl bg-black/40 p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Top IP
              </p>
              <p className="text-[14px] text-white font-mono truncate">{topIp}</p>
              <p className="text-[12px] text-[#777] mt-1">{topIpCount} visits</p>
            </div>

            <div className="border border-[#1a1a1a] rounded-2xl bg-black/40 p-4">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Countries
              </p>
              <p className="text-[22px] font-semibold text-white">{totalCountries}</p>
              <p className="text-[12px] text-[#777] mt-1">observed regions</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.04 }}
            className={`border rounded-2xl p-5 bg-[#050505] ${
              card.danger ? 'border-red-900' : 'border-[#1a1a1a]'
            }`}
          >
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">
              {card.label}
            </p>
            <p className="text-[30px] md:text-[34px] font-semibold text-white tracking-tight">
              {card.value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 2xl:grid-cols-[1.2fr_0.8fr] gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <Panel
            title="Activity Timeline"
            sub="Daily capture distribution across the last 30 days"
          >
            <div className="h-[260px] flex items-end gap-[4px]">
              {daily.map((x, i) => (
                <div
                  key={i}
                  className="flex-1 h-full flex flex-col justify-end items-center group relative"
                >
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-white/80 to-white/35 group-hover:from-blue-400/80 transition"
                    style={{ height: `${Math.max((x.count / maxDaily) * 220, 4)}px` }}
                  />
                  <div className="absolute bottom-full mb-2 hidden group-hover:block bg-[#171717] border border-[#2a2a2a] text-white text-[10px] px-2 py-1 rounded z-10">
                    {x.count}
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <IntelligenceMap countries={countries} />

          <Panel
            title="Campaign Intelligence"
            sub="Most active campaigns by collected volume"
          >
            <div className="space-y-4">
              {byCampaign.map((x, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="text-[13px] text-white truncate">
                      {x.campaign_name || 'Unnamed Campaign'}
                    </span>
                    <span className="text-[12px] text-[#666]">{x.count}</span>
                  </div>
                  <div className="h-2 bg-[#0f0f0f] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-white/60"
                      style={{ width: `${(x.count / maxCampaign) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Panel
              title="Infrastructure Hotspots"
              sub="Most repeated IPs across captured activity"
            >
              <div className="space-y-2">
                {topIps.map((x, i) => (
                  <div
                    key={i}
                    className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex items-center justify-between gap-3"
                  >
                    <span className="text-[13px] text-white font-mono truncate">
                      {x.public_ip}
                    </span>
                    <B>{x.visits}</B>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel
              title="Country Distribution"
              sub="Observed geographies in collected records"
            >
              <div className="space-y-2">
                {countries.map((x, i) => (
                  <div
                    key={i}
                    className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex items-center justify-between gap-3"
                  >
                    <span className="text-[13px] text-white truncate">{x.country}</span>
                    <span className="text-[12px] text-[#777]">{x.count}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <Panel
            title="Risk Matrix"
            sub="Severity distribution across intelligence events"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 2xl:grid-cols-1 gap-4">
              <div className="border border-red-900 bg-red-950 rounded-2xl p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-red-400 mb-2">
                  Critical
                </p>
                <p className="text-[34px] font-bold text-white">{criticalCount}</p>
                <p className="text-[12px] text-red-300/70 mt-1">
                  high-risk operational signals
                </p>
              </div>

              <div className="border border-amber-900 bg-amber-950 rounded-2xl p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-amber-400 mb-2">
                  Medium
                </p>
                <p className="text-[34px] font-bold text-white">{mediumCount}</p>
                <p className="text-[12px] text-amber-300/70 mt-1">
                  elevated but non-critical events
                </p>
              </div>

              <div className="border border-emerald-900 bg-emerald-950 rounded-2xl p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-400 mb-2">
                  Low
                </p>
                <p className="text-[34px] font-bold text-white">{lowCount}</p>
                <p className="text-[12px] text-emerald-300/70 mt-1">
                  lower-severity intelligence records
                </p>
              </div>
            </div>
          </Panel>

          <Panel
            title="Activity Heatmap"
            sub="Compact recent activity overview"
          >
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 28 }, (_, i) => {
                const day = daily[i];
                return (
                  <div
                    key={i}
                    className="aspect-square rounded-md border border-[#111]"
                    style={{
                      background: day
                        ? `rgba(255,255,255,${Math.min(day.count / maxDaily, 1) * 0.8 + 0.08})`
                        : 'rgba(255,255,255,0.03)',
                    }}
                  />
                );
              })}
            </div>
          </Panel>

          <Panel
            title="Operational Summary"
            sub="High-level interpretation of current analytics"
          >
            <div className="space-y-4 text-[14px] text-[#777] leading-[1.9]">
              <p>
                The platform currently contains <span className="text-white">{d.total || 0}</span>{' '}
                total captures across <span className="text-white">{d.traps || 0}</span> traps,
                with <span className="text-white">{d.critical || 0}</span> critical events flagged.
              </p>

              <p>
                Total observed visits reached <span className="text-white">{d.total_visits || 0}</span>,
                while <span className="text-white">{d.unique || 0}</span> unique records and{' '}
                <span className="text-white">{d.profiles || 0}</span> linked profiles are currently
                represented in the analytics layer.
              </p>

              <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                  Current Highlights
                </p>
                <div className="space-y-2 text-[13px]">
                  <div className="flex justify-between gap-3">
                    <span className="text-[#777]">Most active campaign</span>
                    <span className="text-white truncate">{topCampaign}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-[#777]">Most repeated IP</span>
                    <span className="text-white font-mono truncate">{topIp}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-[#777]">Observed countries</span>
                    <span className="text-white">{totalCountries}</span>
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
function GraphPage() {
  const [d, sD] = useState(null);
  const [sel, sSel] = useState(null);
  const [zoom, sZ] = useState(1);

  useEffect(() => {
    api.get('/api/v1/graph/data').then((r) => sD(r.data)).catch(() => {});
  }, []);

  if (!d || !d.nodes?.length) {
    return (
      <div className="space-y-6">
        <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
            Graph Intelligence
          </p>
          <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
            Entity relationship
            <br />
            intelligence graph.
          </h1>
          <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
            Visualize campaigns, IPs, domains, and profiles through a cleaner graph surface.
          </p>
        </div>

        <Panel title="Graph Intelligence">
          <p className="text-center text-[#444] py-10">No graph data yet</p>
        </Panel>
      </div>
    );
  }

  const colors = {
    campaign: '#60a5fa',
    ip: '#f87171',
    domain: '#34d399',
    profile: '#fbbf24',
  };

  const W = 1000;
  const H = 680;

  const pos = d.nodes.map((n, i) => {
    const tier = { campaign: 0, domain: 1, ip: 2, profile: 3 }[n.type] ?? 0;
    const angle = (2 * Math.PI * i) / d.nodes.length;
    const radius = 170 + tier * 60;
    return {
      ...n,
      x: W / 2 + radius * Math.cos(angle),
      y: H / 2 + radius * Math.sin(angle),
    };
  });

  const nm = {};
  pos.forEach((n) => {
    nm[n.id] = n;
  });

  const selectedNode = sel ? nm[sel] : null;
  const selectedEdges = sel
    ? d.edges.filter((e) => e.from === sel || e.to === sel)
    : [];

  const stats = {
    nodes: d.nodes.length,
    edges: d.edges.length,
    campaigns: d.nodes.filter((n) => n.type === 'campaign').length,
    profiles: d.nodes.filter((n) => n.type === 'profile').length,
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.05),transparent_30%)]" />
        <div className="relative flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Graph Intelligence
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Entity relationship
              <br />
              intelligence graph.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Explore relationships between campaigns, domains, IP infrastructure, and linked profiles.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 min-w-[320px]">
            <SC l="Nodes" v={stats.nodes} />
            <SC l="Edges" v={stats.edges} />
            <SC l="Campaigns" v={stats.campaigns} />
            <SC l="Profiles" v={stats.profiles} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-3 items-center flex-wrap">
          {Object.entries(colors).map(([k, c]) => (
            <div key={k} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: c }} />
              <span className="text-[11px] text-[#888] capitalize">{k}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2 items-center">
          <button
            onClick={() => sZ((z) => Math.max(0.4, z - 0.2))}
            className="text-[#888] border border-[#333] px-3 py-1 rounded-lg"
          >
            −
          </button>
          <span className="text-[12px] text-[#888] min-w-[52px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => sZ((z) => Math.min(2.4, z + 0.2))}
            className="text-[#888] border border-[#333] px-3 py-1 rounded-lg"
          >
            +
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <Panel title="Interactive Graph" sub="Click a node to inspect its relationships">
          <div
            className="border border-[#111] rounded-2xl bg-black overflow-hidden"
            style={{ height: wsFullscreen ? '100%' : '620px', flex: wsFullscreen ? 1 : undefined }}
          >
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full h-full"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
            >
              <defs>
                <marker
                  id="graphArrow"
                  viewBox="0 0 10 10"
                  refX="18"
                  refY="5"
                  markerWidth="5"
                  markerHeight="5"
                  orient="auto"
                >
                  <path d="M0 0L10 5L0 10z" fill="#333" />
                </marker>
              </defs>

              {d.edges.map((e, i) => {
                const f = nm[e.from];
                const t = nm[e.to];
                if (!f || !t) return null;

                const active = sel && (sel === e.from || sel === e.to);

                return (
                  <line
                    key={i}
                    x1={f.x}
                    y1={f.y}
                    x2={t.x}
                    y2={t.y}
                    stroke={active ? '#fff' : '#222'}
                    strokeWidth={active ? 1.5 : 0.7}
                    markerEnd="url(#graphArrow)"
                    opacity={sel ? (active ? 1 : 0.12) : 0.55}
                  />
                );
              })}

              {pos.map((n) => (
                <g
                  key={n.id}
                  className="cursor-pointer"
                  onClick={() => sSel(sel === n.id ? null : n.id)}
                >
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r={n.type === 'campaign' ? 13 : 9}
                    fill={colors[n.type] || '#999'}
                    opacity={sel ? (sel === n.id ? 1 : 0.16) : 0.85}
                    stroke={sel === n.id ? '#fff' : 'none'}
                    strokeWidth={2}
                  />
                  <text
                    x={n.x}
                    y={n.y + 22}
                    textAnchor="middle"
                    fill={sel === n.id ? '#fff' : '#666'}
                    fontSize="9"
                  >
                    {(n.label || '').substring(0, 18)}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </Panel>

        <div className="space-y-6">
          <Panel title="Selected Node" sub="Node metadata and relationship summary">
            {selectedNode ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[18px] text-white font-semibold">{selectedNode.label}</p>
                    <p className="text-[12px] text-[#666] mt-1">ID: {selectedNode.id}</p>
                  </div>
                  <div className="flex gap-2">
                    <B v="blue">{selectedNode.type}</B>
                    <Btn v="ghost" onClick={() => sSel(null)}>
                      ×
                    </Btn>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <SC l="Connections" v={selectedEdges.length} />
                  <SC l="Type" v={selectedNode.type} />
                </div>

                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">
                    Connected Nodes
                  </p>
                  <div className="space-y-2">
                    {selectedEdges.length === 0 ? (
                      <p className="text-[12px] text-[#666]">No direct connections</p>
                    ) : (
                      selectedEdges.map((e, i) => {
                        const otherId = e.from === sel ? e.to : e.from;
                        const other = nm[otherId];
                        return (
                          <div
                            key={i}
                            className="flex items-center justify-between border border-[#1a1a1a] rounded-lg px-3 py-2 bg-[#050505]"
                          >
                            <span className="text-[13px] text-white truncate">
                              {other?.label || otherId}
                            </span>
                            <B>{other?.type || 'node'}</B>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-[13px] text-[#666]">Click a node to inspect it.</p>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
function ProfilesPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();

  const [profiles, setProfiles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState('list');
  const [analysis, setAnalysis] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [newForm, setNewForm] = useState({
    codename: '',
    real_name: '',
    priority: 'medium',
  });

  const [entryForm, setEntryForm] = useState({
    title: '',
    content: '',
    entry_type: 'note',
    priority: 'medium',
  });

  const [entryTab, setEntryTab] = useState('all');

  const safeCount = (value) => {
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'number') return value;
    if (!value) return 0;
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  };

  const loadProfiles = useCallback(async () => {
    try {
      const profilesRes = await api.get('/api/v1/profiles');
      setProfiles(profilesRes.data || []);

      try {
        const analyticsRes = await api.get('/api/v1/profiles/analytics');
        setAnalytics(analyticsRes.data || null);
      } catch {
        setAnalytics(null);
      }
    } catch (e) {
      toast(E(e), 'error');
    }
  }, [toast]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const loadDetail = async (id) => {
    try {
      const res = await api.get(`/api/v1/profiles/${id}`);
      setDetail(res.data);
      setView('detail');
      setAnalysis(null);
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const generateCodename = async () => {
    try {
      const r = await api.get('/api/v1/profiles/generate-codename');
      setNewForm((x) => ({ ...x, codename: r.data.codename }));
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const filteredProfiles = showArchived
    ? profiles.filter((p) => p.status === 'archived')
    : profiles.filter((p) => p.status !== 'archived');

  if (view === 'create') {
    return (
      <div className="space-y-6">
        <Modal />

        <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
                Profile Operations
              </p>
              <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
                Create a new
                <br />
                intelligence profile.
              </h1>
              <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
                Build a structured profile to organize captures, notes, evidence,
                and linked intelligence activity.
              </p>
            </div>
          </div>
        </div>

        <Panel title="New Profile" sub="Create a codename-based operational profile">
          <div className="space-y-4 max-w-2xl">
            <div className="flex gap-3">
              <div className="flex-1">
                <Inp
                  label="Codename"
                  value={newForm.codename}
                  onChange={(e) =>
                    setNewForm({ ...newForm, codename: e.target.value })
                  }
                />
              </div>

              <button
                type="button"
                onClick={generateCodename}
                className="mt-auto px-4 py-2 bg-[#111] border border-[#333] rounded-lg text-[12px] text-[#aaa] hover:text-white"
              >
                Generate
              </button>
            </div>

            <Inp
              label="Real Name"
              value={newForm.real_name}
              onChange={(e) =>
                setNewForm({ ...newForm, real_name: e.target.value })
              }
            />

            <div>
              <label className="block text-[12px] text-[#888] mb-2">Priority</label>
              <div className="flex gap-2 flex-wrap">
                {['low', 'medium', 'high', 'critical'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setNewForm({ ...newForm, priority: p })}
                    className={`px-3 py-1 rounded text-[11px] border ${
                      newForm.priority === p
                        ? 'border-white text-white'
                        : 'border-[#222] text-[#666]'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Btn
                onClick={async () => {
                  if (!newForm.codename) return toast('Codename is required', 'error');

                  try {
                    await api.post('/api/v1/profiles', newForm);
                    toast('Profile created', 'success');
                    setNewForm({
                      codename: '',
                      real_name: '',
                      priority: 'medium',
                    });
                    await loadProfiles();
                    setView('list');
                  } catch (e) {
                    toast(E(e), 'error');
                  }
                }}
              >
                Create
              </Btn>

              <Btn v="ghost" onClick={() => setView('list')}>
                Cancel
              </Btn>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  if (view === 'detail' && detail) {
    const entries = detail.entries || [];
    const filteredEntries =
      entryTab === 'all'
        ? entries
        : entries.filter((e) => e.entry_type === entryTab);

    const captureCount = Array.isArray(detail.captures)
      ? detail.captures.length
      : safeCount(detail.captures);

    return (
      <div className="space-y-6">
        <Modal />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => {
                setView('list');
                loadProfiles();
              }}
              className="text-[#666] hover:text-white text-[14px]"
            >
              &larr;
            </button>

            <div>
              <h1 className="text-[22px] font-bold text-white">{detail.codename}</h1>
              <p className="text-[12px] text-[#555]">{detail.real_name || '—'}</p>
            </div>

            <B v={detail.priority === 'critical' ? 'danger' : 'default'}>
              {detail.priority}
            </B>

            {detail.status === 'archived' && <B v="warning">Archived</B>}
          </div>

          <div className="flex gap-2 flex-wrap">
            <Btn
              v="secondary"
              onClick={async () => {
                try {
                  const r = await api.get(`/api/v1/profiles/${detail.id}/analyze`);
                  setAnalysis(r.data);
                  toast('Analysis completed', 'success');
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Analyze
            </Btn>

            <Btn
              v="secondary"
              onClick={async () => {
                try {
                  await api.put(`/api/v1/profiles/${detail.id}/archive`);
                  await loadDetail(detail.id);
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              {detail.status === 'archived' ? 'Restore' : 'Archive'}
            </Btn>

            <Btn
              v="danger"
              onClick={async () => {
                if (!(await ask('Delete this profile?'))) return;
                try {
                  await api.delete(`/api/v1/profiles/${detail.id}`);
                  toast('Profile deleted', 'success');
                  await loadProfiles();
                  setView('list');
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Delete
            </Btn>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SC l="Entries" v={entries.length} />
          <SC l="Captures" v={captureCount} />
          <SC l="Priority" v={detail.priority || '—'} />
          <SC l="Status" v={detail.status || '—'} />
          <SC l="Profile ID" v={detail.id || '—'} />
        </div>

        {analysis && (
          <Panel title="Analysis" sub="AI-assisted profile intelligence summary" extra={<B v="blue">AI</B>}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <SC l="IPs" v={safeCount(analysis?.intelligence_summary?.unique_ips)} />
              <SC l="Captures" v={safeCount(analysis?.intelligence_summary?.total_captures)} />
              <SC l="Risk" v={analysis?.intelligence_summary?.highest_risk || '—'} />
              <SC l="VPN" v={analysis?.intelligence_summary?.vpn_detected ? 'YES' : 'No'} />
              <SC l="Location" v={analysis?.intelligence_summary?.primary_location || '—'} />
            </div>

            {analysis?.recommendations?.length > 0 && (
              <div className="mt-4 space-y-2">
                {analysis.recommendations.map((r, i) => (
                  <p
                    key={i}
                    className="text-[12px] text-[#888] pl-3 border-l-2 border-[#333]"
                  >
                    {r}
                  </p>
                ))}
              </div>
            )}
          </Panel>
        )}

        <Panel title="Add Entry" sub="Append notes, evidence, links, and structured content">
          <div className="space-y-3">
            <div className="flex gap-1 flex-wrap">
              {['note', 'link', 'image', 'file', 'evidence'].map((t) => (
                <button
                  key={t}
                  onClick={() => setEntryForm({ ...entryForm, entry_type: t })}
                  className={`px-2 py-1 rounded text-[11px] border ${
                    entryForm.entry_type === t
                      ? 'border-white text-white'
                      : 'border-[#222] text-[#666]'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <Inp
              label="Title"
              value={entryForm.title}
              onChange={(e) =>
                setEntryForm({ ...entryForm, title: e.target.value })
              }
            />

            <textarea
              className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] h-20 resize-none outline-none"
              value={entryForm.content}
              onChange={(e) =>
                setEntryForm({ ...entryForm, content: e.target.value })
              }
              placeholder="Entry content..."
            />

            <Btn
              onClick={async () => {
                if (!entryForm.title) return toast('Title is required', 'error');

                try {
                  await api.post(`/api/v1/profiles/${detail.id}/entries`, {
                    ...entryForm,
                    content: {
                      text: entryForm.content,
                      priority: entryForm.priority,
                    },
                  });

                  setEntryForm({
                    title: '',
                    content: '',
                    entry_type: 'note',
                    priority: 'medium',
                  });

                  toast('Entry added', 'success');
                  await loadDetail(detail.id);
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Add Entry
            </Btn>
          </div>
        </Panel>

        <div className="flex gap-1 flex-wrap">
          {['all', 'note', 'link', 'image', 'file', 'evidence', 'capture'].map((t) => {
            const count =
              t === 'all'
                ? entries.length + captureCount
                : t === 'capture'
                ? captureCount
                : entries.filter((e) => e.entry_type === t).length;

            return (
              <button
                key={t}
                onClick={() => setEntryTab(t)}
                className={`px-2 py-1 rounded text-[11px] border ${
                  entryTab === t
                    ? 'border-white text-white'
                    : 'border-[#222] text-[#666]'
                }`}
              >
                {t} ({count})
              </button>
            );
          })}
        </div>

        {(entryTab === 'all' || entryTab === 'capture') &&
          (detail.captures || []).map((capture) => (
            <div
              key={capture.id}
              className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]"
            >
              <div className="flex justify-between mb-3 flex-wrap gap-2">
                <span className="text-white font-mono text-[13px]">
                  {capture.public_ip}
                </span>
                <span className="text-[10px] text-[#444]">
                  {new Date(capture.captured_at).toLocaleString()}
                </span>
              </div>

              <CD r={capture.intel_report} />
            </div>
          ))}

        {filteredEntries.map((entry) => {
          const content =
            typeof entry.content === 'object' && entry.content
              ? entry.content
              : { text: entry.content };

          return (
            <div
              key={entry.id}
              className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]"
            >
              <div className="flex justify-between gap-3 flex-wrap">
                <div className="flex gap-2 items-center flex-wrap">
                  <B v="blue">{entry.entry_type}</B>
                  <span className="text-white text-[14px]">{entry.title}</span>
                </div>

                <button
                  onClick={async () => {
                    if (!(await ask('Delete this entry?'))) return;
                    try {
                      await api.delete(
                        `/api/v1/profiles/${detail.id}/entries/${entry.id}`
                      );
                      toast('Entry deleted', 'success');
                      await loadDetail(detail.id);
                    } catch (e) {
                      toast(E(e), 'error');
                    }
                  }}
                  className="text-[10px] text-red-400"
                >
                  Delete
                </button>
              </div>

              {content?.text && (
                <p className="text-[12px] text-[#888] mt-3 leading-[1.8]">
                  {content.text}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Modal />

      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Profile Intelligence
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Operational profiles
              <br />
              and intelligence records.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Organize codename-based profiles, linked captures, structured notes,
              evidence, and analysis from a cleaner case intelligence surface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Btn v={showArchived ? 'secondary' : 'ghost'} onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? 'Show Active' : 'Show Archived'}
            </Btn>
            <Btn onClick={() => setView('create')}>+ New Profile</Btn>
          </div>
        </div>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SC l="Total" v={Number(analytics?.total || 0)} />
          <SC l="Active" v={Number(analytics?.active || 0)} />
          <SC l="Archived" v={Number(analytics?.archived || 0)} />
          <SC l="Entries" v={Number(analytics?.total_entries || 0)} />
        </div>
      )}

      {filteredProfiles.length === 0 ? (
        <div className="border border-dashed border-[#222] rounded-2xl py-20 text-center">
          <p className="text-[#444]">
            {showArchived ? 'No archived profiles' : 'No profiles found'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredProfiles.map((p) => {
            const totalItems =
              safeCount(p.entries) + safeCount(p.captures);

            return (
              <div
                key={p.id}
                onClick={() => loadDetail(p.id)}
                className="border border-[#1a1a1a] rounded-2xl p-5 cursor-pointer hover:border-[#333] bg-[#050505] transition"
              >
                <p className="text-[15px] text-white font-medium">{p.codename}</p>
                <p className="text-[11px] text-[#555] mt-1">{p.real_name || '—'}</p>

                <div className="flex justify-between items-center mt-4">
                  <div className="flex gap-1 flex-wrap">
                    <B v={p.priority === 'critical' ? 'danger' : 'default'}>
                      {p.priority}
                    </B>
                    <B v={p.status === 'active' ? 'success' : 'warning'}>
                      {p.status}
                    </B>
                  </div>

                  <span className="text-[11px] text-[#555]">{totalItems}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function SettingsAppearanceTab() {
  const { theme, setTheme } = useTheme();
  const THEME_OPTIONS = [
    { id: 'dark',   icon: Moon,    title: 'Dark',   desc: 'Full dark interface. Best for low-light environments and OPSEC.' },
    { id: 'system', icon: Monitor, title: 'System', desc: 'Follows your OS preference. Switches automatically.' },
    { id: 'light',  icon: Sun,     title: 'Light',  desc: 'Clean light interface. Preserves ZureX92 identity with adapted colors.' },
  ];
  return (
    <div className="space-y-6">
      <Panel title="Interface Theme" sub="Choose your preferred appearance">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {THEME_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const active = theme === opt.id;
            return (
              <button key={opt.id} type="button" onClick={() => setTheme(opt.id)}
                className={`text-left border rounded-2xl p-5 transition ${active ? 'border-white bg-[#0d0d0d]' : 'border-[#1a1a1a] bg-[#050505] hover:border-[#2a2a2a]'}`}>
                <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-4 ${active ? 'border-white bg-[#111]' : 'border-[#222] bg-black'}`}>
                  <Icon size={16} className={active ? 'text-white' : 'text-[#555]'}/>
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  <p className={`text-[14px] font-semibold ${active ? 'text-white' : 'text-[#888]'}`}>{opt.title}</p>
                  {active && <CheckCircle size={13} className="text-emerald-400"/>}
                </div>
                <p className="text-[12px] text-[#555] leading-[1.65]">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </Panel>
      <Panel title="Preview" sub="Current theme appearance">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: 'Surface', c: 'bg-[#050505] border border-[#1a1a1a]' },
            { l: 'Card', c: 'bg-[#0a0a0a] border border-[#222]' },
            { l: 'Primary', c: 'bg-white' },
            { l: 'Success', c: 'bg-emerald-500' },
          ].map(s => (
            <div key={s.l} className="space-y-2">
              <div className={`h-12 rounded-xl ${s.c}`}/>
              <p className="text-[11px] text-[#555] text-center">{s.l}</p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}


function SettingsPage() {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    country: '',
    city: '',
  });

  const [security, setSecurity] = useState({
    is_restricted: false,
    restrict_reason: '',
    failed_old_password_attempts: 0,
    password_change_count: 0,
  });

  const [pw, setPw] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const loadSettings = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/v1/settings/me');
      setProfile({
        first_name: r.data.first_name || '',
        last_name: r.data.last_name || '',
        username: r.data.username || '',
        email: r.data.email || '',
        phone: r.data.phone || '',
        country: r.data.country || '',
        city: r.data.city || '',
      });

      setSecurity({
        is_restricted: !!r.data.is_restricted,
        restrict_reason: r.data.restrict_reason || '',
        failed_old_password_attempts: r.data.failed_old_password_attempts || 0,
        password_change_count: r.data.password_change_count || 0,
      });
    } catch (e) {
      toast(E(e), 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const saveProfile = async () => {
    if (!profile.username || !profile.email) {
      return toast('Username and email are required', 'error');
    }

    setSavingProfile(true);
    try {
      const r = await api.put('/api/v1/settings/profile', profile);

      const updatedUser = {
        ...(JSON.parse(localStorage.getItem('zx_u') || '{}')),
        ...r.data,
      };
      localStorage.setItem('zx_u', JSON.stringify(updatedUser));

      toast('Profile updated', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }
    setSavingProfile(false);
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password || !pw.confirm_password) {
      return toast('All password fields are required', 'error');
    }

    if (pw.new_password !== pw.confirm_password) {
      return toast('New passwords do not match', 'error');
    }

    if (pw.new_password.length < 8) {
      return toast('New password must be at least 8 characters', 'error');
    }

    setSavingPassword(true);
    try {
      const r = await api.put('/api/v1/settings/password', {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });

      toast(r.data?.detail || 'Password updated', 'success');

      setPw({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });

      setSecurity((prev) => ({
        ...prev,
        failed_old_password_attempts:
          r.data?.failed_old_password_attempts ?? prev.failed_old_password_attempts,
        password_change_count:
          r.data?.password_change_count ?? prev.password_change_count,
        is_restricted: r.data?.is_restricted ?? prev.is_restricted,
        restrict_reason: r.data?.restrict_reason ?? prev.restrict_reason,
      }));
    } catch (e) {
      toast(E(e), 'error');

      if (e?.response?.data) {
        setSecurity((prev) => ({
          ...prev,
          failed_old_password_attempts:
            e.response.data.failed_old_password_attempts ?? prev.failed_old_password_attempts,
          password_change_count:
            e.response.data.password_change_count ?? prev.password_change_count,
          is_restricted: e.response.data.is_restricted ?? prev.is_restricted,
          restrict_reason: e.response.data.restrict_reason ?? prev.restrict_reason,
        }));
      }
    }
    setSavingPassword(false);
  };

  if (loading) {
    return <p className="text-[#666] py-8">Loading settings...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <button onClick={()=>nav('/dashboard')} className="flex items-center gap-1.5 text-[#555] hover:text-white transition text-[13px] mb-4">
              <ArrowLeft size={14}/> Dashboard
            </button>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
          Settings
        </p>

        <h1 className="text-[30px] md:text-[40px] font-semibold text-white leading-[1.02]">
          Account settings
          <br />
          and security controls.
        </h1>

        <p className="text-[14px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
          Manage your personal information, update credentials, and review account
          security state from one centralized settings page.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Restricted" v={security.is_restricted ? 'Yes' : 'No'} c={security.is_restricted ? 'border-red-900' : ''} />
        <SC l="Failed Old PW" v={security.failed_old_password_attempts || 0} />
        <SC l="PW Changes" v={security.password_change_count || 0} />
        <SC l="Username" v={profile.username || '—'} />
      </div>

      {security.is_restricted && (
        <Panel title="Restriction Status" sub="Account access control state">
          <div className="border border-red-900 bg-red-950 rounded-2xl p-4">
            <p className="text-red-400 text-[13px] font-medium mb-2">
              This account is currently restricted.
            </p>
            <p className="text-[12px] text-red-200/80">
              Reason: {security.restrict_reason || 'Policy enforcement'}
            </p>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-6">
        <Panel
          title="General Information"
          sub="Update your basic account information"
          extra={
            <Btn onClick={saveProfile} disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </Btn>
          }
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Inp
              label="First Name"
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
            />
            <Inp
              label="Last Name"
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
            />
            <Inp
              label="Username"
              value={profile.username}
              onChange={(e) => setProfile({ ...profile, username: e.target.value })}
            />
            <Inp
              label="Email"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            />
            <Inp
              label="Phone Number"
              placeholder="+1 555 000 0000"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
            />
            <Inp
              label="Country"
              placeholder="Jordan, USA, UK..."
              value={profile.country}
              onChange={(e) => setProfile({ ...profile, country: e.target.value })}
            />
            <Inp
              label="City"
              placeholder="Amman, London, New York..."
              value={profile.city}
              onChange={(e) => setProfile({ ...profile, city: e.target.value })}
            />
          </div>
        </Panel>

        <Panel
          title="Change Password"
          sub="Update your account password securely"
          extra={
            <Btn onClick={changePassword} disabled={savingPassword}>
              {savingPassword ? 'Updating...' : 'Update Password'}
            </Btn>
          }
        >
          <div className="space-y-4">
            <PwI
              label="Current Password"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            />
            <PwI
              label="New Password"
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            />
            <PwI
              label="Confirm New Password"
              value={pw.confirm_password}
              onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })}
            />

            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">
                Security Policy
              </p>
              <div className="space-y-2 text-[12px] text-[#777] leading-[1.8]">
                <p>- Current password must be correct before any change is allowed.</p>
                <p>- Reusing old passwords should be blocked by backend policy.</p>
                <p>- After 3 invalid old-password attempts, the account can be restricted.</p>
                <p>- Repeated password changes can also be monitored and restricted by policy.</p>
              </div>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Security Overview" sub="Current account security counters and state">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
              Failed current-password attempts
            </p>
            <p className="text-[28px] font-semibold text-white">
              {security.failed_old_password_attempts || 0}
            </p>
          </div>

          <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
              Password changes count
            </p>
            <p className="text-[28px] font-semibold text-white">
              {security.password_change_count || 0}
            </p>
          </div>
        </div>
      </Panel>
    </div>
  );
}
function TicketsPage() {
  const toast = useToast();

  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  const loadTickets = async () => {
    try {
      const r = await api.get('/api/v1/tickets/my');
      const data = r.data || [];
      setTickets(data);
      if (data.length && !selectedTicket) {
        setSelectedTicket(data[0]);
      }
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const submitTicket = async () => {
    if (!form.subject.trim()) return toast('Subject is required', 'error');
    if (!form.description.trim()) return toast('Description is required', 'error');

    setLoading(true);
    try {
      await api.post('/api/v1/tickets/create', form);
      toast('Ticket submitted', 'success');
      setForm({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium',
      });
      await loadTickets();
    } catch (e) {
      toast(E(e), 'error');
    }
    setLoading(false);
  };

  const statusVariant = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'open') return 'danger';
    if (s === 'resolved' || s === 'closed') return 'success';
    if (s === 'pending' || s === 'review') return 'warning';
    return 'default';
  };

  const priorityVariant = (priority) => {
    const p = String(priority || '').toLowerCase();
    if (p === 'critical') return 'danger';
    if (p === 'high') return 'warning';
    if (p === 'medium') return 'blue';
    return 'default';
  };

  const filteredTickets = tickets.filter((ticket) => {
    const ticketStatus = String(ticket.status || '').toLowerCase();
    const matchesTab =
      tab === 'all'
        ? true
        : tab === 'closed'
        ? ['closed', 'resolved'].includes(ticketStatus)
        : ticketStatus === tab;

    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(ticket.subject || '').toLowerCase().includes(q) ||
      String(ticket.description || '').toLowerCase().includes(q) ||
      String(ticket.category || '').toLowerCase().includes(q);

    return matchesTab && matchesSearch;
  });

  const openCount = tickets.filter((t) => String(t.status || '').toLowerCase() === 'open').length;
  const pendingCount = tickets.filter((t) =>
    ['pending', 'review'].includes(String(t.status || '').toLowerCase())
  ).length;
  const closedCount = tickets.filter((t) =>
    ['closed', 'resolved'].includes(String(t.status || '').toLowerCase())
  ).length;
  const criticalCount = tickets.filter((t) => String(t.priority || '').toLowerCase() === 'critical').length;

  useEffect(() => {
    if (!filteredTickets.length) {
      setSelectedTicket(null);
      return;
    }

    if (!selectedTicket || !filteredTickets.find((t) => t.id === selectedTicket.id)) {
      setSelectedTicket(filteredTickets[0]);
    }
  }, [tab, search, tickets]);

  return (
    <div className="space-y-6">
      {/* HERO */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <button onClick={()=>nav('/system')} className="flex items-center gap-1.5 text-[#555] hover:text-white transition text-[13px] mb-4">
              <ArrowLeft size={14}/> System
            </button>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Support Center
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Support tickets
              <br />
              and assistance workflow.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Submit new platform requests, track ticket status, and review your
              support activity from one modern support surface aligned with the
              rest of the system.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <B v="blue">{form.category}</B>
            <B v={priorityVariant(form.priority)}>{form.priority}</B>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Total Tickets" v={tickets.length} />
        <SC l="Open" v={openCount} />
        <SC l="Pending" v={pendingCount} />
        <SC l="Critical" v={criticalCount} />
      </div>

      {/* CREATE + GUIDE */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_0.9fr] gap-6">
        <Panel
          title="Create Ticket"
          sub="Submit a new support request"
          extra={
            <Btn onClick={submitTicket} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Ticket'}
            </Btn>
          }
        >
          <div className="space-y-4">
            <Inp
              label="Subject"
              placeholder="Issue title or request summary"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] text-[#888] mb-1">Category</label>
                <select
                  className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  <option value="general">General</option>
                  <option value="bug">Bug</option>
                  <option value="vulnerability">Vulnerability</option>
                  <option value="feature">Feature</option>
                  <option value="billing">Billing</option>
                  <option value="account">Account</option>
                </select>
              </div>

              <div>
                <label className="block text-[12px] text-[#888] mb-1">Priority</label>
                <div className="flex gap-2 flex-wrap">
                  {['low', 'medium', 'high', 'critical'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setForm({ ...form, priority: p })}
                      className={`px-3 py-2 rounded-xl text-[12px] border transition ${
                        form.priority === p
                          ? 'border-white text-white bg-[#111]'
                          : 'border-[#222] text-[#666] hover:text-white'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[12px] text-[#888] mb-1">Description</label>
              <textarea
                className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-36 resize-none outline-none"
                placeholder="Describe the issue, request, or report..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
        </Panel>

        <Panel title="Support Guidance" sub="Useful submission notes">
          <div className="space-y-4 text-[13px] text-[#777] leading-[1.85]">
            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Clear Subject
              </p>
              <p>
                Use a short and direct subject so the request can be classified quickly.
              </p>
            </div>

            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Technical Detail
              </p>
              <p>
                Include enough operational detail, reproduction steps, or context
                to help resolve the issue faster.
              </p>
            </div>

            <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                Security Reports
              </p>
              <p>
                Use the <span className="text-white">vulnerability</span> category
                for security findings, abuse reports, or platform exposure issues.
              </p>
            </div>
          </div>
        </Panel>
      </div>

      {/* TICKET WORKSPACE */}
      <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        {/* LEFT LIST */}
        <Panel
          title="Ticket Queue"
          sub={`${filteredTickets.length} visible records`}
          extra={
            <div className="flex gap-2 flex-wrap">
              <Tabs
                tabs={[
                  { id: 'all', l: 'All' },
                  { id: 'open', l: 'Open' },
                  { id: 'pending', l: 'Pending' },
                  { id: 'closed', l: 'Closed' },
                ]}
                active={tab}
                onChange={setTab}
              />
            </div>
          }
        >
          <div className="space-y-4">
            <input
              className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {filteredTickets.length === 0 ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                <p className="text-[#444]">No tickets found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
                {filteredTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left border rounded-2xl p-4 transition ${
                      selectedTicket?.id === ticket.id
                        ? 'border-white bg-[#0d0d0d]'
                        : 'border-[#1a1a1a] bg-[#050505] hover:border-[#333]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <B v={statusVariant(ticket.status)}>{ticket.status || 'open'}</B>
                          <B v={priorityVariant(ticket.priority)}>{ticket.priority || 'medium'}</B>
                          <B>{ticket.category || 'general'}</B>
                        </div>

                        <p className="text-[14px] text-white font-medium">
                          {ticket.subject}
                        </p>

                        {ticket.description && (
                          <p className="text-[12px] text-[#666] mt-2 line-clamp-2">
                            {ticket.description}
                          </p>
                        )}
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-[11px] text-[#555]">#{ticket.id}</p>
                        <p className="text-[10px] text-[#444] mt-1">
                          {ticket.created_at
                            ? new Date(ticket.created_at).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Panel>

        {/* RIGHT DETAILS */}
        <Panel title="Ticket Details" sub="Selected ticket overview and timeline">
          {!selectedTicket ? (
            <div className="border border-dashed border-[#222] rounded-2xl py-20 text-center">
              <p className="text-[#444]">Select a ticket to view details</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <p className="text-[22px] font-semibold text-white">
                      {selectedTicket.subject}
                    </p>
                    <p className="text-[12px] text-[#555] mt-2">
                      Ticket #{selectedTicket.id}
                    </p>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <B v={statusVariant(selectedTicket.status)}>
                      {selectedTicket.status || 'open'}
                    </B>
                    <B v={priorityVariant(selectedTicket.priority)}>
                      {selectedTicket.priority || 'medium'}
                    </B>
                    <B>{selectedTicket.category || 'general'}</B>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3 mb-4">
                  <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                      Created
                    </p>
                    <p className="text-[12px] text-white">
                      {selectedTicket.created_at
                        ? new Date(selectedTicket.created_at).toLocaleString()
                        : '—'}
                    </p>
                  </div>

                  <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                      Category
                    </p>
                    <p className="text-[12px] text-white">
                      {selectedTicket.category || 'general'}
                    </p>
                  </div>

                  <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#555] mb-2">
                      Priority
                    </p>
                    <p className="text-[12px] text-white">
                      {selectedTicket.priority || 'medium'}
                    </p>
                  </div>
                </div>

                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">
                    Description
                  </p>
                  <p className="text-[13px] text-[#cfcfcf] leading-[1.9] whitespace-pre-wrap">
                    {selectedTicket.description || 'No description provided.'}
                  </p>
                </div>
              </div>

              <div className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-5">
                  Timeline
                </p>

                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-white" />
                      <div className="w-px flex-1 bg-[#222] mt-2" />
                    </div>
                    <div className="pb-2">
                      <p className="text-[13px] text-white font-medium">Ticket created</p>
                      <p className="text-[12px] text-[#666] mt-1">
                        {selectedTicket.created_at
                          ? new Date(selectedTicket.created_at).toLocaleString()
                          : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-[#333]" />
                    </div>
                    <div>
                      <p className="text-[13px] text-white font-medium">
                        Current status: {selectedTicket.status || 'open'}
                      </p>
                      <p className="text-[12px] text-[#666] mt-1">
                        Waiting for timeline/reply support integration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-[#1a1a1a] rounded-2xl p-5 bg-[#050505]">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">
                  Next Step
                </p>
                <p className="text-[13px] text-[#777] leading-[1.85]">
                  This panel is ready for future support-thread expansion such as
                  replies, admin comments, attachments, and status-change history.
                </p>
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
function ExifPage() {
  const toast = useToast();

  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('url');
  const [selectedFileName, setSelectedFileName] = useState('');

  const extractFromUrl = async () => {
    if (!url.trim()) return toast('Image URL is required', 'error');

    setLoading(true);
    setResult(null);

    try {
      const r = await api.post('/api/v1/exif/extract', { image_url: url });
      setResult(r.data);
      toast('EXIF extracted', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }

    setLoading(false);
  };

  const extractFromFile = async (file) => {
    if (!file) return;

    setLoading(true);
    setResult(null);
    setSelectedFileName(file.name || '');

    try {
      const fd = new FormData();
      fd.append('file', file);

      const r = await api.post('/api/v1/exif/upload', fd);
      setResult(r.data);
      toast('EXIF extracted', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }

    setLoading(false);
  };

  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const topLevelEntries = result
    ? Object.entries(result).filter(([key]) => !['exif', 'gps_raw'].includes(key))
    : [];

  const exifEntries = result?.exif ? Object.entries(result.exif) : [];

  const hasGps =
    !!result?.gps_raw ||
    !!result?.gps ||
    topLevelEntries.some(([k]) => k.toLowerCase().includes('gps'));

  return (
    <div className="space-y-6">
      {/* HERO */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              EXIF Intelligence
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Metadata extraction
              <br />
              for images and uploads.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Extract technical metadata, embedded EXIF records, GPS indicators,
              and image attributes from URLs or uploaded files through a cleaner
              analysis surface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <B v="blue">EXIF</B>
            <B v="success">GPS Ready</B>
            <B>{mode === 'url' ? 'URL Mode' : 'Upload Mode'}</B>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Fields" v={topLevelEntries.length + exifEntries.length} />
        <SC l="Top Level" v={topLevelEntries.length} />
        <SC l="EXIF Fields" v={exifEntries.length} />
        <SC l="GPS" v={hasGps ? 'Detected' : 'Not Found'} />
      </div>

      {/* MAIN */}
      <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        {/* LEFT */}
        <div className="space-y-6">
          <Panel
            title="Extraction Source"
            sub="Choose URL input or upload a local file"
          >
            <div className="space-y-5">
              <div className="flex gap-2">
                <Btn
                  v={mode === 'url' ? 'primary' : 'secondary'}
                  onClick={() => setMode('url')}
                >
                  URL
                </Btn>

                <Btn
                  v={mode === 'file' ? 'primary' : 'secondary'}
                  onClick={() => setMode('file')}
                >
                  Upload
                </Btn>
              </div>

              {mode === 'url' ? (
                <div className="space-y-4">
                  <Inp
                    label="Image URL"
                    placeholder="https://example.com/photo.jpg"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />

                  <Btn onClick={extractFromUrl} disabled={loading}>
                    {loading ? 'Extracting...' : 'Extract Metadata'}
                  </Btn>
                </div>
              ) : (
                <div className="space-y-4">
                  <label className="block text-[12px] text-[#888]">
                    Upload image file
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => extractFromFile(e.target.files?.[0])}
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                  />

                  {selectedFileName && (
                    <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                      <p className="text-[11px] text-[#666] mb-1">Selected file</p>
                      <p className="text-[13px] text-white">{selectedFileName}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Panel>

          <Panel
            title="Extraction Summary"
            sub="Current extraction context and quick status"
          >
            <div className="space-y-3 text-[13px]">
              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Mode</span>
                <span className="text-white">{mode === 'url' ? 'URL' : 'Upload'}</span>
              </div>

              <div className="flex justify-between gap-3">
                <span className="text-[#666]">State</span>
                <span className="text-white">
                  {loading ? 'Processing' : result ? 'Completed' : 'Idle'}
                </span>
              </div>

              <div className="flex justify-between gap-3">
                <span className="text-[#666]">GPS Presence</span>
                <span className="text-white">{hasGps ? 'Detected' : 'Not Found'}</span>
              </div>

              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Source</span>
                <span className="text-white break-all text-right">
                  {mode === 'url' ? url || '—' : selectedFileName || '—'}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <Panel title="Parsed Results" sub="Top-level extracted metadata">
            {!result ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-20 text-center">
                <p className="text-[#444]">No EXIF results yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topLevelEntries.length === 0 ? (
                  <p className="text-[#555] text-[13px]">No top-level metadata found.</p>
                ) : (
                  topLevelEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className="border border-[#1a1a1a] rounded-xl p-3 bg-black"
                    >
                      <div className="flex justify-between gap-3">
                        <span className="text-[12px] text-[#666]">{key}</span>
                        <span className="text-[12px] text-white font-mono text-right break-all">
                          {renderValue(value)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </Panel>

          {result && (
            <Panel title="EXIF Fields" sub={`${exifEntries.length} extracted EXIF fields`}>
              <div className="space-y-3">
                {exifEntries.length === 0 ? (
                  <p className="text-[#555] text-[13px]">No EXIF metadata fields found.</p>
                ) : (
                  exifEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className="border border-[#1a1a1a] rounded-xl p-3 bg-black"
                    >
                      <div className="flex justify-between gap-3">
                        <span className="text-[12px] text-[#666]">{key}</span>
                        <span className="text-[12px] text-white font-mono text-right break-all">
                          {renderValue(value)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
function PagesPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();

  const [pages, setPages] = useState([]);
  const [form, setForm] = useState({ name: '', html_content: '' });
  const [showPreview, setShowPreview] = useState(true);
  const [aiTab, setAiTab] = useState('prompt'); // prompt | clone
  const [aiPrompt, setAiPrompt] = useState('');
  const [cloneUrl, setCloneUrl] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const loadPages = async () => {
    try {
      const r = await api.get('/api/v1/pages');
      setPages(r.data || []);
    } catch (e) { toast(E(e), 'error'); }
  };

  useEffect(() => { loadPages(); }, []);

  const savePage = async () => {
    if (!form.name || !form.html_content) return toast('Name and HTML content are required', 'error');
    try {
      await api.post('/api/v1/pages', form);
      toast('Page saved', 'success');
      setForm({ name: '', html_content: '' });
      loadPages();
    } catch (e) { toast(E(e), 'error'); }
  };

  // ── AI: Generate from prompt ──
  const generateFromPrompt = async () => {
    if (!aiPrompt.trim()) return toast('Enter a description', 'error');
    setAiLoading(true);
    setAiError('');
    try {
      const r = await api.post('/api/v1/ai/generate-page', { mode: 'prompt', prompt: aiPrompt });
      const html = r.data?.html || '';
      if (!html || html.length < 50) throw new Error('AI returned empty response');
      setForm(f => ({ ...f, html_content: html }));
      toast('Page generated! Review and save.', 'success');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI generation failed';
      setAiError(msg);
      toast('Generation failed', 'error');
    }
    setAiLoading(false);
  };

  // ── AI: Clone from URL ──
  const cloneFromUrl = async () => {
    if (!cloneUrl.trim()) return toast('Enter a URL', 'error');
    setAiLoading(true);
    setAiError('');
    try {
      const r = await api.post('/api/v1/ai/generate-page', { mode: 'clone', url: cloneUrl });
      const html = r.data?.html || '';
      if (!html || html.length < 50) throw new Error('AI returned empty response');
      const siteName = (() => { try { return new URL(cloneUrl).hostname.replace('www.',''); } catch { return 'clone'; } })();
      setForm(f => ({ ...f, html_content: html, name: f.name || `Clone - ${siteName}` }));
      toast('Page cloned! Review and save.', 'success');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Clone failed';
      setAiError(msg);
      toast('Clone failed: ' + msg, 'error');
    }
    setAiLoading(false);
  };

  const PROMPT_PRESETS = [
    { l: 'Login Page', p: 'A professional dark-themed login page with email and password fields, a "Sign In" button, and a "Forgot password" link. Modern minimal design.' },
    { l: 'Delivery Tracking', p: 'A package delivery tracking page showing shipment status with a tracking number field, progress steps (Order Placed → Shipped → Out for Delivery → Delivered), and estimated arrival date.' },
    { l: 'Security Alert', p: 'An urgent security alert page warning the user their account needs verification, with a "Verify Now" button and a countdown timer. Use red and dark colors.' },
    { l: 'Bank Portal', p: 'A professional online banking portal login page with account overview, a secure login form, and trust badges. Clean, corporate blue theme.' },
    { l: 'App Download', p: 'A mobile app download landing page with app screenshots, feature highlights, and App Store / Google Play download buttons.' },
    { l: 'Survey Form', p: 'A clean survey form page with 5 multiple choice questions about user satisfaction, a progress bar, and a submit button.' },
    { l: 'Prize Claim', p: 'A congratulations page telling the user they won a prize, with a claim button and a form to enter their details. Exciting, colorful design.' },
    { l: 'Document View', p: 'A Google Docs-style document viewer page with a toolbar, a document title, and text content. Professional, clean design.' },
  ];

  return (
    <div className="space-y-6">
      <Modal />

      {/* HERO */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <button onClick={()=>nav('/operations')} className="flex items-center gap-1.5 text-[#555] hover:text-white transition text-[13px] mb-4">
              <ArrowLeft size={14}/> Operations
            </button>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">Page Builder</p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Build & deploy<br/>custom pages.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Write HTML manually, generate pages with AI from a prompt, or clone the style of any website.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <B v="blue">AI Builder</B>
            <B v="success">Live Preview</B>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Saved Pages" v={pages.length} />
        <SC l="Preview" v={showPreview ? 'Visible' : 'Hidden'} />
        <SC l="Editor Chars" v={form.html_content.length} />
        <SC l="Draft Name" v={form.name || '—'} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        {/* LEFT — EDITOR + AI */}
        <div className="space-y-6">

          {/* AI Builder Panel */}
          <Panel title="AI Page Builder" sub="Generate or clone any page using AI">
            {/* Tabs */}
            <div className="flex border border-[#222] rounded-xl overflow-hidden mb-5">
              <button onClick={() => setAiTab('prompt')}
                className={`flex-1 px-4 py-2.5 text-[13px] font-medium transition ${aiTab==='prompt'?'bg-white text-black':'text-[#666] hover:text-white'}`}>
                Generate from Prompt
              </button>
              <button onClick={() => setAiTab('clone')}
                className={`flex-1 px-4 py-2.5 text-[13px] font-medium transition ${aiTab==='clone'?'bg-white text-black':'text-[#666] hover:text-white'}`}>
                Clone from URL
              </button>
            </div>

            {aiTab === 'prompt' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] text-[#888] mb-2">Describe the page you want</label>
                  <textarea
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[13px] h-28 resize-none outline-none placeholder:text-[#333] focus:border-[#555]"
                    placeholder="e.g. A professional login page for a bank with dark theme, email/password fields, and a secure badge..."
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                  />
                </div>

                {/* Presets */}
                <div>
                  <p className="text-[11px] text-[#555] uppercase tracking-wider mb-2">Quick Presets</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {PROMPT_PRESETS.map(p => (
                      <button key={p.l} type="button" onClick={() => setAiPrompt(p.p)}
                        className="text-left border border-[#1a1a1a] rounded-xl px-3 py-2.5 bg-[#050505] hover:border-[#333] transition">
                        <p className="text-[12px] text-white">{p.l}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={generateFromPrompt} disabled={aiLoading || !aiPrompt.trim()}
                  className="w-full py-3 bg-white text-black rounded-xl text-[14px] font-medium hover:bg-[#eee] transition disabled:opacity-30">
                  {aiLoading ? 'Generating...' : 'Generate Page'}
                </button>
              </div>
            )}

            {aiTab === 'clone' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] text-[#888] mb-2">Website URL to clone</label>
                  <input
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2.5 text-white rounded-xl text-[13px] outline-none placeholder:text-[#333] focus:border-[#555]"
                    placeholder="https://example.com"
                    value={cloneUrl}
                    onChange={e => setCloneUrl(e.target.value)}
                  />
                </div>

                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[12px] text-white font-medium mb-2">How it works</p>
                  <div className="space-y-1.5 text-[12px] text-[#666]">
                    <p>1. Enter any website URL</p>
                    <p>2. AI analyzes the site's style, colors, and layout</p>
                    <p>3. Generates a visually similar standalone HTML page</p>
                    <p>4. Review, customize, and save to your pages</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['https://youtube.com','https://instagram.com','https://facebook.com','https://twitter.com',
                    'https://netflix.com','https://google.com','https://apple.com','https://microsoft.com'
                  ].map(u => (
                    <button key={u} type="button" onClick={() => setCloneUrl(u)}
                      className="border border-[#1a1a1a] rounded-xl px-3 py-2 text-[11px] text-[#666] hover:text-white hover:border-[#333] transition truncate">
                      {u.replace('https://','').replace('.com','')}
                    </button>
                  ))}
                </div>

                <button onClick={cloneFromUrl} disabled={aiLoading || !cloneUrl.trim()}
                  className="w-full py-3 bg-white text-black rounded-xl text-[14px] font-medium hover:bg-[#eee] transition disabled:opacity-30">
                  {aiLoading ? 'Cloning...' : 'Clone Page Style'}
                </button>
              </div>
            )}

            {aiError && (
              <div className="mt-3 border border-red-900 bg-red-950 rounded-xl p-3">
                <p className="text-red-400 text-[12px]">{aiError}</p>
              </div>
            )}
          </Panel>

          {/* Manual Editor */}
          <Panel
            title="Page Editor"
            sub="Write or edit HTML directly"
            extra={
              <div className="flex gap-2">
                <Btn v="secondary" onClick={() => setShowPreview(!showPreview)}>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </Btn>
                <Btn onClick={savePage}>Save Page</Btn>
              </div>
            }
          >
            <div className="space-y-4">
              <Inp label="Page Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Landing Variant 01" />

              <div className={`grid gap-4 ${showPreview ? 'grid-cols-1 2xl:grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="block text-[12px] text-[#888] mb-2">HTML Source</label>
                  <textarea
                    className="w-full bg-black border border-[#222] px-4 py-3 text-green-400 rounded-2xl text-[12px] resize-y font-mono outline-none focus:border-[#444]"
                    style={{ minHeight: '420px' }}
                    value={form.html_content}
                    onChange={e => setForm({ ...form, html_content: e.target.value })}
                    spellCheck={false}
                    placeholder="<html>...</html>"
                  />
                </div>
                {showPreview && (
                  <div>
                    <label className="block text-[12px] text-[#888] mb-2">Live Preview</label>
                    <div className="border border-[#222] rounded-2xl overflow-hidden bg-white min-h-[420px]">
                      <iframe
                        srcDoc={form.html_content || '<div style="padding:24px;font-family:sans-serif;color:#999">Type HTML or generate with AI...</div>'}
                        className="w-full h-[420px]"
                        title="Preview"
                        sandbox="allow-scripts"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Panel>
        </div>

        {/* RIGHT — Saved Pages */}
        <div className="space-y-6">
          <Panel title="Saved Pages" sub={`${pages.length} stored records`}>
            {pages.length === 0 ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                <p className="text-[#444]">No pages saved yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pages.map(page => (
                  <div key={page.id} className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white text-[14px] font-medium truncate">{page.name}</p>
                        <p className="text-[11px] text-[#555] mt-1">Page ID: {page.id}</p>
                      </div>
                      <B v="blue">HTML</B>
                    </div>
                    <div className="flex gap-2 mt-4 flex-wrap">
                      <Btn v="ghost" onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/pages/${page.id}/preview`, '_blank')}>Open</Btn>
                      <Btn v="secondary" onClick={() => setForm({ name: page.name || '', html_content: page.html_content || '' })}>Load</Btn>
                      <Btn v="danger" onClick={async () => {
                        if (!(await ask('Delete this page?'))) return;
                        try { await api.delete(`/api/v1/pages/${page.id}`); toast('Page deleted', 'success'); setPages(prev => prev.filter(x => x.id !== page.id)); }
                        catch (e) { toast(E(e), 'error'); }
                      }}>Delete</Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Usage Notes" sub="Quick reference">
            <div className="space-y-3 text-[13px] text-[#777] leading-[1.8]">
              <p><span className="text-white">AI Generate</span> — Describe any page and the AI builds it instantly.</p>
              <p><span className="text-white">Clone URL</span> — Enter any site URL and AI recreates its visual style.</p>
              <p><span className="text-white">Placeholders</span>: Use <code className="text-emerald-400">{"{{PAYLOAD}}"}</code> for tracking script injection.</p>
              <p><span className="text-white">Preview</span> renders your draft live so you can verify layout.</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}


function InvestigationsPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();

  const [investigations, setInvestigations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [itemFilter, setItemFilter] = useState('all');
  const [workspaceView, setWorkspaceView] = useState('board');
  const [wsFullscreen, setWsFullscreen] = useState(false);
  const [wsZoom, setWsZoom] = useState(1);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);

  const [newCase, setNewCase] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'open',
    case_type: 'identity',
    target_type: 'username',
    target_value: '',
    analyst: '',
    classification: 'internal',
    tags: '',
  });

  const [newItem, setNewItem] = useState({
    item_type: 'note',
    title: '',
    content: '',
  });

  const [workspaceNodes, setWorkspaceNodes] = useState([]);
  const [workspaceLinks, setWorkspaceLinks] = useState([]);
  const [draggingNodeId, setDraggingNodeId] = useState(null);
  const [draggingTemplate, setDraggingTemplate] = useState(null);
  const [connectFromId, setConnectFromId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const workspaceBounds = { width: 1080, height: 720 };
  const laneOrder = ['note', 'target', 'evidence', 'finding', 'link', 'task'];
  const laneConfig = {
    note: { title: 'Context', x: 36 },
    target: { title: 'Targets', x: 212 },
    evidence: { title: 'Evidence', x: 388 },
    finding: { title: 'Findings', x: 564 },
    link: { title: 'Intel Links', x: 740 },
    task: { title: 'Actions', x: 916 },
  };

  const paletteItems = [
    { type: 'note', label: 'Analyst Note', hint: 'Observation, context, narrative, memo.' },
    { type: 'target', label: 'Target', hint: 'Entity, handle, email, device, domain, IP.' },
    { type: 'evidence', label: 'Evidence', hint: 'Artifact, screenshot, file, capture, log.' },
    { type: 'finding', label: 'Finding', hint: 'Assessment, anomaly, conclusion, indicator.' },
    { type: 'link', label: 'Link / IOC', hint: 'URL, domain, IP, or pivot reference.' },
    { type: 'task', label: 'Task', hint: 'Analyst action item or validation step.' },
  ];

  const safeCount = (value) => {
    if (Array.isArray(value)) return value.length;
    const n = Number(value || 0);
    return Number.isNaN(n) ? 0 : n;
  };

  const priorityBadge = (priority) => {
    if (priority === 'critical') return 'danger';
    if (priority === 'high') return 'warning';
    if (priority === 'medium') return 'blue';
    return 'default';
  };

  const itemBadge = (type) => {
    if (type === 'evidence') return 'warning';
    if (type === 'finding') return 'danger';
    if (type === 'target') return 'blue';
    if (type === 'task') return 'success';
    return 'default';
  };

  const statusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (['closed', 'resolved'].includes(s)) return 'success';
    if (['escalated', 'critical'].includes(s)) return 'danger';
    if (['active', 'in_progress', 'review', 'monitoring'].includes(s)) return 'blue';
    return 'default';
  };

  const getCaseStatus = (inv) => inv?.status || (inv?.priority === 'critical' ? 'escalated' : 'open');
  const getLastActivity = (inv) => inv?.updated_at || inv?.modified_at || inv?.created_at || null;
  const humanTime = (value) => {
    try {
      return value ? new Date(value).toLocaleString() : '—';
    } catch {
      return '—';
    }
  };

  const getWorkspaceStorageKey = (id) => `zx_inv_workspace_${id}`;

  const buildWorkspaceNodes = useCallback((items = []) => {
    const counters = {};

    return items.map((item) => {
      const laneKey = laneOrder.includes(item.item_type) ? item.item_type : 'note';
      const currentIndex = counters[laneKey] || 0;
      counters[laneKey] = currentIndex + 1;

      return {
        id: item.id,
        item_id: item.id,
        title: item.title,
        item_type: item.item_type,
        content: item.content?.text || '',
        x: laneConfig[laneKey].x,
        y: 78 + currentIndex * 142,
      };
    });
  }, []);

  const buildDefaultLinks = useCallback((nodes = []) => {
    return nodes.slice(1).map((node, index) => ({
      id: `${nodes[index].id}-${node.id}`,
      from: nodes[index].id,
      to: node.id,
      label: 'flow',
    }));
  }, []);

  const loadWorkspaceState = useCallback((investigationId, items = []) => {
    const fallbackNodes = buildWorkspaceNodes(items);
    const fallbackLinks = buildDefaultLinks(fallbackNodes);

    try {
      const raw = localStorage.getItem(getWorkspaceStorageKey(investigationId));
      if (!raw) return { nodes: fallbackNodes, links: fallbackLinks };

      const parsed = JSON.parse(raw);
      const validIds = new Set(items.map((item) => item.id));
      const savedNodes = Array.isArray(parsed.nodes)
        ? parsed.nodes.filter((node) => validIds.has(node.item_id || node.id))
        : [];
      const savedLinks = Array.isArray(parsed.links)
        ? parsed.links.filter((link) => validIds.has(link.from) && validIds.has(link.to))
        : [];

      const mergedNodes = items.map((item) => {
        const existing = savedNodes.find((node) => (node.item_id || node.id) === item.id);
        if (existing) {
          return {
            ...existing,
            id: item.id,
            item_id: item.id,
            title: item.title,
            item_type: item.item_type,
            content: item.content?.text || '',
          };
        }
        return fallbackNodes.find((node) => node.id === item.id) || {
          id: item.id,
          item_id: item.id,
          title: item.title,
          item_type: item.item_type,
          content: item.content?.text || '',
          x: 36,
          y: 78,
        };
      });

      return {
        nodes: mergedNodes,
        links: savedLinks.length ? savedLinks : buildDefaultLinks(mergedNodes),
      };
    } catch {
      return { nodes: fallbackNodes, links: fallbackLinks };
    }
  }, [buildDefaultLinks, buildWorkspaceNodes]);

  const persistWorkspaceState = useCallback((investigationId, nodes, links) => {
    if (!investigationId) return;
    localStorage.setItem(getWorkspaceStorageKey(investigationId), JSON.stringify({ nodes, links }));
  }, []);

  const loadInvestigations = useCallback(async () => {
    try {
      const r = await api.get('/api/v1/investigations');
      setInvestigations(r.data || []);
    } catch (e) {
      toast(E(e), 'error');
    }
  }, [toast]);

  useEffect(() => {
    loadInvestigations();
  }, [loadInvestigations]);

  useEffect(() => {
    if (!selected?.id) return;
    persistWorkspaceState(selected.id, workspaceNodes, workspaceLinks);
  }, [selected?.id, workspaceNodes, workspaceLinks, persistWorkspaceState]);

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/api/v1/investigations/${id}`);
      const detail = r.data;
      setSelected(detail);
      setItemFilter('all');
      setWorkspaceView('board');
      setSelectedNodeId(null);
      setConnectFromId(null);
      const state = loadWorkspaceState(detail.id, detail?.items || []);
      setWorkspaceNodes(state.nodes);
      setWorkspaceLinks(state.links);
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const refreshSelected = async () => {
    if (!selected?.id) return;
    await loadDetail(selected.id);
  };

  const createCase = async () => {
    if (!newCase.title.trim()) return toast('Title is required', 'error');

    const detailLines = [
      newCase.description?.trim(),
      `Status: ${newCase.status}`,
      `Type: ${newCase.case_type}`,
      `Target: ${newCase.target_type}${newCase.target_value ? ` - ${newCase.target_value}` : ''}`,
      `Analyst: ${newCase.analyst || 'unassigned'}`,
      `Classification: ${newCase.classification}`,
      `Tags: ${newCase.tags || 'none'}`,
    ].filter(Boolean);

    try {
      await api.post('/api/v1/investigations', {
        title: newCase.title,
        priority: newCase.priority,
        description: detailLines.join('\n'),
      });

      toast('Investigation created', 'success');
      setNewCase({
        title: '',
        description: '',
        priority: 'medium',
        status: 'open',
        case_type: 'identity',
        target_type: 'username',
        target_value: '',
        analyst: '',
        classification: 'internal',
        tags: '',
      });
      loadInvestigations();
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const addItem = async () => {
    if (!selected?.id) return;
    if (!newItem.title.trim()) return toast('Item title is required', 'error');

    try {
      await api.post(`/api/v1/investigations/${selected.id}/items`, {
        item_type: newItem.item_type,
        title: newItem.title,
        content: { text: newItem.content },
      });

      toast('Item added', 'success');
      setNewItem({ item_type: 'note', title: '', content: '' });
      await refreshSelected();
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const createItemFromPalette = async (paletteType, dropX, dropY) => {
    if (!selected?.id || !paletteType) return;

    const paletteMeta = paletteItems.find((item) => item.type === paletteType);
    const fallbackCount = workspaceNodes.filter((node) => node.item_type === paletteType).length + 1;

    try {
      const response = await api.post(`/api/v1/investigations/${selected.id}/items`, {
        item_type: paletteType,
        title: `${paletteMeta?.label || paletteType} ${fallbackCount}`,
        content: { text: paletteMeta?.hint || 'New workspace item' },
      });

      const created = response.data;
      const newNode = {
        id: created.id,
        item_id: created.id,
        title: created.title,
        item_type: created.item_type,
        content: created.content?.text || '',
        x: dropX,
        y: dropY,
      };

      const lastNode = workspaceNodes.slice(-1)[0];
      setWorkspaceNodes((prev) => [...prev, newNode]);
      setWorkspaceLinks((prev) => {
        if (!lastNode) return prev;
        return [
          ...prev,
          {
            id: `${lastNode.id}-${newNode.id}-${Date.now()}`,
            from: lastNode.id,
            to: newNode.id,
            label: 'linked',
          },
        ];
      });
      setSelected((prev) => prev ? ({ ...prev, items: [...(prev.items || []), created] }) : prev);
      setSelectedNodeId(created.id);
      toast('Node created', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const filteredInvestigations = investigations
    .filter((inv) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || inv.title?.toLowerCase().includes(q) || inv.description?.toLowerCase().includes(q);
      const matchesPriority = priorityFilter === 'all' || inv.priority === priorityFilter;
      return matchesSearch && matchesPriority;
    })
    .sort((a, b) => {
      if (sortBy === 'priority') {
        const rank = { critical: 4, high: 3, medium: 2, low: 1 };
        return (rank[b.priority] || 0) - (rank[a.priority] || 0);
      }
      if (sortBy === 'title') {
        return String(a.title || '').localeCompare(String(b.title || ''));
      }
      return new Date(getLastActivity(b) || 0) - new Date(getLastActivity(a) || 0);
    });

  const criticalQueue = investigations.filter((x) => x.priority === 'critical');
  const openCount = investigations.filter((x) => getCaseStatus(x) === 'open').length;
  const activeCount = investigations.filter((x) => ['active', 'in_progress', 'monitoring', 'review'].includes(getCaseStatus(x))).length;
  const totalItemsAcrossCases = investigations.reduce((acc, x) => acc + safeCount(x.items_count ?? x.items), 0);

  const detailsLines = String(selected?.description || '').split('\n').map((line) => line.trim()).filter(Boolean);

  const parsedDetailMap = detailsLines.reduce((acc, line) => {
    if (!line.includes(':')) return acc;
    const [key, ...rest] = line.split(':');
    acc[key.trim().toLowerCase()] = rest.join(':').trim();
    return acc;
  }, {});

  const items = selected?.items || [];
  const filteredItems = itemFilter === 'all' ? items : items.filter((item) => item.item_type === itemFilter);
  const evidenceCount = items.filter((i) => i.item_type === 'evidence').length;
  const findingsCount = items.filter((i) => i.item_type === 'finding').length;
  const targetsCount = items.filter((i) => i.item_type === 'target').length;
  const notesCount = items.filter((i) => i.item_type === 'note').length;

  const visibleNodes = workspaceNodes.filter((node) => (itemFilter === 'all' ? true : node.item_type === itemFilter));
  const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
  const visibleLinks = workspaceLinks.filter((link) => visibleNodeIds.has(link.from) && visibleNodeIds.has(link.to));
  const selectedNode = workspaceNodes.find((node) => node.id === selectedNodeId) || null;

  const updateSelectedNode = (patch) => {
    if (!selectedNodeId) return;
    setWorkspaceNodes((prev) => prev.map((node) => node.id === selectedNodeId ? ({ ...node, ...patch }) : node));
  };

  const handleWorkspaceDrop = async (event) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(18, Math.min(event.clientX - rect.left - 80, workspaceBounds.width - 180));
    const y = Math.max(54, Math.min(event.clientY - rect.top - 50, workspaceBounds.height - 140));

    if (draggingTemplate) {
      const template = draggingTemplate;
      setDraggingTemplate(null);
      await createItemFromPalette(template, x, y);
      return;
    }

    if (!draggingNodeId) return;
    setWorkspaceNodes((prev) =>
      prev.map((node) => node.id === draggingNodeId ? ({ ...node, x, y }) : node)
    );
    setDraggingNodeId(null);
  };

  const removeWorkspaceLink = (linkId) => {
    setWorkspaceLinks((prev) => prev.filter((link) => link.id !== linkId));
  };

  const autoLayoutWorkspace = () => {
    const arranged = buildWorkspaceNodes(items);
    setWorkspaceNodes(arranged);
    setWorkspaceLinks((prev) => (prev.length ? prev : buildDefaultLinks(arranged)));
    toast('Auto layout applied', 'success');
  };

  const startLinkFrom = (nodeId) => {
    if (!connectFromId) {
      setConnectFromId(nodeId);
      toast('Select another node to create a connection', 'success');
      return;
    }

    if (connectFromId === nodeId) {
      setConnectFromId(null);
      return;
    }

    const exists = workspaceLinks.some(
      (link) =>
        (link.from === connectFromId && link.to === nodeId) ||
        (link.from === nodeId && link.to === connectFromId)
    );

    if (exists) {
      setConnectFromId(null);
      toast('Connection already exists', 'error');
      return;
    }

    setWorkspaceLinks((prev) => [
      ...prev,
      {
        id: `${connectFromId}-${nodeId}-${Date.now()}`,
        from: connectFromId,
        to: nodeId,
        label: 'linked',
      },
    ]);
    setConnectFromId(null);
  };

  if (selected) {
    return (
      <div className="space-y-6">
        <Modal />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => {
                setSelected(null);
                setWorkspaceNodes([]);
                setWorkspaceLinks([]);
                setItemFilter('all');
                setWorkspaceView('board');
                setSelectedNodeId(null);
                setConnectFromId(null);
                loadInvestigations();
              }}
              className="text-[#666] hover:text-white text-[14px]"
            >
              &larr; Back
            </button>

            <div>
              <h1 className="text-[24px] font-bold text-white">{selected.title}</h1>
              <p className="text-[12px] text-[#555] mt-1">
                {parsedDetailMap.type || 'general case'} · {parsedDetailMap.classification || 'internal'} · last update {humanTime(getLastActivity(selected))}
              </p>
            </div>

            <B v={priorityBadge(selected.priority)}>{selected.priority}</B>
            <B v={statusBadge(parsedDetailMap.status || getCaseStatus(selected))}>
              {parsedDetailMap.status || getCaseStatus(selected)}
            </B>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Btn v={workspaceView === 'board' ? 'primary' : 'secondary'} onClick={() => setWorkspaceView('board')}>Board</Btn>
            <Btn v={workspaceView === 'map' ? 'primary' : 'secondary'} onClick={() => setWorkspaceView('map')}>Link Map</Btn>
            <Btn v="secondary" onClick={autoLayoutWorkspace}>Auto Layout</Btn>
            <Btn v={wsFullscreen?'primary':'secondary'} onClick={()=>setWsFullscreen(v=>!v)}>
              {wsFullscreen?<Minimize2 size={14}/>:<Maximize2 size={14}/>}
            </Btn>
            <Btn
              v="secondary"
              onClick={() => {
                const state = loadWorkspaceState(selected.id, items);
                setWorkspaceNodes(state.nodes);
                setWorkspaceLinks(state.links);
                setSelectedNodeId(null);
                setConnectFromId(null);
                toast('Workspace reset', 'success');
              }}
            >
              Reset
            </Btn>
            <Btn
              v="danger"
              onClick={async () => {
                if (!(await ask('Delete this investigation?'))) return;
                try {
                  await api.delete(`/api/v1/investigations/${selected.id}`);
                  localStorage.removeItem(getWorkspaceStorageKey(selected.id));
                  toast('Investigation deleted', 'success');
                  setSelected(null);
                  setWorkspaceNodes([]);
                  setWorkspaceLinks([]);
                  loadInvestigations();
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Delete
            </Btn>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <SC l="Items" v={items.length} />
          <SC l="Evidence" v={evidenceCount} />
          <SC l="Findings" v={findingsCount} />
          <SC l="Targets" v={targetsCount} />
          <SC l="Notes" v={notesCount} />
          <SC l="Connections" v={workspaceLinks.length} />
        </div>

        {/* Fullscreen Workspace Overlay */}
        <div className={wsFullscreen
          ? "fixed inset-0 z-[500] bg-black flex flex-col"
          : "grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)_320px] gap-6 items-start"
        }>
          {/* Fullscreen toolbar */}
          {wsFullscreen && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#1a1a1a] bg-[#050505] flex-shrink-0">
              <div className="flex items-center gap-3">
                <button onClick={()=>setLeftPanelOpen(v=>!v)} className="flex items-center gap-1.5 px-3 py-1.5 border border-[#222] rounded-lg text-[11px] text-[#888] hover:text-white transition">
                  {leftPanelOpen?<PanelLeftClose size={14}/>:<PanelLeftOpen size={14}/>}
                  <span>{leftPanelOpen?'Hide':'Show'} Left</span>
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={()=>setWsZoom(z=>Math.max(0.3,+(z-0.1).toFixed(1)))} className="w-7 h-7 rounded border border-[#222] flex items-center justify-center text-[#888] hover:text-white"><ZoomOut size={13}/></button>
                  <span className="text-[11px] text-[#666] w-10 text-center">{Math.round(wsZoom*100)}%</span>
                  <button onClick={()=>setWsZoom(z=>Math.min(2,+(z+0.1).toFixed(1)))} className="w-7 h-7 rounded border border-[#222] flex items-center justify-center text-[#888] hover:text-white"><ZoomIn size={13}/></button>
                  <button onClick={()=>setWsZoom(1)} className="px-2 h-7 rounded border border-[#222] text-[10px] text-[#666] hover:text-white ml-1">Reset</button>
                </div>
                <span className="text-[12px] text-white font-medium">{selected.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>setRightPanelOpen(v=>!v)} className="flex items-center gap-1.5 px-3 py-1.5 border border-[#222] rounded-lg text-[11px] text-[#888] hover:text-white transition">
                  <span>{rightPanelOpen?'Hide':'Show'} Right</span>
                  {rightPanelOpen?<PanelRightClose size={14}/>:<PanelRightOpen size={14}/>}
                </button>
                <button onClick={()=>setWsFullscreen(false)} className="w-8 h-8 rounded-lg border border-[#333] flex items-center justify-center text-[#888] hover:text-white">
                  <Minimize2 size={14}/>
                </button>
              </div>
            </div>
          )}
          {/* Main content area in fullscreen */}
          <div className={wsFullscreen ? "flex flex-1 overflow-hidden" : "contents"}>
          <div className={wsFullscreen
            ? (leftPanelOpen ? "w-72 flex-shrink-0 overflow-y-auto border-r border-[#1a1a1a] bg-[#050505] p-4 space-y-4" : "hidden")
            : "space-y-6"
          }>
            <Panel title="Case Intake Summary" sub="Structured metadata and scope">
              <div className="space-y-3">
                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Overview</p>
                  <p className="text-[13px] text-[#bbb] leading-[1.85]">
                    {detailsLines[0] && !detailsLines[0].includes(':') ? detailsLines[0] : 'No overview provided.'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Type</p>
                    <p className="text-[13px] text-white break-words">{parsedDetailMap.type || '—'}</p>
                  </div>
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Analyst</p>
                    <p className="text-[13px] text-white break-words">{parsedDetailMap.analyst || 'unassigned'}</p>
                  </div>
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Target</p>
                    <p className="text-[13px] text-white break-words">{parsedDetailMap.target || '—'}</p>
                  </div>
                  <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Tags</p>
                    <p className="text-[13px] text-white break-words">{parsedDetailMap.tags || 'none'}</p>
                  </div>
                </div>
              </div>
            </Panel>

            <Panel title="Intake Palette" sub="Drag cards into the workspace">
              <div className="space-y-3">
                {paletteItems.map((item) => (
                  <div
                    key={item.type}
                    draggable
                    onDragStart={() => setDraggingTemplate(item.type)}
                    className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505] cursor-grab active:cursor-grabbing hover:border-[#333] transition"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-[13px] text-white font-medium">{item.label}</p>
                      <B v={itemBadge(item.type)}>{item.type}</B>
                    </div>
                    <p className="text-[12px] text-[#666] leading-[1.7]">{item.hint}</p>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Manual Item Form" sub="Create a node with your own title and content">
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  {laneOrder.map((type) => (
                    <button
                      key={type}
                      onClick={() => setNewItem({ ...newItem, item_type: type })}
                      className={`px-3 py-1.5 rounded-xl text-[11px] border transition ${
                        newItem.item_type === type ? 'border-white text-white bg-[#111]' : 'border-[#222] text-[#666] hover:text-white'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                <Inp
                  label="Node Title"
                  placeholder="Telegram handle, IP, evidence file, analyst note..."
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                />

                <textarea
                  className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[12px] h-28 resize-none outline-none"
                  value={newItem.content}
                  onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                  placeholder="Context, metadata, observations, source, or analyst note..."
                />

                <Btn onClick={addItem} className="w-full">Add Node</Btn>
              </div>
            </Panel>
          </div>

          <Panel title="Investigation Workspace" sub="Drag, link, and structure the case visually."
            extra={
              <div className="flex items-center gap-2">
                <button onClick={()=>setWsZoom(z=>Math.max(0.3,+(z-0.1).toFixed(1)))} title="Zoom out" className="w-7 h-7 rounded border border-[#222] flex items-center justify-center text-[#888] hover:text-white transition"><ZoomOut size={12}/></button>
                <span className="text-[11px] text-[#555] w-9 text-center">{Math.round(wsZoom*100)}%</span>
                <button onClick={()=>setWsZoom(z=>Math.min(2.5,+(z+0.1).toFixed(1)))} title="Zoom in" className="w-7 h-7 rounded border border-[#222] flex items-center justify-center text-[#888] hover:text-white transition"><ZoomIn size={12}/></button>
                <button onClick={()=>setWsZoom(1)} title="Reset zoom" className="px-2 h-7 rounded border border-[#222] text-[10px] text-[#555] hover:text-white transition">Reset</button>
              </div>
            }>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Nodes</p>
                  <p className="text-[18px] text-white font-semibold">{visibleNodes.length}</p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Connections</p>
                  <p className="text-[18px] text-white font-semibold">{visibleLinks.length}</p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Drag Mode</p>
                  <p className="text-[18px] text-white font-semibold">{draggingNodeId || draggingTemplate ? 'Active' : 'Ready'}</p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Filter</p>
                  <p className="text-[18px] text-white font-semibold">{itemFilter}</p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Link Mode</p>
                  <p className="text-[18px] text-white font-semibold">{connectFromId ? 'Armed' : 'Off'}</p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-2 flex-wrap">
                  {['all', ...laneOrder].map((type) => (
                    <button
                      key={type}
                      onClick={() => setItemFilter(type)}
                      className={`px-3 py-1.5 rounded-xl text-[11px] border transition ${
                        itemFilter === type ? 'border-white text-white bg-[#111]' : 'border-[#222] text-[#666] hover:text-white'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {connectFromId && (
                  <button
                    onClick={() => setConnectFromId(null)}
                    className="text-[11px] text-amber-400 border border-amber-900 rounded-lg px-3 py-1.5"
                  >
                    Cancel Link Mode
                  </button>
                )}
              </div>

              {workspaceView === 'map' ? (
                <div className="border border-[#1a1a1a] rounded-[24px] bg-black p-5 overflow-auto">
                  <div className="min-w-[720px] h-[620px] relative">
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      {visibleLinks.map((link) => {
                        const from = visibleNodes.find((node) => node.id === link.from);
                        const to = visibleNodes.find((node) => node.id === link.to);
                        if (!from || !to) return null;
                        const x1 = from.x + 76;
                        const y1 = from.y + 54;
                        const x2 = to.x + 76;
                        const y2 = to.y + 54;
                        const midX = (x1 + x2) / 2;
                        return (
                          <path
                            key={link.id}
                            d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
                            stroke="#9ca3af"
                            strokeWidth="1.6"
                            strokeDasharray="6 6"
                            fill="none"
                            opacity="0.9"
                          />
                        );
                      })}
                    </svg>

                    {visibleNodes.map((node) => (
                      <div
                        key={node.id}
                        className="absolute w-[152px] rounded-2xl border border-[#2a2a2a] bg-[#070707] px-4 py-3 shadow-[0_12px_48px_rgba(0,0,0,0.45)] cursor-pointer"
                        style={{ left: node.x, top: node.y }}
                        onClick={() => setSelectedNodeId(node.id)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] text-[#777] uppercase tracking-[0.18em]">{node.item_type}</span>
                          <B v={itemBadge(node.item_type)}>{node.item_type}</B>
                        </div>
                        <p className="text-[13px] text-white font-medium break-words">{node.title}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="relative border border-[#1a1a1a] rounded-[24px] bg-black overflow-auto"
                  style={{ height: '620px' }}
                  onDrop={handleWorkspaceDrop}
                  onDragOver={(event) => event.preventDefault()}
                >
                  <div
                    className="relative"
                    style={{
                      width: workspaceBounds.width,
                      height: workspaceBounds.height,
                      transform: wsFullscreen ? `scale(${wsZoom})` : undefined,
                      transformOrigin: 'top left',
                      backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '32px 32px',
                    }}
                  >
                    <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03),transparent_60%)]" />

                    {laneOrder.map((laneKey) => (
                      <div
                        key={laneKey}
                        className="absolute top-0 bottom-0 border-r border-dashed border-[#171717] pointer-events-none"
                        style={{ left: laneConfig[laneKey].x - 18, width: 176 }}
                      >
                        <div className="sticky top-0 px-12 py-3 text-[11px] uppercase tracking-[0.32em] text-[#4d4d4d] bg-gradient-to-b from-black to-transparent">
                          {laneConfig[laneKey].title}
                        </div>
                      </div>
                    ))}

                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      {visibleLinks.map((link) => {
                        const from = visibleNodes.find((node) => node.id === link.from);
                        const to = visibleNodes.find((node) => node.id === link.to);
                        if (!from || !to) return null;
                        const x1 = from.x + 76;
                        const y1 = from.y + 98;
                        const x2 = to.x + 76;
                        const y2 = to.y + 16;
                        const midY = (y1 + y2) / 2;
                        return (
                          <g key={link.id}>
                            <path
                              d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                              stroke="#9ca3af"
                              strokeWidth="1.8"
                              strokeDasharray="6 6"
                              fill="none"
                              opacity="0.85"
                            />
                            <circle cx={x1} cy={y1} r="4" fill="#737373" />
                            <circle cx={x2} cy={y2} r="4" fill="#737373" />
                          </g>
                        );
                      })}
                    </svg>

                    {visibleNodes.map((node) => {
                      const isSelected = selectedNodeId === node.id;
                      const isArmed = connectFromId === node.id;
                      return (
                        <div
                          key={node.id}
                          draggable
                          onDragStart={() => setDraggingNodeId(node.id)}
                          onClick={() => setSelectedNodeId(node.id)}
                          className={`absolute w-[152px] rounded-2xl border bg-[#060606] shadow-[0_10px_40px_rgba(0,0,0,0.45)] cursor-move select-none transition ${
                            isSelected ? 'border-white' : isArmed ? 'border-amber-500' : 'border-[#262626]'
                          }`}
                          style={{ left: node.x, top: node.y }}
                        >
                          <div className="px-4 py-3 border-b border-[#171717]">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="text-[11px] text-[#666] uppercase tracking-[0.24em]">{node.item_type}</span>
                              <B v={itemBadge(node.item_type)}>{node.item_type}</B>
                            </div>
                            <p className="text-[13px] text-white font-medium break-words">{node.title}</p>
                          </div>

                          <div className="px-4 py-3">
                            <p className="text-[12px] text-[#777] leading-[1.75] break-words line-clamp-3">
                              {node.content || 'No content'}
                            </p>
                          </div>

                          <div className="px-4 py-3 border-t border-[#171717] flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                startLinkFrom(node.id);
                              }}
                              className={`text-[10px] px-2 py-1 rounded-lg border ${
                                connectFromId === node.id
                                  ? 'border-amber-500 text-amber-400'
                                  : 'border-[#2a2a2a] text-[#888] hover:text-white'
                              }`}
                            >
                              {connectFromId === node.id ? 'Pick Node' : 'Link'}
                            </button>
                            <span className="text-[10px] text-[#444]">drag me</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Panel>

          </div>{/* end center workspace */}
          {/* RIGHT panel */}
          <div className={wsFullscreen ? (rightPanelOpen ? "w-80 flex-shrink-0 overflow-y-auto border-l border-[#1a1a1a] bg-[#050505] p-4 space-y-4" : "hidden") : "space-y-6"}>
            <Panel title="Selected Node" sub="Edit the local workspace card and inspect relationships">
              {!selectedNode ? (
                <p className="text-[13px] text-[#666]">Click a node to inspect or edit it.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[18px] font-semibold text-white">{selectedNode.title}</p>
                      <p className="text-[12px] text-[#666] mt-1">{selectedNode.item_type}</p>
                    </div>
                    <B v={itemBadge(selectedNode.item_type)}>{selectedNode.item_type}</B>
                  </div>

                  <Inp
                    label="Node Title"
                    value={selectedNode.title}
                    onChange={(e) => updateSelectedNode({ title: e.target.value })}
                  />

                  <div>
                    <label className="block text-[12px] text-[#888] mb-1">Node Content</label>
                    <textarea
                      className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[12px] h-24 resize-none outline-none"
                      value={selectedNode.content}
                      onChange={(e) => updateSelectedNode({ content: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">X</p>
                      <p className="text-[14px] text-white">{Math.round(selectedNode.x)}</p>
                    </div>
                    <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Y</p>
                      <p className="text-[14px] text-white">{Math.round(selectedNode.y)}</p>
                    </div>
                  </div>

                  <Btn
                    v="danger"
                    className="w-full"
                    onClick={async () => {
                      if (!(await ask('Remove this node from the investigation?'))) return;
                      try {
                        await api.delete(`/api/v1/investigations/${selected.id}/items/${selectedNode.id}`);
                        setWorkspaceNodes((prev) => prev.filter((node) => node.id !== selectedNode.id));
                        setWorkspaceLinks((prev) => prev.filter((link) => link.from !== selectedNode.id && link.to !== selectedNode.id));
                        setSelectedNodeId(null);
                        toast('Node removed', 'success');
                        await refreshSelected();
                      } catch (e) {
                        toast(E(e), 'error');
                      }
                    }}
                  >
                    Delete Node
                  </Btn>
                </div>
              )}
            </Panel>

            <Panel title="Connections" sub={`${visibleLinks.length} visible links`}>
              {visibleLinks.length === 0 ? (
                <p className="text-[13px] text-[#666]">No connections yet.</p>
              ) : (
                <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                  {visibleLinks.map((link) => {
                    const from = workspaceNodes.find((node) => node.id === link.from);
                    const to = workspaceNodes.find((node) => node.id === link.to);
                    return (
                      <div key={link.id} className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="min-w-0">
                            <p className="text-[12px] text-white truncate">{from?.title || link.from}</p>
                            <p className="text-[10px] text-[#555] mt-1">→ {to?.title || link.to}</p>
                          </div>
                          <button
                            onClick={() => removeWorkspaceLink(link.id)}
                            className="text-[10px] text-red-400"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <B v={itemBadge(from?.item_type)}>{from?.item_type || 'node'}</B>
                          <B v={itemBadge(to?.item_type)}>{to?.item_type || 'node'}</B>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            <Panel title="Timeline Items" sub={`${filteredItems.length} visible records`}>
              <div className="flex gap-2 flex-wrap mb-4">
                {['all', ...laneOrder].map((type) => (
                  <button
                    key={type}
                    onClick={() => setItemFilter(type)}
                    className={`px-3 py-1.5 rounded-xl text-[11px] border transition ${
                      itemFilter === type ? 'border-white text-white bg-[#111]' : 'border-[#222] text-[#666] hover:text-white'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              {filteredItems.length === 0 ? (
                <p className="text-[#555] py-8 text-center">No items in this filter</p>
              ) : (
                <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                  {filteredItems.map((item) => (
                    <div key={item.id} className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]">
                      <div className="flex justify-between gap-3 flex-wrap mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <B v={itemBadge(item.item_type)}>{item.item_type}</B>
                          <span className="text-white text-[14px]">{item.title}</span>
                        </div>
                        <span className="text-[10px] text-[#444]">ID {item.id}</span>
                      </div>
                      {item.content?.text && (
                        <p className="text-[12px] text-[#888] leading-[1.8] whitespace-pre-wrap">{item.content.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Modal />

      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Investigation Workspace
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Cases, intake,
              <br />
              and structured operational tracking.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[780px] leading-[1.85]">
              Create richer investigations, filter the registry, and move from intake to a visual workspace without leaving the cases surface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <B v="blue">{openCount} open</B>
            <B v="warning">{activeCount} active</B>
            <B v="danger">{criticalQueue.length} critical</B>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SC l="Total Cases" v={investigations.length} />
        <SC l="Open" v={openCount} />
        <SC l="Active" v={activeCount} />
        <SC l="Critical" v={criticalQueue.length} />
        <SC l="All Items" v={totalItemsAcrossCases} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-6">
        <div className="space-y-6">
          <Panel title="New Investigation Intake" sub="Capture more context than a basic title + description form">
            <div className="space-y-4">
              <Inp
                label="Title"
                value={newCase.title}
                onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                placeholder="Credential harvesting cluster"
              />

              <div>
                <label className="block text-[12px] text-[#888] mb-1">Overview</label>
                <textarea
                  className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-xl text-[12px] h-28 resize-none outline-none"
                  value={newCase.description}
                  onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                  placeholder="Describe the case scope, objective, context, or observed activity..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Priority</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={newCase.priority}
                    onChange={(e) => setNewCase({ ...newCase, priority: e.target.value })}
                  >
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                    <option value="critical">critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Status</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={newCase.status}
                    onChange={(e) => setNewCase({ ...newCase, status: e.target.value })}
                  >
                    <option value="open">open</option>
                    <option value="active">active</option>
                    <option value="monitoring">monitoring</option>
                    <option value="review">review</option>
                    <option value="closed">closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Case Type</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={newCase.case_type}
                    onChange={(e) => setNewCase({ ...newCase, case_type: e.target.value })}
                  >
                    <option value="identity">identity</option>
                    <option value="fraud">fraud</option>
                    <option value="phishing">phishing</option>
                    <option value="infrastructure">infrastructure</option>
                    <option value="behavioral">behavioral</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Target Type</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={newCase.target_type}
                    onChange={(e) => setNewCase({ ...newCase, target_type: e.target.value })}
                  >
                    <option value="username">username</option>
                    <option value="email">email</option>
                    <option value="domain">domain</option>
                    <option value="ip">ip</option>
                    <option value="phone">phone</option>
                    <option value="device">device</option>
                  </select>
                </div>
              </div>

              <Inp
                label="Target Value"
                value={newCase.target_value}
                onChange={(e) => setNewCase({ ...newCase, target_value: e.target.value })}
                placeholder="@handle / domain / IP / email"
              />

              <div className="grid grid-cols-2 gap-3">
                <Inp
                  label="Assigned Analyst"
                  value={newCase.analyst}
                  onChange={(e) => setNewCase({ ...newCase, analyst: e.target.value })}
                  placeholder="saleh"
                />
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Classification</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={newCase.classification}
                    onChange={(e) => setNewCase({ ...newCase, classification: e.target.value })}
                  >
                    <option value="internal">internal</option>
                    <option value="restricted">restricted</option>
                    <option value="confidential">confidential</option>
                  </select>
                </div>
              </div>

              <Inp
                label="Tags"
                value={newCase.tags}
                onChange={(e) => setNewCase({ ...newCase, tags: e.target.value })}
                placeholder="fraud, telegram, osint"
              />

              <Btn onClick={createCase} className="w-full">Create Investigation</Btn>
            </div>
          </Panel>

          <Panel title="Critical Queue" sub="Highest-priority investigations needing attention">
            {criticalQueue.length === 0 ? (
              <p className="text-[13px] text-[#666]">No critical investigations</p>
            ) : (
              <div className="space-y-3">
                {criticalQueue.slice(0, 4).map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => loadDetail(inv.id)}
                    className="w-full text-left border border-[#1a1a1a] hover:border-[#333] rounded-2xl p-4 bg-black transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white text-[14px] font-medium truncate">{inv.title}</p>
                        <p className="text-[11px] text-[#555] mt-1">{humanTime(getLastActivity(inv))}</p>
                      </div>
                      <B v="danger">critical</B>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel
            title="Investigations Registry"
            sub={`${filteredInvestigations.length} visible cases`}
            extra={
              <div className="flex gap-2 flex-wrap">
                <input
                  className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-[12px] text-white rounded-lg outline-none w-40"
                  placeholder="Search cases..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-[12px] text-white rounded-lg outline-none"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="all">All priorities</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="critical">critical</option>
                </select>
                <select
                  className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-[12px] text-white rounded-lg outline-none"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="updated">Latest activity</option>
                  <option value="priority">Priority</option>
                  <option value="title">Title</option>
                </select>
              </div>
            }
          >
            {filteredInvestigations.length === 0 ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                <p className="text-[#444]">No investigations found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvestigations.map((inv) => {
                  const lines = String(inv.description || '').split('\n');
                  const summary = lines[0] || 'No description';
                  const status = getCaseStatus(inv);
                  const itemCount = safeCount(inv.items_count ?? inv.items);

                  return (
                    <button
                      key={inv.id}
                      onClick={() => loadDetail(inv.id)}
                      className="w-full text-left border border-[#1a1a1a] hover:border-[#333] rounded-2xl p-5 bg-[#050505] transition"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="text-white text-[15px] font-medium">{inv.title}</span>
                            <B v={priorityBadge(inv.priority)}>{inv.priority}</B>
                            <B v={statusBadge(status)}>{status}</B>
                          </div>
                          <p className="text-[12px] text-[#666] leading-[1.75] line-clamp-2">
                            {summary}
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-2 min-w-[180px]">
                          <div className="border border-[#1a1a1a] rounded-lg p-3 bg-black">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Items</p>
                            <p className="text-[16px] text-white font-semibold">{itemCount}</p>
                          </div>
                          <div className="border border-[#1a1a1a] rounded-lg p-3 bg-black">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[#555] mb-1">Updated</p>
                            <p className="text-[11px] text-white">{humanTime(getLastActivity(inv))}</p>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}


function OsintPage() {
  const toast = useToast();

  const [queryText, setQueryText] = useState('');
  const [queryType, setQueryType] = useState('username');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [historyQuery, setHistoryQuery] = useState('');
  const [resultView, setResultView] = useState('cards');

  const searchTypes = [
    { id: 'username', label: 'Username', hint: '@handle / alias', example: 'm911x0' },
    { id: 'email', label: 'Email', hint: 'address intelligence', example: 'name@example.com' },
    { id: 'phone', label: 'Phone', hint: 'number lookup', example: '+9627...' },
    { id: 'ip', label: 'IP', hint: 'infrastructure pivot', example: '8.8.8.8' },
    { id: 'domain', label: 'Domain', hint: 'host / root domain', example: 'example.com' },
  ];

  const presets = {
    username: ['m911x0', 'john_doe', 'admin', 'support'],
    email: ['hello@example.com', 'contact@example.org', 'ops@example.net'],
    phone: ['+962790000000', '+12025550123', '+447700900123'],
    ip: ['8.8.8.8', '1.1.1.1', '185.199.108.153'],
    domain: ['example.com', 'github.com', 'cloudflare.com'],
  };

  const loadHistory = async () => {
    try {
      const r = await api.get('/api/v1/osint/history');
      setHistory(r.data || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const runSearch = async (e, override) => {
    if (e?.preventDefault) e.preventDefault();
    const finalQuery = String(override?.queryText ?? queryText).trim();
    const finalType = override?.queryType || queryType;

    if (!finalQuery) {
      toast('Query is required', 'error');
      return;
    }

    setLoading(true);
    setResult(null);
    setSelectedPlatforms([]);
    setPlatformFilter('all');

    try {
      const r = await api.post('/api/v1/osint/search', {
        query_text: finalQuery,
        query_type: finalType,
      });

      setQueryText(finalQuery);
      setQueryType(finalType);
      setResult(r.data);
      await loadHistory();
      toast('OSINT search completed', 'success');
    } catch (e2) {
      toast(E(e2), 'error');
    }

    setLoading(false);
  };

  const platforms = result?.results?.platforms || [];
  const foundPlatforms = platforms.filter((p) => p.found);
  const notFoundPlatforms = platforms.filter((p) => !p.found);
  const totalPlatforms = platforms.length;
  const matchCount = result?.summary?.matches || foundPlatforms.length;
  const sourceCount = result?.summary?.sources || totalPlatforms;
  const historyFiltered = history.filter((item) => {
    const q = historyQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      String(item.query_text || '').toLowerCase().includes(q) ||
      String(item.query_type || '').toLowerCase().includes(q)
    );
  });

  const visiblePlatforms = platforms.filter((platform) => {
    if (platformFilter === 'found') return !!platform.found;
    if (platformFilter === 'not_found') return !platform.found;
    if (platformFilter === 'selected') return selectedPlatforms.includes(platform.platform);
    return true;
  });

  const groupedPlatforms = visiblePlatforms.reduce((acc, platform) => {
    const group = platform.found ? 'Found' : 'Not Found';
    if (!acc[group]) acc[group] = [];
    acc[group].push(platform);
    return acc;
  }, {});

  const selectedDataPayload = foundPlatforms
    .filter((platform) => selectedPlatforms.includes(platform.platform))
    .map((platform) => ({
      platform: platform.platform,
      url: platform.url || '',
      found: platform.found,
      metadata: platform,
    }));

  const linkSelectedToProfile = async (profileId) => {
    if (!profileId || selectedPlatforms.length === 0) return;

    try {
      await api.post(`/api/v1/profiles/${profileId}/link-osint`, {
        title: `OSINT: ${queryText}`,
        data: {
          query_text: queryText,
          query_type: queryType,
          selected: selectedDataPayload,
          summary: result?.summary || {},
        },
      });
      toast('Selected OSINT results linked', 'success');
      setSelectedPlatforms([]);
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const exportResult = () => {
    if (!result) return;

    const blob = new Blob([JSON.stringify(result, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zurex92_osint_${queryType}_${String(queryText || 'query').replace(/[^a-z0-9-_]+/gi, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const rerunHistoryItem = (item) => {
    if (!item?.query_text || !item?.query_type) return;
    runSearch(null, {
      queryText: item.query_text,
      queryType: item.query_type,
    });
  };

  const togglePlatform = (platformName) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformName)
        ? prev.filter((x) => x !== platformName)
        : [...prev, platformName]
    );
  };

  const allFoundNames = foundPlatforms.map((p) => p.platform);
  const intelligenceTimeline = [
    result && {
      label: 'Search executed',
      value: `${queryType} · ${queryText}`,
      time: new Date().toLocaleTimeString(),
    },
    result && {
      label: 'Sources scanned',
      value: `${sourceCount} sources`,
      time: `${matchCount} matches`,
    },
    selectedPlatforms.length > 0 && {
      label: 'Selection prepared',
      value: `${selectedPlatforms.length} platform(s) selected`,
      time: 'Ready to link',
    },
    history[0] && {
      label: 'Last stored history record',
      value: `${history[0].query_type} · ${history[0].query_text}`,
      time: history[0].created_at ? new Date(history[0].created_at).toLocaleString() : '—',
    },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col 2xl:flex-row 2xl:items-end 2xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              OSINT Intelligence
            </p>
            <h1 className="text-[30px] md:text-[42px] font-semibold tracking-tight text-white leading-[1.02]">
              Open-source intelligence
              <br />
              operational workspace.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[860px] leading-[1.85]">
              Run structured OSINT lookups across usernames, emails, phones, IPs, and domains,
              review platform matches, filter findings, export results, and link selected intelligence
              directly into profile records from one improved workspace.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 min-w-[320px]">
            <SC l="History" v={history.length} />
            <SC l="Matches" v={matchCount} />
            <SC l="Found" v={foundPlatforms.length} />
            <SC l="Selected" v={selectedPlatforms.length} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <SC l="Query Type" v={queryType} />
        <SC l="Sources" v={sourceCount} />
        <SC l="Found Platforms" v={foundPlatforms.length} />
        <SC l="Not Found" v={notFoundPlatforms.length} />
        <SC l="Result View" v={resultView} />
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[1.15fr_0.85fr] gap-6">
        <div className="space-y-6">
          <Panel
            title="OSINT Search Console"
            sub="Run a query, swap type, and launch presets quickly"
            extra={
              <div className="flex gap-2 flex-wrap">
                <Btn v="secondary" onClick={() => { setResult(null); setSelectedPlatforms([]); }}>
                  Clear Results
                </Btn>
                <Btn onClick={runSearch} disabled={loading}>
                  {loading ? 'Searching...' : 'Run Search'}
                </Btn>
              </div>
            }
          >
            <div className="space-y-5">
              <div className="flex gap-2 flex-wrap">
                {searchTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => {
                      setQueryType(type.id);
                      setQueryText('');
                    }}
                    className={`px-3 py-2 rounded-xl text-[12px] border transition ${
                      queryType === type.id
                        ? 'border-white text-white bg-[#111]'
                        : 'border-[#222] text-[#666] hover:text-white'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              <form onSubmit={runSearch} className="space-y-4">
                <Inp
                  label="Query"
                  placeholder={`Search by ${queryType}...`}
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                />

                <div className="flex flex-wrap gap-2">
                  {(presets[queryType] || []).map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setQueryText(preset)}
                      className="px-3 py-1.5 rounded-full border border-[#222] bg-black text-[11px] text-[#777] hover:text-white hover:border-[#333]"
                    >
                      {preset}
                    </button>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border border-[#1a1a1a] rounded-2xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                      Active Mode
                    </p>
                    <p className="text-[14px] text-white">{searchTypes.find((x) => x.id === queryType)?.label}</p>
                    <p className="text-[12px] text-[#666] mt-2">
                      {searchTypes.find((x) => x.id === queryType)?.hint}
                    </p>
                  </div>

                  <div className="border border-[#1a1a1a] rounded-2xl p-4 bg-black">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                      Example
                    </p>
                    <p className="text-[14px] text-white break-all">
                      {searchTypes.find((x) => x.id === queryType)?.example}
                    </p>
                    <p className="text-[12px] text-[#666] mt-2">
                      Use realistic values to improve source correlation.
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </Panel>

          <Panel
            title="Result Workspace"
            sub="Review detected platforms and manage result selection"
            extra={
              result ? (
                <div className="flex gap-2 flex-wrap">
                  <Tabs
                    tabs={[
                      { id: 'cards', l: 'Cards' },
                      { id: 'grouped', l: 'Grouped' },
                    ]}
                    active={resultView}
                    onChange={setResultView}
                  />
                  <Btn v="secondary" onClick={exportResult}>Export JSON</Btn>
                </div>
              ) : null
            }
          >
            {!result ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-20 text-center">
                <p className="text-[#444]">Run an OSINT query to populate this workspace</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <B v="blue">{sourceCount} sources</B>
                  <B v="success">{matchCount} matches</B>
                  <button
                    type="button"
                    onClick={() => setPlatformFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-[11px] border ${platformFilter === 'all' ? 'border-white text-white' : 'border-[#222] text-[#666]'}`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlatformFilter('found')}
                    className={`px-3 py-1.5 rounded-full text-[11px] border ${platformFilter === 'found' ? 'border-white text-white' : 'border-[#222] text-[#666]'}`}
                  >
                    Found
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlatformFilter('not_found')}
                    className={`px-3 py-1.5 rounded-full text-[11px] border ${platformFilter === 'not_found' ? 'border-white text-white' : 'border-[#222] text-[#666]'}`}
                  >
                    Not Found
                  </button>
                  <button
                    type="button"
                    onClick={() => setPlatformFilter('selected')}
                    className={`px-3 py-1.5 rounded-full text-[11px] border ${platformFilter === 'selected' ? 'border-white text-white' : 'border-[#222] text-[#666]'}`}
                  >
                    Selected
                  </button>

                  {foundPlatforms.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedPlatforms(
                          selectedPlatforms.length === allFoundNames.length ? [] : allFoundNames
                        )
                      }
                      className="px-3 py-1.5 rounded-full text-[11px] border border-[#333] text-[#aaa] hover:text-white"
                    >
                      {selectedPlatforms.length === allFoundNames.length ? 'Clear Found Selection' : 'Select All Found'}
                    </button>
                  )}
                </div>

                {resultView === 'cards' ? (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {visiblePlatforms.map((platform, i) => {
                      const isSelected = selectedPlatforms.includes(platform.platform);
                      return (
                        <div
                          key={`${platform.platform}-${i}`}
                          className={`border rounded-2xl p-4 transition ${
                            platform.found ? 'border-emerald-900 bg-emerald-950/20' : 'border-[#1a1a1a] bg-[#050505]'
                          } ${isSelected ? 'ring-1 ring-white' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                {platform.found ? (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => togglePlatform(platform.platform)}
                                    className="accent-white"
                                  />
                                ) : (
                                  <div className="w-3 h-3 rounded-full bg-[#333]" />
                                )}
                                <span className="text-white text-[14px] font-medium">{platform.platform}</span>
                                {platform.found ? <B v="success">Found</B> : <B>Not Found</B>}
                              </div>
                              <p className="text-[12px] text-[#666] leading-[1.75]">
                                {platform.url || 'No direct profile URL returned'}
                              </p>
                            </div>

                            {platform.found && platform.url ? (
                              <a
                                href={platform.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] text-[#888] hover:text-white flex-shrink-0"
                              >
                                Open →
                              </a>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(groupedPlatforms).map(([group, rows]) => (
                      <div key={group} className="border border-[#1a1a1a] rounded-2xl overflow-hidden bg-[#050505]">
                        <div className="px-4 py-3 border-b border-[#111] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <B v={group === 'Found' ? 'success' : 'default'}>{group}</B>
                            <span className="text-[12px] text-[#666]">{rows.length} platform(s)</span>
                          </div>
                        </div>
                        <div className="divide-y divide-[#111]">
                          {rows.map((platform, i) => {
                            const isSelected = selectedPlatforms.includes(platform.platform);
                            return (
                              <div key={`${group}-${platform.platform}-${i}`} className="px-4 py-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  {platform.found ? (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => togglePlatform(platform.platform)}
                                      className="accent-white"
                                    />
                                  ) : (
                                    <div className="w-3 h-3 rounded-full bg-[#333]" />
                                  )}
                                  <span className="text-[13px] text-white truncate">{platform.platform}</span>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {platform.url && (
                                    <a
                                      href={platform.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[11px] text-[#888] hover:text-white"
                                    >
                                      Open →
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {visiblePlatforms.length === 0 && (
                  <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                    <p className="text-[#444]">No platforms match the active filter</p>
                  </div>
                )}
              </div>
            )}
          </Panel>

          <Panel title="Search History" sub="Recent OSINT lookups with one-click rerun">
            <div className="space-y-4">
              <Inp
                label="History Search"
                placeholder="Filter history..."
                value={historyQuery}
                onChange={(e) => setHistoryQuery(e.target.value)}
              />

              {historyFiltered.length === 0 ? (
                <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                  <p className="text-[#444]">No history records found</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                  {historyFiltered.map((item) => (
                    <div
                      key={item.id}
                      className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505] flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <B>{item.query_type}</B>
                          <span className="text-white text-[13px] break-all">{item.query_text}</span>
                        </div>
                        <p className="text-[11px] text-[#555]">
                          {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                        </p>
                      </div>

                      <Btn v="secondary" className="px-3 py-1.5" onClick={() => rerunHistoryItem(item)}>
                        Rerun
                      </Btn>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Current Query" sub="Active OSINT search context">
            <div className="space-y-3 text-[13px]">
              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Type</span>
                <span className="text-white">{queryType}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Query</span>
                <span className="text-white break-all text-right">{queryText || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Visible Platforms</span>
                <span className="text-white">{visiblePlatforms.length}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-[#666]">Selected Platforms</span>
                <span className="text-white">{selectedPlatforms.length}</span>
              </div>
            </div>
          </Panel>

          <Panel title="Link Selected Findings" sub="Attach chosen OSINT findings to a profile">
            {selectedPlatforms.length === 0 ? (
              <p className="text-[12px] text-[#555] leading-[1.8]">
                Select one or more found platforms first, then choose a profile to attach this intelligence package.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {selectedPlatforms.map((p) => (
                    <B key={p} v="blue">{p}</B>
                  ))}
                </div>

                <PSearch onSelect={linkSelectedToProfile} />
              </div>
            )}
          </Panel>

          <Panel title="Intelligence Timeline" sub="Fast timeline for the current OSINT session">
            <div className="space-y-3">
              {intelligenceTimeline.length === 0 ? (
                <p className="text-[12px] text-[#555]">Run a query to populate the session timeline.</p>
              ) : (
                intelligenceTimeline.map((entry, i) => (
                  <div key={`${entry.label}-${i}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-white mt-1"/>
                      {i < intelligenceTimeline.length - 1 && <div className="w-px flex-1 bg-[#222] mt-2" />}
                    </div>
                    <div className="pb-4">
                      <p className="text-[13px] text-white">{entry.label}</p>
                      <p className="text-[12px] text-[#777] mt-1">{entry.value}</p>
                      <p className="text-[11px] text-[#555] mt-1">{entry.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Panel>

          <Panel title="Operator Notes" sub="Best practices for OSINT execution">
            <div className="space-y-3 text-[13px] text-[#777] leading-[1.8]">
              <p><span className="text-white">Use usernames first</span> for broad profile discovery and external platform correlation.</p>
              <p><span className="text-white">Use domain or IP queries</span> when pivoting into infrastructure-linked intelligence.</p>
              <p><span className="text-white">Link selected results</span> into profiles to preserve context and keep investigations structured.</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
function SocialPage(){return<div className="flex items-center justify-center h-64"><h2 className="text-[24px] font-bold text-[#222]">Coming Soon</h2></div>;}
function NotifsPage() {
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = async () => {
    try {
      const r = await api.get('/api/v1/notifications');
      setNotifications(r.data || []);
    } catch {}
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="space-y-6">
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Notification Center
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Operational alerts
              <br />
              and platform updates.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Review unread alerts, system events, and activity updates from one
              focused notification surface.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SC l="Total" v={notifications.length} />
        <SC l="Unread" v={unreadCount} />
        <SC l="Read" v={notifications.length - unreadCount} />
      </div>

      <Panel
        title="Notifications"
        sub={`${notifications.length} total records`}
        extra={
          unreadCount > 0 ? (
            <Btn
              v="secondary"
              onClick={async () => {
                try {
                  await Promise.all(
                    notifications
                      .filter((n) => !n.is_read)
                      .map((n) =>
                        api.put(`/api/v1/notifications/${n.id}/read`).catch(() => {})
                      )
                  );

                  setNotifications((prev) =>
                    prev.map((n) => ({ ...n, is_read: true }))
                  );
                } catch {}
              }}
            >
              Mark all read
            </Btn>
          ) : null
        }
      >
        {notifications.length === 0 ? (
          <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
            <p className="text-[#444]">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={async () => {
                  if (!n.is_read) {
                    await api.put(`/api/v1/notifications/${n.id}/read`).catch(() => {});
                    setNotifications((prev) =>
                      prev.map((x) =>
                        x.id === n.id ? { ...x, is_read: true } : x
                      )
                    );
                  }
                }}
                className={`border rounded-2xl p-4 cursor-pointer transition ${
                  n.is_read
                    ? 'border-[#1a1a1a] bg-[#050505] opacity-70'
                    : 'border-[#2a2a2a] bg-black'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!n.is_read ? <B v="blue">New</B> : <B>Read</B>}
                    <span className="text-white text-[14px] font-medium">
                      {n.title}
                    </span>
                  </div>

                  <span className="text-[10px] text-[#444]">
                    {n.created_at
                      ? new Date(n.created_at).toLocaleString()
                      : '—'}
                  </span>
                </div>

                <p className="text-[12px] text-[#888] leading-[1.8]">
                  {n.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
// ── Canary shared input components — OUTSIDE CanaryPage to prevent re-mount ──
function CanaryInput({label, value, onChange, placeholder='', type='text', min, max}) {
  return (
    <div>
      {label && <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">{label}</label>}
      <input type={type} min={min} max={max}
        className="w-full bg-black border border-[#1e1e1e] px-4 py-2.5 text-white rounded-xl text-[13px] outline-none focus:border-[#333] placeholder:text-[#2a2a2a]"
        value={value} onChange={onChange} placeholder={placeholder}/>
    </div>
  );
}
function CanaryTextarea({label, value, onChange, placeholder='', rows=4}) {
  return (
    <div>
      {label && <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">{label}</label>}
      <textarea rows={rows}
        className="w-full bg-black border border-[#1e1e1e] px-4 py-3 text-white text-[13px] rounded-xl resize-none outline-none focus:border-[#333] placeholder:text-[#2a2a2a]"
        value={value} onChange={onChange} placeholder={placeholder}/>
    </div>
  );
}
function CanarySection({title, icon, children}) {
  const Icon = icon;
  return (
    <div className="border border-[#1a1a1a] rounded-[20px] bg-[#050505] overflow-hidden">
      <div className="px-5 py-4 border-b border-[#0a0a0a] flex items-center gap-2">
        {Icon && <Icon size={14} className="text-[#555]"/>}
        <p className="text-white font-semibold text-[14px]">{title}</p>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function CanaryPage() {
  const toast = useToast();
  const {ask, Modal} = useModal();
  const [legal, setLegal] = useState(() => !!localStorage.getItem('canary_legal_v1'));
  const [showTut, setShowTut] = useState(false);
  const [tutStep, setTutStep] = useState(0);

  // Token types definition
  const TOKEN_TYPES = [
    {id:'credit_card', label:'Credit Card',    icon:CreditCard, color:'#f59e0b', desc:'Fake credit card image. Fires when target views or uses the card data.'},
    {id:'qr',          label:'QR Code',        icon:ScanLine,   color:'#4ade80', desc:'QR code that fires when scanned. You choose where it redirects.'},
    {id:'image',       label:'Image Tracker',  icon:Flag,       color:'#a78bfa', desc:'Invisible tracking pixel. Fires when image loads in any viewer.'},
    {id:'excel',       label:'Microsoft Excel', icon:FileText,  color:'#22c55e', desc:'Real .xlsx file with hidden beacon. Fires when opened in Excel.'},
    {id:'word',        label:'Microsoft Word',  icon:FileText,  color:'#3b82f6', desc:'Real .docx document with hidden beacon. Fires when opened in Word.'},
    {id:'pdf',         label:'PDF Document',    icon:FileText,  color:'#ef4444', desc:'PDF with embedded action. Fires when opened in any PDF viewer.'},
    {id:'email',       label:'Email Template',  icon:Mail,      color:'#ec4899', desc:'HTML email with tracking pixel. Fires when the email is opened.'},
    {id:'folder',      label:'Windows Folder',  icon:Folder,    color:'#f97316', desc:'Windows folder canary. Fires when target browses the folder.'},
    {id:'zip',         label:'ZIP Archive',     icon:Package,   color:'#94a3b8', desc:'Archive with beacon. Fires when extracted on target machine.'},
  ];

  // Tutorial
  const TUT = [
    {t:'What is a Canary Token?', b:'A silent intelligence beacon. When a target opens your file or image, you instantly receive their IP address, location, ISP, OS, VPN status, and more — completely silently.'},
    {t:'Platform Token', b:'Choose from 9 token types. We generate a real, professional-looking file with the beacon embedded invisibly. Fully customizable — content, colors, data.'},
    {t:'Upload & Inject', b:'Have your own file? Upload it (.xlsx, .docx, .pdf) and we inject the beacon invisibly. The result is identical to your original file.'},
    {t:'Intelligence on Trigger', b:'Every trigger captures: IP, country, city, ISP, ASN, VPN detection, GPS coordinates (IP-based), OS, browser, application, timezone, and more.'},
    {t:'Email Alerts', b:'You receive a professional HTML email alert instantly when any canary fires, with the full intelligence report included.'},
  ];

  // Views: list | create | manage
  const [view, setView]         = useState('list');
  const [tokens, setTokens]     = useState([]);
  const [stats, setStats]       = useState({total:0, active:0, triggers:0});
  const [showArchived, setShowArchived] = useState(false);

  // Create state
  const [createStep, setCreateStep]   = useState('type'); // type | config | done
  const [selType, setSelType]         = useState('credit_card');
  const [tokenName, setTokenName]     = useState('');
  const [tokenDesc, setTokenDesc]     = useState('');
  const [newToken, setNewToken]       = useState(null);
  const [creating, setCreating]       = useState(false);
  const [genLoading, setGenLoading]   = useState(false);
  const [injectFile, setInjectFile]   = useState(null);
  const [injecting, setInjecting]     = useState(false);

  // Per-type params
  const [pCard,  setPCard]  = useState({holder:'', card_number:'', expiry:'', network:'VISA', bg_color:'#1a1a2e'});
  const [pQr,    setPQr]    = useState({redirect_url:'https://google.com', label:'', fg_color:'#111111', bg_color:'#ffffff'});
  const [pImg,   setPImg]   = useState({width:1, height:1, format:'PNG', color:'transparent'});
  const [pXl,    setPXl]    = useState({title:'Confidential Report', sheet_name:'Data', author:'', company:'', col_headers:'ID,Name,Value,Date,Status', n_rows:20, theme_color:'4ade80'});
  const [pWd,    setPWd]    = useState({title:'Internal Memorandum', author:'', company:'', content:'This document contains confidential information.', classified:false});
  const [pPdf,   setPPdf]   = useState({title:'Confidential Report', subtitle:'For Authorized Personnel Only', author:'', company:'', content:'', footer:'CONFIDENTIAL'});
  const [pEmail, setPEmail] = useState({subject:'Important Security Notification', from_name:'IT Security Team', body:'Please review the attached information.'});
  const [pFolder,setPFolder]= useState({folder_name:'Project Files 2024', readme:''});
  const [pZip,   setPZip]   = useState({inner_name:'README.txt', content:'Confidential documents enclosed.'});

  // Manage state
  const [active, setActive]       = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [editName, setEditName]   = useState('');
  const [editDesc, setEditDesc]   = useState('');
  const [injectFile2, setInjectFile2] = useState(null);
  const [injecting2, setInjecting2]   = useState(false);
  const [genLoad2, setGenLoad2]       = useState(false);

  const getParams = () => ({
    credit_card: pCard, qr: {...pQr, beacon_url: newToken?.beacon_url},
    image: pImg, excel: pXl, word: pWd, pdf: pPdf,
    email: pEmail, folder: pFolder, zip: pZip,
  }[selType] || {});

  const load = async () => {
    try {
      const r = await api.get(`/api/v1/canary/tokens${showArchived?'?archived=1':''}`);
      const list = r.data || [];
      setTokens(list);
      setStats({total:list.length, active:list.filter(t=>t.is_active).length, triggers:list.reduce((a,t)=>a+(t.trigger_count||0),0)});
    } catch {}
  };
  useEffect(() => { if(legal) load(); }, [legal, showArchived]);

  const loadManage = async (t) => {
    try {
      const r = await api.get(`/api/v1/canary/tokens/${t.id}`);
      setActive(r.data); setEditName(r.data.name||''); setEditDesc(r.data.description||'');
      setEditMode(false); setInjectFile2(null); setView('manage');
    } catch(e) { toast(E(e),'error'); }
  };

  const startCreate = () => {
    setCreateStep('type'); setSelType('credit_card'); setTokenName(''); setTokenDesc('');
    setNewToken(null); setInjectFile(null); setView('create');
  };

  const createToken = async () => {
    if(!tokenName.trim()) return toast('Token name required','error');
    setCreating(true);
    try {
      const r = await api.post('/api/v1/canary/tokens', {
        name:tokenName, token_type:selType, description:tokenDesc, build_params:getParams()
      });
      setNewToken(r.data); setCreateStep('config');
    } catch(e) { toast(E(e),'error'); }
    setCreating(false);
  };

  const downloadFile = async () => {
    if(!newToken) return;
    setGenLoading(true);
    try {
      const params = getParams();
      const r = await api.post(`/api/v1/canary/tokens/${newToken.id}/generate`, params, {responseType:'blob'});
      const exts = {credit_card:'.png',qr:'.png',image:'.png',excel:'.xlsx',word:'.docx',pdf:'.pdf',email:'.html',folder:'.zip',zip:'.zip'};
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download=`${tokenName.replace(/[^a-z0-9]/gi,'_')||'canary'}${exts[selType]||'.bin'}`; a.click(); URL.revokeObjectURL(url);
      setCreateStep('done'); toast('File downloaded ','success');
    } catch(e) { toast(E(e),'error'); }
    setGenLoading(false);
  };

  const injectUpload = async () => {
    if(!injectFile||!newToken) return toast('Select a file first','error');
    setInjecting(true);
    try {
      const fd = new FormData(); fd.append('file', injectFile);
      const r = await api.post(`/api/v1/canary/tokens/${newToken.id}/inject`, fd, {responseType:'blob', headers:{'Content-Type':'multipart/form-data'}});
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download=`${injectFile.name.replace(/\.[^.]+$/,'')}_canary${injectFile.name.match(/\.[^.]+$/)?.[0]||''}`; a.click(); URL.revokeObjectURL(url);
      setCreateStep('done'); toast('File injected & downloaded ','success');
    } catch(e) { toast(E(e),'error'); }
    setInjecting(false);
  };

  const reDownload = async () => {
    if(!active) return;
    setGenLoad2(true);
    try {
      const bp = typeof active.build_params==='string'?JSON.parse(active.build_params||'{}'):(active.build_params||{});
      const r = await api.post(`/api/v1/canary/tokens/${active.id}/generate`, bp, {responseType:'blob'});
      const exts = {credit_card:'.png',qr:'.png',image:'.png',excel:'.xlsx',word:'.docx',pdf:'.pdf',email:'.html',folder:'.zip',zip:'.zip'};
      const ext = exts[active.token_type]||'.bin';
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download=`${active.name.replace(/[^a-z0-9]/gi,'_')}_canary${ext}`; a.click(); URL.revokeObjectURL(url);
      toast('Downloaded ','success');
    } catch(e) { toast(E(e),'error'); }
    setGenLoad2(false);
  };

  const injectManage = async () => {
    if(!injectFile2||!active) return;
    setInjecting2(true);
    try {
      const fd = new FormData(); fd.append('file', injectFile2);
      const r = await api.post(`/api/v1/canary/tokens/${active.id}/inject`, fd, {responseType:'blob', headers:{'Content-Type':'multipart/form-data'}});
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download=`${injectFile2.name.replace(/\.[^.]+$/,'')}_canary${injectFile2.name.match(/\.[^.]+$/)?.[0]||''}`; a.click(); URL.revokeObjectURL(url);
      toast('Injected & downloaded ','success');
    } catch(e) { toast(E(e),'error'); }
    setInjecting2(false);
  };

  const saveEdit = async () => {
    try { await api.put(`/api/v1/canary/tokens/${active.id}`,{name:editName,description:editDesc}); toast('Saved','success'); setEditMode(false); loadManage(active); } catch(e){toast(E(e),'error');}
  };
  const toggleTok = async (id) => {
    try { const r=await api.put(`/api/v1/canary/tokens/${id}/toggle`); toast(r.data.is_active?'Activated':'Deactivated','success'); await load(); if(active?.id===id)setActive(a=>({...a,is_active:r.data.is_active})); } catch(e){toast(E(e),'error');}
  };
  const archiveTok = async (id) => {
    try { await api.put(`/api/v1/canary/tokens/${id}/archive`); toast('Archived','success'); setView('list'); await load(); } catch(e){toast(E(e),'error');}
  };
  const deleteTok = async (id) => {
    if(!(await ask('Delete this token and all its triggers?'))) return;
    try { await api.delete(`/api/v1/canary/tokens/${id}`); toast('Deleted','success'); setView('list'); await load(); } catch(e){toast(E(e),'error');}
  };
  const exportReport = async () => {
    try {
      const r=await api.get(`/api/v1/canary/tokens/${active.id}/report.html`,{responseType:'blob'});
      const url=URL.createObjectURL(new Blob([r.data],{type:'text/html'}));
      const a=document.createElement('a'); a.href=url; a.download=`canary_report_${active.name.replace(/[^a-z0-9]/gi,'_')}.html`; a.click(); URL.revokeObjectURL(url);
      toast('Report exported','success');
    } catch(e){toast(E(e),'error');}
  };
  const copy = (text, label='Copied') => { try{navigator.clipboard?.writeText(text);}catch{} toast(label,'success'); };

  // ── Per-type customization + preview ────────────────────────────────────────
  const TypeBuilder = () => {
    switch(selType) {
      case 'credit_card': return (
        <CanarySection title="Credit Card Customization" icon={CreditCard}>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Card Holder Name" value={pCard.holder} onChange={e=>setPCard(p=>({...p,holder:e.target.value}))} placeholder="JOHN DOE"/>
            <CanaryInput label="Card Number (leave blank for random)" value={pCard.card_number} onChange={e=>setPCard(p=>({...p,card_number:e.target.value}))} placeholder="4532 xxxx xxxx 7891"/>
            <CanaryInput label="Expiry (leave blank for random)" value={pCard.expiry} onChange={e=>setPCard(p=>({...p,expiry:e.target.value}))} placeholder="12/27"/>
            <div>
              <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Card Network</label>
              <div className="flex gap-2">
                {['VISA','MASTERCARD','AMEX','DISCOVER'].map(n=>(
                  <button key={n} type="button" onClick={()=>setPCard(p=>({...p,network:n}))} className={`px-3 py-1.5 text-[11px] border rounded-lg transition ${pCard.network===n?'border-white text-white':'border-[#222] text-[#555]'}`}>{n}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Card Color</label>
            <div className="flex gap-2">
              {[['#1a1a2e','Navy'],['#0f2027','Dark'],['#1a2e1a','Green'],['#2e1a1a','Crimson'],['#1a1a1a','Black']].map(([c,l])=>(
                <button key={c} type="button" onClick={()=>setPCard(p=>({...p,bg_color:c}))} title={l}
                  className={`w-9 h-9 rounded-lg border-2 transition ${pCard.bg_color===c?'border-white scale-110':'border-transparent opacity-60 hover:opacity-100'}`} style={{background:c}}/>
              ))}
            </div>
          </div>
          {/* Preview */}
          <div className="flex justify-center pt-2">
            <div className="w-72 h-44 rounded-2xl p-5 flex flex-col justify-between relative overflow-hidden" style={{background:`linear-gradient(135deg, ${pCard.bg_color||'#1a1a2e'}, #0a0a14)`, border:'1px solid #333', boxShadow:'0 20px 40px rgba(0,0,0,.5)'}}>
              <div className="flex justify-between items-start">
                <div className="flex gap-1"><div className="w-6 h-1 bg-white/10 rounded-full"/><div className="w-6 h-1 bg-white/10 rounded-full mt-1"/></div>
                <span className="text-[11px] text-white/60 font-semibold tracking-wider">{pCard.network||'VISA'}</span>
              </div>
              <div className="w-8 h-5 rounded bg-amber-500/80"/>
              <div>
                <p className="text-white/80 font-mono text-[13px] tracking-widest mb-1">{pCard.card_number||'4532 •••• •••• 7891'}</p>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-white/40 text-[8px] uppercase">Valid Thru</p>
                    <p className="text-white/70 text-[12px] font-mono">{pCard.expiry||'12/27'}</p>
                  </div>
                  <p className="text-white/80 text-[11px] font-semibold tracking-wider">{(pCard.holder||'CARD HOLDER').toUpperCase()}</p>
                </div>
              </div>
            </div>
          </div>
        </CanarySection>
      );

      case 'qr': return (
        <CanarySection title="QR Code Customization" icon={ScanLine}>
          <CanaryInput label="Redirect URL (where QR leads)" value={pQr.redirect_url} onChange={e=>setPQr(p=>({...p,redirect_url:e.target.value}))} placeholder="https://example.com"/>
          <CanaryInput label="Label Under QR" value={pQr.label} onChange={e=>setPQr(p=>({...p,label:e.target.value}))} placeholder="Scan to verify identity"/>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">QR Color</label>
              <div className="flex gap-2 flex-wrap">
                {[['#111111','Black'],['#1a3a1a','Dark Green'],['#1a1a3a','Dark Blue'],['#3a1a1a','Dark Red']].map(([c,l])=>(
                  <button key={c} type="button" onClick={()=>setPQr(p=>({...p,fg_color:c}))} className={`px-3 py-1.5 text-[11px] border rounded-lg transition flex items-center gap-1.5 ${pQr.fg_color===c?'border-white text-white':'border-[#222] text-[#555]'}`}>
                    <span className="w-3 h-3 rounded-sm" style={{background:c}}/>{l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Background</label>
              <div className="flex gap-2">
                {[['#ffffff','White'],['#f5f5f0','Off-White']].map(([c,l])=>(
                  <button key={c} type="button" onClick={()=>setPQr(p=>({...p,bg_color:c}))} className={`px-3 py-1.5 text-[11px] border rounded-lg transition ${pQr.bg_color===c?'border-white text-white':'border-[#222] text-[#555]'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          {/* QR Preview */}
          <div className="flex justify-center pt-2">
            <div className="p-5 rounded-xl border border-[#1a1a1a] flex flex-col items-center gap-3" style={{background:pQr.bg_color||'#ffffff'}}>
              <svg viewBox="0 0 7 7" width="140" height="140">
                {[[0,0],[1,0],[2,0],[0,1],[2,1],[0,2],[1,2],[2,2],[4,0],[5,0],[6,0],[4,1],[6,1],[4,2],[5,2],[6,2],[0,4],[0,5],[0,6],[1,6],[2,4],[2,6],[3,3],[4,4],[5,5],[6,6],[4,6],[6,4]].map(([x,y],i)=>(
                  <rect key={i} x={x} y={y} width="1" height="1" fill={pQr.fg_color||'#111111'}/>
                ))}
              </svg>
              {pQr.label && <p className="text-[11px]" style={{color:pQr.fg_color||'#111111'}}>{pQr.label}</p>}
              <p className="text-[9px] text-[#999]">Preview — actual QR generated on download</p>
            </div>
          </div>
        </CanarySection>
      );

      case 'image': return (
        <CanarySection title="Image Tracker Customization" icon={Flag}>
          <div className="grid md:grid-cols-3 gap-3">
            <CanaryInput label="Width (px)" type="number" min="1" max="2000" value={pImg.width} onChange={e=>setPImg(p=>({...p,width:parseInt(e.target.value)||1}))}/>
            <CanaryInput label="Height (px)" type="number" min="1" max="2000" value={pImg.height} onChange={e=>setPImg(p=>({...p,height:parseInt(e.target.value)||1}))}/>
            <div>
              <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Format</label>
              <div className="flex gap-2">
                {['PNG','JPEG','GIF'].map(f=>(
                  <button key={f} type="button" onClick={()=>setPImg(p=>({...p,format:f}))} className={`px-3 py-2 rounded-xl border text-[12px] font-mono transition ${pImg.format===f?'border-white text-white':'border-[#222] text-[#555]'}`}>.{f.toLowerCase()}</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Color</label>
            <div className="flex gap-2 flex-wrap">
              {[['transparent','Invisible (recommended)'],['#ffffff','White'],['#000000','Black'],['#ff0000','Red']].map(([c,l])=>(
                <button key={c} type="button" onClick={()=>setPImg(p=>({...p,color:c}))} className={`flex items-center gap-2 px-3 py-1.5 border rounded-xl text-[12px] transition ${pImg.color===c?'border-white text-white':'border-[#222] text-[#555]'}`}>
                  <span className="w-3 h-3 rounded-sm border border-[#333]" style={{background:c==='transparent'?'repeating-conic-gradient(#333 0% 25%,#111 0% 50%) 0 0/8px 8px':c}}/>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="text-center py-3 border border-[#1a1a1a] rounded-xl bg-black">
            <p className="text-[12px] text-[#555]">{pImg.width}×{pImg.height}px · {pImg.format} · {pImg.color==='transparent'?'Invisible':pImg.color}</p>
            <p className="text-[11px] text-[#333] mt-1">Fires when image loads in browser, email client, Word, or any viewer</p>
          </div>
        </CanarySection>
      );

      case 'excel': return (
        <CanarySection title="Excel File Customization" icon={FileText}>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Document Title" value={pXl.title} onChange={e=>setPXl(p=>({...p,title:e.target.value}))} placeholder="Confidential Report Q4"/>
            <CanaryInput label="Sheet Name" value={pXl.sheet_name} onChange={e=>setPXl(p=>({...p,sheet_name:e.target.value}))} placeholder="Sheet1"/>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Author" value={pXl.author} onChange={e=>setPXl(p=>({...p,author:e.target.value}))} placeholder="John Smith"/>
            <CanaryInput label="Company" value={pXl.company} onChange={e=>setPXl(p=>({...p,company:e.target.value}))} placeholder="Acme Corp"/>
          </div>
          <CanaryInput label="Column Headers (comma-separated)" value={pXl.col_headers} onChange={e=>setPXl(p=>({...p,col_headers:e.target.value}))} placeholder="ID,Name,Department,Salary,Date,Status"/>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Data Rows" type="number" min="5" max="200" value={pXl.n_rows} onChange={e=>setPXl(p=>({...p,n_rows:parseInt(e.target.value)||20}))}/>
            <div>
              <label className="block text-[11px] text-[#555] uppercase tracking-wider mb-1.5">Header Color</label>
              <div className="flex gap-2">
                {['4ade80','60a5fa','f59e0b','f87171','a78bfa'].map(c=>(
                  <button key={c} type="button" onClick={()=>setPXl(p=>({...p,theme_color:c}))} className={`w-8 h-8 rounded-lg border-2 transition ${pXl.theme_color===c?'border-white scale-110':'border-transparent opacity-60 hover:opacity-100'}`} style={{background:`#${c}`}}/>
                ))}
              </div>
            </div>
          </div>
          {/* Excel Preview */}
          <div className="border border-[#1a1a1a] rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-[#0a0a0a] border-b border-[#111] text-[11px] text-[#444]">{pXl.title||'Spreadsheet'}.xlsx</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr style={{background:'#141414'}}>
                  {(pXl.col_headers||'ID,Name,Value,Date,Status').split(',').map((c,i)=>(
                    <th key={i} className="px-3 py-2 text-left border border-[#1a1a1a] font-semibold" style={{color:`#${pXl.theme_color||'4ade80'}`}}>{c.trim()}</th>
                  ))}
                </tr></thead>
                <tbody>{Array.from({length:4}).map((_,i)=>(
                  <tr key={i} style={{background:i%2===0?'#0d0d0d':'#111'}}>
                    {(pXl.col_headers||'ID,Name,Value,Date,Status').split(',').map((_,j)=>(
                      <td key={j} className="px-3 py-2 border border-[#111]">
                        <div className="h-2 rounded bg-[#1a1a1a]" style={{width:`${45+j*10}%`}}/>
                      </td>
                    ))}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </CanarySection>
      );

      case 'word': return (
        <CanarySection title="Word Document Customization" icon={FileText}>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Document Title" value={pWd.title} onChange={e=>setPWd(p=>({...p,title:e.target.value}))} placeholder="Internal Memorandum"/>
            <CanaryInput label="Author" value={pWd.author} onChange={e=>setPWd(p=>({...p,author:e.target.value}))} placeholder="John Smith"/>
          </div>
          <CanaryInput label="Organization" value={pWd.company} onChange={e=>setPWd(p=>({...p,company:e.target.value}))} placeholder="Company Name"/>
          <CanaryTextarea label="Document Content" value={pWd.content} onChange={e=>setPWd(p=>({...p,content:e.target.value}))} placeholder="Document body text..." rows={4}/>
          <button type="button" onClick={()=>setPWd(p=>({...p,classified:!p.classified}))}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-[13px] transition ${pWd.classified?'border-red-700/40 bg-red-950/20 text-red-400':'border-[#222] text-[#555] hover:text-white'}`}>
            <AlertTriangle size={13}/> Classified Banner: {pWd.classified?'ON':'OFF'}
          </button>
          {/* Word Preview */}
          <div className="border border-[#ccc] rounded-xl overflow-hidden bg-white">
            <div className="px-4 py-2 bg-[#ebebeb] border-b border-[#ddd] text-[11px] text-[#888]">Word Document Preview</div>
            <div className="p-6 max-h-[180px] overflow-hidden">
              {pWd.classified && <div className="text-center text-[10px] font-bold text-red-700 border border-red-300 bg-red-50 rounded py-1 mb-3">CLASSIFIED — AUTHORIZED PERSONNEL ONLY</div>}
              <h2 className="text-[17px] font-bold text-gray-800 mb-1">{pWd.title||'Document Title'}</h2>
              <p className="text-[11px] text-gray-400 mb-3">{[pWd.author,pWd.company].filter(Boolean).join(' · ') || 'Author · Organization'}</p>
              <p className="text-[12px] text-gray-600 leading-[1.7]">{pWd.content||'Document content will appear here...'}</p>
            </div>
          </div>
        </CanarySection>
      );

      case 'pdf': return (
        <CanarySection title="PDF Document Customization" icon={FileText}>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Title" value={pPdf.title} onChange={e=>setPPdf(p=>({...p,title:e.target.value}))} placeholder="Confidential Report"/>
            <CanaryInput label="Subtitle" value={pPdf.subtitle} onChange={e=>setPPdf(p=>({...p,subtitle:e.target.value}))} placeholder="For Authorized Personnel"/>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Author" value={pPdf.author} onChange={e=>setPPdf(p=>({...p,author:e.target.value}))} placeholder="J. Smith"/>
            <CanaryInput label="Organization" value={pPdf.company} onChange={e=>setPPdf(p=>({...p,company:e.target.value}))} placeholder="Organization"/>
          </div>
          <CanaryTextarea label="Report Content" value={pPdf.content} onChange={e=>setPPdf(p=>({...p,content:e.target.value}))} placeholder="Report body..." rows={3}/>
          <CanaryInput label="Footer" value={pPdf.footer} onChange={e=>setPPdf(p=>({...p,footer:e.target.value}))} placeholder="CONFIDENTIAL — DO NOT DISTRIBUTE"/>
          {/* PDF Preview */}
          <div className="border border-[#1a1a1a] rounded-xl overflow-hidden bg-[#e8e8e8] p-4 flex justify-center">
            <div className="bg-white shadow-xl p-7 w-[180px] min-h-[240px] flex flex-col text-center">
              <div className="text-[11px] font-bold text-gray-800 mb-0.5">{pPdf.title||'Report'}</div>
              {pPdf.subtitle&&<div className="text-[9px] text-gray-400 mb-3">{pPdf.subtitle}</div>}
              <div className="flex-1 border-t border-gray-200 pt-2 text-left">
                <p className="text-[8px] text-gray-500 leading-[1.5]">{(pPdf.content||'').slice(0,150)}</p>
              </div>
              {pPdf.footer&&<div className="border-t border-gray-100 pt-1 mt-2"><p className="text-[7px] text-gray-300">{pPdf.footer}</p></div>}
            </div>
          </div>
        </CanarySection>
      );

      case 'email': return (
        <CanarySection title="Email Template Customization" icon={Mail}>
          <div className="grid md:grid-cols-2 gap-3">
            <CanaryInput label="Subject" value={pEmail.subject} onChange={e=>setPEmail(p=>({...p,subject:e.target.value}))} placeholder="Important Security Notification"/>
            <CanaryInput label="From Name" value={pEmail.from_name} onChange={e=>setPEmail(p=>({...p,from_name:e.target.value}))} placeholder="IT Security Team"/>
          </div>
          <CanaryTextarea label="Email Body" value={pEmail.body} onChange={e=>setPEmail(p=>({...p,body:e.target.value}))} placeholder="Email body..." rows={3}/>
          {/* Email Preview */}
          <div className="border border-[#1a1a1a] rounded-xl overflow-hidden bg-[#f4f4f4] p-3">
            <div className="bg-white rounded-lg overflow-hidden shadow-sm max-w-[340px] mx-auto">
              <div className="bg-[#1a1a2e] py-3 px-4 text-center"><span className="text-[#4ade80] text-[13px] font-bold">{pEmail.from_name||'Sender'}</span></div>
              <div className="p-4">
                <p className="text-[12px] font-bold text-gray-800 mb-2">{pEmail.subject||'Subject'}</p>
                <p className="text-[11px] text-gray-500 leading-[1.5]">{pEmail.body||'Email body...'}</p>
              </div>
            </div>
          </div>
        </CanarySection>
      );

      case 'folder': return (
        <CanarySection title="Windows Folder Customization" icon={Folder}>
          <CanaryInput label="Folder Name" value={pFolder.folder_name} onChange={e=>setPFolder(p=>({...p,folder_name:e.target.value}))} placeholder="Project Files 2024"/>
          <CanaryTextarea label="README Content (optional)" value={pFolder.readme} onChange={e=>setPFolder(p=>({...p,readme:e.target.value}))} placeholder="Folder description..." rows={2}/>
          <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
            <div className="flex items-center gap-3">
              <Folder size={32} className="text-amber-400"/>
              <div>
                <p className="text-white text-[13px] font-semibold">{pFolder.folder_name||'Project Files'}</p>
                <p className="text-[11px] text-[#444]">Windows Folder Canary — fires when target browses via desktop.ini</p>
              </div>
            </div>
          </div>
        </CanarySection>
      );

      case 'zip': return (
        <CanarySection title="ZIP Archive Customization" icon={Package}>
          <CanaryInput label="Inner File Name" value={pZip.inner_name} onChange={e=>setPZip(p=>({...p,inner_name:e.target.value}))} placeholder="README.txt"/>
          <CanaryTextarea label="Inner File Content" value={pZip.content} onChange={e=>setPZip(p=>({...p,content:e.target.value}))} placeholder="Archive contents..." rows={3}/>
        </CanarySection>
      );

      default: return null;
    }
  };

  // ── LEGAL GATE ─────────────────────────────────────────────────────────────
  if(!legal) return (
    <div className="max-w-[560px] mx-auto pt-6">
      <div className="border border-amber-800/40 rounded-[28px] bg-[#050505] overflow-hidden">
        <div className="p-8 border-b border-[#0d0d0d] space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-amber-950 border border-amber-700 rounded-2xl flex items-center justify-center flex-shrink-0"><AlertTriangle size={22} className="text-amber-400"/></div>
            <div>
              <p className="text-white font-bold text-[20px]">Canary Token Intelligence</p>
              <p className="text-[12px] text-amber-400 mt-0.5">All Operations Logged — Authorized Use Only</p>
            </div>
          </div>
          <div className="border border-amber-800/30 bg-amber-950/10 rounded-2xl p-5 space-y-3 text-[13px] text-[#bbb] leading-[1.9]">
            <p>Canary tokens are silent intelligence beacons. When a target opens your file, you instantly capture their IP, location, ISP, VPN status, OS, device, and more — without any visible indication.</p>
            <p className="text-amber-300 font-semibold">All operations here are fully logged with timestamp, operator ID, and source IP. Continued use confirms agreement with the Terms of Service accepted at registration.</p>
          </div>
        </div>
        <div className="p-6 flex gap-3">
          <button onClick={()=>window.history.back()} className="flex-1 py-3 border border-[#222] rounded-xl text-[14px] text-[#666] hover:text-white transition">Back</button>
          <button onClick={()=>{localStorage.setItem('canary_legal_v1','1');setLegal(true);setShowTut(true);}} className="flex-1 py-3 bg-amber-600 text-white rounded-xl text-[14px] font-semibold hover:bg-amber-700 transition">Understood — Enter</button>
        </div>
      </div>
    </div>
  );

  // ── TUTORIAL ───────────────────────────────────────────────────────────────
  const Tutorial = () => !showTut ? null : (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-[480px] border border-[#1a1a1a] rounded-[28px] bg-[#050505] overflow-hidden">
        <div className="px-8 py-5 border-b border-[#0a0a0a] flex items-center justify-between">
          <p className="text-[11px] text-[#444] uppercase tracking-wider">{tutStep+1} / {TUT.length}</p>
          <button onClick={()=>setShowTut(false)} className="text-[#333] hover:text-white transition text-[20px] leading-none">&times;</button>
        </div>
        <div className="px-8 py-7">
          <div className="w-10 h-10 bg-amber-950 border border-amber-800 rounded-2xl flex items-center justify-center mb-4"><Radio size={16} className="text-amber-400"/></div>
          <h2 className="text-[18px] font-bold text-white mb-3">{TUT[tutStep].t}</h2>
          <p className="text-[13px] text-[#888] leading-[1.8]">{TUT[tutStep].b}</p>
        </div>
        <div className="flex gap-0.5 px-8 pb-3">{TUT.map((_,i)=><div key={i} className={`h-1 flex-1 rounded-full ${i<=tutStep?'bg-amber-500':'bg-[#222]'}`}/>)}</div>
        <div className="px-8 pb-7 flex gap-3">
          {tutStep>0&&<button onClick={()=>setTutStep(s=>s-1)} className="px-4 py-2.5 border border-[#222] text-[#666] hover:text-white rounded-xl text-[13px] transition">Back</button>}
          <button onClick={()=>tutStep<TUT.length-1?setTutStep(s=>s+1):setShowTut(false)} className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-[13px] font-semibold hover:bg-amber-700 transition">{tutStep<TUT.length-1?'Next':'Done'}</button>
        </div>
      </div>
    </div>
  );

  // ── MANAGE VIEW ─────────────────────────────────────────────────────────────
  if(view==='manage' && active) {
    const tinfo = TOKEN_TYPES.find(t=>t.id===active.token_type)||TOKEN_TYPES[0];
    const TIcon = tinfo.icon;
    const triggers = active.triggers||[];
    return (
      <div className="space-y-4">
        <Modal/><Tutorial/>
        {/* Header */}
        <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button onClick={()=>{setView('list');load();}} className="flex items-center gap-1.5 text-[#555] hover:text-white text-[13px] transition flex-shrink-0"><ArrowLeft size={14}/>Back</button>
              <div className="w-10 h-10 rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] flex items-center justify-center flex-shrink-0">
                <TIcon size={16} style={{color:tinfo.color}}/>
              </div>
              <div>
                {editMode
                  ? <input className="bg-black border border-[#333] px-3 py-1.5 text-white rounded-xl text-[15px] outline-none focus:border-[#555] w-full max-w-[280px]" value={editName} onChange={e=>setEditName(e.target.value)}/>
                  : <p className="text-white font-bold text-[17px]">{active.name}</p>
                }
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[11px] border border-[#222] text-[#555] rounded-full px-2 py-0.5">{tinfo.label}</span>
                  <span className={`text-[11px] border rounded-full px-2 py-0.5 ${active.is_active?'border-emerald-800/40 text-emerald-400':'border-[#222] text-[#444]'}`}>{active.is_active?'Active':'Inactive'}</span>
                  {triggers.length>0&&<span className="text-[11px] border border-red-800/40 text-red-400 rounded-full px-2 py-0.5">{triggers.length} triggers</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={reDownload} disabled={genLoad2} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 text-white rounded-xl text-[12px] font-semibold hover:bg-emerald-600 disabled:opacity-40 transition">
                {genLoad2?<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>:<Download size={13}/>} Download
              </button>
              {editMode
                ?<><button onClick={saveEdit} className="px-3 py-2 bg-blue-700 text-white rounded-xl text-[12px] font-semibold hover:bg-blue-600 transition">Save</button>
                  <button onClick={()=>setEditMode(false)} className="px-3 py-2 border border-[#222] text-[#666] rounded-xl text-[12px] hover:text-white transition">Cancel</button></>
                :<button onClick={()=>setEditMode(true)} className="flex items-center gap-1.5 px-3 py-2 border border-[#222] text-[#666] rounded-xl text-[12px] hover:text-white transition"><Edit3 size={12}/>Edit</button>
              }
              <button onClick={()=>toggleTok(active.id)} className="px-3 py-2 border border-[#222] text-[#666] rounded-xl text-[12px] hover:text-white transition">{active.is_active?'Disable':'Enable'}</button>
              <button onClick={()=>archiveTok(active.id)} className="px-3 py-2 border border-[#222] text-[#666] rounded-xl text-[12px] hover:text-white transition">Archive</button>
              <button onClick={()=>deleteTok(active.id)} className="px-3 py-2 border border-red-800/40 text-red-400 rounded-xl text-[12px] hover:bg-red-950/20 transition"><Trash2 size={12}/></button>
            </div>
          </div>
          {editMode&&<div className="mt-3"><input className="w-full bg-black border border-[#1a1a1a] px-4 py-2.5 text-[#aaa] rounded-xl text-[13px] outline-none focus:border-[#333] placeholder:text-[#222]" value={editDesc} onChange={e=>setEditDesc(e.target.value)} placeholder="Description..."/></div>}
        </div>

        {/* Beacon info */}
        <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#0a0a0a]"><p className="text-white font-semibold">Beacon URL</p></div>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 bg-black border border-[#1a1a1a] rounded-xl px-4 py-3">
              <p className="text-[12px] text-emerald-400 font-mono flex-1 break-all">{active.beacon_url}</p>
              <button onClick={()=>copy(active.beacon_url,'URL copied')} className="text-[#444] hover:text-white flex-shrink-0 transition"><Copy size={13}/></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[['HTML',`<img src="${active.beacon_url}" width="1" height="1" style="opacity:0"/>`],['Markdown',`![](${active.beacon_url})`],['Email',`<img src="${active.beacon_url}" alt="" style="display:none">`]].map(([l,c])=>(
                <div key={l} className="border border-[#1a1a1a] rounded-xl p-3 bg-black">
                  <p className="text-[10px] text-[#444] uppercase mb-1">{l}</p>
                  <code className="text-[10px] text-blue-400 break-all block">{c.slice(0,55)}...</code>
                  <button onClick={()=>copy(c,`${l} copied`)} className="text-[10px] text-[#333] hover:text-white mt-1.5 transition">Copy</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Inject another file */}
        <div className="border border-blue-900/20 rounded-[24px] bg-[#050505] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#0a0a0a] flex items-center gap-2"><Upload size={13} className="text-blue-400"/><p className="text-white font-semibold text-[14px]">Inject Into Your Own File</p></div>
          <div className="p-5 space-y-3">
            <p className="text-[13px] text-[#555]">Upload your own .xlsx, .docx, or .pdf — we inject this token's beacon invisibly. The file looks identical to the original.</p>
            <label className="flex items-center gap-3 border border-dashed border-[#1a1a1a] rounded-xl px-4 py-4 cursor-pointer hover:border-[#333] transition">
              <Upload size={14} className="text-[#444] flex-shrink-0"/>
              <p className="text-[13px] text-[#555] flex-1">{injectFile2?injectFile2.name:'Choose .xlsx, .docx, or .pdf...'}</p>
              <input type="file" accept=".xlsx,.docx,.pdf,.xls,.doc" onChange={e=>setInjectFile2(e.target.files?.[0]||null)} className="hidden"/>
            </label>
            <button type="button" onClick={injectManage} disabled={!injectFile2||injecting2} className="w-full py-3 bg-blue-800 text-white rounded-xl text-[13px] font-bold hover:bg-blue-700 disabled:opacity-40 transition flex items-center justify-center gap-2">
              {injecting2?<><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"/><span>Injecting...</span></>:<><Fingerprint size={14}/><span>Inject & Download</span></>}
            </button>
          </div>
        </div>

        {/* Triggers */}
        <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#0a0a0a] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-white font-semibold">Triggers ({triggers.length})</p>
              {triggers.length>0&&<span className="text-[10px] border border-red-800/40 text-red-400 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>}
            </div>
            {triggers.length>0&&(
              <button onClick={exportReport} className="flex items-center gap-1.5 px-3 py-1.5 border border-[#1a1a1a] text-[#555] hover:text-white rounded-xl text-[12px] transition"><Download size={12}/>HTML Report</button>
            )}
          </div>
          {!triggers.length?(
            <div className="py-16 text-center"><Radio size={28} className="mx-auto text-[#111] mb-3"/><p className="text-[#333]">No triggers yet — deploy the file and wait</p></div>
          ):(
            <div className="divide-y divide-[#0a0a0a] max-h-[700px] overflow-y-auto">
              {triggers.map((tr,i)=>{
                const x=typeof tr.additional_info==='object'?(tr.additional_info||{}):{};
                return (
                  <div key={tr.id||i} className="p-5 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-2 h-2 rounded-full bg-red-500"/>
                        <span className="text-white font-mono font-bold text-[14px]">{tr.ip||'?'}</span>
                        {(x.is_vpn||x.is_proxy)&&<span className="text-[10px] border border-amber-700/40 text-amber-400 px-2 py-0.5 rounded-full">VPN/Proxy</span>}
                        <span className="text-[12px] text-[#555]">{[tr.country,tr.city].filter(Boolean).join(' · ')}</span>
                      </div>
                      <span className="text-[11px] text-[#333]">{tr.triggered_at?new Date(tr.triggered_at).toLocaleString():'—'}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[['OS',x.os],['App/Browser',x.browser],['Device',x.device_type],['ISP',tr.isp||x.isp],['ASN',x.asn],['Org',x.org],['Region',x.region],['Timezone',x.timezone],['Currency',x.currency],['Calling Code',x.calling_code],['Language',x.accept_lang]].filter(([,v])=>v).map(([l,v])=>(
                        <div key={l} className="border border-[#0d0d0d] bg-black rounded-xl p-2.5">
                          <p className="text-[9px] text-[#2a2a2a] uppercase tracking-wider">{l}</p>
                          <p className="text-[12px] text-[#888] truncate mt-0.5">{v}</p>
                        </div>
                      ))}
                    </div>
                    {x.lat&&x.lon&&(
                      <div className="flex items-center gap-2 border border-emerald-900/30 bg-emerald-950/10 rounded-xl px-4 py-2.5">
                        <Navigation size={12} className="text-emerald-400 flex-shrink-0"/>
                        <a href={`https://maps.google.com/?q=${x.lat},${x.lon}`} target="_blank" rel="noreferrer" className="text-[12px] text-emerald-400 hover:underline font-mono">{parseFloat(x.lat).toFixed(5)}, {parseFloat(x.lon).toFixed(5)}</a>
                        <span className="text-[10px] text-[#333]">IP-based</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── CREATE VIEW ─────────────────────────────────────────────────────────────
  if(view==='create') return (
    <div className="space-y-4">
      <Modal/><Tutorial/>
      <div className="flex items-center gap-3">
        <button onClick={()=>{setView('list');load();}} className="flex items-center gap-1.5 text-[#555] hover:text-white text-[13px] transition"><ArrowLeft size={14}/>Back</button>
        <h1 className="text-[22px] font-bold text-white">New Canary Token</h1>
      </div>

      {/* STEP 1: Type + Name */}
      {createStep==='type'&&(
        <div className="space-y-4">
          <CanarySection title="Token Details">
            <CanaryInput label="Token Name *" value={tokenName} onChange={e=>setTokenName(e.target.value)} placeholder="Sensitive Report, Employee List, Q4 Budget..."/>
            <CanaryInput label="Description (optional)" value={tokenDesc} onChange={e=>setTokenDesc(e.target.value)} placeholder="Where this will be deployed..."/>
          </CanarySection>
          <CanarySection title="Select Token Type">
            <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {TOKEN_TYPES.map(t=>{
                const TI=t.icon;
                return (
                  <button key={t.id} type="button" onClick={()=>setSelType(t.id)}
                    className={`text-left border rounded-[16px] p-3.5 transition ${selType===t.id?'border-amber-600 bg-amber-950/10':'border-[#1a1a1a] bg-black hover:border-[#2a2a2a]'}`}>
                    <TI size={20} className="mb-2.5" style={{color:selType===t.id?t.color:'#444'}}/>
                    <p className={`text-[12px] font-semibold mb-0.5 ${selType===t.id?'text-white':'text-[#555]'}`}>{t.label}</p>
                    <p className="text-[9px] text-[#333] leading-[1.4]">{t.desc}</p>
                  </button>
                );
              })}
            </div>
          </CanarySection>
          <button type="button" onClick={createToken} disabled={creating||!tokenName.trim()}
            className="w-full py-4 bg-amber-600 text-white rounded-2xl text-[15px] font-bold hover:bg-amber-700 disabled:opacity-30 transition flex items-center justify-center gap-2">
            {creating?<><span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"/><span>Creating...</span></>:<><Radio size={16}/><span>Create & Customize</span></>}
          </button>
        </div>
      )}

      {/* STEP 2: Config */}
      {createStep==='config'&&newToken&&(
        <div className="space-y-4">
          <div className="border border-emerald-900/30 rounded-[20px] bg-[#050505] p-4 flex items-center gap-3">
            <CheckCircle size={16} className="text-emerald-400 flex-shrink-0"/>
            <p className="text-[13px] text-[#888]">Token created — customize and download your file below.</p>
            <span className="ml-auto text-[11px] border border-emerald-800/40 text-emerald-400 px-2 py-0.5 rounded-full flex-shrink-0">Active</span>
          </div>

          <TypeBuilder/>

          <button type="button" onClick={downloadFile} disabled={genLoading}
            className="w-full py-4 bg-emerald-600 text-white rounded-2xl text-[15px] font-bold hover:bg-emerald-700 disabled:opacity-40 transition flex items-center justify-center gap-2">
            {genLoading?<><span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"/><span>Generating...</span></>:<><Download size={17}/><span>Download {TOKEN_TYPES.find(t=>t.id===selType)?.label}</span></>}
          </button>

          {/* Upload & Inject alternative */}
          <div className="border border-blue-900/20 rounded-[24px] bg-[#050505] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#0a0a0a] flex items-center gap-2"><Upload size={13} className="text-blue-400"/><p className="text-white font-semibold text-[14px]">Or: Inject Into Your Own File</p></div>
            <div className="p-5 space-y-3">
              <p className="text-[13px] text-[#555]">Upload your own .xlsx, .docx, or .pdf — the beacon will be injected invisibly into it.</p>
              <label className="flex items-center gap-3 border border-dashed border-[#1a1a1a] rounded-xl px-4 py-4 cursor-pointer hover:border-[#333] transition">
                <Upload size={14} className="text-[#444] flex-shrink-0"/>
                <p className="text-[13px] text-[#555] flex-1">{injectFile?injectFile.name:'Choose file...'}</p>
                <input type="file" accept=".xlsx,.docx,.pdf,.xls,.doc" onChange={e=>setInjectFile(e.target.files?.[0]||null)} className="hidden"/>
              </label>
              <button type="button" onClick={injectUpload} disabled={!injectFile||injecting}
                className="w-full py-3 bg-blue-800 text-white rounded-xl text-[13px] font-bold hover:bg-blue-700 disabled:opacity-40 transition flex items-center justify-center gap-2">
                {injecting?<><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"/><span>Injecting...</span></>:<><Fingerprint size={14}/><span>Inject & Download</span></>}
              </button>
            </div>
          </div>

          <button onClick={()=>setCreateStep('done')} className="w-full py-2.5 border border-[#1a1a1a] text-[#444] hover:text-white rounded-xl text-[13px] transition">Skip to Token Info</button>
        </div>
      )}

      {/* STEP 3: Done */}
      {createStep==='done'&&newToken&&(
        <div className="space-y-4">
          <div className="border border-emerald-900/30 rounded-[24px] bg-[#050505] p-8 text-center">
            <div className="w-16 h-16 bg-emerald-950 border border-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-5"><Radio size={24} className="text-emerald-400"/></div>
            <h2 className="text-[22px] font-bold text-white mb-2">Token Active</h2>
            <p className="text-[14px] text-[#555]">Deploy the file — you'll receive an alert the moment it's opened.</p>
          </div>
          <div className="border border-[#1a1a1a] rounded-[24px] bg-[#050505] p-5">
            <p className="text-[10px] text-[#444] uppercase tracking-wider mb-2">Beacon URL</p>
            <div className="flex items-center gap-2 bg-black border border-[#1a1a1a] rounded-xl px-4 py-3">
              <p className="text-[12px] text-emerald-400 font-mono flex-1 break-all">{newToken.beacon_url}</p>
              <button onClick={()=>copy(newToken.beacon_url)} className="text-[#444] hover:text-white flex-shrink-0 transition"><Copy size={12}/></button>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={()=>{setView('list');load();}} className="flex-1 py-3 border border-[#1a1a1a] text-[#555] hover:text-white rounded-xl text-[14px] transition">All Tokens</button>
            <button onClick={()=>loadManage(newToken)} className="flex-1 py-3 bg-amber-600 text-white rounded-xl text-[14px] font-semibold hover:bg-amber-700 transition">Manage Token</button>
          </div>
        </div>
      )}
    </div>
  );

  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <Modal/><Tutorial/>
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">Canary Intelligence</p>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-white">Canary Tokens</h1>
            <p className="text-[14px] text-[#666] mt-2 max-w-[600px] leading-[1.8]">Silent file-based intelligence beacons. Full intelligence capture the moment a target opens your document, image, or file. Email alert on every trigger.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={()=>setShowTut(true)} className="flex items-center gap-1.5 px-4 py-2.5 border border-[#1a1a1a] text-[#555] hover:text-white rounded-xl text-[13px] transition"><Info size={13}/>Guide</button>
            <button onClick={startCreate} className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-600 text-white rounded-xl text-[13px] font-semibold hover:bg-amber-700 transition"><Radio size={13}/>New Token</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[['Total',stats.total,'text-white'],['Active',stats.active,'text-emerald-400'],['Triggers',stats.triggers,'text-red-400']].map(([l,v,c])=>(
          <div key={l} className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505] text-center">
            <p className={`text-[28px] font-bold leading-none ${c}`}>{v}</p>
            <p className="text-[11px] text-[#333] mt-1.5 uppercase tracking-wider">{l}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#333]">{tokens.length} {showArchived?'archived':'active'} tokens</p>
        <button onClick={()=>setShowArchived(a=>!a)} className="text-[12px] text-[#444] hover:text-white transition">{showArchived?'Show Active':'Show Archived'}</button>
      </div>
      {!tokens.length?(
        <div className="border border-dashed border-[#1a1a1a] rounded-[28px] py-24 text-center">
          <Radio size={36} className="mx-auto text-[#111] mb-4"/>
          <p className="text-[#333] text-[17px] font-semibold mb-2">No canary tokens yet</p>
          <p className="text-[#2a2a2a] text-[13px] mb-6">Create your first token to start detecting unauthorized access</p>
          <button onClick={startCreate} className="px-6 py-3 bg-amber-600 text-white rounded-2xl text-[14px] font-semibold hover:bg-amber-700 transition">Create First Token</button>
        </div>
      ):(
        <div className="space-y-2">
          {tokens.map(t=>{
            const tt=TOKEN_TYPES.find(x=>x.id===t.token_type)||TOKEN_TYPES[0]; const TI=tt.icon;
            return (
              <div key={t.id} onClick={()=>loadManage(t)} className={`border rounded-[20px] p-4 bg-[#050505] cursor-pointer hover:border-[#2a2a2a] transition ${t.is_active?'border-[#1a1a1a]':'border-[#0d0d0d] opacity-50'}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-2xl border border-[#1a1a1a] bg-black flex items-center justify-center flex-shrink-0">
                    <TI size={16} style={{color:t.trigger_count>0?'#ef4444':tt.color}}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-white font-semibold text-[14px]">{t.name}</span>
                      <span className={`text-[10px] border px-1.5 py-0.5 rounded-full ${t.is_active?'border-emerald-800/40 text-emerald-400':'border-[#1a1a1a] text-[#333]'}`}>{t.is_active?'Active':'Inactive'}</span>
                      <span className="text-[10px] border border-[#111] text-[#333] px-1.5 py-0.5 rounded-full">{tt.label}</span>
                      {t.trigger_count>0&&<span className="text-[10px] border border-red-800/40 text-red-400 px-1.5 py-0.5 rounded-full">{t.trigger_count} triggers</span>}
                    </div>
                    {t.description&&<p className="text-[11px] text-[#333] truncate">{t.description}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0" onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>copy(t.beacon_url,'URL copied')} className="p-2 border border-[#111] rounded-xl text-[#333] hover:text-white transition"><Copy size={12}/></button>
                    <button onClick={()=>toggleTok(t.id)} className="px-3 py-1.5 border border-[#111] text-[#333] rounded-xl text-[11px] hover:text-white transition">{t.is_active?'Off':'On'}</button>
                    <button onClick={()=>deleteTok(t.id)} className="px-3 py-1.5 border border-red-900/30 text-red-600 rounded-xl text-[11px] hover:bg-red-950/20 transition"><Trash2 size={11}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function TicketChatPage({ user }) {
  const { tid } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const fetchTicket = useCallback(async () => {
    try {
      const r = await api.get(isAdmin ? '/api/v1/tickets/all' : '/api/v1/tickets/my');
      const found = r.data?.find(t => String(t.id) === String(tid));
      if (!found) throw new Error('Ticket not found');
      setSel(found);
    } catch (e) { toast(E(e), 'error'); nav('/support'); }
    setLoading(false);
  }, [tid, isAdmin, toast, nav]);

  const fetchMsgs = useCallback(async () => {
    if (!tid) return;
    try {
      const r = await api.get(`/api/v1/tickets/${tid}/messages`);
      setMsgs(r.data || []);
    } catch (e) { toast(E(e), 'error'); }
  }, [tid, toast]);

  useEffect(() => {
    fetchTicket();
    fetchMsgs();
    const i = setInterval(fetchMsgs, 8000);
    return () => clearInterval(i);
  }, [fetchTicket, fetchMsgs]);

  const send = async () => {
    if (!newMsg.trim()) return;
    try {
      await api.post(`/api/v1/tickets/${tid}/messages`, { message: newMsg });
      setNewMsg('');
      fetchMsgs();
    } catch (e) { toast(E(e), 'error'); }
  };

  const claim = async () => {
    try {
      await api.post(`/api/v1/tickets/${tid}/claim`);
      toast('Ticket claimed', 'success');
      fetchTicket();
    } catch (e) { toast(E(e), 'error'); }
  };

  const setStatus = async (s) => {
    try {
      await api.patch(`/api/v1/tickets/${tid}/status`, { status: s });
      toast(`Status: ${s}`, 'success');
      fetchTicket();
    } catch (e) { toast(E(e), 'error'); }
  };

  if (loading) return <div className="p-8 text-[#555] animate-pulse">Initializing direct support line...</div>;
  if (!sel) return null;

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] border border-[#1a1a1a] rounded-[28px] bg-[#050505] overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-[#111] bg-[#080808] flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => nav(-1)} className="h-10 w-10 flex items-center justify-center rounded-xl bg-black border border-[#222] text-[#888] hover:text-white transition-all">
            <ArrowLeft size={16}/>
          </button>
          <div>
            <h1 className="text-white font-semibold text-[16px] tracking-tight">{sel.subject}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-[#555] font-mono">#{sel.id}</span>
              <B v={String(sel.status) === 'open' ? 'warning' : String(sel.status) === 'resolved' ? 'success' : 'default'}>{sel.status}</B>
              {sel.admin_name && <span className="text-[10px] text-emerald-500">Expert: {sel.admin_name}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && !sel.admin_id && <Btn v="success" className="!px-3 !py-1.5 !text-[11px]" onClick={claim}>Claim Ticket</Btn>}
          {isAdmin && (
            <select className="bg-black border border-[#222] text-[11px] text-white px-3 py-1.5 rounded-lg outline-none" value={sel.status} onChange={e => setStatus(e.target.value)}>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
        <div className="flex justify-start mb-8">
          <div className="max-w-[85%] bg-[#111] border border-[#222] p-5 rounded-[22px] rounded-tl-none">
            <p className="text-emerald-500 text-[10px] font-black uppercase tracking-widest mb-2">Original Request</p>
            <p className="text-[14px] text-white/90 leading-relaxed font-light">{sel.description}</p>
            <p className="text-[9px] text-[#444] mt-4 font-mono">{new Date(sel.created_at).toLocaleString()}</p>
          </div>
        </div>

        {msgs.map(m => (
          <div key={m.id} className={`flex ${String(m.sender_id) === String(user.user_id) ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-4 rounded-[20px] ${String(m.sender_id) === String(user.user_id) ? 'bg-white text-black rounded-tr-none' : 'bg-[#0a0a0a] text-white border border-[#333] rounded-tl-none'}`}>
              <p className="text-[9px] font-bold uppercase mb-1 opacity-50">{m.sender_name} {m.sender_role === 'admin' ? '(ADMIN)' : ''}</p>
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.message}</p>
              <p className={`text-[8px] mt-2 opacity-50 ${String(m.sender_id) === String(user.user_id) ? 'text-right' : 'text-left'}`}>{new Date(m.created_at).toLocaleTimeString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="p-6 border-t border-[#111] bg-[#050505]">
        {sel.status === 'closed' ? (
          <div className="text-center py-4 bg-red-950/20 rounded-xl border border-red-900/30 text-red-500 uppercase text-[10px] font-bold tracking-widest">
            Ticket Closed
          </div>
        ) : (
          <div className="flex gap-3">
            <input className="flex-1 bg-black border border-[#222] px-5 py-3 text-white rounded-[18px] text-[14px] outline-none focus:border-emerald-500 transition-all placeholder:text-[#333]" placeholder="Type your message..." value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} />
            <button onClick={send} className="px-6 py-3 bg-white text-black rounded-[18px] font-bold text-[13px] hover:bg-[#eee] transition-all">Send</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SupportPage({ user }) {
  const toast = useToast();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [load, setLoad] = useState(false);
  const [sub, setSub] = useState('');
  const [desc, setDesc] = useState('');
  const [cat, setCat] = useState('general');
  const [pri, setPri] = useState('medium');

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const fetchTickets = useCallback(async () => {
    try {
      const r = await api.get(isAdmin ? '/api/v1/tickets/all' : '/api/v1/tickets/my');
      setTickets(r.data || []);
    } catch (e) { toast(E(e), 'error'); }
  }, [isAdmin, toast]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const create = async () => {
    if (!sub.trim() || !desc.trim()) return toast('Subject/Message required', 'error');
    setLoad(true);
    try {
      await api.post('/api/v1/tickets/create', { subject: sub, description: desc, category: cat, priority: pri });
      toast('Ticket created', 'success');
      setSub(''); setDesc('');
      fetchTickets();
    } catch (e) { toast(E(e), 'error'); }
    setLoad(false);
  };

  const openTickets = tickets.filter(t => t.status === 'open').length;
  const inProgress = tickets.filter(t => t.status === 'in_progress').length;

  return (
    <div className="space-y-6">
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h1 className="text-[32px] font-semibold text-white tracking-tight leading-none">Support Center</h1>
            <p className="text-[#555] mt-3 text-[14px]">Strategic operations support and platform guidance nexus.</p>
          </div>
          <div className="flex gap-2">
            <B v="warning">{openTickets} New</B>
            <B v="blue">{inProgress} Active</B>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_450px] gap-6">
        {/* Ticket List */}
        <Panel title="Active Tickets" sub="Current pending and in-progress requests" extra={<Btn v="secondary" className="!px-3 !py-1 !text-[11px]" onClick={fetchTickets}>Refresh</Btn>}>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            {tickets.length === 0 && <div className="py-20 text-center text-[#333] text-sm">No tickets found.</div>}
            {tickets.map(t => (
              <div key={t.id} className="p-5 border border-[#1a1a1a] bg-[#080808] rounded-2xl flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <B v={String(t.status) === 'open' ? 'warning' : String(t.status) === 'resolved' ? 'success' : 'default'}>{t.status}</B>
                    <span className="text-[10px] font-mono text-[#444]">#{t.id}</span>
                  </div>
                  <h3 className="text-white font-medium text-[15px] truncate">{t.subject}</h3>
                  <p className="text-[11px] text-[#555] mt-1">{new Date(t.updated_at).toLocaleDateString()} • {t.admin_name ? `Assigned to ${t.admin_name}` : 'Awaiting Assignee'}</p>
                </div>
                <Btn v="secondary" className="!px-4 !py-2" onClick={() => nav(`/tickets/chat/${t.id}`)}>Open Chat</Btn>
              </div>
            ))}
          </div>
        </Panel>

        {/* Create New */}
        <div className="space-y-6">
          <Panel title="New Ticket" sub="Initiate a new support request">
            <div className="space-y-4">
              <Inp label="Subject" placeholder="Summary of the issue" value={sub} onChange={e => setSub(e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] text-[#555] ml-1">CATEGORY</label>
                  <select className="w-full bg-black border border-[#222] p-2 text-white rounded-lg text-sm outline-none" value={cat} onChange={e => setCat(e.target.value)}>
                    <option value="general">General</option><option value="bug">Technical Bug</option><option value="security">Security</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[#555] ml-1">PRIORITY</label>
                  <select className="w-full bg-black border border-[#222] p-2 text-white rounded-lg text-sm outline-none" value={pri} onChange={e => setPri(e.target.value)}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-[#555] ml-1">MESSAGE</label>
                <textarea className="w-full bg-black border border-[#222] p-4 text-white rounded-xl h-[120px] text-sm resize-none outline-none focus:border-[#444]" placeholder="Detailed description..." value={desc} onChange={e => setDesc(e.target.value)} />
              </div>
              <Btn onClick={create} disabled={load} className="w-full">{load ? 'Sending...' : 'Transmit Request'}</Btn>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
function SOCPage() {
  const toast = useToast();

  const [overview, setOverview] = useState(null);
  const [feed, setFeed] = useState([]);
  const [health, setHealth] = useState(null);
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [userDetail, setUserDetail] = useState(null);
  const [requests, setRequests] = useState(null);
  const [suspicious, setSuspicious] = useState([]);
  const [tab, setTab] = useState('overview');
  const [suspendId, setSuspendId] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  const loadSOC = useCallback(() => {
    api.get('/api/v1/soc/overview').then((r) => setOverview(r.data)).catch(() => {});
    api.get('/api/v1/soc/health').then((r) => setHealth(r.data)).catch(() => {});
    api.get('/api/v1/soc/live-feed').then((r) => setFeed(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/ip-blocks').then((r) => setIps(r.data || [])).catch(() => {});
    api.get('/api/v1/soc/requests').then((r) => setRequests(r.data)).catch(() => {});
    api.get('/api/v1/soc/suspicious').then((r) => setSuspicious(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSOC();
    const i = setInterval(loadSOC, 10000);
    return () => clearInterval(i);
  }, [loadSOC]);

  if (!overview) {
    return <p className="text-[#555] py-8">Loading SOC...</p>;
  }

  return (
    <div className="space-y-6">
      {/* HERO */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Security Operations Center
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Platform monitoring
              <br />
              and security oversight.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Monitor platform health, suspicious activity, recent events, user behavior,
              and operational requests through one centralized control surface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <B v="success">Live</B>
            <B v={health?.database_status === 'connected' ? 'success' : 'danger'}>
              DB {health?.database_status || 'unknown'}
            </B>
            <B v="blue">SOC</B>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SC l="Users" v={overview.total_users || 0} />
        <SC l="Active 24h" v={overview.active_users_24h || 0} />
        <SC l="Traps" v={overview.total_traps || 0} />
        <SC l="Captures" v={overview.total_captures || 0} />
        <SC l="Open Tickets" v={overview.open_tickets || 0} c={overview.open_tickets > 0 ? 'border-red-900' : ''} />
      </div>

      {/* ALERTS */}
      {suspicious.length > 0 && (
        <Panel title="Suspicious Activity" sub="Flagged traffic and security anomalies" extra={<B v="danger">{suspicious.length}</B>}>
          <div className="space-y-3">
            {suspicious.map((item, i) => (
              <div
                key={i}
                className="border border-red-900 bg-red-950 rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <B v="danger">{item.severity}</B>
                  <span className="text-white font-mono text-[13px]">{item.ip}</span>
                  <span className="text-[#999] text-[12px]">{item.request_count} reqs</span>
                </div>

                <Btn
                  v="danger"
                  onClick={async () => {
                    try {
                      await api.post('/api/v1/admin/ip-blocks', { ip: item.ip });
                      toast('IP blocked', 'success');
                      loadSOC();
                    } catch (e) {
                      toast(E(e), 'error');
                    }
                  }}
                >
                  Block IP
                </Btn>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* TABS */}
      <Tabs
        tabs={[
          { id: 'overview', l: 'Overview' },
          { id: 'requests', l: 'Requests' },
          { id: 'users', l: 'Users' },
          { id: 'security', l: 'Security' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'overview' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {health && (
            <Panel
              title="Platform Health"
              sub="Core service status"
              extra={
                <B v={health.database_status === 'connected' ? 'success' : 'danger'}>
                  {health.database_status}
                </B>
              }
            >
              <div className="grid grid-cols-3 gap-4">
                <SC l="API" v={health.api_status || '—'} />
                <SC l="Database" v={health.database_status || '—'} />
                <SC l="Sessions" v={health.active_sessions_1h || 0} />
              </div>
            </Panel>
          )}

          <Panel title="Security Alerts" sub="Recent security-sensitive events">
            {(overview.security_alerts || []).length === 0 ? (
              <p className="text-[#555] py-6">No security alerts</p>
            ) : (
              <div className="space-y-2">
                {(overview.security_alerts || []).map((alert) => (
                  <div key={alert.id} className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex justify-between gap-3">
                    <div className="flex gap-2 flex-wrap items-center">
                      <B v="danger">{alert.action}</B>
                      <span className="text-white text-[12px]">{alert.username}</span>
                    </div>
                    <span className="text-[10px] text-[#444]">
                      {new Date(alert.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <div className="xl:col-span-2">
            <Panel title="Live Feed" sub="Real-time operational events" extra={<span className="text-[10px] text-emerald-400 animate-pulse">● LIVE</span>}>
              <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
                {feed.length === 0 ? (
                  <p className="text-[#555] py-6">No events</p>
                ) : (
                  feed.map((item) => (
                    <div
                      key={item.id}
                      className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex justify-between gap-3"
                    >
                      <div className="flex gap-2 flex-wrap items-center min-w-0">
                        <B v={item.category === 'security' ? 'danger' : 'default'}>
                          {item.category || 'sys'}
                        </B>
                        <B>{item.action}</B>
                        {item.username && (
                          <span className="text-white text-[12px]">{item.username}</span>
                        )}
                      </div>

                      <span className="text-[10px] text-[#444] whitespace-nowrap">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <Panel title="Recent Requests" sub="Latest application traffic" extra={<B>{requests?.total || 0}</B>}>
          {!requests?.recent?.length ? (
            <p className="text-[#555]">No requests</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1a1a1a]">
                    <th className="text-left px-3 py-2 text-[11px] text-[#555]">Time</th>
                    <th className="text-left px-3 py-2 text-[11px] text-[#555]">IP</th>
                    <th className="text-left px-3 py-2 text-[11px] text-[#555]">Path</th>
                    <th className="text-left px-3 py-2 text-[11px] text-[#555]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.recent.slice().reverse().map((r, i) => (
                    <tr key={i} className="border-b border-[#0a0a0a]">
                      <td className="px-3 py-2 text-[11px] text-[#888]">{r.time}</td>
                      <td className="px-3 py-2 text-[11px] text-white font-mono">{r.ip}</td>
                      <td className="px-3 py-2 text-[11px] text-[#888] truncate max-w-[260px]">{r.path}</td>
                      <td className="px-3 py-2">
                        <B v={r.status >= 400 ? 'danger' : 'success'}>{r.status}</B>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === 'users' && (
        <div className="space-y-6">
          <Panel title="Recent Logins" sub="User activity and access overview">
            {(overview.recent_logins || []).length === 0 ? (
              <p className="text-[#555] py-6">No user activity</p>
            ) : (
              <div className="space-y-2">
                {(overview.recent_logins || []).map((u) => (
                  <div
                    key={u.id}
                    className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex justify-between gap-3 cursor-pointer hover:border-[#333]"
                    onClick={async () => {
                      try {
                        const r = await api.get(`/api/v1/soc/user/${u.id}/activity`);
                        setUserDetail(r.data);
                      } catch (e) {
                        toast(E(e), 'error');
                      }
                    }}
                  >
                    <div className="flex gap-2 flex-wrap items-center">
                      <span className="text-white text-[13px]">{u.username}</span>
                      <B>{u.role}</B>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] text-[#444]">
                        {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                      </span>
                      <Btn
                        v="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSuspendId(u.id);
                        }}
                      >
                        Suspend
                      </Btn>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {suspendId && (
            <Panel title="Suspend User" sub="Restrict account access">
              <div className="flex flex-col md:flex-row gap-3">
                <input
                  className="flex-1 bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg outline-none"
                  placeholder="Reason..."
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                />
                <Btn
                  v="danger"
                  onClick={async () => {
                    try {
                      await api.post(`/api/v1/soc/suspend/${suspendId}`, {
                        reason: suspendReason || 'SOC action',
                      });
                      toast('User suspended', 'success');
                      setSuspendId(null);
                      setSuspendReason('');
                      loadSOC();
                    } catch (e) {
                      toast(E(e), 'error');
                    }
                  }}
                >
                  Confirm
                </Btn>
                <Btn v="ghost" onClick={() => setSuspendId(null)}>
                  Cancel
                </Btn>
              </div>
            </Panel>
          )}

          {userDetail && (
            <Panel title={userDetail.user.username} sub="Expanded user activity" extra={<Btn v="ghost" onClick={() => setUserDetail(null)}>×</Btn>}>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <SC l="Traps" v={userDetail.stats.traps || 0} />
                <SC l="Captures" v={userDetail.stats.captures || 0} />
                <SC l="Profiles" v={userDetail.stats.profiles || 0} />
                <SC l="OSINT" v={userDetail.stats.osint_searches || 0} />
                <SC l="Messages" v={userDetail.stats.messages_sent || 0} />
              </div>
            </Panel>
          )}
        </div>
      )}

      {tab === 'security' && (
        <Panel title="IP Blocking" sub="Manage blocked IP addresses">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <input
              className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg outline-none w-full md:w-64"
              placeholder="IP address..."
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
            />
            <Btn
              onClick={async () => {
                if (!newIp) return;
                try {
                  await api.post('/api/v1/admin/ip-blocks', { ip: newIp });
                  setNewIp('');
                  toast('IP blocked', 'success');
                  loadSOC();
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Block
            </Btn>
          </div>

          <div className="space-y-2">
            {ips.length === 0 ? (
              <p className="text-[#555] py-6">No blocked IPs</p>
            ) : (
              ips.map((ip) => (
                <div
                  key={ip.id}
                  className="border border-[#1a1a1a] rounded-xl p-3 bg-black flex justify-between gap-3"
                >
                  <span className="text-white font-mono text-[13px]">{ip.ip_address}</span>
                  <Btn
                    v="ghost"
                    onClick={async () => {
                      await api.delete(`/api/v1/admin/ip-blocks/${ip.id}`);
                      loadSOC();
                    }}
                  >
                    Unblock
                  </Btn>
                </div>
              ))
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
function AdminPage({ user: currentUser }) {
  const toast = useToast();
  const { ask, Modal } = useModal();
  const isSuperAdm = currentUser?.role === 'superadmin';

  const [us, sU] = useState([]);
  const [au, sA] = useState([]);
  const [tab, sT] = useState('users');
  const [edit, sE] = useState(null);
  const [ef, sEf] = useState({});
  const [cu, sCu] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [nf, sNf] = useState({
    user_ids: [],
    title: '',
    message: '',
  });
  const [tks, sTk] = useState([]);
  const [pw, sPw] = useState({ uid: null, pw: '' });
  const [ftS, sFtS] = useState({});
  const [arts, sArt] = useState([]);
  const [cmsPages, setCmsPages] = useState([]);
  const [cmsSections, setCmsSections] = useState([]);
  const [bugReports, setBugReports] = useState([]);
  const [bbFilter, setBbFilter] = useState('all');
  const [regRequests, setRegRequests] = useState([]);
  const [regFilter, setRegFilter] = useState('pending');
  const [poolDomains, setPoolDomains] = useState([]);
  const [newPoolDomain, setNewPoolDomain] = useState('');
  const [editingPageId, setEditingPageId] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [pageForm, setPageForm] = useState({ title: '', slug: '', nav_group: 'company', content: '', image: '', is_published: true });
  const [sectionForm, setSectionForm] = useState({ title: '', body: '', image: '', sort_order: 0, is_active: true });
  const [na, sNA] = useState({
    title: '',
    content: '',
    author_name: '',
    image_url: '',
  });
  const [popups, setPopups] = useState([]);
  const [popupForm, setPopupForm] = useState({
    title: '',
    message: '',
    popup_type: 'info',
    image_url: '',
    button_text: '',
    button_link: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  const reload = async () => {
    try {
      sU((await api.get('/api/v1/admin/users')).data || []);
    } catch {}
  };

  useEffect(() => {
    reload();
    api.get('/api/v1/admin/audit').then((r) => sA(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/tickets').then((r) => sTk(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/settings').then((r) => sFtS(r.data || {})).catch(() => {});
    api.get('/api/v1/admin/articles').then((r) => sArt(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/popups').then((r) => setPopups(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/cms/pages').then((r) => setCmsPages(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/cms/sections').then((r) => setCmsSections(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/bug-bounty/reports').then((r) => setBugReports(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/registration-requests?status=pending').then((r) => setRegRequests(r.data || [])).catch(() => {});
    api.get('/api/v1/admin/domain-pool').then(r => setPoolDomains(r.data || [])).catch(() => {});
  }, []);

  const upd = async (id, d) => {
    try {
      await api.put(`/api/v1/admin/users/${id}`, d);
      reload();
      sE(null);
      toast('Updated', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }
  };

  const totalUsers = us.length;
  const totalAdmins = us.filter((u) => u.role === 'admin').length;
  const openTickets = tks.filter((t) => t.status === 'open').length;
  const totalArticles = arts.length;

  const uploadAdminImage = async (file, onDone) => {
    if (!file) return;
    try {
      setUploadingImage(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/v1/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onDone(res.data?.url || '');
      toast('Image uploaded', 'success');
    } catch (e) {
      toast(E(e), 'error');
    }
    setUploadingImage(false);
  };

  return (
    <div className="space-y-6">
      <Modal />

      {/* HERO */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Administrative Control
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Platform administration
              <br />
              and operational control.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Manage users, tickets, homepage settings, footer links, pages,
              articles, bug bounty reports, notifications, and audit activity.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <B v="warning">Admin</B>
            <B v="blue">{totalUsers} Users</B>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Users" v={totalUsers} />
        <SC l="Admins" v={totalAdmins} />
        <SC l="Open Tickets" v={openTickets} />
        <SC l="Articles" v={totalArticles} />
      </div>

      {/* TABS */}
      <Tabs
        tabs={[
          { id: 'users', l: 'Users' },
          { id: 'create', l: 'Create' },
          { id: 'tickets', l: 'Tickets' },
          { id: 'homepage', l: 'Homepage CMS' },
          { id: 'articles', l: 'Articles' },
          { id: 'popups', l: 'Popups' },
          { id: 'bugbounty', l: 'Bug Bounty' },
          { id: 'requests', l: `Requests${regRequests.length>0?' ('+regRequests.length+')':''}` },
          { id: 'domains', l: `Domains (${poolDomains.length})` },
          { id: 'notify', l: 'Notify' },
        ]}
        active={tab}
        onChange={sT}
      />

      {/* USERS */}
      {tab === 'users' && (
        <div className="space-y-6">
          {edit && (
            <Panel
              title={`Edit: ${edit.username}`}
              sub="Modify account info, role, and feature visibility"
            >
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Inp
                    label="Username"
                    value={ef.username ?? edit.username}
                    onChange={(e) => sEf({ ...ef, username: e.target.value })}
                  />
                  <Inp
                    label="Email"
                    value={ef.email ?? edit.email}
                    onChange={(e) => sEf({ ...ef, email: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Inp
                    label="First Name"
                    value={ef.first_name ?? edit.first_name ?? ''}
                    onChange={(e) => sEf({ ...ef, first_name: e.target.value })}
                  />
                  <Inp
                    label="Last Name"
                    value={ef.last_name ?? edit.last_name ?? ''}
                    onChange={(e) => sEf({ ...ef, last_name: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Inp
                    label="Phone"
                    value={ef.phone ?? edit.phone ?? ''}
                    onChange={(e) => sEf({ ...ef, phone: e.target.value })}
                  />
                  <Inp
                    label="Location"
                    value={ef.location ?? edit.location ?? ''}
                    onChange={(e) => sEf({ ...ef, location: e.target.value })}
                  />
                </div>

                <div className="flex gap-2 flex-wrap items-center">
                  <span className="text-[11px] text-[#555] uppercase mr-2">Role:</span>
                  {(isSuperAdm ? ['user', 'monitor', 'admin', 'superadmin'] : ['user', 'monitor']).map((r) => (
                    <button
                      key={r}
                      onClick={() => sEf({ ...ef, role: r })}
                      className={`px-3 py-1 rounded text-[11px] border ${
                        (ef.role || edit.role) === r
                          ? 'border-white text-white'
                          : 'border-[#222] text-[#666]'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                <div className="grid md:grid-cols-3 gap-4 border border-[#1a1a1a] p-4 rounded-2xl bg-black">
                  <Tog l="Active Account" c={ef.is_active ?? edit.is_active} o={() => sEf({...ef, is_active: !(ef.is_active ?? edit.is_active)})} />
                  <Tog l="Banned" c={ef.is_banned ?? edit.is_banned} o={() => sEf({...ef, is_banned: !(ef.is_banned ?? edit.is_banned)})} />
                  <Tog l="Restricted" c={ef.is_restricted ?? edit.is_restricted} o={() => sEf({...ef, is_restricted: !(ef.is_restricted ?? edit.is_restricted)})} />
                </div>

                {(ef.is_restricted ?? edit.is_restricted) && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#555] ml-1 uppercase">Restriction Reason</label>
                    <textarea 
                      className="w-full bg-[#0a0a0a] border border-[#222] p-3 text-white rounded-xl text-sm h-20 resize-none outline-none focus:border-[#444]"
                      value={ef.restrict_reason ?? edit.restrict_reason ?? ''}
                      onChange={(e) => sEf({...ef, restrict_reason: e.target.value})}
                      placeholder="Why is this account restricted?"
                    />
                  </div>
                )}

                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-black border border-[#1a1a1a] p-6 rounded-[24px]">
                    {[
                      { grp: 'Operations', items: ['deploy', 'feed', 'links', 'pages', 'canary', 'soc'] },
                      { grp: 'Analysis', items: ['investigations', 'profiles', 'graph'] },
                      { grp: 'Intelligence', items: ['osint', 'dorking', 'exif', 'social'] },
                      { grp: 'Platform', items: ['analytics', 'support', 'settings', 'notifications'] }
                    ].map((g) => (
                      <div key={g.grp} className="space-y-3">
                        <p className="text-[10px] text-[#444] font-bold uppercase tracking-[0.2em] mb-4 border-b border-[#111] pb-2">{g.grp}</p>
                        <div className="space-y-1">
                          {g.items.map((feat) => (
                            <Tog
                              key={feat}
                              l={feat.charAt(0).toUpperCase() + feat.slice(1)}
                              c={(ef.visible_features || edit.visible_features || {})[feat] !== false}
                              o={() => {
                                const cur = {
                                  ...(ef.visible_features || edit.visible_features || {}),
                                };
                                cur[feat] = cur[feat] === false;
                                sEf({ ...ef, visible_features: cur });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Btn
                    onClick={() => {
                      const d = {};
                      Object.entries(ef).forEach(([k, v]) => {
                        if (v !== undefined) d[k] = v;
                      });
                      upd(edit.id, d);
                    }}
                  >
                    Save Changes
                  </Btn>
                  <Btn v="ghost" onClick={() => sE(null)}>
                    Cancel
                  </Btn>
                </div>
              </div>
            </Panel>
          )}

          {pw.uid && (
            <Panel title="Change Password" sub="Reset user password">
              <div className="flex flex-col md:flex-row gap-3">
                <PwI
                  placeholder="New password (8+)"
                  value={pw.pw}
                  onChange={(e) => sPw({ ...pw, pw: e.target.value })}
                />
                <Btn
                  onClick={async () => {
                    try {
                      await api.put(`/api/v1/admin/users/${pw.uid}/password`, {
                        password: pw.pw,
                      });
                      toast('Changed', 'success');
                      sPw({ uid: null, pw: '' });
                    } catch (e) {
                      toast(E(e), 'error');
                    }
                  }}
                >
                  Set
                </Btn>
                <Btn v="ghost" onClick={() => sPw({ uid: null, pw: '' })}>
                  Cancel
                </Btn>
              </div>
            </Panel>
          )}

          <Panel title="Users" sub="Manage roles, restrictions, sessions, and deletion">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1a1a1a] bg-[#060606]">
                    <th className="text-left px-4 py-3 text-[11px] text-[#555] uppercase tracking-wider">User</th>
                    <th className="text-left px-4 py-3 text-[11px] text-[#555] uppercase tracking-wider">Profile</th>
                    <th className="text-left px-4 py-3 text-[11px] text-[#555] uppercase tracking-wider">Contact</th>
                    <th className="text-left px-4 py-3 text-[11px] text-[#555] uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 text-[11px] text-[#555] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {us.map((u) => (
                    <tr key={u.id} className="border-b border-[#0a0a0a] hover:bg-[#060606]">
                      <td className="px-4 py-3">
                        <p className="text-white text-[13px] font-medium">{u.username}</p>
                        <B v={u.role === 'superadmin' ? 'blue' : u.role === 'admin' ? 'warning' : 'default'} className="!text-[9px]">{u.role}</B>
                      </td>

                      <td className="px-4 py-3">
                        <p className="text-[12px] text-white">{(u.first_name || u.last_name) ? `${u.first_name || ''} ${u.last_name || ''}` : '—'}</p>
                        <p className="text-[10px] text-[#555]">{u.location || 'Unknown Location'}</p>
                      </td>

                      <td className="px-4 py-3">
                        <p className="text-[11px] text-[#888]">{u.email}</p>
                        <p className="text-[10px] text-[#444]">{u.phone || 'No phone'}</p>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex gap-1 flex-wrap">
                          {u.is_banned ? <B v="danger">Banned</B> : (u.is_active ? <B v="success">Active</B> : <B v="warning">Inactive</B>)}
                          {u.is_restricted && <B v="danger" title={u.restrict_reason}>Restricted</B>}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end flex-wrap">
                          {(isSuperAdm || (u.role !== 'admin' && u.role !== 'superadmin')) && (
                            <>
                              <Btn
                                v="ghost"
                                onClick={() => {
                                  sE(u);
                                  sEf({});
                                }}
                              >
                                Edit
                              </Btn>

                              <Btn v="ghost" onClick={() => sPw({ uid: u.id, pw: '' })}>
                                PW
                              </Btn>

                              <Btn
                                v={u.is_restricted ? 'secondary' : 'ghost'}
                                onClick={() =>
                                  upd(u.id, {
                                    is_restricted: !u.is_restricted,
                                    restrict_reason: u.is_restricted ? '' : 'Admin',
                                  })
                                }
                              >
                                {u.is_restricted ? 'Unrestrict' : 'Restrict'}
                              </Btn>

                              <Btn
                                v="ghost"
                                onClick={async () => {
                                  try {
                                    const r = await api.post(`/api/v1/admin/impersonate/${u.id}`);
                                    localStorage.setItem('zx_t', r.data.access_token);
                                    localStorage.setItem('zx_u', JSON.stringify(r.data.user));
                                    window.location.href = '/dashboard';
                                  } catch (e) {
                                    toast(E(e), 'error');
                                  }
                                }}
                              >
                                Session
                              </Btn>

                              {u.role !== 'superadmin' && (
                                <Btn
                                  v="danger"
                                  onClick={async () => {
                                    if (await ask(`Delete ${u.username}?`)) {
                                      try {
                                        await api.delete(`/api/v1/admin/users/${u.id}`);
                                        reload();
                                      } catch (e) {
                                        toast(E(e), 'error');
                                      }
                                    }
                                  }}
                                >
                                  Del
                                </Btn>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {/* CREATE */}
      {tab === 'create' && (
        <Panel title="Create User" sub="Provision a new user or admin account">
          <div className="space-y-4 max-w-2xl">
            <div className="grid md:grid-cols-2 gap-4">
              <Inp
                label="Username"
                value={cu.username}
                onChange={(e) => sCu({ ...cu, username: e.target.value })}
              />
              <Inp
                label="Email"
                value={cu.email}
                onChange={(e) => sCu({ ...cu, email: e.target.value })}
              />
              <PwI
                label="Password"
                value={cu.password}
                onChange={(e) => sCu({ ...cu, password: e.target.value })}
              />
            </div>

            <div className="flex gap-2">
              {(isSuperAdm ? ['user', 'monitor', 'admin', 'superadmin'] : ['user', 'monitor']).map((r) => (
                <button
                  key={r}
                  onClick={() => sCu({ ...cu, role: r })}
                  className={`px-3 py-1 rounded text-[11px] border ${
                    cu.role === r ? 'border-white text-white' : 'border-[#222] text-[#666]'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            <Btn
              onClick={async () => {
                if (!cu.username || !cu.email || !cu.password) return toast('Required', 'error');
                try {
                  await api.post('/api/v1/admin/users/create', cu);
                  reload();
                  sCu({ username: '', email: '', password: '', role: 'user' });
                  toast('Created', 'success');
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Create
            </Btn>
          </div>
        </Panel>
      )}

      {/* TICKETS */}
      {tab === 'tickets' && (
        <Panel title="Tickets" sub="Review and resolve support requests">
          {tks.length === 0 ? (
            <p className="text-[#555] py-4">None</p>
          ) : (
            <div className="space-y-3">
              {tks.map((t) => (
                <div
                  key={t.id}
                  className="border border-[#1a1a1a] rounded-xl p-4 bg-black flex justify-between gap-3 flex-wrap"
                >
                  <div>
                    <div className="flex gap-2 flex-wrap items-center">
                      <B v={t.status === 'open' ? 'danger' : 'success'}>{t.status}</B>
                      <span className="text-white">{t.subject}</span>
                    </div>
                    {t.description && (
                      <p className="text-[11px] text-[#888] mt-2">{t.description}</p>
                    )}
                  </div>

                  {t.status === 'open' && (
                    <Btn
                      v="ghost"
                      onClick={async () => {
                        try {
                          await api.put(`/api/v1/admin/tickets/${t.id}`, { status: 'resolved' });
                          sTk((await api.get('/api/v1/admin/tickets')).data || []);
                          toast('Resolved', 'success');
                        } catch {}
                      }}
                    >
                      Resolve
                    </Btn>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* HOMEPAGE */}
      {tab === 'homepage' && (
        <div className="space-y-6">
          <Panel
            title="Hero & Core Settings"
            sub="Control badge, title, description, CTAs, footer label, and top-level public content"
            extra={
              <Btn
                onClick={async () => {
                  try {
                    await api.put('/api/v1/admin/settings', ftS);
                    toast('Homepage settings saved', 'success');
                  } catch (e) {
                    toast(E(e), 'error');
                  }
                }}
              >
                Save All
              </Btn>
            }
          >
            <div className="grid xl:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[
                  ['site_name', 'Site name'],
                  ['hero_badge', 'Hero badge'],
                  ['hero_title', 'Hero title'],
                  ['hero_description', 'Hero description'],
                  ['primary_cta_text', 'Primary CTA'],
                  ['secondary_cta_text', 'Secondary CTA'],
                  ['footer_text', 'Footer text'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-[12px] text-[#888] mb-1">{label}</label>
                    {key.includes('description') || key.includes('title') ? (
                      <textarea
                        className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-24 resize-none outline-none"
                        value={ftS[key] || ''}
                        onChange={(e) => sFtS({ ...ftS, [key]: e.target.value })}
                      />
                    ) : (
                      <input
                        className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                        value={ftS[key] || ''}
                        onChange={(e) => sFtS({ ...ftS, [key]: e.target.value })}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                {[
                  ['footer_company_title', 'About / company title'],
                  ['footer_company_text', 'About / company text'],
                  ['contact_phone', 'Contact phone'],
                  ['contact_email', 'Contact email'],
                  ['contact_location', 'Contact location'],
                  ['footer_services_title', 'Services column title'],
                  ['footer_legal_title', 'Legal column title'],
                  ['footer_support_title', 'Support column title'],
                  ['social_x', 'X link'],
                  ['social_linkedin', 'LinkedIn link'],
                  ['social_instagram', 'Instagram link'],
                  ['social_facebook', 'Facebook link'],
                  ['social_youtube', 'YouTube link'],
                  ['social_tiktok', 'TikTok link'],
                  ['social_telegram', 'Telegram link'],
                  ['social_whatsapp', 'WhatsApp link'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-[12px] text-[#888] mb-1">{label}</label>
                    {key.includes('text') ? (
                      <textarea
                        className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-24 resize-none outline-none"
                        value={ftS[key] || ''}
                        onChange={(e) => sFtS({ ...ftS, [key]: e.target.value })}
                      />
                    ) : (
                      <input
                        className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                        value={ftS[key] || ''}
                        onChange={(e) => sFtS({ ...ftS, [key]: e.target.value })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <div className="grid xl:grid-cols-2 gap-6">
            <Panel title="Footer Pages" sub="Create public pages that appear in the landing footer">
              <div className="space-y-4">
                <Inp label="Title" value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} />
                <Inp label="Slug" value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-') })} />
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Footer group</label>
                  <select
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[13px] outline-none"
                    value={pageForm.nav_group}
                    onChange={(e) => setPageForm({ ...pageForm, nav_group: e.target.value })}
                  >
                    <option value="company">Company</option>
                    <option value="services">Services</option>
                    <option value="legal">Legal</option>
                    <option value="support">Support</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
                  <Inp label="Image URL" value={pageForm.image} onChange={(e) => setPageForm({ ...pageForm, image: e.target.value })} />
                  <div>
                    <label className="block text-[12px] text-[#888] mb-1">Upload image</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                      onChange={(e) => uploadAdminImage(e.target.files?.[0], (url) => setPageForm((prev) => ({ ...prev, image: url })))}
                    />
                  </div>
                </div>
                <textarea
                  className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-32 resize-y outline-none"
                  placeholder="Page content..."
                  value={pageForm.content}
                  onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })}
                />
                <div className="flex gap-2 flex-wrap">
                  <Btn
                    onClick={async () => {
                      if (!pageForm.title || !pageForm.slug) return toast('Title and slug are required', 'error');
                      try {
                        if (editingPageId) {
                          await api.put(`/api/v1/admin/cms/pages/${editingPageId}`, pageForm);
                          toast('Page updated', 'success');
                        } else {
                          await api.post('/api/v1/admin/cms/pages', pageForm);
                          toast('Page created', 'success');
                        }
                        setCmsPages((await api.get('/api/v1/admin/cms/pages')).data || []);
                        setEditingPageId(null);
                        setPageForm({ title: '', slug: '', nav_group: 'company', content: '', image: '', is_published: true });
                      } catch (e) {
                        toast(E(e), 'error');
                      }
                    }}
                  >
                    {editingPageId ? 'Update Page' : 'Create Page'}
                  </Btn>
                  {editingPageId && (
                    <Btn
                      v="ghost"
                      onClick={() => {
                        setEditingPageId(null);
                        setPageForm({ title: '', slug: '', nav_group: 'company', content: '', image: '', is_published: true });
                      }}
                    >
                      Cancel
                    </Btn>
                  )}
                </div>
              </div>
            </Panel>

            <Panel title="Homepage Sections" sub="Add flexible title/body/image blocks to the landing page">
              <div className="space-y-4">
                <Inp label="Section title" value={sectionForm.title} onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })} />
                <Inp label="Sort order" type="number" value={sectionForm.sort_order} onChange={(e) => setSectionForm({ ...sectionForm, sort_order: Number(e.target.value || 0) })} />
                <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
                  <Inp label="Image URL" value={sectionForm.image} onChange={(e) => setSectionForm({ ...sectionForm, image: e.target.value })} />
                  <div>
                    <label className="block text-[12px] text-[#888] mb-1">Upload image</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                      onChange={(e) => uploadAdminImage(e.target.files?.[0], (url) => setSectionForm((prev) => ({ ...prev, image: url })))}
                    />
                  </div>
                </div>
                <textarea
                  className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-32 resize-y outline-none"
                  placeholder="Section body..."
                  value={sectionForm.body}
                  onChange={(e) => setSectionForm({ ...sectionForm, body: e.target.value })}
                />
                <div className="flex gap-2 flex-wrap">
                  <Btn
                    onClick={async () => {
                      if (!sectionForm.title) return toast('Section title is required', 'error');
                      try {
                        if (editingSectionId) {
                          await api.put(`/api/v1/admin/cms/sections/${editingSectionId}`, sectionForm);
                          toast('Section updated', 'success');
                        } else {
                          await api.post('/api/v1/admin/cms/sections', sectionForm);
                          toast('Section created', 'success');
                        }
                        setCmsSections((await api.get('/api/v1/admin/cms/sections')).data || []);
                        setEditingSectionId(null);
                        setSectionForm({ title: '', body: '', image: '', sort_order: 0, is_active: true });
                      } catch (e) {
                        toast(E(e), 'error');
                      }
                    }}
                  >
                    {editingSectionId ? 'Update Section' : 'Add Section'}
                  </Btn>
                  {editingSectionId && (
                    <Btn
                      v="ghost"
                      onClick={() => {
                        setEditingSectionId(null);
                        setSectionForm({ title: '', body: '', image: '', sort_order: 0, is_active: true });
                      }}
                    >
                      Cancel
                    </Btn>
                  )}
                </div>
              </div>
            </Panel>
          </div>

          <Panel title="Published Footer Pages" sub={`${cmsPages.length} managed records`}>
            <div className="space-y-3">
              {cmsPages.length === 0 ? (
                <p className="text-[#555] py-4">No pages yet</p>
              ) : (
                cmsPages.map((page) => (
                  <div key={page.id} className="border border-[#1a1a1a] rounded-xl p-4 bg-black flex justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex gap-2 items-center flex-wrap mb-2">
                        <B v={page.is_published ? 'success' : 'warning'}>{page.is_published ? 'published' : 'draft'}</B>
                        <B>{page.nav_group || 'company'}</B>
                        <span className="text-white text-[14px]">{page.title}</span>
                      </div>
                      <p className="text-[11px] text-[#666]">/page/{page.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Btn
                        v="ghost"
                        onClick={() => {
                          setEditingPageId(page.id);
                          setPageForm({
                            title: page.title || '',
                            slug: page.slug || '',
                            nav_group: page.nav_group || 'company',
                            content: page.content || '',
                            image: page.image || '',
                            is_published: !!page.is_published,
                          });
                        }}
                      >
                        Edit
                      </Btn>
                      <Btn
                        v="ghost"
                        onClick={async () => {
                          try {
                            await api.put(`/api/v1/admin/cms/pages/${page.id}`, { is_published: !page.is_published });
                            setCmsPages((await api.get('/api/v1/admin/cms/pages')).data || []);
                          } catch (e) {
                            toast(E(e), 'error');
                          }
                        }}
                      >
                        {page.is_published ? 'Unpublish' : 'Publish'}
                      </Btn>
                      <Btn
                        v="danger"
                        onClick={async () => {
                          if (await ask('Delete this page?')) {
                            await api.delete(`/api/v1/admin/cms/pages/${page.id}`);
                            setCmsPages((prev) => prev.filter((item) => item.id !== page.id));
                          }
                        }}
                      >
                        Delete
                      </Btn>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Panel>

          <Panel title="Managed Sections" sub={`${cmsSections.length} homepage blocks`}>
            <div className="space-y-3">
              {cmsSections.length === 0 ? (
                <p className="text-[#555] py-4">No homepage sections yet</p>
              ) : (
                cmsSections.map((section) => (
                  <div key={section.id} className="border border-[#1a1a1a] rounded-xl p-4 bg-black flex justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex gap-2 items-center flex-wrap mb-2">
                        <B v={section.is_active ? 'success' : 'warning'}>{section.is_active ? 'active' : 'hidden'}</B>
                        <span className="text-white text-[14px]">{section.title}</span>
                        <span className="text-[11px] text-[#555]">order {section.sort_order || 0}</span>
                      </div>
                      <p className="text-[11px] text-[#666] line-clamp-2 max-w-[900px]">{section.body}</p>
                    </div>
                    <div className="flex gap-2">
                      <Btn
                        v="ghost"
                        onClick={() => {
                          setEditingSectionId(section.id);
                          setSectionForm({
                            title: section.title || '',
                            body: section.body || '',
                            image: section.image || '',
                            sort_order: section.sort_order || 0,
                            is_active: section.is_active !== false,
                          });
                        }}
                      >
                        Edit
                      </Btn>
                      <Btn
                        v="ghost"
                        onClick={async () => {
                          try {
                            await api.put(`/api/v1/admin/cms/sections/${section.id}`, { is_active: !section.is_active });
                            setCmsSections((await api.get('/api/v1/admin/cms/sections')).data || []);
                          } catch (e) {
                            toast(E(e), 'error');
                          }
                        }}
                      >
                        {section.is_active ? 'Hide' : 'Show'}
                      </Btn>
                      <Btn
                        v="danger"
                        onClick={async () => {
                          if (await ask('Delete this section?')) {
                            await api.delete(`/api/v1/admin/cms/sections/${section.id}`);
                            setCmsSections((prev) => prev.filter((item) => item.id !== section.id));
                          }
                        }}
                      >
                        Delete
                      </Btn>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Panel>
        </div>
      )}

      {/* ARTICLES */}
      {tab === 'articles' && (
        <div className="space-y-6">
          <Panel title="Create Article" sub="Publish content with cover images to the public landing page">
            <div className="space-y-4 max-w-3xl">
              <Inp
                label="Title"
                value={na.title}
                onChange={(e) => sNA({ ...na, title: e.target.value })}
              />
              <Inp
                label="Author"
                value={na.author_name}
                onChange={(e) => sNA({ ...na, author_name: e.target.value })}
              />
              <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
                <Inp
                  label="Image URL"
                  value={na.image_url}
                  onChange={(e) => sNA({ ...na, image_url: e.target.value })}
                />
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Upload cover</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                    onChange={(e) => uploadAdminImage(e.target.files?.[0], (url) => sNA((prev) => ({ ...prev, image_url: url })))}
                  />
                </div>
              </div>
              {na.image_url && (
                <img src={na.image_url} alt="Article cover" className="w-full max-w-xl h-[220px] object-cover rounded-2xl border border-[#1a1a1a]" />
              )}
              <textarea
                className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-36 resize-y outline-none"
                value={na.content}
                onChange={(e) => sNA({ ...na, content: e.target.value })}
                placeholder="Write article content..."
              />
              <Btn
                onClick={async () => {
                  if (!na.title) return;
                  try {
                    await api.post('/api/v1/admin/articles', na);
                    sArt((await api.get('/api/v1/admin/articles')).data || []);
                    sNA({ title: '', content: '', author_name: '', image_url: '' });
                    toast('Published', 'success');
                  } catch (e) {
                    toast(E(e), 'error');
                  }
                }}
              >
                {uploadingImage ? 'Uploading...' : 'Publish'}
              </Btn>
            </div>
          </Panel>

          <Panel title="Articles" sub="Manage publication state and deletion">
            <div className="space-y-3">
              {arts.map((a) => (
                <div
                  key={a.id}
                  className="border border-[#1a1a1a] rounded-xl p-4 bg-black flex justify-between gap-4 flex-wrap"
                >
                  <div className="flex gap-4 items-start min-w-0">
                    {a.image_url ? (
                      <img src={a.image_url} alt={a.title} className="w-24 h-20 object-cover rounded-xl border border-[#1a1a1a]" />
                    ) : null}
                    <div>
                      <p className="text-white">{a.title}</p>
                      <p className="text-[11px] text-[#666] mt-1">{a.author_name || '—'}</p>
                      <div className="mt-2">
                        <B v={a.is_published ? 'success' : 'warning'}>{a.is_published ? 'published' : 'draft'}</B>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Btn
                      v="ghost"
                      onClick={async () => {
                        await api.put(`/api/v1/admin/articles/${a.id}`, {
                          is_published: !a.is_published,
                        });
                        sArt((await api.get('/api/v1/admin/articles')).data || []);
                      }}
                    >
                      {a.is_published ? 'Unpublish' : 'Publish'}
                    </Btn>
                    <Btn
                      v="danger"
                      onClick={async () => {
                        if (await ask('Delete?')) {
                          await api.delete(`/api/v1/admin/articles/${a.id}`);
                          sArt((x) => x.filter((y) => y.id !== a.id));
                        }
                      }}
                    >
                      Del
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* POPUPS */}
      {tab === 'popups' && (
        <div className="space-y-6">
          <Panel title="Announcement Popup" sub="Create promotional, operational, or informational popups for the landing page">
            <div className="space-y-4 max-w-3xl">
              <Inp label="Title" value={popupForm.title} onChange={(e) => setPopupForm({ ...popupForm, title: e.target.value })} />
              <textarea
                className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-3 text-white rounded-lg text-[13px] h-28 resize-none outline-none"
                value={popupForm.message}
                onChange={(e) => setPopupForm({ ...popupForm, message: e.target.value })}
                placeholder="Popup message..."
              />
              <div className="grid md:grid-cols-2 gap-4">
                <Inp label="Button text" value={popupForm.button_text} onChange={(e) => setPopupForm({ ...popupForm, button_text: e.target.value })} />
                <Inp label="Button link" value={popupForm.button_link} onChange={(e) => setPopupForm({ ...popupForm, button_link: e.target.value })} />
              </div>
              <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
                <Inp label="Image URL" value={popupForm.image_url} onChange={(e) => setPopupForm({ ...popupForm, image_url: e.target.value })} />
                <div>
                  <label className="block text-[12px] text-[#888] mb-1">Upload image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none"
                    onChange={(e) => uploadAdminImage(e.target.files?.[0], (url) => setPopupForm((prev) => ({ ...prev, image_url: url })))}
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['info', 'promo', 'warning'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setPopupForm({ ...popupForm, popup_type: type })}
                    className={`px-3 py-2 rounded-xl text-[12px] border transition ${
                      popupForm.popup_type === type ? 'border-white text-white bg-[#111]' : 'border-[#222] text-[#666] hover:text-white'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {popupForm.image_url && <img src={popupForm.image_url} alt="Popup preview" className="w-full max-w-xl h-[220px] object-cover rounded-2xl border border-[#1a1a1a]" />}
              <Btn
                onClick={async () => {
                  if (!popupForm.title || !popupForm.message) return toast('Title and message are required', 'error');
                  try {
                    await api.post('/api/v1/admin/popups', popupForm);
                    setPopups((await api.get('/api/v1/admin/popups')).data || []);
                    setPopupForm({ title: '', message: '', popup_type: 'info', image_url: '', button_text: '', button_link: '' });
                    toast('Popup created', 'success');
                  } catch (e) {
                    toast(E(e), 'error');
                  }
                }}
              >
                Create Popup
              </Btn>
            </div>
          </Panel>

          <Panel title="Popup Queue" sub="Toggle active announcements or remove them">
            <div className="space-y-3">
              {popups.length === 0 ? (
                <p className="text-[#555] py-4">No popups created</p>
              ) : popups.map((popup) => (
                <div key={popup.id} className="border border-[#1a1a1a] rounded-xl p-4 bg-black flex justify-between gap-4 flex-wrap">
                  <div className="flex gap-4 items-start min-w-0">
                    {popup.image_url ? <img src={popup.image_url} alt={popup.title} className="w-24 h-20 object-cover rounded-xl border border-[#1a1a1a]" /> : null}
                    <div>
                      <div className="flex gap-2 flex-wrap items-center mb-2">
                        <B v={popup.is_active ? 'success' : 'default'}>{popup.is_active ? 'active' : 'inactive'}</B>
                        <B>{popup.popup_type || 'info'}</B>
                      </div>
                      <p className="text-white">{popup.title}</p>
                      <p className="text-[11px] text-[#666] mt-1 max-w-[680px]">{popup.message}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Btn
                      v="ghost"
                      onClick={async () => {
                        await api.put(`/api/v1/admin/popups/${popup.id}/toggle`);
                        setPopups((await api.get('/api/v1/admin/popups')).data || []);
                      }}
                    >
                      {popup.is_active ? 'Disable' : 'Enable'}
                    </Btn>
                    <Btn
                      v="danger"
                      onClick={async () => {
                        if (await ask('Delete this popup?')) {
                          await api.delete(`/api/v1/admin/popups/${popup.id}`);
                          setPopups((x) => x.filter((item) => item.id !== popup.id));
                        }
                      }}
                    >
                      Delete
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}


      {/* BUG BOUNTY */}
      {tab === 'bugbounty' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              {l:'Total Reports', v: bugReports.length},
              {l:'Critical', v: bugReports.filter(r=>r.severity==='critical').length, danger:true},
              {l:'High', v: bugReports.filter(r=>r.severity==='high').length},
              {l:'New', v: bugReports.filter(r=>!r.status||r.status==='new').length},
              {l:'Resolved', v: bugReports.filter(r=>r.status==='resolved').length},
            ].map((c,i)=><div key={i} className={`border rounded-2xl p-5 bg-[#050505] ${c.danger?'border-red-900':'border-[#1a1a1a]'}`}><p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">{c.l}</p><p className="text-[30px] font-semibold text-white">{c.v}</p></div>)}
          </div>

          <Panel title="Bug Bounty Reports" sub="Security vulnerability reports from the public program"
            extra={<div className="flex gap-2 flex-wrap">
              {['all','new','reviewing','resolved','rejected'].map(s=>(
                <button key={s} onClick={()=>setBbFilter(s)} className={`px-3 py-1 rounded-lg text-[11px] border capitalize transition ${bbFilter===s?'border-white text-white bg-[#111]':'border-[#222] text-[#555]'}`}>{s}</button>
              ))}
            </div>}
          >
            <div className="space-y-4">
              {bugReports.filter(r=>bbFilter==='all'||(r.status||'new')===bbFilter).length === 0 ? (
                <p className="text-[#555] py-4 text-center">No reports found</p>
              ) : (
                bugReports.filter(r=>bbFilter==='all'||(r.status||'new')===bbFilter).map((report) => (
                  <div key={report.id} className="border border-[#1a1a1a] rounded-2xl bg-[#050505] overflow-hidden">
                    {/* Report header */}
                    <div className="px-5 py-4 border-b border-[#111] flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex gap-2 items-center flex-wrap mb-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${
                            report.severity==='critical'?'border-red-800 bg-red-950 text-red-400':
                            report.severity==='high'?'border-orange-800 bg-orange-950 text-orange-400':
                            report.severity==='medium'?'border-amber-800 bg-amber-950 text-amber-400':
                            report.severity==='low'?'border-blue-800 bg-blue-950 text-blue-400':
                            'border-emerald-800 bg-emerald-950 text-emerald-400'
                          }`}>{report.severity||'medium'}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            (report.status||'new')==='resolved'?'border-emerald-800 bg-emerald-950 text-emerald-400':
                            (report.status||'new')==='reviewing'?'border-amber-800 bg-amber-950 text-amber-400':
                            (report.status||'new')==='rejected'?'border-red-800 bg-red-950 text-red-400':
                            'border-[#333] bg-[#111] text-[#888]'
                          }`}>{report.status||'new'}</span>
                          {report.vuln_type&&<span className="text-[10px] px-2 py-0.5 rounded-full border border-[#333] text-[#666]">{report.vuln_type}</span>}
                          {report.cvss_score&&<span className="text-[10px] px-2 py-0.5 rounded-full border border-purple-800 bg-purple-950 text-purple-400">CVSS {report.cvss_score}</span>}
                        </div>
                        <p className="text-white text-[16px] font-semibold">BBR-{String(report.id).padStart(5,'0')} — {report.title}</p>
                        <div className="flex gap-4 mt-1 text-[11px] text-[#555]">
                          <span>{report.name||'Anonymous'}</span>
                          {report.email&&<span>{report.email}</span>}
                          {report.handle&&<span>{report.handle}</span>}
                          <span>{report.created_at?new Date(report.created_at).toLocaleString():'—'}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap flex-shrink-0">
                        {['new','reviewing','resolved','rejected'].map(status => (
                          <Btn key={status} v={(report.status||'new')===status?'secondary':'ghost'}
                            onClick={async()=>{
                              try{await api.put(`/api/v1/admin/bug-bounty/reports/${report.id}`,{status});setBugReports((await api.get('/api/v1/admin/bug-bounty/reports')).data||[]);toast(`Status: ${status}`,'success');}catch(e){toast(E(e),'error');}
                            }}>
                            {status}
                          </Btn>
                        ))}
                        <Btn v="danger" onClick={async()=>{if(await ask('Delete this report?')){try{await api.delete(`/api/v1/admin/bug-bounty/reports/${report.id}`);setBugReports(x=>x.filter(i=>i.id!==report.id));}catch(e){toast(E(e),'error');}}}}>Del</Btn>
                      </div>
                    </div>
                    {/* Report body */}
                    <div className="px-5 py-4 space-y-4">
                      {/* Target info */}
                      <div className="grid md:grid-cols-2 gap-3">
                        {report.target_url&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-1">Target URL</p><p className="text-[12px] text-white font-mono break-all">{report.target_url}</p></div>}
                        {report.affected_component&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-1">Component</p><p className="text-[12px] text-white">{report.affected_component}</p></div>}
                        {report.cvss_vector&&<div className="border border-[#111] rounded-xl p-3 bg-black md:col-span-2"><p className="text-[10px] text-[#444] uppercase mb-1">CVSS Vector</p><p className="text-[11px] text-purple-400 font-mono break-all">{report.cvss_vector}</p></div>}
                      </div>
                      {/* Summary */}
                      {(report.summary||report.report_body)&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-2">Summary</p><p className="text-[13px] text-[#aaa] leading-[1.8] whitespace-pre-wrap">{report.summary||report.report_body}</p></div>}
                      {/* Steps */}
                      {report.steps_to_reproduce&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-2">Steps to Reproduce</p><p className="text-[12px] text-[#aaa] font-mono leading-[1.8] whitespace-pre-wrap">{report.steps_to_reproduce}</p></div>}
                      {/* Impact + Remediation */}
                      {(report.impact||report.remediation)&&<div className="grid md:grid-cols-2 gap-3">
                        {report.impact&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-2">Impact</p><p className="text-[12px] text-[#aaa] leading-[1.8]">{report.impact}</p></div>}
                        {report.remediation&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-2">Remediation</p><p className="text-[12px] text-[#aaa] leading-[1.8]">{report.remediation}</p></div>}
                      </div>}
                      {/* PoC */}
                      {report.poc_code&&<div className="border border-[#111] rounded-xl overflow-hidden bg-black"><div className="px-3 py-2 border-b border-[#111] flex items-center gap-2"><span className="text-[10px] text-[#444] uppercase">Proof of Concept</span></div><pre className="px-4 py-3 text-[11px] text-emerald-400 font-mono overflow-x-auto whitespace-pre-wrap">{report.poc_code}</pre></div>}
                      {/* Attachment */}
                      {report.attachment_url&&<div className="border border-[#111] rounded-xl p-3 bg-black"><p className="text-[10px] text-[#444] uppercase mb-1">Evidence</p><a href={report.attachment_url} target="_blank" rel="noreferrer" className="text-[12px] text-blue-400 hover:text-blue-300 break-all">{report.attachment_url}</a></div>}
                      {/* Reward */}
                      <div className="flex gap-3 items-center pt-2 border-t border-[#111]">
                        <input type="number" placeholder="Reward ($)" className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none w-32" defaultValue={report.reward_amount||''} id={`reward-${report.id}`}/>
                        <input type="text" placeholder="Reward note..." className="bg-[#0a0a0a] border border-[#222] px-3 py-2 text-white rounded-lg text-[12px] outline-none flex-1" defaultValue={report.reward_note||''} id={`rnote-${report.id}`}/>
                        <Btn v="secondary" onClick={async()=>{
                          const amount = document.getElementById(`reward-${report.id}`)?.value;
                          const note = document.getElementById(`rnote-${report.id}`)?.value;
                          try{await api.put(`/api/v1/admin/bug-bounty/reports/${report.id}`,{status:report.status||'new',reward_amount:amount||null,reward_note:note||''});toast('Reward saved','success');}catch(e){toast(E(e),'error');}
                        }}>Set Reward</Btn>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Panel>
        </div>
      )}


      {/* REGISTRATION REQUESTS */}
      {tab === 'requests' && (
        <div className="space-y-6">
          <Panel title="Access Requests" sub="Pending user registration approvals"
            extra={<div className="flex gap-2">
              {['pending','approved','rejected'].map(s=>(
                <button key={s} onClick={()=>{setRegFilter(s);api.get(`/api/v1/admin/registration-requests?status=${s}`).then(r=>setRegRequests(r.data||[])).catch(()=>{});}}
                  className={`px-3 py-1 rounded-lg text-[11px] border capitalize transition ${regFilter===s?'border-white text-white bg-[#111]':'border-[#222] text-[#555]'}`}>{s}</button>
              ))}
            </div>}>
            {regRequests.length===0 ? (
              <p className="text-[#555] py-8 text-center">No {regFilter} requests</p>
            ) : (
              <div className="space-y-3">
                {regRequests.map(r=>(
                  <div key={r.id} className="border border-[#1a1a1a] rounded-2xl bg-[#050505] p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="text-white text-[15px] font-semibold">{r.username}</span>
                          <B v={r.status==='pending'?'warning':r.status==='approved'?'success':'danger'}>{r.status}</B>
                        </div>
                        <div className="grid md:grid-cols-3 gap-3 text-[12px] text-[#888]">
                          <span>{r.email}</span>
                          {(r.first_name||r.last_name)&&<span>{r.first_name} {r.last_name}</span>}
                          {r.ip_address&&<span>{r.ip_address}</span>}
                          {r.how_heard&&<span>{r.how_heard}</span>}
                          <span>{r.created_at?new Date(r.created_at).toLocaleString():'—'}</span>
                        </div>
                        {r.reject_reason&&<p className="text-[12px] text-red-400 mt-2">Reason: {r.reject_reason}</p>}
                      </div>
                      {r.status==='pending'&&(
                        <div className="flex gap-2 flex-shrink-0">
                          <Btn v="secondary" onClick={async()=>{
                            try{await api.post(`/api/v1/admin/registration-requests/${r.id}/approve`);toast('Account activated','success');api.get('/api/v1/admin/registration-requests?status=pending').then(x=>setRegRequests(x.data||[]));}catch(e){toast(E(e),'error');}
                          }}>Approve</Btn>
                          <Btn v="danger" onClick={async()=>{
                            const reason = prompt('Rejection reason (optional):') || '';
                            try{await api.post(`/api/v1/admin/registration-requests/${r.id}/reject`,{reason});toast('Request rejected','success');api.get('/api/v1/admin/registration-requests?status=pending').then(x=>setRegRequests(x.data||[]));}catch(e){toast(E(e),'error');}
                          }}>Reject</Btn>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {/* DOMAIN POOL */}
      {tab === 'domains' && (
        <div className="space-y-6">
          <Panel title="Domain Pool Management" sub="Domains available to all users for deployments"
            extra={
              <div className="flex gap-2">
                <input className="bg-[#0a0a0a] border border-[#222] px-3 py-1.5 text-white rounded-lg text-[13px] outline-none w-48" placeholder="example.com" value={newPoolDomain} onChange={e=>setNewPoolDomain(e.target.value.toLowerCase())}/>
                <Btn onClick={async()=>{
                  if(!newPoolDomain) return;
                  try{ const r=await api.post('/api/v1/admin/domain-pool',{domain:newPoolDomain}); setPoolDomains(d=>[r.data,...d]); setNewPoolDomain(''); toast('Domain added','success'); }catch(e){toast(E(e),'error');}
                }}>Add Domain</Btn>
              </div>
            }>
            {poolDomains.length===0
              ? <p className="text-[#555] py-8 text-center">No domains in pool. Add one above.</p>
              : <div className="space-y-2">
                  {poolDomains.map(d=>(
                    <div key={d.id} className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505] flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Globe size={14} className="text-[#555]"/>
                        <div>
                          <p className="text-white text-[14px] font-medium">{d.domain}</p>
                          <p className="text-[11px] text-[#555]">Source: {d.source} · Added {d.created_at?new Date(d.created_at).toLocaleDateString():'—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <B v={d.is_active?'success':'danger'}>{d.is_active?'Active':'Inactive'}</B>
                        <Btn v="ghost" onClick={async()=>{
                          try{ const r=await api.put(`/api/v1/admin/domain-pool/${d.id}/toggle`); setPoolDomains(prev=>prev.map(x=>x.id===d.id?{...x,is_active:r.data.is_active}:x)); }catch(e){toast(E(e),'error');}
                        }} className="!text-[11px] !px-3">{d.is_active?'Disable':'Enable'}</Btn>
                        <Btn v="danger" onClick={async()=>{
                          try{ await api.delete(`/api/v1/admin/domain-pool/${d.id}`); setPoolDomains(prev=>prev.filter(x=>x.id!==d.id)); toast('Removed','success'); }catch(e){toast(E(e),'error');}
                        }} className="!text-[11px] !px-3">Remove</Btn>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Panel>
        </div>
      )}

      {/* NOTIFY */}
      {tab === 'notify' && (
        <Panel title="Notify Users" sub="Send in-platform notifications">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1">
              {us.map((u) => (
                <button
                  key={u.id}
                  onClick={() => {
                    const ids = nf.user_ids.includes(u.id)
                      ? nf.user_ids.filter((i) => i !== u.id)
                      : [...nf.user_ids, u.id];
                    sNf({ ...nf, user_ids: ids });
                  }}
                  className={`px-2 py-1 rounded text-[11px] border ${
                    nf.user_ids.includes(u.id)
                      ? 'border-white text-white'
                      : 'border-[#222] text-[#666]'
                  }`}
                >
                  {u.username}
                </button>
              ))}
            </div>

            <Inp label="Title" value={nf.title} onChange={(e) => sNf({ ...nf, title: e.target.value })} />
            <Inp label="Message" value={nf.message} onChange={(e) => sNf({ ...nf, message: e.target.value })} />

            <Btn
              onClick={async () => {
                if (!nf.title || !nf.message || !nf.user_ids.length) return;
                try {
                  await api.post('/api/v1/admin/notifications', {
                    ...nf,
                    notif_type: 'info',
                  });
                  toast('Sent', 'success');
                  sNf({ user_ids: [], title: '', message: '' });
                } catch (e) {
                  toast(E(e), 'error');
                }
              }}
            >
              Send
            </Btn>
          </div>
        </Panel>
      )}
    </div>
  );
}
// ═══ LAYOUT ═══
// ═══ LAYOUT ═══
function PR({ children, user, admin = false, superadmin = false, feature = null }) {
  if (!user) return <Navigate to="/login" replace />;
  
  const isLocked = (superadmin && user.role !== 'superadmin') ||
                   (admin && !['admin', 'superadmin'].includes(user.role)) ||
                   (feature && user?.visible_features?.[feature] === false);

  if (isLocked) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-20 h-20 bg-red-950/20 rounded-3xl flex items-center justify-center text-red-500 mb-6 border border-red-900/30 shadow-[0_0_50px_-12px_rgba(239,68,68,0.2)]"
        >
          <Lock size={40} />
        </motion.div>
        <h2 className="text-[24px] font-bold text-white mb-2 uppercase tracking-tight italic">Access Restricted</h2>
        <p className="text-[#666] max-w-sm text-[14px] leading-relaxed mb-8">
          Your account does not have the required clearance to access this operational surface. 
          Please contact platform administration if you believe this is an error.
        </p>
        <button 
          onClick={() => window.location.href = '/dashboard'}
          className="px-8 py-3 bg-white text-black rounded-xl text-[13px] font-bold uppercase tracking-widest hover:bg-gray-200 transition shadow-2xl"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  return children;
}

function Layout({ user, onLogout, children, theme, setTheme }) {
  const nav = useNavigate();
  const loc = useLocation();
  const isAdm = ["admin", "superadmin"].includes(user?.role);
  const isSuperAdm = user?.role === "superadmin";
  const [nc, sNC] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const loadNotifications = () => {
      api
        .get('/api/v1/notifications')
        .then((r) => sNC((r.data || []).filter((n) => !n.is_read).length))
        .catch(() => {});
    };

    loadNotifications();
    const i = setInterval(loadNotifications, 15000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const navGroups = [
    { id: 'dashboard', label: 'Dashboard', direct: '/dashboard', paths: ['/dashboard'] },
    {
      id: 'operations',
      label: 'Operations',
      direct: '/operations',
      fkey: 'deploy', // Placeholder or use specialized key
      paths: ['/operations', '/deploy', '/feed', '/pages', '/canary'],
      items: [
        { to: '/deploy', l: 'Deploy', fkey: 'deploy' },
        { to: '/feed', l: 'Feed', fkey: 'feed' },
        { to: '/pages', l: 'Pages', fkey: 'pages' },
        { to: '/canary', l: 'Canary', fkey: 'canary' },
      ],
    },
    {
      id: 'investigations',
      label: 'Investigations',
      direct: '/investigations-hub',
      fkey: 'investigations',
      paths: ['/investigations-hub', '/investigations', '/profiles', '/graph'],
      items: [
        { to: '/investigations', l: 'Cases', fkey: 'investigations' },
        { to: '/profiles', l: 'Profiles', fkey: 'profiles' },
        { to: '/graph', l: 'Graph Intelligence', fkey: 'graph' },
      ],
    },
    {
      id: 'osint',
      label: 'OSINT',
      direct: '/osint-hub',
      fkey: 'osint',
      paths: ['/osint-hub', '/osint', '/dorking', '/exif'],
      items: [
        { to: '/osint', l: 'Workspace', fkey: 'osint' },
        { to: '/dorking', l: 'Dorking', fkey: 'dorking' },
        { to: '/exif', l: 'EXIF', fkey: 'exif' },
      ],
    },
    {
      id: 'system',
      label: 'System',
      direct: '/system',
      paths: ['/system', '/tickets', '/settings'],
      items: [
        { to: '/tickets', l: 'Support', fkey: 'support' },
        { to: '/settings', l: 'Settings', fkey: 'settings' },
      ],
    },
  ];

  if (isAdm) {
    navGroups.push({
      id: 'control',
      label: 'Control',
      direct: '/control',
      paths: ['/control', '/soc', '/admin', '/analytics'],
      items: [
        { to: '/soc', l: 'SOC', fkey: 'soc' },
        { to: '/admin', l: 'Admin', fkey: 'admin' },
        { to: '/analytics', l: 'Analytics', fkey: 'analytics' },
      ],
    });
  }

  const isGroupActive = (group) => (group.paths || [group.direct]).includes(loc.pathname);
  const currentGroupLabel = navGroups.find((group) => isGroupActive(group))?.label || 'Workspace';

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="sticky top-0 z-50 border-b border-[#1a1a1a] bg-black/88 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="flex items-center gap-2 cursor-pointer flex-shrink-0"
              onClick={() => nav('/dashboard')}
            >
              <ZIcon s={15} />
              <span className="text-[13px] font-semibold hidden sm:inline">ZureX92</span>
            </button>

            <span className="text-[11px] text-[#333] hidden sm:inline">/{user?.username}</span>
            <span className="hidden md:inline text-[11px] text-[#333]">•</span>
            <span className="hidden md:inline text-[11px] text-[#666] uppercase tracking-[0.22em]">{currentGroupLabel}</span>
            {isAdm && <B v="warning">admin</B>}

            <div className="hidden lg:flex items-center gap-1 min-w-0">
              {navGroups.map((group) => {
                const active = isGroupActive(group);
                const isLocked = group.fkey && user?.visible_features?.[group.fkey] === false;

                return (
                  <button
                    key={group.id}
                    onClick={() => !isLocked && nav(group.direct)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] whitespace-nowrap transition-all duration-500 flex items-center gap-1.5 border ${
                      isLocked 
                        ? 'text-red-950 border-transparent cursor-not-allowed' 
                        : active ? 'text-white bg-[#111] border-[#222]' : 'text-[#444] border-transparent hover:text-white'
                    }`}
                  >
                    {isLocked ? <Lock size={10} className="text-red-900/40" /> : null}
                    {group.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {!user?.phone && (
              <a href="/settings" className="relative h-9 w-9 rounded-xl border border-amber-800 bg-amber-950/30 flex items-center justify-center text-amber-400 hover:border-amber-600 transition" title="Add your phone number to avoid account suspension">
                <AlertTriangle size={14}/>
              </a>
            )}
            <ThemeToggle theme={theme} setTheme={setTheme}/>
            <button
              onClick={() => nav('/notifications')}
              className="relative h-9 w-9 rounded-xl border border-[#1a1a1a] bg-[#050505] flex items-center justify-center text-[#777] hover:text-white hover:border-[#333] transition"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {nc > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-white text-black text-[9px] font-bold rounded-full flex items-center justify-center">
                  {nc > 99 ? '99+' : nc}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden h-9 w-9 rounded-xl border border-[#1a1a1a] bg-[#050505] flex items-center justify-center text-[#777] hover:text-white hover:border-[#333] transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileOpen ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
              </svg>
            </button>

            <button onClick={onLogout} className="hidden sm:inline text-[11px] text-[#555] hover:text-white px-2 py-1">
              Logout
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-b border-[#1a1a1a] bg-black/96 backdrop-blur-xl">
          <div className="max-w-[1400px] mx-auto px-4 py-4 space-y-3">
            {navGroups.map((group) => (
              <div key={group.id} className="border border-[#141414] rounded-2xl bg-[#050505] overflow-hidden">
                <button
                  onClick={() => nav(group.direct)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left"
                >
                  <div>
                    <p className={`text-[12px] font-medium ${isGroupActive(group) ? 'text-white' : 'text-[#8a8a8a]'}`}>{group.label}</p>
                    <p className="text-[11px] text-[#4f4f4f] mt-1">Open section</p>
                  </div>
                  <span className="text-[#555]">→</span>
                </button>
                {!!group.items?.length && (
                  <div className="px-3 pb-3 grid grid-cols-2 gap-2">
                    {group.items.map((item) => {
                      const isLocked = item.fkey && user?.visible_features?.[item.fkey] === false;
                      return (
                        <button
                          key={item.to}
                          onClick={() => !isLocked && nav(item.to)}
                          className={`px-3 py-2 rounded-xl text-[12px] text-left border flex items-center justify-between transition ${
                            isLocked 
                              ? 'border-red-900/20 bg-[#050000] text-red-900/60 cursor-not-allowed' 
                              : 'border-[#181818] bg-black text-[#777] hover:text-white hover:border-[#2b2b2b]'
                          }`}
                        >
                          <span>{item.l}</span>
                          {isLocked && <Lock size={10} className="text-red-900/40" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            <button onClick={onLogout} className="w-full text-left px-4 py-3 rounded-2xl border border-[#141414] bg-[#050505] text-[12px] text-[#777] hover:text-white transition">
              Logout
            </button>
          </div>
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-4 py-6">{children}</main>
    </div>
  );
}

function DorkingPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();

  const [target, setTarget] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('general');
  const [recent, setRecent] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/api/v1/dorks');
      setUserTemplates(data);
    } catch { }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const internalLibrary = {
    general: [
      { title: 'Public Documents', q: 'site:{target} filetype:pdf OR filetype:doc OR filetype:docx' },
      { title: 'Public Spreadsheets', q: 'site:{target} filetype:xls OR filetype:xlsx OR filetype:csv' },
      { title: 'Index Listings', q: 'site:{target} intitle:"index of"' },
    ],
    panels: [
      { title: 'Admin Panels', q: 'site:{target} inurl:admin OR inurl:dashboard' },
      { title: 'Login Portals', q: 'site:{target} inurl:login OR inurl:signin' },
      { title: 'Control Panels', q: 'site:{target} inurl:cpanel OR inurl:panel' },
    ],
    apps: [
      { title: 'Jenkins', q: 'site:{target} inurl:jenkins OR intitle:jenkins' },
      { title: 'Kibana', q: 'site:{target} inurl:app/kibana' },
      { title: 'Grafana', q: 'site:{target} intitle:grafana' },
    ],
    api: [
      { title: 'API Endpoints', q: 'site:{target} inurl:api OR inurl:swagger OR inurl:graphql' },
      { title: 'OpenAPI Docs', q: 'site:{target} inurl:swagger OR inurl:openapi' },
      { title: 'GraphQL', q: 'site:{target} inurl:graphql' },
    ],
  };

  const activeLibrary = [
    ...(internalLibrary[category] || []),
    ...userTemplates.filter(t => t.category === category).map(t => ({ title: t.name, q: t.dork_pattern, id: t.id }))
  ];
  
  const totalTemplates = Object.values(internalLibrary).reduce((acc, arr) => acc + arr.length, 0) + userTemplates.length;

  const applyTemplate = (tpl) => {
    const filled = (tpl.q || tpl.dork_pattern).replaceAll('{target}', target || 'example.com');
    setQuery(filled);
    toast('Template applied', 'success');
  };

  const deleteTemplate = async (id) => {
    if (await ask('Are you sure you want to delete this custom template?')) {
      try {
        await api.delete(`/api/v1/dorks/${id}`);
        fetchTemplates();
        toast('Template deleted', 'success');
      } catch (e) { toast(E(e), 'error'); }
    }
  };

  const addTemplate = async (e) => {
    e.preventDefault();
    const d = new FormData(e.target);
    const body = {
      name: d.get('name'),
      category: d.get('category'),
      dork_pattern: d.get('pattern'),
      description: d.get('desc')
    };
    try {
      setLoading(true);
      await api.post('/api/v1/dorks', body);
      fetchTemplates();
      toast('Template saved', 'success');
      setLoading(false);
    } catch (e) {
      toast(E(e), 'error');
      setLoading(false);
    }
  };

  const openAddModal = () => {
    ask(
      <form onSubmit={addTemplate} className="space-y-4 p-2">
        <Inp name="name" label="Template Name" placeholder="e.g. AWS S3 Leaks" required />
        <div className="space-y-1">
          <label className="text-[12px] text-[#666]">Category</label>
          <select name="category" className="w-full bg-black border border-[#1a1a1a] rounded-xl px-4 py-3 text-[14px] text-white">
            <option value="general">General</option>
            <option value="panels">Panels</option>
            <option value="apps">Apps</option>
            <option value="api">API</option>
          </select>
        </div>
        <Inp name="pattern" label="Dork Pattern" placeholder="site:{target} inurl:s3" required />
        <Inp name="desc" label="Description" placeholder="Optional" />
        <Btn type="submit" className="w-full py-3" disabled={loading}>Save Template</Btn>
      </form>,
      'Create New Template'
    );
  };

  const copyQuery = async () => {
    if (!query.trim()) return toast('No query to copy', 'error');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(query);
        toast('Query copied', 'success');
        return;
      } catch (e) {}
    }
    const el = document.createElement('textarea');
    el.value = query;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    toast('Query copied', 'success');
  };

  const clearAll = () => {
    setTarget('');
    setQuery('');
  };

  const runQuery = () => {
    if (!query.trim()) return toast('Query is required', 'error');

    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    window.open(googleUrl, '_blank');
    toast('Query opened', 'success');
  };

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'panels', label: 'Panels' },
    { id: 'apps', label: 'Apps' },
    { id: 'api', label: 'API' },
  ];

  return (
    <div className="space-y-6">
      <Modal />
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">
              Dork Intelligence
            </p>
            <h1 className="text-[30px] md:text-[40px] font-semibold tracking-tight text-white leading-[1.02]">
              Search query
              <br />
              workspace and library.
            </h1>
            <p className="text-[14px] md:text-[15px] text-[#666] mt-4 max-w-[760px] leading-[1.85]">
              Build structured search queries, apply reusable templates, and keep
              your dork workspace organized from one cleaner interface.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <B v="blue">Query Builder</B>
            <B v="success">Library</B>
            <B>Workspace</B>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SC l="Templates" v={totalTemplates} />
        <SC l="Recent Queries" v={recent.length} />
        <SC l="Category" v={category} />
        <SC l="Last Run" v={lastRun ? new Date(lastRun).toLocaleTimeString() : "—"} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
        <div className="space-y-6">
          <Panel
            title="Dork Workspace"
            sub="Build and run a structured search query"
            extra={
              <div className="flex gap-2">
                <Btn v="secondary" onClick={copyQuery}>
                  Copy Query
                </Btn>
                <Btn v="ghost" onClick={clearAll}>
                  Clear
                </Btn>
              </div>
            }
          >
            <div className="space-y-5">
              <Inp
                label="Target"
                placeholder="example.com"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              />

              <div>
                <label className="block text-[12px] text-[#888] mb-2">Category</label>
                <div className="flex gap-2 flex-wrap">
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      className={`px-3 py-2 rounded-xl text-[12px] border transition ${
                        category === c.id
                          ? "border-white text-white bg-[#111]"
                          : "border-[#222] text-[#666] hover:text-white"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[12px] text-[#888] mb-2">Query</label>
                <textarea
                  className="w-full bg-black border border-[#222] px-4 py-3 text-white rounded-2xl text-[13px] h-[140px] resize-none outline-none placeholder:text-[#333] font-mono"
                  placeholder='site:example.com filetype:pdf'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <div className="border border-[#1a1a1a] rounded-2xl p-4 bg-black">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">
                  Preview
                </p>
                <p className="text-[12px] text-white font-mono break-all">
                  {query || 'No query yet'}
                </p>
              </div>

              <Btn onClick={runQuery} className="w-full py-3">
                Run Query
              </Btn>
            </div>
          </Panel>

          <Panel title="Recent Queries" sub={`${recent.length} recent records`}>
            {recent.length === 0 ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                <p className="text-[#444]">No recent queries yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recent.map((r) => (
                  <div
                    key={r.id}
                    className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div className="flex gap-2 items-center flex-wrap">
                        <B v="blue">{r.category}</B>
                        <span className="text-[13px] text-white">{r.target || 'No target'}</span>
                      </div>
                      <span className="text-[10px] text-[#444]">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>

                    <p className="text-[12px] text-[#777] font-mono break-all">
                      {r.query}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Dork Library" sub={`${activeLibrary.length} templates in this category`} extra={<Btn v="secondary" className="!text-[10px] !px-3" onClick={openAddModal}>Add Template</Btn>}>
            <div className="space-y-3">
              {activeLibrary.map((item, i) => (
                <div
                  key={i}
                  className="border border-[#1a1a1a] rounded-2xl p-4 bg-[#050505]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white text-[15px] font-medium">{item.title}</p>
                      <p className="text-[12px] text-[#666] mt-2 font-mono break-all">
                        {item.q}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      {item.id && (
                        <button type="button" onClick={() => deleteTemplate(item.id)} className="text-[12px] text-red-500/50 hover:text-red-500 transition-colors">
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => applyTemplate(item)}
                        className="text-[12px] text-[#888] hover:text-white flex-shrink-0"
                      >
                        Use
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Workspace Notes" sub="Quick usage guidance">
            <div className="space-y-3 text-[13px] text-[#777] leading-[1.8]">
              <p>
                Choose a <span className="text-white">category</span> first, then
                apply a template and customize it inside the workspace.
              </p>
              <p>
                Use the <span className="text-white">target field</span> so templates
                can automatically inject the selected domain.
              </p>
              <p>
                Keep your <span className="text-white">recent queries</span> visible
                for faster reuse across repeated workflows.
              </p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function MyDomainsPage() {
  const toast = useToast();
  const { ask, Modal } = useModal();
  const [domains, setDomains] = useState([]);
  const [cname, setCname] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/api/v1/domains/my');
      setDomains(r.data?.domains || []);
      setCname(r.data?.cname_target || '');
    } catch(e) {}
  };

  useEffect(() => { load(); }, []);

  const addDomain = async () => {
    if (!newDomain.trim()) return toast('Enter a domain', 'error');
    const domain = newDomain.toLowerCase().replace(/^https?:\/\//,'').split('/')[0].trim();
    setAdding(true);
    try {
      const r = await api.post('/api/v1/domains/my', { domain });
      setDomains(d => [...d, r.data]);
      setNewDomain('');
      toast('Domain added successfully', 'success');
    } catch(e) { toast(E(e), 'error'); }
    setAdding(false);
  };

  const verifyDomain = async (d) => {
    setVerifying(d.id);
    try {
      const r = await api.post(`/api/v1/domains/my/${d.id}/verify`);
      if (r.data.verified) {
        toast('Domain verified!', 'success');
        setDomains(prev => prev.map(x => x.id===d.id ? {...x,is_verified:true} : x));
      } else {
        toast('DNS not yet propagated. Try again in a few minutes.', 'error');
      }
    } catch(e) { toast(E(e), 'error'); }
    setVerifying(null);
  };

  const removeDomain = async (d) => {
    if (!(await ask(`Remove domain "${d.domain}"?`))) return;
    try {
      await api.delete(`/api/v1/domains/my/${d.id}`);
      setDomains(prev => prev.filter(x => x.id !== d.id));
      toast('Domain removed', 'success');
    } catch(e) { toast(E(e), 'error'); }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast('Copied', 'success'); } catch(e) {}
  };

  return (
    <div className="space-y-6">
      <Modal/>

      {/* Hero */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <button onClick={()=>nav('/system')} className="flex items-center gap-1.5 text-[#555] hover:text-white transition text-[13px] mb-4">
              <ArrowLeft size={14}/> System
            </button>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#666] mb-3">System — Domain Privacy</p>
        <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-white">Your Domains</h1>
        <p className="text-[14px] text-[#666] mt-2 max-w-[680px] leading-[1.85]">
          Connect your own domains to serve tracking links with full operational privacy. No platform branding exposed.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">

        {/* LEFT */}
        <div className="space-y-6">

          {/* Add domain */}
          <Panel title="Add Custom Domain" sub="Connect a domain you own">
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  className="flex-1 bg-[#0a0a0a] border border-[#222] px-4 py-3 text-white rounded-xl text-[14px] outline-none placeholder:text-[#333] focus:border-[#555]"
                  placeholder="yourdomain.com or sub.yourdomain.com"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addDomain()}
                />
                <Btn onClick={addDomain} disabled={adding}>
                  {adding ? 'Adding...' : <><Plus size={14}/> Add</>}
                </Btn>
              </div>
              <p className="text-[13px] text-[#555]">
                After adding, follow the DNS setup instructions to verify ownership.
              </p>
            </div>
          </Panel>

          {/* Domain list */}
          <Panel title={`Your Domains (${domains.length})`} sub="Manage connected domains">
            {domains.length === 0 ? (
              <div className="border border-dashed border-[#222] rounded-2xl py-16 text-center">
                <Globe size={32} className="mx-auto text-[#333] mb-3"/>
                <p className="text-white text-[16px] font-medium mb-2">No domains connected</p>
                <p className="text-[#555] text-[13px]">Add a domain above to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {domains.map(d => (
                  <div key={d.id} className="border border-[#1a1a1a] rounded-2xl bg-[#050505] p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${d.is_verified ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}/>
                        <div className="min-w-0">
                          <p className="text-white text-[15px] font-semibold truncate">{d.domain}</p>
                          <p className="text-[12px] text-[#555] mt-0.5">
                            Added {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                          </p>
                        </div>
                        <B v={d.is_verified ? 'success' : 'warning'}>
                          {d.is_verified ? 'Verified' : 'Pending DNS'}
                        </B>
                      </div>
                      <div className="flex gap-2">
                        {!d.is_verified && (
                          <Btn v="secondary" onClick={() => verifyDomain(d)} disabled={verifying === d.id}>
                            {verifying === d.id ? 'Checking...' : 'Verify DNS'}
                          </Btn>
                        )}
                        <Btn v="danger" onClick={() => removeDomain(d)} className="!text-[12px] !px-3">Remove</Btn>
                      </div>
                    </div>

                    {/* DNS instructions if not verified */}
                    {!d.is_verified && (
                      <div className="border border-amber-900/30 bg-amber-950/10 rounded-xl p-4 text-[13px] space-y-3">
                        <p className="text-amber-400 font-medium flex items-center gap-2">
                          <AlertTriangle size={14}/> DNS Setup Required
                        </p>
                        <div className="space-y-2">
                          <p className="text-[#888]">Add this record to your domain's DNS settings:</p>
                          <div className="bg-black border border-[#222] rounded-xl overflow-hidden">
                            <div className="grid grid-cols-3 gap-0 text-[11px] text-[#555] uppercase px-4 py-2 border-b border-[#111]">
                              <span>Type</span><span>Name</span><span>Value</span>
                            </div>
                            <div className="grid grid-cols-3 gap-0 px-4 py-3">
                              <span className="text-emerald-400 font-mono text-[12px]">CNAME</span>
                              <span className="text-white font-mono text-[12px]">@ (root)</span>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-blue-400 font-mono text-[11px] truncate">{cname || 'saqer-x2.vercel.app'}</span>
                                <button onClick={() => copyText(cname || 'saqer-x2.vercel.app')} className="flex-shrink-0">
                                  <Copy size={11} className="text-[#555] hover:text-white"/>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-[12px] text-[#666] space-y-1">
                          <p className="text-white font-medium">Step by step:</p>
                          <p>1. Go to your domain registrar (Namecheap, GoDaddy, Cloudflare, etc.)</p>
                          <p>2. Find <span className="text-white">DNS Management</span> or <span className="text-white">DNS Records</span></p>
                          <p>3. Add a new <span className="text-emerald-400">CNAME</span> record with the value above</p>
                          <p>4. Wait 5–30 minutes for propagation, then click <span className="text-white">Verify DNS</span></p>
                        </div>
                        <div className="text-[12px] text-[#555]">
                          Also add this domain in your <span className="text-white">Vercel project → Settings → Domains</span> to complete setup.
                        </div>
                      </div>
                    )}

                    {d.is_verified && (
                      <div className="border border-emerald-900/30 bg-emerald-950/10 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle size={14} className="text-emerald-400"/>
                          <p className="text-emerald-400 text-[13px] font-medium">Domain verified and ready</p>
                        </div>
                        <p className="text-[12px] text-[#666]">
                          Your links will be served as: <span className="text-white font-mono">https://{d.domain}/r/[slug]</span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <Panel title="How it works" sub="Domain privacy for operations">
            <div className="space-y-5 text-[14px] text-[#777] leading-[1.85]">
              {[
                { t: '1 — Register', d: 'Buy a domain from any registrar. Namecheap and Porkbun have great prices ($2–$15/year).' },
                { t: '2 — Add CNAME', d: 'Point the domain to the platform backend using a CNAME record in your DNS settings.' },
                { t: '3 — Add here', d: 'Enter the domain in this page. Verify it to confirm DNS is configured correctly.' },
                { t: '4 — Deploy', d: 'When deploying a link, select "Custom Domain" and pick this domain. All links use your domain.' },
              ].map(s => (
                <div key={s.t} className="border-l-2 border-[#1a1a1a] pl-4">
                  <p className="text-white font-medium mb-1">{s.t}</p>
                  <p>{s.d}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Registrar Recommendations" sub="Where to buy domains">
            <div className="space-y-2">
              {[
                { name: 'Porkbun', url: 'porkbun.com', desc: 'Cheapest prices, great UI', badge: 'Best Price' },
                { name: 'Namecheap', url: 'namecheap.com', desc: 'Reliable, good support', badge: 'Popular' },
                { name: 'Cloudflare', url: 'cloudflare.com/registrar', desc: 'At-cost pricing, privacy protection', badge: 'Privacy' },
                { name: 'GoDaddy', url: 'godaddy.com', desc: 'Wide selection, easy DNS', badge: '' },
              ].map(r => (
                <div key={r.name} className="border border-[#1a1a1a] rounded-xl p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white text-[13px] font-medium">{r.name}</p>
                    <p className="text-[11px] text-[#555]">{r.desc}</p>
                  </div>
                  {r.badge && <B v="default">{r.badge}</B>}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Quick Tips" sub="Best practices">
            <div className="space-y-2 text-[13px] text-[#666]">
              <p>Use <span className="text-white">generic domain names</span> — avoid names that sound like tracking tools.</p>
              <p>Buy domains with <span className="text-white">WHOIS privacy</span> enabled to hide your identity.</p>
              <p>Rotate domains per operation to avoid pattern detection.</p>
              <p>Use <span className="text-white">.com / .net / .org</span> TLDs — more trusted by social platforms.</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}


function DashboardPage({ user }) {
  const nav = useNavigate();
  const toast = useToast();
  const [s, sS] = useState({});
  const [editingShortcuts, setEditingShortcuts] = useState(false);
  const isMonitor = user?.role === 'monitor';
  const isAdm = ['admin','superadmin'].includes(user?.role);

  const ALL_SHORTCUTS = [
    { key:'deploy',     title:'Deploy',          desc:'Create intelligence link',     to:'/deploy',          icon:Rocket },
    { key:'feed',       title:'Feed',             desc:'Review captures',              to:'/feed',            icon:Activity },
    { key:'investigations', title:'Cases',        desc:'Manage investigations',        to:'/investigations',  icon:FolderOpen },
    { key:'profiles',   title:'Profiles',         desc:'Linked identity profiles',     to:'/profiles',        icon:User },
    { key:'osint',      title:'OSINT',            desc:'Open-source intelligence',     to:'/osint',           icon:Radar },
    { key:'graph',      title:'Graph',            desc:'Entity relationship map',      to:'/graph',           icon:Network },
    { key:'analytics',  title:'Analytics',        desc:'Platform metrics',             to:'/analytics',       icon:BarChart3 },
    { key:'canary',     title:'Canary',           desc:'Tracking tokens',              to:'/canary',          icon:Radio },
    { key:'pages',      title:'Pages',            desc:'Custom page builder',          to:'/pages',           icon:FileText },
    { key:'exif',       title:'EXIF',             desc:'Image metadata',               to:'/exif',            icon:ImageIcon },
    { key:'dorking',    title:'Dorking',          desc:'Search operators',             to:'/dorking',         icon:Search },
    { key:'soc',        title:'SOC',              desc:'Security operations',          to:'/soc',             icon:Shield },
    { key:'admin',      title:'Admin',            desc:'Platform administration',      to:'/admin',           icon:Settings },
    { key:'tickets',    title:'Support',          desc:'Help tickets',                 to:'/tickets',         icon:LifeBuoy },
    { key:'links',      title:'Links',            desc:'Manage operational links',     to:'/links',           icon:Link2 },
  ];

  const DEFAULT_SHORTCUTS = ['deploy','feed','investigations','analytics','graph','profiles'];

  const [activeShortcuts, setActiveShortcuts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('zx_shortcuts')) || DEFAULT_SHORTCUTS; }
    catch { return DEFAULT_SHORTCUTS; }
  });

  const saveShortcuts = (keys) => {
    setActiveShortcuts(keys);
    localStorage.setItem('zx_shortcuts', JSON.stringify(keys));
    setEditingShortcuts(false);
    toast('Shortcuts saved', 'success');
  };

  const toggleShortcut = (key) => {
    setActiveShortcuts(prev =>
      prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]
    );
  };

  useEffect(() => {
    api.get('/api/v1/stats').then(r => sS(r.data)).catch(() => {});
  }, []);

  const missingPhone = user && !user?.phone;

  const visibleShortcuts = ALL_SHORTCUTS.filter(s =>
    activeShortcuts.includes(s.key) &&
    (!isMonitor || s.key === 'soc') &&
    (isAdm || !['soc','admin'].includes(s.key))
  );

  const statCards = isMonitor
    ? [{label:'Captures',value:s.total||0},{label:'Critical',value:s.critical||0},{label:'Profiles',value:s.profiles||0}]
    : [{label:'Captures',value:s.total||0},{label:'Critical',value:s.critical||0,danger:true},
       {label:'Unique',value:s.unique||0},{label:'Traps',value:s.traps||0},
       {label:'Profiles',value:s.profiles||0},{label:'Users',value:s.users||0}];

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      {missingPhone && (
        <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}}
          className="border border-amber-800 bg-amber-950/40 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle size={18} className="text-amber-400 flex-shrink-0"/>
            <div>
              <p className="text-amber-400 text-[14px] font-medium">Complete your account profile</p>
              <p className="text-amber-400/70 text-[13px]">Add your phone number to avoid account suspension.</p>
            </div>
          </div>
          <a href="/settings" className="text-[13px] border border-amber-700 text-amber-400 px-3 py-1.5 rounded-lg hover:bg-amber-950 transition">Update Profile</a>
        </motion.div>
      )}

      {/* Hero */}
      <div className="border border-[#1a1a1a] rounded-[28px] bg-[#050505] p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <ZIcon s={22}/>
              <span className="text-[12px] uppercase tracking-[0.24em] text-[#666]">Control Center</span>
            </div>
            <h1 className="text-[30px] md:text-[38px] font-semibold tracking-tight text-white">Dashboard</h1>
            <p className="text-[15px] text-[#666] mt-2 max-w-[720px] leading-[1.8]">
              Operational overview — intelligence, investigations, analytics, and platform activity.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isMonitor && <Btn onClick={()=>nav('/deploy')}><Rocket size={14}/> Deploy</Btn>}
            {!isMonitor && <Btn v="secondary" onClick={()=>nav('/feed')}><Activity size={14}/> Feed</Btn>}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">
        {statCards.map((card, i) => (
          <motion.div key={card.label} initial={{opacity:0,y:18}} animate={{opacity:1,y:0}}
            transition={{duration:0.35,delay:i*0.04}}
            className={`border rounded-2xl p-5 bg-[#050505] ${card.danger?'border-red-900':'border-[#1a1a1a]'}`}>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-3">{card.label}</p>
            <p className="text-[32px] md:text-[36px] font-semibold text-white tracking-tight">{card.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        {/* Quick Access */}
        <Panel title="Quick Access"
          sub={`${visibleShortcuts.length} shortcuts`}
          extra={
            <button onClick={()=>setEditingShortcuts(v=>!v)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#222] rounded-lg text-[11px] text-[#888] hover:text-white hover:border-[#333] transition">
              <Edit3 size={12}/>
              {editingShortcuts ? 'Done' : 'Customize'}
            </button>
          }>
          {editingShortcuts ? (
            <div className="space-y-3">
              <p className="text-[13px] text-[#666]">Select the sections you want in Quick Access:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ALL_SHORTCUTS.map(s => {
                  const active = activeShortcuts.includes(s.key);
                  const Icon = s.icon;
                  return (
                    <button key={s.key} onClick={()=>toggleShortcut(s.key)}
                      className={`text-left border rounded-xl p-3 transition flex items-center gap-2 ${active?'border-white bg-[#0d0d0d]':'border-[#1a1a1a] bg-[#050505] hover:border-[#333]'}`}>
                      <Icon size={14} className={active?'text-white':'text-[#444]'}/>
                      <div>
                        <p className={`text-[12px] font-medium ${active?'text-white':'text-[#555]'}`}>{s.title}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-2 border-t border-[#111]">
                <Btn onClick={()=>saveShortcuts(activeShortcuts)}>Save</Btn>
                <Btn v="ghost" onClick={()=>{setActiveShortcuts(DEFAULT_SHORTCUTS);setEditingShortcuts(false);}}>Reset Default</Btn>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {visibleShortcuts.map((item, i) => {
                const isLocked = user?.visible_features?.[item.fkey||item.key] === false;
                const Icon = item.icon;
                return (
                  <motion.button key={item.to} initial={{opacity:0,y:18}} animate={{opacity:1,y:0}}
                    transition={{duration:0.35,delay:0.08+i*0.04}}
                    onClick={()=>!isLocked&&nav(item.to)}
                    className={`text-left border rounded-[20px] p-5 relative overflow-hidden transition-all duration-300 ${
                      isLocked
                        ? 'border-red-900/30 bg-[#080202] cursor-not-allowed'
                        : 'border-[#1f1f1f] bg-black hover:border-[#333] hover:bg-[#080808]'
                    }`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center border transition ${
                        isLocked ? 'border-red-900/30 bg-red-950/20' : 'border-[#1a1a1a] bg-[#0a0a0a] group-hover:border-[#333]'
                      }`}>
                        {isLocked ? <Lock size={14} className="text-red-500/60"/> : <Icon size={14} className="text-[#888]"/>}
                      </div>
                      {isLocked && <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"/>}
                    </div>
                    <p className={`text-[14px] font-semibold tracking-tight ${isLocked?'text-red-500/80':'text-white'}`}>{item.title}</p>
                    <p className={`text-[12px] leading-[1.6] mt-1 ${isLocked?'text-red-950':'text-[#555]'}`}>
                      {isLocked ? 'Access restricted' : item.desc}
                    </p>
                  </motion.button>
                );
              })}
            </div>
          )}
        </Panel>

        {/* Right column */}
        <div className="space-y-6">
          {/* Platform Overview */}
          <Panel title="Platform Overview" sub="Current state">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Collection</p>
                  <p className="text-[13px] text-[#777] space-y-1">
                    <span className="block"><span className="text-white">{s.traps||0}</span> active traps</span>
                    <span className="block"><span className="text-white">{s.total||0}</span> captures</span>
                    <span className="block"><span className="text-white">{s.unique||0}</span> unique sessions</span>
                  </p>
                </div>
                <div className="border border-[#1a1a1a] rounded-xl p-4 bg-black">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#555] mb-2">Intelligence</p>
                  <p className="text-[13px] text-[#777] space-y-1">
                    <span className="block"><span className="text-white">{s.profiles||0}</span> profiles</span>
                    <span className="block"><span className="text-red-400">{s.critical||0}</span> critical</span>
                    <span className="block"><span className="text-white">{s.users||0}</span> users</span>
                  </p>
                </div>
              </div>
            </div>
          </Panel>

          {/* Operational Summary */}
          <Panel title="Summary" sub="Platform snapshot">
            <div className="space-y-3 text-[14px] text-[#777] leading-[1.8]">
              <p>
                <span className="text-white">{s.total||0}</span> captures across{' '}
                <span className="text-white">{s.traps||0}</span> traps,{' '}
                <span className="text-red-400">{s.critical||0}</span> critical events.
              </p>
              <p>
                <span className="text-white">{s.profiles||0}</span> profiles,{' '}
                <span className="text-white">{s.users||0}</span> platform users.
              </p>
              {isAdm && (
                <div className="pt-2 border-t border-[#111]">
                  <div className="grid grid-cols-2 gap-2">
                    {[{t:'SOC Center',to:'/soc',icon:Shield},{t:'Admin Panel',to:'/admin',icon:Settings},{t:'Tickets',to:'/tickets',icon:LifeBuoy},{t:'Analytics',to:'/analytics',icon:BarChart3}].map(item=>{
                      const Icon = item.icon;
                      return (
                        <button key={item.to} onClick={()=>nav(item.to)}
                          className="flex items-center gap-2 border border-[#1a1a1a] rounded-xl px-3 py-2.5 bg-black text-[13px] text-white hover:border-[#2a2a2a] transition">
                          <Icon size={13} className="text-[#666]"/>
                          {item.t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Panel>
          </div>{/* end right panel */}
          </div>{/* end flex-1 overflow-hidden */}
        </div>



  );
}


function TermsPage() {
  return (
    <PW title="Terms & Conditions">
      <div className="space-y-6">
        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Use of Platform</h2>
          <p>
            ZureX92 is intended only for authorized use within approved operational, security, research,
            and investigative environments.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Access Responsibility</h2>
          <p>
            Users are responsible for maintaining account security, handling credentials safely,
            and using the platform in accordance with applicable internal policies and legal requirements.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Restrictions</h2>
          <p>
            Unauthorized misuse, disruption, abuse of access, or attempts to compromise the platform
            are prohibited and may result in suspension or termination of access.
          </p>
        </div>
      </div>
    </PW>
  );
}
function SuperAdminPage() {
  return (
    <PW title="Super Admin">
      <div className="space-y-6">
        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Super Admin Control</h2>
          <p>
            This workspace is reserved for the highest administrative level across the platform.
            It is intended for full-system oversight, privileged access control, and sensitive platform operations.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Access Scope</h2>
          <p>
            Super administrators can access elevated management surfaces, advanced platform controls,
            and higher-level administrative workflows beyond standard admin permissions.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Operational Visibility</h2>
          <p>
            This area can be extended to include user governance, sensitive audit visibility,
            privileged actions, infrastructure status, and platform-wide security controls.
          </p>
        </div>
      </div>
    </PW>
  );
}
function PrivacyPage() {
  return (
    <PW title="Privacy Policy">
      <div className="space-y-6">
        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Overview</h2>
          <p>
            ZureX92 respects user privacy and is committed to protecting operational and account data handled inside the platform.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Data Handling</h2>
          <p>
            We process only the information required for platform operation, account access, security logging, and authorized investigative workflows.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Security</h2>
          <p>
            Access controls, audit visibility, and internal safeguards are used to reduce unauthorized access and protect stored platform data.
          </p>
        </div>

        <div>
          <h2 className="text-white text-[22px] font-semibold mb-3">Contact</h2>
          <p>
            For privacy-related questions, use the official support and contact channels available through the platform.
          </p>
        </div>
      </div>
    </PW>
  );
}

function App(){
  const { theme, setTheme } = useTheme();
  const[user,sU]=useState(null);
  const[profiles,sP]=useState([]);
  const[authReady,setAuthReady]=useState(false);
  const nav=useNavigate();
  const loc=useLocation();
  const lastSync=useRef(0);

  const syncUser = useCallback(async () => {
    const t = localStorage.getItem('zx_t');
    if (!t) return;
    try {
      const { data } = await api.get('/api/v1/auth/me');
      if (data) {
        sU(data);
        localStorage.setItem('zx_u', JSON.stringify(data));
        lastSync.current = Date.now();
      }
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, []);

  useEffect(()=>{
    const t=localStorage.getItem('zx_t');
    const s=localStorage.getItem('zx_u');
    if(t&&s){
      try{
        const parsed = JSON.parse(s);
        sU(parsed);
        // Refresh immediately on load
        syncUser();
      }
      catch{localStorage.clear();}
    }
    setAuthReady(true);
  }, [syncUser]);

  // Sync on navigation (throttled to 10s)
  useEffect(() => {
    if (!user) return;
    const now = Date.now();
    if (now - lastSync.current > 10000) {
      syncUser();
    }
  }, [loc.pathname, user, syncUser]);

  useEffect(()=>{if(user)api.get('/api/v1/profiles').then(r=>sP(r.data)).catch(()=>{});},[user]);
  const auth=u=>sU(u);
  const logout=()=>{localStorage.clear();sU(null);nav('/');};
  const loadP=()=>api.get('/api/v1/profiles').then(r=>sP(r.data)).catch(()=>{});
  const W=({children,admin,feature})=><PR user={user} admin={admin} feature={feature}><Layout user={user} onLogout={logout} theme={theme} setTheme={setTheme}>{children}</Layout></PR>;

  if(!authReady){
    return <div className="min-h-screen bg-black text-white flex items-center justify-center"><div className="text-[12px] text-[#666] uppercase tracking-[0.28em]">Loading workspace</div></div>;
  }
  return <ToastProvider><Routes>
    <Route path="/" element={user?<Navigate to="/dashboard" replace/>:<Landing/>}/>
    <Route path="/login" element={user?<Navigate to="/dashboard" replace/>:<Auth mode="login" onAuth={auth}/>}/>
    <Route path="/register" element={user?<Navigate to="/dashboard" replace/>:<Auth mode="register" onAuth={auth}/>}/>
    <Route path="/privacy" element={<PrivacyPage/>}/><Route path="/terms" element={<TermsPage/>}/><Route path="/about" element={<AboutPage/>}/><Route path="/faqs" element={<FAQPage/>}/><Route path="/contact" element={<ContactPage/>}/>
    <Route path="/dashboard" element={<W><DashboardPage user={user}/></W>}/>
    <Route path="/operations" element={<W><HubPage config={HUB_SECTIONS.operations} user={user}/></W>}/>
    <Route path="/investigations-hub" element={<W><HubPage config={HUB_SECTIONS.investigations} user={user}/></W>}/>
    <Route path="/osint-hub" element={<W><HubPage config={HUB_SECTIONS.osint} user={user}/></W>}/>
    <Route path="/control" element={<W admin><HubPage config={HUB_SECTIONS.control} user={user}/></W>}/>
    <Route path="/system" element={<W><HubPage config={HUB_SECTIONS.system} user={user}/></W>}/>
        <Route path="/my-domains" element={<W><MyDomainsPage/></W>}/>
    <Route path="/deploy" element={<W feature="deploy"><DeployPage/></W>}/>
    <Route path="/feed" element={<W feature="feed"><FeedPage profiles={profiles} onRefresh={loadP}/></W>}/>
    <Route path="/analytics" element={<W feature="analytics"><AnalyticsPage/></W>}/>
    <Route path="/profiles" element={<W feature="profiles"><ProfilesPage/></W>}/>
    <Route path="/osint" element={<W feature="osint"><OsintPage profiles={profiles}/></W>}/>
    <Route path="/exif" element={<W feature="exif"><ExifPage/></W>}/>
    <Route path="/dorking" element={<W feature="dorking"><DorkingPage/></W>}/>
    <Route path="/pages" element={<W feature="pages"><PagesPage/></W>}/>
    <Route path="/canary" element={<W feature="canary"><CanaryPage/></W>}/>
    <Route path="/support" element={<W feature="support"><SupportPage user={user}/></W>} />
    <Route path="/support/chat/:tid" element={<W feature="support"><TicketChatPage user={user}/></W>} />
    <Route path="/tickets" element={<W feature="support"><SupportPage user={user}/></W>}/>
    <Route path="/tickets/chat/:tid" element={<W feature="support"><TicketChatPage user={user}/></W>} />
    <Route path="/settings" element={<W feature="settings"><SettingsPage/></W>}/>
    <Route path="/notifications" element={<W feature="notifications"><NotifsPage/></W>}/>
    <Route path="/investigations" element={<W feature="investigations"><InvestigationsPage/></W>}/>
    <Route path="/graph" element={<W feature="graph"><GraphPage/></W>}/>
    <Route path="/social" element={<W feature="social"><SocialPage/></W>}/>
    <Route path="/intel" element={<W feature="feed"><IntelSearchPage/></W>}/>
    <Route path="/campaigns" element={<W feature="analytics"><CampaignPage/></W>}/>
    <Route path="/advanced-traps" element={<W feature="deploy"><AdvancedTrapsPage/></W>}/>
    <Route path="/threat-intel" element={<W feature="feed"><ThreatIntelPage/></W>}/>
    <Route path="/domains" element={<W admin><DomainManagerPage/></W>}/>
    <Route path="/workflows" element={<W feature="feed"><WorkflowPage/></W>}/>
    <Route path="/ext-integrations" element={<W feature="feed"><IntegrationsPage/></W>}/>
    <Route path="/case-builder" element={<W feature="investigations"><CaseBuilderPage/></W>}/>
    <Route path="/adv-osint" element={<W feature="osint"><AdvancedOsintPage/></W>}/>
    <Route path="/soc" element={<W admin feature="soc"><SOCPage/></W>}/>
    <Route path="/admin" element={<W admin feature="admin"><AdminPage user={user}/></W>}/>
    <Route path="/links" element={<W feature="links"><LinksPage/></W>}/>
    <Route
  path="/superadmin"
  element={
    <PR user={user} superadmin>
      <SuperAdminPage />
    </PR>
  }
/>
    <Route path="/canary-docs" element={<CanaryDocsPage/>}/><Route path="/pricing" element={<PricingPage/>}/><Route path="/blog" element={<BlogPage/>}/><Route path="/page/:slug" element={<DynamicPage/>}/>
    <Route path="*" element={<Navigate to={user?"/dashboard":"/"} replace/>}/>
  </Routes></ToastProvider>;}
export default App;